import DashboardLayout from '../../Layouts/DashboardLayout';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import logger from '../../../utils/logger';
import { saveReviewsSnapshot, saveBusinessProfileSnapshot, getYourData, saveCompetitorTrackingSnapshot, getCompetitorInsights } from '../../../utils/yourDataStore';
import CriticalTasksService from '../../../services/CriticalTasksService';
import { analyzeCompetitorCategories } from '../../../utils/categoryAnalysis';
import ProfileStrengthTaskService from '../../../services/ProfileStrengthTaskService';
import CompetitorAnalysisService from '../../../services/CompetitorAnalysisService';
import HeatmapDataService from '../../../services/HeatmapDataService';
import { checkCooldown, getCompetitorCooldownDays, getCompetitorGmbsLimit } from '../../../services/SubscriptionService';
import { usePermissions } from '../../../contexts/PermissionContext';
import { useAccessGate } from '../../../contexts/AccessGateContext';
import { useProfilePlan } from '../../../contexts/ProfilePlanContext';
import { getSession } from '../../../utils/authUtils';
import UpgradeToProModal from '../../UpgradeToProModal/UpgradeToProModal';
import TrialOfferSnackbar from '../../TrialOfferSnackbar/TrialOfferSnackbar';
import cacheManager from '../../../utils/cacheManager';
import { loadGoogleMapsApi } from '../../../utils/mapsLoader';
import { Box, Stack, Typography, Button, Paper, Rating, LinearProgress, Chip, CircularProgress, Alert, Snackbar, Menu, MenuItem, ListItemText, ListItemIcon, Divider, Modal, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Input, Badge, Avatar, Autocomplete, Checkbox, FormControlLabel, Select, FormControl, InputLabel, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import CompetitorAnalysisWithPopup from '../../competitors/CompetitorAnalysisWithPopup';
import HeatmapMap from '../../Heatmap/HeatmapMap';
import { generateGridPoints } from '../../../heatmap/lib/geolocation';
import SchedulePostModal from '../components/SchedulePostModal';
import VideoUploadModal from '../components/VideoUploadModal';
import GMBService from '../../../services/GMBService';
import ApiService from '../../../services/api';
import GMBNotificationService from '../../../services/GMBNotificationService';
import * as OpenAI from '../../../services/gmbRanking/geminiService';
import LocationIcon from '../../icons/LocationIcon';
import VerifiedCheckIcon from '../../icons/VerifiedCheckIcon';
import StarRatingIcon from '../../icons/StarRatingIcon';
import AddPhotoIcon from '../../icons/AddPhotoIcon';
import VideoIcon from '../../icons/VideoIcon';
import WebsiteGlobeIcon from '../../icons/WebsiteGlobeIcon';
import DirectionsIcon from '../../icons/DirectionsIcon';
import ShareIcon from '../../icons/ShareIcon';
import FacebookIcon from '../../icons/FacebookIcon';
import TwitterIcon from '../../icons/TwitterIcon';
import WhatsAppIcon from '../../icons/WhatsAppIcon';
import QRCodeIcon from '../../icons/QRCodeIcon';
import SwitchAccountIcon from '../../icons/SwitchAccountIcon';
import ProfileGaugeIcon from '../../icons/ProfileGaugeIcon';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ProfileWithPlanBadge from '../../ProfileWithPlanBadge';
import ImageUploadService from '../../../services/ImageUploadService';

// Save business to localStorage for heatmap and other features
const saveSelectedBusiness = (locationData, accountName) => {
  if (locationData) {
    const businessToSave = {
      ...locationData,
      accountName: accountName,
      name: locationData.name,
      title: locationData.title,
      latlng: locationData.latlng,
      storefrontAddress: locationData.storefrontAddress,
      // Explicitly include categories for keyword suggestions (used by HeatmapPage, ProfileStrengthPage, etc.)
      categories: locationData.categories
    };
    localStorage.setItem('selectedBusiness', JSON.stringify(businessToSave));
    logger.log('[BusinessProfile] Saved business to localStorage:', businessToSave);
    logger.log('[BusinessProfile] Business categories:', locationData.categories);
  }
};

// Styled Components
const PageContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#F8F8F8',
  minHeight: '100vh',
  padding: '0'
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  padding: '0 40px 40px 40px',
  [theme.breakpoints.down('md')]: {
    padding: '0 24px 24px 24px'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '0 16px 16px 16px'
  }
}));

const PageHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: '36px',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    gap: '16px',
    marginBottom: '24px'
  }
}));

const HeaderLeft = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px'
}));

const HeaderTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '28px',
  fontWeight: 600,
  color: '#0B91D6',
  margin: 0,
  [theme.breakpoints.down('md')]: {
    fontSize: '24px'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '20px'
  }
}));

const HeaderSubtitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  color: '#30302E',
  margin: 0,
  [theme.breakpoints.down('sm')]: {
    fontSize: '12px'
  }
}));

const HeaderLink = styled(Button)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 600,
  color: '#0B91D6',
  backgroundColor: 'transparent',
  border: '1px solid #0B91D6',
  textTransform: 'none',
  padding: '8px 16px',
  borderRadius: '8px',
  transition: 'all 0.2s ease',
  margin: '0 auto',
  display: 'block',
  width: 'fit-content',
  '&:hover': {
    backgroundColor: '#0B91D6',
    color: '#ffffff',
    borderColor: '#0B91D6'
  }
}));

const SwitchAccountButton = styled(Button)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  width: "150px",
  height: "45px",
  padding: '12px 16px',
  borderRadius: '4px',
  border: '0.6px solid #A0A0AA',
  backgroundColor: 'rgba(160, 160, 170, 0.10)',
  color: '#121927',
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 400,
  textTransform: 'none',
  '&:hover': {
    backgroundColor: 'rgba(160, 160, 170, 0.15)'
  },
  [theme.breakpoints.down('sm')]: {
    width: '120px',
    height: '40px',
    padding: '8px 12px',
    fontSize: '11px',
    gap: '8px'
  }
}));

const MainGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 320px',
  gap: '36px',
  marginBottom: '26px',
  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: '1fr',
    gap: '24px'
  }
}));

const BusinessProfileCard = styled(Paper)(({ theme }) => ({
  borderRadius: '12px',
  border: '0.6px solid #F6F0F0',
  padding: '40px',
  backgroundColor: '#ffffff',
  boxShadow: 'none',
  [theme.breakpoints.down('md')]: {
    padding: '24px'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '16px'
  }
}));

const BusinessTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Poppins, Inter, sans-serif',
  fontSize: '22px',
  fontWeight: 600,
  color: '#121927',
  margin: 0,
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flexWrap: 'wrap',
  letterSpacing: '-0.2px',
  lineHeight: '1.3',
  [theme.breakpoints.down('sm')]: {
    fontSize: '16px',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '6px'
  }
}));

const VerifiedBadge = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 400,
  color: '#0B91D6'
}));

const EditProfileLink = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '16px',
  fontWeight: 600,
  color: '#0B91D6',
  cursor: 'pointer',
  textDecoration: 'underline',
  whiteSpace: 'nowrap',
  padding: '8px 16px',
  borderRadius: '6px',
  backgroundColor: 'rgba(11, 145, 214, 0.08)',
  border: '1px solid rgba(11, 145, 214, 0.2)',
  transition: 'all 0.2s ease',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  [theme.breakpoints.down('sm')]: {
    fontSize: '14px',
    marginTop: '4px',
    padding: '6px 12px'
  },
  '&:hover': {
    backgroundColor: 'rgba(11, 145, 214, 0.15)',
    borderColor: 'rgba(11, 145, 214, 0.3)',
    textDecoration: 'underline'
  }
}));

const AddressText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  color: '#121927',
  margin: '8px 0 0 0',
  display: 'flex',
  alignItems: 'flex-start',
  gap: '8px'
}));

const ReviewsRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '5px',
  marginTop: '8px'
}));

const ReviewsLabel = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  color: '#121927'
}));

const ReviewsRating = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  color: '#121927'
}));

const ContactGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '10px',
  marginTop: '10px',
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: 'repeat(2, 1fr)'
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
    gap: '16px'
  }
}));

const ContactItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '10px'
}));

const ContactLabel = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  color: '#121927'
}));

const ContactValue = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  color: '#0B91D6'
}));

const HoursText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  color: '#121927',
  marginTop: '10px'
}));

const HoursValue = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  color: '#121927',
  marginTop: '10px'
}));

const PhotosSection = styled(Box)(({ theme }) => ({
  marginTop: '18px'
}));

const PhotosTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '16px',
  fontWeight: 600,
  color: '#121927',
  marginBottom: '18px'
}));

const PhotosGrid = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '18px',
  alignItems: 'center',
  flexWrap: 'wrap',
  [theme.breakpoints.down('sm')]: {
    gap: '12px'
  }
}));

const CoverPhotoContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '200px',
  borderRadius: '12px',
  overflow: 'hidden',
  marginBottom: '18px',
  position: 'relative',
  cursor: 'pointer',
  '&:hover': {
    opacity: 0.9
  },
  [theme.breakpoints.down('sm')]: {
    height: '150px'
  }
}));

const CoverPhotoLabel = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '8px',
  left: '8px',
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  color: '#ffffff',
  padding: '4px 8px',
  borderRadius: '4px',
  fontSize: '12px',
  fontWeight: 500,
  fontFamily: 'Inter, sans-serif'
}));

const PhotoItem = styled(Box)(({ theme }) => ({
  width: '120px',
  height: '133px',
  borderRadius: '12px',
  overflow: 'hidden',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  [theme.breakpoints.down('sm')]: {
    width: '100px',
    height: '110px'
  }
}));

const AddPhotoButton = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '23px',
  width: '120px',
  height: '133px',
  cursor: 'pointer'
}));

const AddPhotoText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 400,
  color: '#0B91D6'
}));

const VideosSection = styled(Box)(({ theme }) => ({
  marginTop: '18px'
}));

const VideosTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '16px',
  fontWeight: 600,
  color: '#121927',
  marginBottom: '18px'
}));

const VideosGrid = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '18px',
  alignItems: 'center',
  flexWrap: 'wrap',
  [theme.breakpoints.down('sm')]: {
    gap: '12px'
  }
}));

const VideoItem = styled(Box)(({ theme }) => ({
  width: '120px',
  height: '133px',
  borderRadius: '12px',
  overflow: 'hidden',
  position: 'relative',
  cursor: 'pointer',
  backgroundColor: '#f3f4f6',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': {
    backgroundColor: '#e5e7eb'
  },
  [theme.breakpoints.down('sm')]: {
    width: '100px',
    height: '110px'
  }
}));

const VideoDuration = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: '8px',
  right: '8px',
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  color: '#ffffff',
  padding: '2px 6px',
  borderRadius: '4px',
  fontSize: '10px',
  fontWeight: 500,
  fontFamily: 'Inter, sans-serif'
}));

const VideoThumbnail = styled('img')(({ theme }) => ({
  width: '100%',
  height: '100%',
  objectFit: 'cover'
}));

const VideoPlayButton = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#ffffff',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    transform: 'translate(-50%, -50%) scale(1.1)'
  }
}));

const AddVideoButton = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '23px',
  width: '120px',
  height: '133px',
  cursor: 'pointer',
  border: '2px dashed #d1d5db',
  borderRadius: '12px',
  '&:hover': {
    borderColor: '#0B91D6',
    backgroundColor: '#f8f9fa'
  }
}));

const AddVideoText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 400,
  color: '#0B91D6'
}));

const ActionsGrid = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '16px',
  marginTop: '18px',
  flexWrap: 'nowrap',
  overflowX: 'auto',
  [theme.breakpoints.down('sm')]: {
    gap: '12px',
    marginTop: '12px'
  }
}));

const ActionButton = styled(Button)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '8px',
  padding: '12px 16px',
  borderRadius: '4px',
  border: '0.6px solid #F6F0F0',
  backgroundColor: '#ffffff',
  color: '#121927',
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 600,
  textTransform: 'none',
  minWidth: '80px',
  '&:hover': {
    backgroundColor: '#f5f5f5'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '8px 12px',
    fontSize: '12px',
    minWidth: '70px'
  }
}));

const ActionSubtext = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '10px',
  fontWeight: 400,
  color: '#0B91D6',
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('sm')]: {
    maxWidth: '80px',
    fontSize: '9px'
  }
}));

const AppointmentsSection = styled(Box)(({ theme }) => ({
  marginTop: '10px'
}));

const AppointmentsLabel = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  color: '#121927'
}));

const AppointmentsValue = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  color: '#121927',
  marginTop: '10px'
}));

// Competitor Ranking Section Styles
const CompetitorRankingSection = styled(Box)(({ theme }) => ({
  marginTop: '24px',
  padding: '20px',
  backgroundColor: '#ffffff',
  borderRadius: '8px',
  border: '1px solid #e5e7eb'
}));

const CompetitorRankingTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '16px',
  fontWeight: 600,
  color: '#121927',
  marginBottom: '16px'
}));

const CompetitorCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 16px',
  backgroundColor: '#f8f9fa',
  borderRadius: '6px',
  marginBottom: '8px',
  border: '1px solid #e5e7eb',
  transition: 'all 0.2s ease',
  '&:last-child': {
    marginBottom: 0
  }
}));

const CompetitorInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '2px'
}));

const CompetitorName = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  color: '#121927'
}));

const CompetitorScore = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 400,
  color: '#6b7280'
}));

const RankBadge = styled(Box)(({ theme, rank }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  backgroundColor: rank === 1 ? '#34A853' : rank <= 3 ? '#f59e0b' : rank <= 5 ? '#0B91D6' : '#ef4444',
  color: '#ffffff',
  fontSize: '14px',
  fontWeight: 600
}));

// Products and Services Section Styles
const ProductsServicesSection = styled(Box)(({ theme }) => ({
  marginTop: '24px',
  padding: '20px',
  backgroundColor: '#ffffff',
  borderRadius: '8px',
  border: '1px solid #e5e7eb'
}));

const ProductsServicesTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '18px',
  fontWeight: 600,
  color: '#121927',
  marginBottom: '16px'
}));

const TabsContainer = styled(Box)(({ theme }) => ({
  borderBottom: '1px solid #e5e7eb',
  marginBottom: '20px'
}));

const TabButton = styled(Button)(({ theme, active }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  color: active ? '#0B91D6' : '#6b7280',
  textTransform: 'none',
  padding: '8px 16px',
  marginRight: '24px',
  borderBottom: active ? '2px solid #0B91D6' : '2px solid transparent',
  borderRadius: 0,
  minWidth: 'auto',
  '&:hover': {
    backgroundColor: 'transparent',
    color: '#0B91D6'
  }
}));

const ProductsGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: '16px',
  marginTop: '16px',
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
    gap: '12px'
  }
}));

const ProductCard = styled(Box)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderRadius: '8px',
  border: '1px solid #e5e7eb',
  overflow: 'hidden',
  transition: 'box-shadow 0.2s ease',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
  }
}));

const ProductImage = styled('img')(({ theme }) => ({
  width: '100%',
  height: '120px',
  objectFit: 'cover',
  backgroundColor: '#f3f4f6'
}));

const ProductContent = styled(Box)(({ theme }) => ({
  padding: '12px'
}));

const ProductTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  color: '#121927',
  marginBottom: '4px'
}));

const ProductPrice = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 600,
  color: '#0B91D6'
}));

const ProductsViewAllButton = styled(Button)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  color: '#0B91D6',
  textTransform: 'none',
  padding: '8px 16px',
  border: '1px solid #0B91D6',
  borderRadius: '6px',
  backgroundColor: 'transparent',
  marginTop: '16px',
  alignSelf: 'flex-start',
  '&:hover': {
    backgroundColor: '#0B91D6',
    color: '#ffffff'
  }
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '16px'
}));

// Modal Styles
const ModalContainer = styled(Modal)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px'
}));

const ModalContent = styled(Box)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderRadius: '12px',
  padding: '24px',
  maxWidth: '800px',
  width: '100%',
  maxHeight: '80vh',
  overflow: 'auto',
  outline: 'none',
  boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  [theme.breakpoints.down('sm')]: {
    padding: '16px',
    maxHeight: '90vh',
    maxWidth: '95vw'
  }
}));

const ModalHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '24px',
  paddingBottom: '16px',
  borderBottom: '1px solid #e5e7eb'
}));

const ModalTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '20px',
  fontWeight: 600,
  color: '#121927'
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  color: '#6b7280',
  '&:hover': {
    backgroundColor: '#f3f4f6',
    color: '#121927'
  }
}));

const ModalProductsGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
  gap: '20px',
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
    gap: '16px'
  }
}));

const ProfileStrengthCard = styled(Paper)(({ theme }) => ({
  borderRadius: '12px',
  border: '0.6px solid #F6F0F0',
  padding: '32px 20px',
  backgroundColor: '#ffffff',
  boxShadow: 'none',
  width: '100%',
  maxWidth: '320px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  [theme.breakpoints.down('lg')]: {
    maxWidth: '100%'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '24px 16px'
  }
}));

const StrengthTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '26px',
  fontWeight: 600,
  color: '#30302E',
  textAlign: 'center',
  [theme.breakpoints.down('sm')]: {
    fontSize: '22px'
  }
}));

const GaugeContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '8px'
}));

const ScoreText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '24px',
  fontWeight: 700,
  color: '#000000',
  textAlign: 'center',
  marginTop: '8px'
}));

const ActionButtonsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  width: '100%'
}));

const BoostButton = styled(Button)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 600,
  color: '#ffffff',
  backgroundColor: '#0B91D6',
  borderRadius: '4px',
  padding: '12px 16px',
  textTransform: 'capitalize',
  '&:hover': {
    backgroundColor: '#0980c2'
  }
}));

const ScoreButton = styled(Button)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 600,
  color: '#0B91D6',
  backgroundColor: 'transparent',
  border: 'none',
  padding: '0',
  textTransform: 'capitalize',
  textDecoration: 'none',
  '&:hover': {
    backgroundColor: 'transparent',
    textDecoration: 'underline'
  }
}));

const CompetitorButton = styled(Button)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 600,
  color: '#34A853',
  backgroundColor: 'transparent',
  border: 'none',
  padding: '0',
  textTransform: 'capitalize',
  textDecoration: 'underline',
  '&:hover': {
    backgroundColor: 'transparent'
  }
}));

const AddCampaignButton = styled(Button)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 600,
  color: '#0B91D6',
  border: '1px solid #0B91D6',
  borderRadius: '6px',
  backgroundColor: '#ffffff',
  padding: '10px 16px',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: 'rgba(11, 145, 214, 0.08)'
  }
}));

const SchedulePostButton = styled(Button)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 600,
  color: '#ffffff',
  backgroundColor: '#34A853',
  borderRadius: '4px',
  padding: '12px 16px',
  textTransform: 'capitalize',
  '&:hover': {
    backgroundColor: '#2D8E47'
  }
}));

// Google Calendar Styles
const CalendarCard = styled(Paper)(({ theme }) => ({
  borderRadius: '12px',
  border: '0.6px solid #F6F0F0',
  padding: '20px',
  backgroundColor: '#ffffff',
  boxShadow: 'none',
  marginTop: '26px',
  width: '100%',
  maxWidth: '320px',
  [theme.breakpoints.down('lg')]: {
    maxWidth: '100%'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '16px'
  }
}));

const CalendarTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '16px',
  fontWeight: 600,
  color: '#121927',
  marginBottom: '16px'
}));

const CalendarContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '380px',
  border: '1px solid #E5E7EB',
  borderRadius: '8px',
  overflow: 'hidden',
  backgroundColor: '#ffffff'
}));

const CalendarHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 12px',
  backgroundColor: '#F8F9FA',
  borderBottom: '1px solid #E5E7EB'
}));

const CalendarMonth = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 600,
  color: '#121927'
}));

const CalendarNavButton = styled(IconButton)(({ theme }) => ({
  width: '28px',
  height: '28px',
  color: '#0B91D6',
  '&:hover': {
    backgroundColor: 'rgba(11, 145, 214, 0.1)'
  }
}));

const CalendarGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  height: 'calc(100% - 41px)'
}));

const CalendarDayHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '6px 4px',
  backgroundColor: '#F8F9FA',
  borderBottom: '1px solid #E5E7EB',
  fontSize: '10px',
  fontWeight: 500,
  color: '#6B7280'
}));

const CalendarDay = styled(Box)(({ theme, isToday, hasEvent, isOtherMonth }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '4px',
  fontSize: '12px',
  fontWeight: isToday ? 600 : 400,
  color: isOtherMonth ? '#D1D5DB' : isToday ? '#ffffff' : '#121927',
  backgroundColor: isToday ? '#0B91D6' : hasEvent ? '#FEF3C7' : 'transparent',
  borderBottom: '1px solid #F3F4F6',
  borderRight: '1px solid #F3F4F6',
  cursor: hasEvent ? 'pointer' : 'default',
  position: 'relative',
  minHeight: '28px',
  '&:hover': {
    backgroundColor: isToday ? '#0980C2' : hasEvent ? '#FDE68A' : '#F9FAFB'
  },
  '&:last-child': {
    borderRight: 'none'
  }
}));

const EventDot = styled(Box)(({ theme, isScheduledPost }) => ({
  position: 'absolute',
  bottom: '4px',
  left: '50%',
  transform: 'translateX(-50%)',
  width: isScheduledPost ? '6px' : '4px',
  height: isScheduledPost ? '6px' : '4px',
  borderRadius: '50%',
  backgroundColor: isScheduledPost ? '#34A853' : '#0B91D6',
  boxShadow: isScheduledPost ? '0 0 4px rgba(52, 168, 83, 0.5)' : 'none'
}));

const CalendarActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  gap: '12px',
  marginTop: '12px'
}));

const CalendarActionButton = styled(Button)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 500,
  textTransform: 'none',
  borderRadius: '8px',
  padding: '8px 16px',
  minWidth: 'auto',
  backgroundColor: 'transparent',
  color: '#0B91D6',
  border: '1px solid #0B91D6',
  width: '100%',
  flex: 1,
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#0B91D6',
    color: '#ffffff',
    borderColor: '#0B91D6'
  }
}));

// Add Event Modal Styles
const EventModalContainer = styled(Modal)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px'
}));

const EventModalContent = styled(Box)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderRadius: '12px',
  padding: '24px',
  maxWidth: '500px',
  width: '100%',
  maxHeight: '80vh',
  overflow: 'auto',
  outline: 'none',
  boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
}));

const EventModalHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '24px',
  paddingBottom: '16px',
  borderBottom: '1px solid #e5e7eb'
}));

const EventModalTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '20px',
  fontWeight: 600,
  color: '#121927'
}));

const EventTypeTab = styled(Button)(({ theme, active }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  color: active ? '#0B91D6' : '#6b7280',
  textTransform: 'none',
  padding: '8px 16px',
  marginRight: '8px',
  borderBottom: active ? '2px solid #0B91D6' : '2px solid transparent',
  borderRadius: 0,
  minWidth: 'auto',
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: 'transparent',
    color: '#0B91D6'
  }
}));

const EventFormContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '20px'
}));

const EventDescriptionField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    fontFamily: 'Inter, sans-serif',
    fontSize: '14px',
    borderRadius: '8px',
    '& fieldset': {
      borderColor: '#e5e7eb'
    },
    '&:hover fieldset': {
      borderColor: '#0B91D6'
    },
    '&.Mui-focused fieldset': {
      borderColor: '#0B91D6'
    }
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'Inter, sans-serif',
    fontSize: '14px',
    color: '#6b7280'
  }
}));

const EventImageUploadArea = styled(Box)(({ theme }) => ({
  border: '2px dashed #d1d5db',
  borderRadius: '8px',
  padding: '40px 20px',
  textAlign: 'center',
  backgroundColor: '#f9fafb',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: '#0B91D6',
    backgroundColor: '#f0f9ff'
  }
}));

const EventImageUploadText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  color: '#6b7280',
  marginBottom: '8px'
}));

const EventImageUploadButton = styled(Button)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  color: '#0B91D6',
  backgroundColor: 'transparent',
  border: '1px solid #0B91D6',
  borderRadius: '6px',
  padding: '8px 16px',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: 'rgba(11, 145, 214, 0.1)',
    borderColor: '#0980c2'
  }
}));

// Full Calendar Modal Styles
const FullCalendarModal = styled(Modal)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px'
}));

const FullCalendarContent = styled(Box)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderRadius: '12px',
  padding: '24px',
  maxWidth: '800px',
  width: '100%',
  maxHeight: '90vh',
  overflow: 'auto',
  outline: 'none',
  boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
}));

const FullCalendarHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '24px',
  paddingBottom: '16px',
  borderBottom: '1px solid #e5e7eb'
}));

const FullCalendarTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '24px',
  fontWeight: 600,
  color: '#121927'
}));

const FullCalendarContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '500px',
  border: '1px solid #E5E7EB',
  borderRadius: '8px',
  overflow: 'hidden',
  backgroundColor: '#ffffff'
}));

const FullCalendarGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  height: 'calc(100% - 41px)'
}));

const FullCalendarDay = styled(Box)(({ theme, isToday, hasEvent, isOtherMonth }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '8px',
  fontSize: '14px',
  fontWeight: isToday ? 600 : 400,
  color: isOtherMonth ? '#D1D5DB' : isToday ? '#ffffff' : '#121927',
  backgroundColor: isToday ? '#0B91D6' : hasEvent ? '#FEF3C7' : 'transparent',
  borderBottom: '1px solid #F3F4F6',
  borderRight: '1px solid #F3F4F6',
  cursor: hasEvent ? 'pointer' : 'default',
  position: 'relative',
  minHeight: '60px',
  '&:hover': {
    backgroundColor: isToday ? '#0980C2' : hasEvent ? '#FDE68A' : '#F9FAFB'
  },
  '&:last-child': {
    borderRight: 'none'
  }
}));


const EventPostButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#0B91D6',
  color: 'white',
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  textTransform: 'none',
  borderRadius: '6px',
  padding: '10px 24px',
  alignSelf: 'flex-end',
  '&:hover': {
    backgroundColor: '#0980c2'
  },
  '&:disabled': {
    backgroundColor: '#9ca3af',
    color: '#ffffff'
  }
}));

const ActionCenterCard = styled(Paper)(({ theme }) => ({
  borderRadius: '12px',
  padding: '26px 40px',
  backgroundColor: '#ffffff',
  boxShadow: 'none',
  marginBottom: '26px',
  [theme.breakpoints.down('md')]: {
    padding: '20px 24px'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '16px'
  }
}));

// Competitor Analysis Modal - Now using separate component

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '16px',
  fontWeight: 600,
  color: '#121927',
  marginBottom: '21px'
}));

const TasksGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(5, 1fr)',
  gap: '21px',
  marginBottom: '21px',
  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: 'repeat(3, 1fr)'
  },
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px'
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
    gap: '12px'
  }
}));

const TaskCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '10px',
  padding: '12px',
  border: '1px solid #f0f0f0',
  borderRadius: '8px'
}));

const TaskLabel = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 500,
  color: '#121927',
  textAlign: 'center'
}));

const TaskStatus = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  textAlign: 'center'
}));

const ViewAllButton = styled(Button)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 600,
  color: '#ffffff',
  backgroundColor: '#0B91D6',
  borderRadius: '4px',
  padding: '8px 16px',
  textTransform: 'capitalize',
  margin: '0 auto',
  display: 'block',
  '&:hover': {
    backgroundColor: '#0980c2'
  }
}));

const PerformanceGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '16px',
  marginBottom: '16px',
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
    gap: '12px'
  }
}));

const PerformanceCard = styled(Paper)(({ theme }) => ({
  borderRadius: '12px',
  padding: '26px 40px',
  backgroundColor: '#ffffff',
  boxShadow: 'none',
  [theme.breakpoints.down('md')]: {
    padding: '20px 24px'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '16px'
  }
}));

const MetricsGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: '14px',
  marginBottom: '12px',
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
    gap: '10px'
  }
}));

const MetricCard = styled(Box)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderRadius: '12px',
  padding: '16px',
  border: '1px solid #f0f0f0'
}));

const MetricLabel = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 600,
  color: '#121927',
  marginBottom: '14px'
}));

const MetricValue = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '24px',
  fontWeight: 600,
  color: '#ffffff',
  marginBottom: '18px'
}));

const MetricChange = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  color: '#ffffff',
  display: 'flex',
  alignItems: 'center',
  gap: '5px'
}));

const FeedSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '11.22px'
}));

const FeedItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '5.28px',
  paddingBottom: '11.22px',
  borderBottom: '0.33px solid #F6F0F0'
}));

const FeedHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '5.28px'
}));

const FeedTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 600,
  color: '#121927'
}));

const FeedDescription = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 400,
  color: '#121927',
  lineHeight: '18px',
  whiteSpace: 'pre-line',
  wordBreak: 'break-word',
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
}));

const ViewDetailsButton = styled(Button)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 600,
  color: '#ffffff',
  backgroundColor: '#0B91D6',
  borderRadius: '4px',
  padding: '8px 16px',
  textTransform: 'capitalize',
  alignSelf: 'flex-start',
  '&:hover': {
    backgroundColor: '#0980c2'
  }
}));

const ReviewsCard = styled(Paper)(({ theme }) => ({
  borderRadius: '24px',
  padding: '32px',
  backgroundColor: '#ffffff',
  boxShadow: '0 30px 60px rgba(15, 23, 42, 0.08)',
  border: '1px solid #e2e8f0',
  marginBottom: '32px',
  overflow: 'hidden',
  [theme.breakpoints.down('md')]: {
    padding: '24px'
  }
}));

const SectionSubtitle = styled(Typography)(() => ({
  fontSize: '13px',
  fontWeight: 500,
  color: '#64748b',
  marginTop: '4px'
}));

const ReviewsHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '24px',
  marginBottom: '28px'
}));

const ScorePanel = styled(Box)(() => ({
  flex: '1 1 280px',
  minWidth: '260px',
  background: 'linear-gradient(135deg, #0B91D6 0%, #0B91D6 45%, #0B91D6 100%)',
  color: '#ffffff',
  borderRadius: '18px',
  padding: '28px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: "''",
    position: 'absolute',
    width: '170px',
    height: '170px',
    borderRadius: '50%',
    background: 'rgba(255, 255, 255, 0.18)',
    filter: 'blur(0.5px)',
    bottom: '-60px',
    right: '-40px'
  }
}));

const ScoreValue = styled(Typography)(() => ({
  fontSize: '56px',
  fontWeight: 700,
  lineHeight: 1,
  fontFamily: 'Inter, sans-serif'
}));

const ScoreSubtitle = styled(Typography)(() => ({
  fontSize: '14px',
  fontWeight: 500,
  color: 'rgba(255,255,255,0.82)'
}));

const ScoreMetaRow = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  flexWrap: 'wrap'
}));

const TrendChip = styled(Box)(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '6px 12px',
  borderRadius: '999px',
  backgroundColor: 'rgba(255,255,255,0.16)',
  fontSize: '12px',
  fontWeight: 600,
  color: '#ffffff'
}));

const ReviewsSummaryGrid = styled(Box)(({ theme }) => ({
  flex: '1 1 320px',
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
  gap: '16px'
}));

const SummaryCard = styled(Box)(() => ({
  borderRadius: '16px',
  border: '1px solid #e2e8f0',
  padding: '16px 18px',
  backgroundColor: '#f8fafc',
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  minHeight: '100%'
}));

const SummaryValue = styled(Typography)(() => ({
  fontSize: '26px',
  fontWeight: 700,
  color: '#0f172a',
  fontFamily: 'Inter, sans-serif'
}));

const SummaryLabel = styled(Typography)(() => ({
  fontSize: '12px',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: '#64748b',
  fontWeight: 600
}));

const SummaryDelta = styled(Typography)(() => ({
  fontSize: '12px',
  fontWeight: 600,
  color: '#0B91D6'
}));

const ReviewsBody = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'minmax(260px, 320px) minmax(0, 1fr)',
  gap: '32px',
  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: '1fr'
  }
}));

const DistributionPanel = styled(Box)(() => ({
  borderRadius: '18px',
  border: '1px solid #e2e8f0',
  backgroundColor: '#f8fafc',
  padding: '20px',
  display: 'flex',
  flexDirection: 'column',
  gap: '18px'
}));

const PanelHeader = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
}));

const PanelTitle = styled(Typography)(() => ({
  fontSize: '15px',
  fontWeight: 600,
  color: '#0f172a'
}));

const PanelSubtitle = styled(Typography)(() => ({
  fontSize: '12px',
  color: '#64748b'
}));

const DistributionList = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px'
}));

const RatingRow = styled(Box)(() => ({
  display: 'grid',
  gridTemplateColumns: '64px 1fr 80px',
  alignItems: 'center',
  gap: '12px'
}));

const RatingLabel = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '13px',
  fontWeight: 600,
  color: '#1f2937'
}));

const RatingBar = styled(Box)(() => ({
  position: 'relative',
  width: '100%',
  height: '10px',
  borderRadius: '999px',
  backgroundColor: '#e2e8f0',
  overflow: 'hidden'
}));

const RatingFill = styled(Box)(({ ownerState }) => {
  const gradients = {
    5: 'linear-gradient(90deg, #22C55E 0%, #16A34A 100%)',
    4: 'linear-gradient(90deg, #4ADE80 0%, #22C55E 100%)',
    3: 'linear-gradient(90deg, #FACC15 0%, #F59E0B 100%)',
    2: 'linear-gradient(90deg, #FB923C 0%, #F97316 100%)',
    1: 'linear-gradient(90deg, #F87171 0%, #EF4444 100%)'
  };
  const percentage = Math.max(0, Math.min(ownerState?.percentage ?? 0, 100));
  const rating = ownerState?.rating ?? 5;
  return {
    position: 'absolute',
    inset: 0,
    width: `${percentage}%`,
    borderRadius: 'inherit',
    background: gradients[rating] || gradients[5],
    transition: 'width 0.4s ease'
  };
});

const RatingMeta = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '6px'
}));

const RatingCount = styled(Typography)(() => ({
  fontSize: '12px',
  color: '#475569',
  fontWeight: 500
}));

const RatingPercent = styled(Typography)(() => ({
  fontSize: '11px',
  fontWeight: 600,
  color: '#0B91D6'
}));

const SentimentGrid = styled(Box)(() => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
  gap: '12px'
}));

const SentimentCard = styled(Box)(({ ownerState }) => ({
  borderRadius: '12px',
  padding: '12px 14px',
  backgroundColor: ownerState?.background || '#ffffff',
  border: '1px solid #e2e8f0',
  display: 'flex',
  flexDirection: 'column',
  gap: '4px'
}));

const SentimentLabel = styled(Typography)(() => ({
  fontSize: '11px',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: '#64748b',
  fontWeight: 600
}));

const SentimentValue = styled(Typography)(({ ownerState }) => ({
  fontSize: '18px',
  fontWeight: 700,
  color: ownerState?.color || '#0f172a',
  fontFamily: 'Inter, sans-serif'
}));

const SentimentSubtext = styled(Typography)(() => ({
  fontSize: '12px',
  color: '#64748b'
}));

const ReviewsContentPanel = styled(Box)(() => ({
  borderRadius: '18px',
  border: '1px solid #e2e8f0',
  padding: '20px',
  backgroundColor: '#ffffff',
  display: 'flex',
  flexDirection: 'column',
  gap: '20px'
}));

const ReviewsFilterBar = styled(Box)(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px'
}));

const FilterChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'active'
})(({ active }) => ({
  borderRadius: '999px',
  fontWeight: 600,
  fontSize: '12px',
  letterSpacing: '0.01em',
  padding: '2px 6px',
  backgroundColor: active ? '#0B91D6' : '#f1f5f9',
  color: active ? '#ffffff' : '#0f172a',
  border: active ? '1px solid #0B91D6' : '1px solid transparent',
  '&:hover': {
    backgroundColor: active ? '#0A7BBB' : '#e2e8f0'
  }
}));

const ReviewsList = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '14px'
}));

const ReviewItem = styled(Box)(() => ({
  display: 'flex',
  gap: '14px',
  padding: '16px',
  borderRadius: '14px',
  border: '1px solid #e2e8f0',
  backgroundColor: '#f8fafc',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 12px 24px rgba(15, 23, 42, 0.08)'
  }
}));

const ReviewerAvatar = styled(Box)(() => ({
  width: '44px',
  height: '44px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #0B91D6 0%, #0B91D6 100%)',
  color: '#ffffff',
  fontWeight: 700,
  fontSize: '16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  textTransform: 'uppercase'
}));

const ReviewContent = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  minWidth: 0
}));

const ReviewHeaderRow = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flexWrap: 'wrap'
}));

const ReviewerName = styled(Typography)(() => ({
  fontSize: '14px',
  fontWeight: 600,
  color: '#0f172a',
  flex: '1 1 auto',
  fontFamily: 'Inter, sans-serif'
}));

const ReviewMeta = styled(Typography)(() => ({
  fontSize: '12px',
  color: '#94a3b8'
}));

const ReviewTag = styled(Box)(({ ownerState }) => ({
  padding: '4px 8px',
  borderRadius: '999px',
  fontSize: '11px',
  fontWeight: 600,
  lineHeight: 1,
  backgroundColor: ownerState?.variant === 'outline' ? 'transparent' : ownerState?.background || '#e2e8f0',
  color: ownerState?.color || '#0f172a',
  border: ownerState?.variant === 'outline' ? `1px solid ${ownerState?.color || '#cbd5f5'}` : 'none'
}));

const ReviewComment = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'muted'
})(({ muted }) => ({
  fontSize: '13px',
  color: muted ? '#94a3b8' : '#475569',
  lineHeight: 1.5,
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden'
}));

const ReviewMetaRow = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  flexWrap: 'wrap'
}));

const EmptyReviewsState = styled(Box)(() => ({
  padding: '36px 24px',
  borderRadius: '16px',
  border: '1px dashed #d1d5db',
  textAlign: 'center',
  color: '#64748b',
  backgroundColor: '#f8fafc'
}));

const ReviewsFooter = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '12px',
  flexWrap: 'wrap'
}));

const SecondaryActionText = styled(Typography)(() => ({
  fontSize: '12px',
  color: '#64748b'
}));

const REVIEW_FILTERS = [
  { value: 'all', label: 'All Reviews' },
  { value: 5, label: '5★' },
  { value: 4, label: '4★' },
  { value: 3, label: '3★' },
  { value: 2, label: '2★' },
  { value: 1, label: '1★' },
  { value: 'withComment', label: 'With Comments' }
];
const FeedCard = styled(Paper)(({ theme }) => ({
  borderRadius: '12px',
  padding: '26px 40px',
  backgroundColor: '#ffffff',
  boxShadow: 'none',
  marginBottom: '26px'
}));

const FeedGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '19px',
  marginBottom: '19px',
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px'
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
    gap: '12px'
  }
}));

const FeedCardItem = styled(Box)(({ theme }) => ({
  borderRadius: '14px 14px 0px 0px',
  overflow: 'hidden',
  backgroundColor: '#ffffff',
  cursor: 'pointer',
  transition: 'box-shadow 0.2s ease, transform 0.1s ease',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
  },
  '&:active': {
    transform: 'scale(0.99)'
  }
}));

const FeedImage = styled('img')(({ theme }) => ({
  width: '100%',
  height: '200px',
  objectFit: 'cover'
}));

// Images displayed inside modals should be fully visible and keep aspect ratio
const ModalImage = styled('img')(({ theme }) => ({
  display: 'block',
  width: '100%',
  height: 'auto',
  maxHeight: '65vh',
  objectFit: 'contain',
  backgroundColor: '#f8f9fa',
  borderRadius: '8px'
}));

const FeedContent = styled(Box)(({ theme }) => ({
  padding: '12px 16px'
}));

const FeedDate = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 400,
  color: '#A0A0AA',
  marginBottom: '8px'
}));

const FeedText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 400,
  color: '#121927',
  lineHeight: '18px'
}));

// Mock data
const mockTasks = [
  { label: 'Filler text is', status: '60% completed', color: '#34A853' },
  { label: 'Filler text is', status: '23% completed', color: '#EF232A' },
  { label: 'Filler text is', status: 'Not Started Yet', color: '#A0A0AA' },
  { label: 'Filler text is', status: '100% completed', color: '#34A853' },
  { label: 'Filler text is', status: '23% completed', color: '#EF232A' }
];

// Function to format numbers with commas
const formatNumber = (num) => {
  if (num === null || num === undefined || isNaN(num)) return '0';
  return num.toLocaleString();
};

// Function to get metrics data (real or fallback)
const getMetricsData = (performanceData) => {
  if (!performanceData) {
    // Fallback mock data if API fails
    return [
      { label: 'Local Views (Last 30 days)', value: '2,300', color: '#34A853' },
      { label: 'Calls from GBP (Last 30 days)', value: '156', color: '#34A853' },
      { label: 'Direction Requests (Last 30 days)', value: '89', color: '#34A853' },
      { label: 'Website Clicks (Last 30 days)', value: '234', color: '#34A853' }
    ];
  }

  return [
    {
      label: 'Local Views (Last 30 days)',
      value: formatNumber(performanceData.localViews),
      color: '#34A853'
    },
    {
      label: 'Calls from GBP (Last 30 days)',
      value: formatNumber(performanceData.callClicks),
      color: '#34A853'
    },
    {
      label: 'Direction Requests (Last 30 days)',
      value: formatNumber(performanceData.directionRequests),
      color: '#34A853'
    },
    {
      label: 'Website Clicks (Last 30 days)',
      value: formatNumber(performanceData.websiteClicks),
      color: '#34A853'
    }
  ];
};

const mockFeedItems = [
  { icon: 'check', title: 'Profile changes saved!', description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua' },
  { icon: 'photo', title: 'New photos have been added to your GMB profile.', description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua' },
  { icon: 'review', title: 'New review received.', description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua' },
  { icon: 'review-red', title: 'New review received.', description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqu' }
];

// Replaced by real GMB media fetched from API

// Image helpers: try proxy when available to avoid CORS/hotlink issues
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
const buildProxyUrl = (src) => {
  if (!API_BASE_URL) return null;
  const base = API_BASE_URL.replace(/\/$/, '');
  // Assuming backend has /proxy/image?url=
  return `${base}/proxy/image?url=${encodeURIComponent(src)}`;
};

const placeholderSmall = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjEzMyIgdmlld0JveD0iMCAwIDEyMCAxMzMiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMjAiIGhlaWdodD0iMTMzIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik00MCA0MEg4MFY5M0g0MFY0MFoiIGZpbGw9IiM5Q0EzQUYiLz4KPC9zdmc+Cg==';
const placeholderLarge = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik04MCA2MEgxMjBWMTQwSDgwVjYwWiIgZmlsbD0iIzlDQTNBRiIvPgo8L3N2Zz4K';

const mockSocialFeed = [
  { image: '/images/social-feed-1.jpg', date: 'Posted on - 26 june 2025', text: 'Filler text is text that shares some characteristics of a real written text, but is random or otherwise generated. It may be used to display a sample of fonts.' },
  { image: '/images/social-feed-2.png', date: 'Posted on - 26 june 2025', text: 'Filler text is text that shares some characteristics of a real written text, but is random or otherwise generated. It may be used to display a sample of fonts.' },
  { image: '/images/social-feed-3.png', date: 'Posted on - 26 june 2025', text: 'Filler text is text that shares some characteristics of a real written text, but is random or otherwise generated. It may be used to display a sample of fonts.' }
];

const BusinessProfile = () => {
  const navigate = useNavigate();
  const [locationData, setLocationData] = useState(null);
  const [reviewsData, setReviewsData] = useState(null);
  const [mediaItems, setMediaItems] = useState([]);
  const [performanceData, setPerformanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [availableProfiles, setAvailableProfiles] = useState([]);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  // Photo popup state
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null); // { original, proxy }

  // Video state
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [videos, setVideos] = useState([]);
  const [videosRefreshing, setVideosRefreshing] = useState(false);
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoUploadError, setVideoUploadError] = useState('');
  const [videoUploadSuccess, setVideoUploadSuccess] = useState(false);

  // (Campaign modal removed) -- direct redirect to analysis will be used instead

  // AI Auto-Reply Dialog state
  const [aiReplyDialogOpen, setAiReplyDialogOpen] = useState(false);
  const [aiReplySelectedTime, setAiReplySelectedTime] = useState('Immediately');
  const [aiReplySelectedRatings, setAiReplySelectedRatings] = useState(['5', '4', '3']);
  const [aiReplySaving, setAiReplySaving] = useState(false);

  const [currentAccount, setCurrentAccount] = useState(null);
  const [verificationState, setVerificationState] = useState({
    loading: true,
    error: null,
    simplified: 'unknown',
    raw: null,
    isVerified: false,
  });

  // State for fresh newReviewUri fetched from Google API
  const [freshNewReviewUri, setFreshNewReviewUri] = useState(null);

  // Get locationId from URL params or route params
  const params = useParams();
  const [searchParams] = useSearchParams();
  const locationId = params.id || searchParams.get('id');

  // State for managing expanded views
  // Handlers for photo modal
  const handlePhotoClick = (originalSrc, proxied) => {
    if (!originalSrc && !proxied) return;
    setSelectedPhoto({ original: originalSrc || '', proxy: proxied || '' });
    setPhotoModalOpen(true);
  };

  const closePhotoModal = () => {
    setPhotoModalOpen(false);
    setSelectedPhoto(null);
  };

  // Helper function to parse ISO 8601 duration (PT1M30S) or seconds to MM:SS format
  const parseDuration = (duration) => {
    if (!duration || duration === '00:00' || duration === '0:00') {
      return null; // Don't show duration if it's 0
    }

    // If already in MM:SS format, return it
    if (/^\d{1,2}:\d{2}$/.test(duration)) {
      return duration;
    }

    // Parse ISO 8601 duration format (PT1M30S)
    if (typeof duration === 'string' && duration.startsWith('PT')) {
      const matches = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
      if (matches) {
        const hours = parseInt(matches[1] || 0);
        const minutes = parseInt(matches[2] || 0);
        const seconds = parseInt(matches[3] || 0);

        const totalMinutes = hours * 60 + minutes;
        if (totalMinutes > 0 || seconds > 0) {
          return `${totalMinutes}:${seconds.toString().padStart(2, '0')}`;
        }
      }
    }

    // Parse if it's in seconds (number or string)
    const secondsNum = parseFloat(duration);
    if (!isNaN(secondsNum) && secondsNum > 0) {
      const minutes = Math.floor(secondsNum / 60);
      const seconds = Math.floor(secondsNum % 60);
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }

    return null;
  };

  // Video handlers
  const handleVideoClick = (video) => {
    setSelectedVideo(video);
    setVideoModalOpen(true);
  };

  const closeVideoModal = () => {
    setVideoModalOpen(false);
    setSelectedVideo(null);
  };

  const handleVideoUpload = () => {
    setVideoUploadOpen(true);
  };

  // AI Auto-Reply handlers
  const handleAiReplyRatingToggle = (rating) => {
    setAiReplySelectedRatings(prev =>
      prev.includes(rating)
        ? prev.filter(r => r !== rating)
        : [...prev, rating]
    );
  };

  const handleAiReplySave = async () => {
    try {
      setAiReplySaving(true);

      const account = JSON.parse(localStorage.getItem('selectedGMBAccount') || 'null');
      const location = JSON.parse(localStorage.getItem('selectedGMBLocation') || 'null');

      if (!account?.name || !location?.name) {
        throw new Error('Please select a GMB account and location first');
      }

      // Save auto-reply settings to backend
      await ApiService.post('/customer/auto-reply/settings', {
        accountName: account.name,
        locationName: location.name,
        locationId: location.name?.split('/').pop() || location.id,
        enabled: true,
        triggerTime: aiReplySelectedTime,
        ratings: aiReplySelectedRatings,
        tone: 'professional',
        businessName: localStorage.getItem('businessName') || account.accountName,
        businessCategory: localStorage.getItem('businessCategory') || 'business'
      });

      // Save to localStorage for UI state
      localStorage.setItem('autoReplySettings', JSON.stringify({
        enabled: true,
        triggerTime: aiReplySelectedTime,
        ratings: aiReplySelectedRatings
      }));

      setAiReplyDialogOpen(false);
      alert('AI Auto-Reply activated successfully! AI will automatically reply to new reviews based on your settings.');
    } catch (error) {
      console.error('Error saving AI reply settings:', error);
      alert(error.message || 'Failed to save settings. Please try again.');
    } finally {
      setAiReplySaving(false);
    }
  };

  // GMB single post handlers
  const handleOpenGmbPost = (post) => {
    setSelectedGmbPost(post);
    setGmbPostModalOpen(true);
  };

  const handleCloseGmbPost = () => {
    setGmbPostModalOpen(false);
    setSelectedGmbPost(null);
  };

  // Videos modal handlers
  const handleOpenVideosModal = () => {
    setVideosModalOpen(true);
  };

  const handleCloseVideosModal = () => {
    setVideosModalOpen(false);
  };

  const uploadVideoToGMB = async (file) => {
    try {
      setVideoUploading(true);
      setVideoUploadError('');

      const accessToken = await GMBService.getAccessToken();
      const accountId = currentAccount?.name?.split('/').pop();

      if (!accountId || !locationId) {
        throw new Error('Missing account or location information');
      }

      // Validate file size (100MB limit)
      if (file.size > 75 * 1024 * 1024) {
        throw new Error('Video file size must be less than 75MB');
      }

      // Validate file type
      const allowedTypes = ['video/mp4', 'video/mov', 'video/avi', 'video/quicktime'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error('Please select a valid video file (MP4, MOV, or AVI)');
      }

      logger.log('Uploading video to GMB:', file.name, file.size, file.type);

      // Upload video to GMB using the service
      const result = await GMBService.uploadVideo(accessToken, accountId, locationId, file);

      logger.log('Video uploaded successfully:', result);

      // Refresh videos list from GMB
      await refreshVideos();

      setVideoUploadSuccess(true);
      setVideoUploadOpen(false);

      // Clear success message after 3 seconds
      setTimeout(() => setVideoUploadSuccess(false), 3000);

      return result;
    } catch (error) {
      logger.error('Error uploading video:', error);
      setVideoUploadError(error.message);
      throw error;
    } finally {
      setVideoUploading(false);
    }
  };

  const refreshVideos = useCallback(async () => {
    try {
      setVideosRefreshing(true);
      const accessToken = await GMBService.getAccessToken();
      const accountId = currentAccount?.name?.split('/').pop();

      if (!accountId || !locationId) {
        logger.warn('Cannot refresh videos: missing account or location');
        return;
      }

      const updatedVideos = await GMBService.getMedia(accessToken, accountId, locationId);
      const videos = updatedVideos.filter(i => i.mediaFormat === 'VIDEO' && (i.googleUrl || i.thumbnailUrl || i.sourceUrl));

      const processedVideos = videos.map(video => ({
        id: video.name?.split('/').pop() || Math.random().toString(),
        name: video.description || 'Business Video',
        url: video.googleUrl || video.sourceUrl || video.thumbnailUrl,
        thumbnail: video.thumbnailUrl || video.googleUrl || video.sourceUrl,
        duration: parseDuration(video.duration),
        uploadDate: video.createTime || new Date().toISOString()
      }));

      setVideos(processedVideos);
      logger.log('Videos refreshed:', processedVideos.length);
    } catch (error) {
      logger.error('Error refreshing videos:', error);
    } finally {
      setVideosRefreshing(false);
    }
  }, [currentAccount, locationId]);

  const [showAllTasks, setShowAllTasks] = useState(false);
  const [showPerformanceDetails, setShowPerformanceDetails] = useState(false);
  const [showFeedDetails, setShowFeedDetails] = useState(false);
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [reviewFilter, setReviewFilter] = useState('all');
  const [showAllGMBFeed, setShowAllGMBFeed] = useState(false);
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [photoUploadOpen, setPhotoUploadOpen] = useState(false);
  const [selectedPhotoFile, setSelectedPhotoFile] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoUploadError, setPhotoUploadError] = useState('');
  const [photoUploadSuccess, setPhotoUploadSuccess] = useState(false);
  const [videoUploadOpen, setVideoUploadOpen] = useState(false);
  const [selectedVideoFile, setSelectedVideoFile] = useState(null);
  const [showAllSocialFeed, setShowAllSocialFeed] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [newNotificationCount, setNewNotificationCount] = useState(0);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [localPosts, setLocalPosts] = useState([]);
  const [localPostsLoading, setLocalPostsLoading] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [qrProviderIndex, setQrProviderIndex] = useState(0);
  const [shareAnchorEl, setShareAnchorEl] = useState(null);
  const [activeTab, setActiveTab] = useState('services');
  const [productsModalOpen, setProductsModalOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [servicesLoading, setServicesLoading] = useState(false);

  // Service Edit/Add Modal States
  const [serviceEditModalOpen, setServiceEditModalOpen] = useState(false);
  const [serviceAddModalOpen, setServiceAddModalOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceSaving, setServiceSaving] = useState(false);
  const [serviceFormData, setServiceFormData] = useState({
    name: '',
    description: '',
    category: '',
    categoryId: '',
    serviceTypeId: ''
  });
  const [serviceCategoryOptions, setServiceCategoryOptions] = useState([]);
  const [serviceSuggestions, setServiceSuggestions] = useState([]);
  const [serviceSuggestionsLoading, setServiceSuggestionsLoading] = useState(false);
  useEffect(() => {
    let canceled = false;
    const fetchCategoryOptions = async () => {
      const primary = locationData?.categories?.primaryCategory;
      const additionals = Array.isArray(locationData?.categories?.additionalCategories)
        ? locationData.categories.additionalCategories
        : [];

      const options = [];
      const addOption = (category) => {
        const id = category?.categoryId || category?.name;
        if (!id) return;
        const label = category.displayName || category.name || id;
        if (!options.some(option => option.id === id)) {
          options.push({ id, label });
        }
      };

      addOption(primary);
      additionals.forEach(addOption);

      if (!options.length && primary?.displayName) {
        try {
          const accessToken = localStorage.getItem('googleAccessToken') || sessionStorage.getItem('googleAccessToken');
          const matchingCategories = await GMBService.searchCategoriesWithServices(
            primary.displayName,
            'US',
            'en',
            accessToken
          );
          matchingCategories.forEach(cat => addOption({
            categoryId: cat.categoryId,
            displayName: cat.displayName
          }));
        } catch (error) {
          logger.warn('[BusinessProfile] Failed to enrich category options:', error);
        }
      }

      if (!canceled) {
        setServiceCategoryOptions(options);
      }
    };

    fetchCategoryOptions();
    return () => {
      canceled = true;
    };
  }, [locationData]);

  useEffect(() => {
    if (!serviceFormData.categoryId && serviceFormData.category) {
      const target = serviceFormData.category.toLowerCase().trim();
      const match = serviceCategoryOptions.find(
        option =>
          (option.label?.toLowerCase().trim() === target) ||
          (option.id?.toLowerCase().trim() === target)
      );
      if (match) {
        setServiceFormData(prev => ({ ...prev, categoryId: match.id }));
      }
    }
  }, [serviceCategoryOptions, serviceFormData.category, serviceFormData.categoryId]);

  const loadServiceSuggestions = useCallback(async () => {
    const normalizedCategoryId =
      serviceFormData.categoryId?.trim() ||
      serviceCategoryOptions.find(option => {
        const needle = serviceFormData.category?.toLowerCase().trim();
        return (
          !!needle &&
          ((option.label?.toLowerCase() || '').trim() === needle ||
            (option.id?.toLowerCase() || '').trim() === needle)
        );
      })?.id ||
      locationData?.categories?.primaryCategory?.categoryId ||
      serviceCategoryOptions[0]?.id ||
      '';
    if (!normalizedCategoryId || !locationId) {
      setServiceSuggestions([]);
      return;
    }

    setServiceSuggestionsLoading(true);
    try {
      const regionCode = locationData?.storefrontAddress?.regionCode || locationData?.regionCode || 'US';
      const languageCode = locationData?.languageCode || 'en';
      const accessToken = localStorage.getItem('googleAccessToken') || sessionStorage.getItem('googleAccessToken');
      const suggestions = await GMBService.getStructuredServiceTypes(
        normalizedCategoryId,
        regionCode,
        languageCode,
        accessToken
      );
      setServiceSuggestions(
        Array.isArray(suggestions)
          ? suggestions.slice(0, 12)
          : []
      );
    } catch (error) {
      logger.warn('[BusinessProfile] Failed to load service suggestions:', error);
      setServiceSuggestions([]);
    } finally {
      setServiceSuggestionsLoading(false);
    }
  }, [
    locationData,
    locationId,
    serviceCategoryOptions,
    serviceFormData.category,
    serviceFormData.categoryId
  ]);

  useEffect(() => {
    loadServiceSuggestions();
  }, [loadServiceSuggestions]);

  const fetchCategorySuggestions = useCallback(async (searchTerm) => {
    if (!searchTerm) {
      setCategorySuggestions([]);
      return;
    }
    const requestId = ++categoryRequestId.current;
    setCategorySuggestionLoading(true);
    try {
      const accessToken = localStorage.getItem('googleAccessToken') || sessionStorage.getItem('googleAccessToken');
      const regionCode = locationData?.storefrontAddress?.regionCode || locationData?.regionCode || 'US';
      const languageCode = locationData?.languageCode || 'en';
      const response = await GMBService.searchCategoriesWithServices(searchTerm, regionCode, languageCode, accessToken);
      if (categoryRequestId.current !== requestId) {
        return;
      }
      const options = response
        .map(cat => ({
          id: cat.categoryId,
          label: cat.displayName
        }))
        .filter(option => option.id || option.label);
      setCategorySuggestions(options);
    } catch (error) {
      logger.warn('[BusinessProfile] Error fetching category suggestions:', error);
    } finally {
      if (categoryRequestId.current === requestId) {
        setCategorySuggestionLoading(false);
      }
    }
  }, [locationData]);

  // Appointment Link Edit/Add Modal States
  const [appointmentEditModalOpen, setAppointmentEditModalOpen] = useState(false);
  const [appointmentAddModalOpen, setAppointmentAddModalOpen] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [appointmentSaving, setAppointmentSaving] = useState(false);
  const [appointmentFormData, setAppointmentFormData] = useState({
    linkType: 'APPOINTMENT',
    url: '',
    label: '',
    description: ''
  });

  // GMB Post Edit Modal States
  const [postEditModalOpen, setPostEditModalOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [postSaving, setPostSaving] = useState(false);
  const [postFormData, setPostFormData] = useState({
    summary: '',
    callToAction: {
      actionType: 'LEARN_MORE',
      url: ''
    },
    event: {
      title: '',
      schedule: {
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: ''
      }
    },
    offer: {
      couponCode: '',
      redeemOnlineUrl: '',
      termsConditions: ''
    }
  });
  const [performanceModalOpen, setPerformanceModalOpen] = useState(false);
  const [reviewsModalOpen, setReviewsModalOpen] = useState(false);
  const [modalReplyTexts, setModalReplyTexts] = useState({}); // Store reply texts for each review
  const [submittingModalReply, setSubmittingModalReply] = useState({}); // Track submission state per review
  const [gmbFeedModalOpen, setGmbFeedModalOpen] = useState(false);
  const [socialFeedModalOpen, setSocialFeedModalOpen] = useState(false);
  const [gmbPostModalOpen, setGmbPostModalOpen] = useState(false);
  const [selectedGmbPost, setSelectedGmbPost] = useState(null);
  // Photos and Videos modals
  const [photosModalOpen, setPhotosModalOpen] = useState(false);
  const [videosModalOpen, setVideosModalOpen] = useState(false);
  const [deletingMediaId, setDeletingMediaId] = useState(null);
  const [serviceDetailsModalOpen, setServiceDetailsModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [serviceDetailsLoading, setServiceDetailsLoading] = useState(false);
  // Map picker
  const [mapPickerOpen, setMapPickerOpen] = useState(false);
  const [additionalCategories, setAdditionalCategories] = useState([]);
  const [primaryCategorySelection, setPrimaryCategorySelection] = useState(null);
  const [primaryCategoryInputValue, setPrimaryCategoryInputValue] = useState('');
  const [categorySuggestions, setCategorySuggestions] = useState([]);
  const [categorySuggestionLoading, setCategorySuggestionLoading] = useState(false);
  const [categorySearchText, setCategorySearchText] = useState('');
  const categorySuggestionTimer = useRef(null);
  const categoryRequestId = useRef(0);
  const [categorySubmitting, setCategorySubmitting] = useState(false);
  const [categorySubmitError, setCategorySubmitError] = useState('');
  const [categorySubmitSuccess, setCategorySubmitSuccess] = useState(false);
  const [editStatus, setEditStatus] = useState(null);
  const deriveEditStatus = useCallback((loc) => {
    if (!loc) return null;
    const pendingReview =
      loc.locationState?.pendingReview ||
      loc.metadata?.pendingReview ||
      loc.pendingReview;
    if (!pendingReview) return null;
    const normalize = (value) => String(value || '').toLowerCase();
    const stateValue = normalize(pendingReview.state || pendingReview.reviewState || pendingReview.status);
    const detailText = pendingReview.comment || pendingReview.reason || pendingReview.description || '';
    const typeName = pendingReview.type ? String(pendingReview.type).replace(/_/g, ' ') : 'Profile';
    if (stateValue.includes('pending')) {
      return {
        type: 'pending',
        message: `${typeName} edits are pending review with Google.`,
        detail: detailText
      };
    }
    if (stateValue.includes('reject')) {
      return {
        type: 'rejected',
        message: `${typeName} edits were rejected by Google.`,
        detail: detailText
      };
    }
    return null;
  }, []);
  const categoryOptions = useMemo(() => {
    const map = new Map();
    const addOption = (option) => {
      if (!option) return;
      const key = option.id || option.label;
      if (!key) return;
      if (!map.has(key)) {
        map.set(key, { id: option.id, label: option.label });
      }
    };

    categorySuggestions.forEach(addOption);
    addOption(primaryCategorySelection);
    additionalCategories.forEach(entry => addOption(entry.selected));

    return Array.from(map.values());
  }, [additionalCategories, categorySuggestions, primaryCategorySelection]);

  useEffect(() => {
    if (categorySuggestionTimer.current) {
      clearTimeout(categorySuggestionTimer.current);
      categorySuggestionTimer.current = null;
    }

    const trimmedText = (categorySearchText || '').trim();
    if (trimmedText.length < 2) {
      setCategorySuggestions([]);
      setCategorySuggestionLoading(false);
      return;
    }

    categorySuggestionTimer.current = setTimeout(() => {
      categorySuggestionTimer.current = null;
      fetchCategorySuggestions(trimmedText);
    }, 400);

    return () => {
      if (categorySuggestionTimer.current) {
        clearTimeout(categorySuggestionTimer.current);
        categorySuggestionTimer.current = null;
      }
    };
  }, [categorySearchText, fetchCategorySuggestions]);
  const buildCategoryPayload = useCallback(() => {
    const existingPrimaryCategoryId =
      locationData?.categories?.primaryCategory?.categoryId ||
      locationData?.categories?.primaryCategory?.name ||
      '';
    const selectedPrimaryCategoryId = (primaryCategorySelection?.id || '').trim();
    const selectedPrimaryCategoryLabel = primaryCategorySelection?.label?.trim() || '';
    const primaryChanged = Boolean(
      selectedPrimaryCategoryId && selectedPrimaryCategoryId !== existingPrimaryCategoryId
    );

    const existingAdditionalCategoryIds = (locationData?.categories?.additionalCategories || [])
      .map(cat => (cat?.categoryId || cat?.name || '').trim())
      .filter(Boolean);
    const selectedAdditionalCategories = additionalCategories.filter(entry => entry.selected?.id);
    const newAdditionalCategoryIds = selectedAdditionalCategories.map(entry => entry.selected.id.trim());
    const additionalsChanged =
      newAdditionalCategoryIds.length !== existingAdditionalCategoryIds.length ||
      newAdditionalCategoryIds.some((id, index) => id !== existingAdditionalCategoryIds[index]);

    if (!primaryChanged && !additionalsChanged) {
      return null;
    }

    const payload = {};
    const primaryPayloadId = selectedPrimaryCategoryId || existingPrimaryCategoryId;
    const primaryPayloadLabel =
      selectedPrimaryCategoryLabel || locationData?.categories?.primaryCategory?.displayName || '';

    if (primaryPayloadId) {
      payload.primaryCategory = {
        name: primaryPayloadId
      };
      if (primaryPayloadLabel) {
        payload.primaryCategory.displayName = primaryPayloadLabel;
      }
    }

    if (additionalsChanged) {
      payload.additionalCategories =
        newAdditionalCategoryIds.length > 0
          ? newAdditionalCategoryIds.map((id, index) => ({
            name: id,
            displayName: selectedAdditionalCategories[index]?.selected?.label || ''
          }))
          : [];
    } else if (locationData?.categories?.additionalCategories?.length) {
      payload.additionalCategories = locationData.categories.additionalCategories.map(cat => ({
        name: cat.categoryId || cat.name,
        displayName: cat.displayName || cat.name
      }));
    }

    if (Object.keys(payload).length === 0) {
      return null;
    }

    return payload;
  }, [additionalCategories, locationData, primaryCategorySelection]);
  const categoryPayload = useMemo(() => buildCategoryPayload(), [buildCategoryPayload]);
  const [pickedLatLng, setPickedLatLng] = useState(null);
  const [leafletReady, setLeafletReady] = useState(false);
  const [reverseGeocoding, setReverseGeocoding] = useState(false);
  const [updatingProfile, setUpdatingProfile] = useState(false);
  const [addressUpdated, setAddressUpdated] = useState(false);
  const [hasFormChanges, setHasFormChanges] = useState(false);
  const [initialFormValues, setInitialFormValues] = useState({});
  const [changedFields, setChangedFields] = useState({}); // Track which fields have changed
  const [generatingAIDescription, setGeneratingAIDescription] = useState(false);
  const [phoneNumbers, setPhoneNumbers] = useState([{ id: 1, value: '' }]); // Array of phone numbers
  const [whatsappPhoneNumber, setWhatsappPhoneNumber] = useState(''); // WhatsApp phone number
  const [serviceAreas, setServiceAreas] = useState([{ id: 1, value: '', placeId: '' }]); // Array of service areas with placeId
  const [serviceAreaBusinessType, setServiceAreaBusinessType] = useState('CUSTOMER_AND_BUSINESS_LOCATION'); // Service area business type
  const [serviceAreaSuggestions, setServiceAreaSuggestions] = useState({}); // Suggestions for each service area
  const [loadingSuggestions, setLoadingSuggestions] = useState({}); // Loading state for each service area
  const debounceTimeout = React.useRef(null); // Debounce helper for autocomplete

  // Regular Hours State - each day can have multiple time periods
  const [regularHours, setRegularHours] = useState({
    monday: { isOpen: false, periods: [{ id: 1, openTime: '09:00', closeTime: '17:00' }] },
    tuesday: { isOpen: false, periods: [{ id: 1, openTime: '09:00', closeTime: '17:00' }] },
    wednesday: { isOpen: false, periods: [{ id: 1, openTime: '09:00', closeTime: '17:00' }] },
    thursday: { isOpen: false, periods: [{ id: 1, openTime: '09:00', closeTime: '17:00' }] },
    friday: { isOpen: false, periods: [{ id: 1, openTime: '09:00', closeTime: '17:00' }] },
    saturday: { isOpen: false, periods: [{ id: 1, openTime: '09:00', closeTime: '17:00' }] },
    sunday: { isOpen: false, periods: [{ id: 1, openTime: '09:00', closeTime: '17:00' }] }
  });

  // Special Hours State - array of special hours entries
  const [specialHours, setSpecialHours] = useState([]);

  // Calendar state
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarEvents, setCalendarEvents] = useState([
    { date: new Date(2024, 11, 15), title: 'Team Meeting' },
    { date: new Date(2024, 11, 20), title: 'Client Call' },
    { date: new Date(2024, 11, 25), title: 'Review Session' }
  ]);
  const [addEventModalOpen, setAddEventModalOpen] = useState(false);
  const [fullCalendarModalOpen, setFullCalendarModalOpen] = useState(false);

  // Profile Strength Score state
  const [profileScore, setProfileScore] = useState(0); // Default to 0 until user runs real analysis
  const [maxScore] = useState(1000);

  // Competitor Analysis data state
  const [competitorData, setCompetitorData] = useState(null);
  const [gmbScore, setGmbScore] = useState(null);
  const [dataLoadedFromCache, setDataLoadedFromCache] = useState(false);
  const [keywordResults, setKeywordResults] = useState({}); // Store results for each keyword
  const [selectedKeyword, setSelectedKeyword] = useState(null); // Track selected keyword
  const [lastAnalysisTime, setLastAnalysisTime] = useState(null); // Track last analysis timestamp
  const [canRunAnalysis, setCanRunAnalysis] = useState(true); // Track if user can run analysis
  const [daysUntilNextAnalysis, setDaysUntilNextAnalysis] = useState(0); // Days remaining
  const [autoRunKeyword, setAutoRunKeyword] = useState(null);
  const [autoOpenKeywordInput, setAutoOpenKeywordInput] = useState(false);
  const autoRunForTimestampRef = useRef(null);
  const autoRunScheduledRef = useRef(false);
  const prevCanRunAnalysisRef = useRef(canRunAnalysis);

  // Permission system - plan-based access
  const { isAdmin, canAccessFeature, loading: permissionsLoading } = usePermissions();
  const { isWhitelisted, isChecking: accessChecking } = useAccessGate();
  const { planCode: profilePlanCode, hasSubscription } = useProfilePlan();
  const [trialOfferOpen, setTrialOfferOpen] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState('');

  // Use ref to preserve the first valid plan we get from locationData
  // This prevents the plan from changing when ProfilePlanContext loads later
  const initialPlanRef = useRef(null);

  // Reset ref when switching to a different profile
  useEffect(() => {
    initialPlanRef.current = null;
  }, [locationId]);

  // Debug: Log when locationData changes
  useEffect(() => {
    if (locationData) {
      console.log('[BusinessProfile] 🔄 locationData updated:', {
        hasLocationData: !!locationData,
        hasPlan: !!locationData.plan,
        plan: locationData.plan,
        title: locationData.title,
        name: locationData.name,
        timestamp: new Date().toISOString()
      });
    }
  }, [locationData]);

  // Cache the FIRST valid plan to prevent backend from overwriting with wrong data
  // This useEffect runs BEFORE useMemo recalculates when profilePlanCode changes
  useEffect(() => {
    if (!initialPlanRef.current) {
      if (locationData?.plan) {
        console.log('[BusinessProfile] 🔒 Caching plan from locationData:', locationData.plan);
        initialPlanRef.current = locationData.plan;
      } else if (profilePlanCode) {
        console.log('[BusinessProfile] 🔒 Caching plan from ProfilePlanContext:', profilePlanCode);
        initialPlanRef.current = profilePlanCode;
      }
    } else {
      console.log('[BusinessProfile] ⏭️  Ignoring plan change - already cached:', {
        cached: initialPlanRef.current,
        newLocationDataPlan: locationData?.plan,
        newProfilePlanCode: profilePlanCode
      });
    }
  }, [locationData?.plan, profilePlanCode]);

  // Use plan from locationData (database GmbAccount.plan column) as primary source, 
  // fallback to ProfilePlanContext. Use memoized value to prevent race conditions.
  const planCode = useMemo(() => {
    // Priority 1: Use preserved initial plan from locationData (most reliable)
    if (initialPlanRef.current) {
      return initialPlanRef.current;
    }

    // Priority 2: Use current locationData.plan
    if (locationData?.plan) {
      return locationData.plan;
    }

    // Priority 3: Use ProfilePlanContext
    return profilePlanCode;
  }, [locationData?.plan, profilePlanCode]);

  console.log('[BusinessProfile] 📊 Plan detection:', {
    locationDataPlan: locationData?.plan,
    initialPlanRef: initialPlanRef.current,
    profilePlanContextPlan: profilePlanCode,
    finalPlanCode: planCode,
    source: initialPlanRef.current ? 'database (cached)' : locationData?.plan ? 'database (GmbAccount.plan)' : 'ProfilePlanContext',
    timestamp: new Date().toISOString(),
    locationDataExists: !!locationData,
    hasSubscription
  });

  // hasPlanAccess: true if selected GMB has a plan assigned
  // Use planCode (cached value) instead of profilePlanCode to stay consistent
  const hasPlanAccess = planCode || (hasSubscription && profilePlanCode);

  console.log('[BusinessProfile] ✅ Final plan state:', {
    planCode,
    hasPlanAccess,
    willShowUpgradePrompt: !hasPlanAccess
  });

  // Admin viewing mode - admins always have access
  const [isAdminViewingClient, setIsAdminViewingClient] = useState(false);

  // Check if admin is viewing a client's profile
  useEffect(() => {
    const checkAdminViewingMode = async () => {
      try {
        const adminAccessData = sessionStorage.getItem('adminAccessData');
        if (adminAccessData) {
          setIsAdminViewingClient(true);
        } else {
          setIsAdminViewingClient(false);
        }
      } catch (error) {
        logger.error('[BusinessProfile] Error checking admin viewing mode:', error);
        setIsAdminViewingClient(false);
      }
    };

    checkAdminViewingMode();
  }, []);

  // Check if user can run competitor analysis (plan-based cooldown) from backend
  useEffect(() => {
    const checkAnalysisEligibility = async () => {
      try {
        const businessName = locationData?.title || locationData?.name || '';
        logger.log('🔍 [Restriction Check] Business name:', businessName);

        if (!businessName || !locationId) {
          logger.log('⚠️ [Restriction Check] No business name or locationId, skipping check');
          return;
        }

        logger.log('🔍 [Restriction Check] Checking cooldown via backend for locationId:', locationId);

        // Call backend cooldown endpoint which uses the GMB profile's assigned plan
        const cooldownResult = await checkCooldown(locationId, 'competitorAnalysis');

        logger.log('🔍 [Restriction Check] Cooldown result:', cooldownResult);
        logger.log('🔍 [Restriction Check] Plan:', cooldownResult.planCode, 'Cooldown days:', cooldownResult.cooldownDays);

        const canRun = cooldownResult && cooldownResult.canRunNow === true;

        // Prefer explicit daysRemaining from backend when > 0, otherwise fall back to plan cooldown days
        let daysRem = 0;
        if (cooldownResult) {
          if (Number.isFinite(cooldownResult.daysRemaining) && cooldownResult.daysRemaining > 0) {
            daysRem = cooldownResult.daysRemaining;
          } else if (Number.isFinite(cooldownResult.cooldownDays) && cooldownResult.cooldownDays > 0 && cooldownResult.cooldownDays < 100) {
            // Only use cooldownDays if it's a reasonable value (< 100 days)
            daysRem = cooldownResult.cooldownDays;
          } else {
            // Default to 15 days (basic plan) if no valid cooldown info
            const effectivePlan = cooldownResult?.planCode || planCode;
            daysRem = (effectivePlan && effectivePlan !== 'none') ? getCompetitorCooldownDays(effectivePlan) : 15;
          }
        }

        // Cap daysRem at 30 days max to avoid showing unrealistic values like 999
        daysRem = Math.min(daysRem, 30);

        if (canRun) {
          setCanRunAnalysis(true);
          setDaysUntilNextAnalysis(0);
          logger.log('✅ [Restriction Check] ALLOWED - User can run analysis (Plan:', cooldownResult?.planCode + ')');
        } else {
          setCanRunAnalysis(false);
          setDaysUntilNextAnalysis(daysRem);
          if (cooldownResult && cooldownResult.lastAnalysisDate) {
            setLastAnalysisTime(new Date(cooldownResult.lastAnalysisDate).getTime());
          }
          logger.log('⏳ [Restriction Check] RESTRICTED - User must wait', daysRem, 'more days (Plan:', cooldownResult?.planCode, 'requires', cooldownResult?.cooldownDays, 'day cooldown)');
        }
      } catch (error) {
        logger.error('❌ [Restriction Check] Error checking eligibility:', error);
        // Fallback: compute cooldown from local plan and localStorage timestamp (handles unauthenticated dev mode)
        try {
          const businessName = locationData?.title || locationData?.name || '';
          const storageKey = `competitor_analysis_last_run_${businessName.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`;
          const lastRunTs = Number(localStorage.getItem(storageKey)) || null;
          const cooldownDays = getCompetitorCooldownDays(planCode);

          if (!lastRunTs) {
            setCanRunAnalysis(true);
            setDaysUntilNextAnalysis(0);
          } else {
            const elapsedDays = Math.floor((Date.now() - lastRunTs) / (24 * 60 * 60 * 1000));
            const daysRem = Math.max(0, cooldownDays - elapsedDays);
            setCanRunAnalysis(daysRem === 0);
            setDaysUntilNextAnalysis(daysRem);
            setLastAnalysisTime(lastRunTs);
            logger.log('⏳ [Restriction Check - Fallback] Using local plan', planCode, 'cooldownDays', cooldownDays, 'elapsed', elapsedDays, 'daysRemaining', daysRem);
          }
        } catch (fbErr) {
          logger.error('❌ [Restriction Check] Fallback failed:', fbErr);
          setCanRunAnalysis(true);
        }
      }
    };

    checkAnalysisEligibility();
  }, [locationData, locationId]);

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  // Debug: Log when competitor data or gmbScore changes
  useEffect(() => {
    if (competitorData) {
      logger.log('🎯 [Debug] ✅ Competitor data loaded and available!');
      logger.log('📊 Competitors count:', competitorData.competitors?.length || 0);
      logger.log('🏆 My Business Score:', competitorData.myBusinessScore);
      logger.log('🥇 My Business Rank:', competitorData.myBusinessRank);
      logger.log('📅 Analysis Timestamp:', new Date(competitorData.timestamp).toLocaleString());

      // Show success message in console
      logger.log('%c✅ COMPETITOR ANALYSIS DATA LOADED SUCCESSFULLY', 'color: green; font-size: 14px; font-weight: bold;');
      logger.log('%cCompetitive Ranking will be displayed on the page', 'color: blue; font-size: 12px;');
    }
  }, [competitorData]);

  useEffect(() => {
    if (gmbScore !== null && gmbScore > 0) {
      logger.log('🎯 [Debug] ✅ GMB Score loaded and available:', gmbScore);
      logger.log('%c✅ PROFILE STRENGTH SCORE LOADED: ' + gmbScore + '/1000', 'color: green; font-size: 14px; font-weight: bold;');
      logger.log('%cProfile Strength gauge will display this score', 'color: blue; font-size: 12px;');
    }
  }, [gmbScore]);

  // Clear ALL cached data when switching to a different business profile (locationId change)
  useEffect(() => {
    logger.log('🔄 [Profile Switch] LocationId changed to:', locationId);
    logger.log('🧹 [Profile Switch] Clearing ALL cached data for fresh profile load');

    // Clear cacheManager cache for this profile
    if (locationId) {
      const cacheKey = `business_profile_${locationId}`;
      cacheManager.remove(cacheKey);
      logger.log('🗑️ [Profile Switch] Cleared cacheManager for:', cacheKey);
    }

    // Clear competitor analysis data
    setCompetitorData(null);
    setGmbScore(null);
    setDataLoadedFromCache(false);
    setKeywordResults({});
    setSelectedKeyword(null);

    // Clear profile strength data
    setProfileScore(0);

    // Clear location data to force fresh fetch
    setLocationData(null);

    // Clear reviews
    setReviewsData(null);

    // Clear posts
    setLocalPosts([]);

    // Clear services
    setServices([]);

    // Clear calendar events (will reload from localStorage for this specific locationId)
    setCalendarEvents([]);

    // Clear photos
    setMediaItems([]);

    // Clear fresh newReviewUri
    setFreshNewReviewUri(null);

    // Clear any loading states
    setLoading(true);

    // Immediately fetch competitor score from database for quick display
    // This runs AFTER clearing states so the fetched value is preserved
    if (locationId) {
      const fetchQuickScore = async () => {
        try {
          const normalizedId = String(locationId).replace('locations/', '');
          const response = await ApiService.post('/competitor-analysis/scores-by-locations', {
            locationIds: [normalizedId]
          });

          if (response?.data?.data || response?.data) {
            const scoresData = response.data.data || response.data;
            const scoreInfo = scoresData[normalizedId];
            if (scoreInfo && scoreInfo.score) {
              logger.log('⚡ [Quick Score] Loaded score from DB:', scoreInfo.score);
              setGmbScore(scoreInfo.score);
            }
          }
        } catch (err) {
          logger.warn('[Quick Score] Failed to fetch quick score:', err?.message);
        }
      };

      fetchQuickScore();
    }

    logger.log('✅ [Profile Switch] All data cleared, ready for fresh profile load');
  }, [locationId]);

  // Fetch fresh newReviewUri from Google API if not available in locationData
  useEffect(() => {
    const fetchNewReviewUri = async () => {
      // Check if we already have newReviewUri in locationData
      const existingUri = locationData?.metadata?.newReviewUri || locationData?.newReviewUri;
      if (existingUri) {
        logger.log('[Review Link] Using existing newReviewUri from locationData:', existingUri);
        setFreshNewReviewUri(existingUri);
        return;
      }

      // Only fetch if we have a locationId
      if (!locationId) return;

      try {
        logger.log('[Review Link] Fetching fresh location data for newReviewUri...');
        const freshLocationData = await GMBService.getLocation(locationId);

        const newUri = freshLocationData?.metadata?.newReviewUri;
        if (newUri) {
          logger.log('[Review Link] ✅ Got fresh newReviewUri:', newUri);
          setFreshNewReviewUri(newUri);
        } else {
          logger.log('[Review Link] newReviewUri not available from Google API');
        }
      } catch (err) {
        logger.warn('[Review Link] Failed to fetch fresh location data:', err?.message);
      }
    };

    // Only run when we have locationData loaded (to check if newReviewUri exists)
    // or when locationId changes
    if (locationId && !loading) {
      fetchNewReviewUri();
    }
  }, [locationId, locationData, loading]);

  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    type: 'Update',
    images: [],
    selectedButton: 'None',
    buttonLink: '',
    terms: '',
    couponCode: '',
    redeemLink: ''
  });
  const [activeOfferFields, setActiveOfferFields] = useState({
    terms: false,
    couponCode: false,
    redeemLink: false
  });
  const [dragOver, setDragOver] = useState(false);
  const [buttonDropdownOpen, setButtonDropdownOpen] = useState(false);
  const [buttonAnchorEl, setButtonAnchorEl] = useState(null);
  const [competitorAnalysisOpen, setCompetitorAnalysisOpen] = useState(false);
  const [heatmapModalOpen, setHeatmapModalOpen] = useState(false);
  const [schedulePostModalOpen, setSchedulePostModalOpen] = useState(false);
  const [scheduledPostDetailsOpen, setScheduledPostDetailsOpen] = useState(false);
  const [selectedScheduledPost, setSelectedScheduledPost] = useState(null);
  const getScheduledTimestamp = (postLike) => {
    if (!postLike) return null;
    return (
      postLike.scheduledTime ||
      postLike.scheduledFor ||
      postLike.scheduled_time ||
      postLike.scheduled_at ||
      null
    );
  };

  const normalizeScheduledPost = (postLike) => {
    if (!postLike || typeof postLike !== 'object') return null;
    const timestamp = getScheduledTimestamp(postLike);
    if (!timestamp) {
      return { ...postLike };
    }
    return {
      ...postLike,
      scheduledTime: timestamp,
      scheduledFor: timestamp
    };
  };

  const normalizeScheduledPosts = (posts) =>
    Array.isArray(posts) ? posts.map(normalizeScheduledPost).filter(Boolean) : [];

  const getValidScheduledDate = (postLike) => {
    const timestamp = getScheduledTimestamp(postLike);
    if (!timestamp) return null;
    const date = new Date(timestamp);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  useEffect(() => {
    const transitionedToReady = !prevCanRunAnalysisRef.current && canRunAnalysis;
    const hasKeywordData = selectedKeyword && keywordResults[selectedKeyword];
    const cooldownComplete = daysUntilNextAnalysis <= 0;

    if (
      transitionedToReady &&
      cooldownComplete &&
      (isAdmin || hasPlanAccess) &&
      !permissionsLoading &&
      lastAnalysisTime &&
      hasKeywordData &&
      !autoRunKeyword &&
      autoRunForTimestampRef.current !== lastAnalysisTime
    ) {
      autoRunScheduledRef.current = true;
      autoRunForTimestampRef.current = lastAnalysisTime;
      setAutoRunKeyword(selectedKeyword);
      if (!competitorAnalysisOpen) {
        setCompetitorAnalysisOpen(true);
      }
    }

    prevCanRunAnalysisRef.current = canRunAnalysis;
  }, [
    canRunAnalysis,
    daysUntilNextAnalysis,
    selectedKeyword,
    keywordResults,
    isAdmin,
    hasPlanAccess,
    permissionsLoading,
    lastAnalysisTime,
    competitorAnalysisOpen,
    autoRunKeyword
  ]);

  useEffect(() => {
    if (!canRunAnalysis) {
      autoRunScheduledRef.current = false;
    }
  }, [canRunAnalysis]);

  // Task management state
  const [taskStats, setTaskStats] = useState({
    totalTasks: 0,
    completedTasks: 0,
    pendingTasks: 0,
    delayedTasks: 0,
    completedPercentage: 0,
    recentTasks: []
  });

  // AI Reply and Post stats for Action Center
  const [aiReplyStats, setAiReplyStats] = useState({
    totalRepliesSent: 0,
    enabledLocations: 0
  });
  const [postStats, setPostStats] = useState({
    totalPosts: 0,
    scheduledPosts: 0,
    completedPosts: 0
  });

  // Review details modal state
  const [reviewDetailsOpen, setReviewDetailsOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [generatingReply, setGeneratingReply] = useState(false);
  const [submittingReply, setSubmittingReply] = useState(false);

  // Pagination state for reviews modal
  const [reviewsPage, setReviewsPage] = useState(1);
  const [reviewsPerPage] = useState(10); // Show 10 reviews per page

  // Edit/Delete reply state
  const [editingReplyIndex, setEditingReplyIndex] = useState(null);
  const [editReplyText, setEditReplyText] = useState('');
  const [deletingReply, setDeletingReply] = useState({});

  // Load task data using CriticalTasksService - same as TaskManagement page
  useEffect(() => {
    const loadTaskData = () => {
      try {
        // Build gmbData object from available profile data for CriticalTasksService
        const gmbData = {
          // Profile data
          ...locationData,
          profile: locationData,

          // Verification status from VoiceOfMerchant
          verificationStatus: verificationState?.simplified,
          isVerified: verificationState?.isVerified,
          hasVoiceOfMerchant: verificationState?.raw?.hasVoiceOfMerchant,
          hasBusinessAuthority: verificationState?.raw?.hasBusinessAuthority,
          metadata: locationData?.metadata || {},

          // Phone numbers
          phoneNumbers: locationData?.phoneNumbers || {},
          phoneNumber: locationData?.phoneNumbers?.primaryPhone || locationData?.phoneNumber,

          // Website
          websiteUri: locationData?.websiteUri || locationData?.website,

          // Categories
          categories: locationData?.categories || {},

          // Address
          storefrontAddress: locationData?.storefrontAddress || locationData?.address || {},

          // Location coordinates
          latlng: locationData?.latlng || locationData?.location?.latlng,

          // Business status
          businessStatus: locationData?.openInfo?.status || locationData?.businessStatus,
          openInfo: locationData?.openInfo || {},

          // Media/Photos
          media: mediaItems || [],

          // Reviews
          reviews: reviewsData?.reviews || reviewsData || [],
          reviewCount: reviewsData?.totalReviewCount || reviewsData?.reviews?.length || 0,

          // Competitor insights
          competitorInsights: getCompetitorInsights(locationId)
        };

        // Get tasks from CriticalTasksService (same method used by TaskManagement page)
        const { criticalTasks, defaultTasks, allTasks, hasCriticalTasks } =
          CriticalTasksService.getProfileTasks(gmbData);

        logger.log('[BusinessProfile] CriticalTasksService tasks:', {
          critical: criticalTasks.length,
          default: defaultTasks.length,
          total: allTasks.length
        });

        // Convert tasks to stats format for Action Centre display
        const completedCount = 0; // CriticalTasks are always pending until resolved
        const pendingCount = allTasks.length;
        const delayedCount = criticalTasks.length; // Critical tasks are "delayed"

        const stats = {
          totalTasks: allTasks.length,
          completedTasks: completedCount,
          pendingTasks: pendingCount,
          delayedTasks: delayedCount,
          completedPercentage: allTasks.length > 0 ? Math.round((completedCount / allTasks.length) * 100) : 0,
          recentTasks: allTasks.slice(0, 5).map(task => ({
            name: task.title || task.description || 'Task',
            completed: 0,
            total: 1,
            status: task.priority === 'Critical' ? 'delayed' : 'pending',
            priority: task.priority,
            actionType: task.actionType,
            actionUrl: task.actionUrl,
            actionLabel: task.actionLabel
          }))
        };

        setTaskStats(stats);
        logger.log('[BusinessProfile] Task stats loaded:', stats);
      } catch (error) {
        logger.error('[BusinessProfile] Error loading task stats:', error);
      }
    };

    // Only load tasks when we have profile data
    if (locationData) {
      loadTaskData();
    }
  }, [locationData, verificationState, mediaItems, reviewsData, locationId]);

  // Load AI Reply stats and Post stats for Action Center
  useEffect(() => {
    const loadActionCenterStats = async () => {
      try {
        // Fetch AI Reply stats
        const aiReplyResponse = await fetch('/api/customer/auto-reply/status', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include'
        });
        if (aiReplyResponse.ok) {
          const aiReplyData = await aiReplyResponse.json();
          if (aiReplyData.status && aiReplyData.data) {
            setAiReplyStats({
              totalRepliesSent: aiReplyData.data.totalRepliesSent || 0,
              enabledLocations: aiReplyData.data.enabledLocations || 0
            });
          }
        }

        // Fetch Post stats
        const postStatsUrl = locationId
          ? `/api/customer/post-scheduler/stats?locationId=${encodeURIComponent(locationId)}`
          : '/api/customer/post-scheduler/stats';
        const postResponse = await fetch(postStatsUrl, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include'
        });
        if (postResponse.ok) {
          const postData = await postResponse.json();
          if (postData.status && postData.data) {
            setPostStats({
              totalPosts: postData.data.totalPosts || 0,
              scheduledPosts: postData.data.scheduledPosts || 0,
              completedPosts: postData.data.completedPosts || 0
            });
          }
        }

        logger.log('[BusinessProfile] Action Center stats loaded');
      } catch (error) {
        logger.error('[BusinessProfile] Error loading Action Center stats:', error);
      }
    };

    loadActionCenterStats();
  }, [locationId]);

  // Load scheduled posts from localStorage
  useEffect(() => {
    const loadScheduledPosts = () => {
      try {
        logger.log('🔄 [loadScheduledPosts] Loading for locationId:', locationId);
        const savedPosts = localStorage.getItem(`scheduledPosts_${locationId}`);
        logger.log('📦 [loadScheduledPosts] Raw localStorage data:', savedPosts);

        if (savedPosts) {
          const posts = normalizeScheduledPosts(JSON.parse(savedPosts));
          logger.log('📝 [loadScheduledPosts] Parsed posts:', posts);

          // Filter out posts that are already published or in the past
          const now = new Date();
          logger.log('⏰ [loadScheduledPosts] Current time:', now.toISOString());

          const upcomingPosts = posts.filter(post => {
            const scheduledDate = getValidScheduledDate(post);
            if (!scheduledDate) {
              logger.warn('[loadScheduledPosts] Skipping post with invalid scheduled time:', post);
              return false;
            }
            const isUpcoming = scheduledDate > now && !post.published;
            logger.log(`  Post "${post.summary?.substring(0, 20)}...": scheduled=${scheduledDate.toISOString()}, isUpcoming=${isUpcoming}`);
            return isUpcoming;
          });

          logger.log('✅ [loadScheduledPosts] Upcoming posts:', upcomingPosts.length);

          // Update localStorage if we filtered any posts
          if (upcomingPosts.length !== posts.length) {
            localStorage.setItem(`scheduledPosts_${locationId}`, JSON.stringify(upcomingPosts));
            logger.log('🧹 [loadScheduledPosts] Cleaned up past posts');
          }

          const scheduledEvents = upcomingPosts
            .map(post => {
              const eventDate = getValidScheduledDate(post);
              if (!eventDate) return null;
              return {
                date: eventDate,
                title: `dY". ${post.summary.substring(0, 30)}${post.summary.length > 30 ? '...' : ''}`,
                type: 'scheduled_post',
                postData: post
              };
            })
            .filter(Boolean);


          logger.log('📅 [loadScheduledPosts] Created calendar events:', scheduledEvents);

          // Merge with existing calendar events
          setCalendarEvents(prev => {
            const existingEvents = prev.filter(e => e.type !== 'scheduled_post');
            const merged = [...existingEvents, ...scheduledEvents];
            logger.log('🎯 [loadScheduledPosts] Total calendar events:', merged.length);
            return merged;
          });

          logger.log('[BusinessProfile] Loaded scheduled posts:', scheduledEvents.length);
        } else {
          logger.log('📭 [loadScheduledPosts] No saved posts found');
        }
      } catch (error) {
        logger.error('[BusinessProfile] Error loading scheduled posts:', error);
      }
    };

    if (locationId) {
      loadScheduledPosts();
    }
  }, [locationId]);

  // Check and publish scheduled posts at the right time
  useEffect(() => {
    const checkAndPublishScheduledPosts = async () => {
      try {
        const savedPosts = localStorage.getItem(`scheduledPosts_${locationId}`);
        if (!savedPosts) return;

        const posts = normalizeScheduledPosts(JSON.parse(savedPosts));
        const now = new Date();
        const accountId = currentAccount?.name?.split('/').pop();

        let postsToUpdate = [...posts];
        let hasChanges = false;

        for (let i = 0; i < postsToUpdate.length; i++) {
          const post = postsToUpdate[i];
          const scheduledDate = getValidScheduledDate(post);
          if (!scheduledDate) {
            logger.warn('[ScheduledPost] Skipping post with invalid scheduled time:', post);
            continue;
          }

          // Check if the scheduled time has arrived (within 1 minute window)
          const timeDiff = scheduledDate - now;
          const isTimeToPublish = timeDiff <= 60000 && timeDiff >= -60000 && !post.published;

          if (isTimeToPublish && accountId && locationId) {
            try {
              logger.log('[ScheduledPost] Publishing post:', post.summary);

              // Build the post data for GMB API
              const postData = {
                languageCode: 'en',
                summary: post.summary,
                topicType: post.topicType || 'STANDARD'
              };

              // Add media if provided (only if it's a publicly accessible URL, not base64)
              if (post.mediaUrl && !post.mediaUrl.startsWith('data:')) {
                postData.media = [{
                  mediaFormat: 'PHOTO',
                  sourceUrl: post.mediaUrl
                }];
              } else if (post.mediaUrl && post.mediaUrl.startsWith('data:')) {
                logger.warn('[ScheduledPost] Skipping base64 image - GMB requires publicly accessible URLs');
              }

              // Add call to action if provided
              if (post.actionType) {
                postData.callToAction = {
                  actionType: post.actionType
                };
                if (post.actionUrl && post.actionType !== 'CALL') {
                  postData.callToAction.url = post.actionUrl;
                }
              }

              // Publish to GMB API (without scheduledTime to post immediately)
              const result = await GMBService.createLocalPost(
                null, // Will use token from TokenManager
                accountId,
                locationId,
                postData
              );

              logger.log('[ScheduledPost] Post published successfully:', result);

              // Mark as published
              postsToUpdate[i] = { ...post, published: true, publishedAt: now.toISOString() };
              hasChanges = true;

              // Show success notification
              const hasSkippedImage = post.mediaUrl && post.mediaUrl.startsWith('data:');
              const message = hasSkippedImage
                ? `✅ Scheduled post published!\n\n"${post.summary.substring(0, 100)}..."\n\n⚠️ Note: Image was not included (only publicly accessible URLs are supported by Google)`
                : `✅ Scheduled post published successfully!\n\n"${post.summary.substring(0, 100)}..."`;
              alert(message);

            } catch (error) {
              logger.error('[ScheduledPost] Error publishing post:', error);
              // Mark as failed but don't remove from list
              postsToUpdate[i] = { ...post, publishError: error.message };
              hasChanges = true;
            }
          }
        }

        // Update localStorage if any posts were published or failed
        if (hasChanges) {
          // Remove published posts after 24 hours
          const cleanedPosts = postsToUpdate.filter(post => {
            if (!post.published) return true;
            const publishedDate = new Date(post.publishedAt);
            const hoursSincePublished = (now - publishedDate) / (1000 * 60 * 60);
            return hoursSincePublished < 24;
          });

          localStorage.setItem(`scheduledPosts_${locationId}`, JSON.stringify(cleanedPosts));

          // Reload calendar events
          const scheduledEvents = cleanedPosts
            .filter(p => !p.published)
            .map(post => {
              const eventDate = getValidScheduledDate(post);
              if (!eventDate) return null;
              return {
                date: eventDate,
                title: `dY". ${post.summary.substring(0, 30)}${post.summary.length > 30 ? '...' : ''}`,
                type: 'scheduled_post',
                postData: post
              };
            })
            .filter(Boolean);


          setCalendarEvents(prev => {
            const existingEvents = prev.filter(e => e.type !== 'scheduled_post');
            return [...existingEvents, ...scheduledEvents];
          });
        }
      } catch (error) {
        logger.error('[ScheduledPost] Error checking scheduled posts:', error);
      }
    };

    if (!locationId || !currentAccount) return;

    // Check immediately on mount
    checkAndPublishScheduledPosts();

    // Then check every minute
    const intervalId = setInterval(checkAndPublishScheduledPosts, 60000); // Check every 60 seconds

    return () => clearInterval(intervalId);
  }, [locationId, currentAccount]);

  // Initialize map picker when dialog opens
  useEffect(() => {
    if (!mapPickerOpen) return;

    const initializeMap = () => {
      const box = document.getElementById('mapPickerBox');
      if (!box) {
        logger.warn('Map picker box not found in DOM');
        return;
      }

      // Check if box has dimensions
      const rect = box.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) {
        logger.warn('Map picker box has no dimensions, retrying...', rect);
        // Retry after a longer delay
        setTimeout(initializeMap, 200);
        return;
      }

      logger.log('Map picker box dimensions:', rect.width, 'x', rect.height);

      // Clear any existing content
      box.innerHTML = '';

      // Add loading indicator
      box.innerHTML = '<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #6b7280; font-size: 14px;">Loading map...</div>';

      const loadLeaflet = () => {
        return new Promise((resolve) => {
          // Check if Leaflet is already loaded
          if (window.L && typeof window.L.map === 'function') {
            resolve();
            return;
          }

          // Load CSS
          if (!document.getElementById('leaflet-css')) {
            const link = document.createElement('link');
            link.id = 'leaflet-css';
            link.rel = 'stylesheet';
            link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
            document.head.appendChild(link);
          }

          // Load JS
          if (!document.getElementById('leaflet-js')) {
            const script = document.createElement('script');
            script.id = 'leaflet-js';
            script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
            script.onload = () => resolve();
            script.onerror = () => {
              logger.error('Failed to load Leaflet');
              resolve(); // Continue with fallback
            };
            document.head.appendChild(script);
          } else {
            resolve();
          }
        });
      };

      loadLeaflet().then(() => {
        // Clear loading indicator
        box.innerHTML = '';

        if (window.L && typeof window.L.map === 'function') {
          try {
            logger.log('Initializing Leaflet map...');

            // Initialize map
            const map = window.L.map('mapPickerBox', {
              center: [30.7046, 76.7179], // Default to Punjab, India
              zoom: 12,
              zoomControl: true,
              preferCanvas: false,
              attributionControl: true
            });

            // Store map instance for cleanup
            box._mapInstance = map;

            // Add tile layer
            window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
              attribution: '© OpenStreetMap contributors',
              maxZoom: 19
            }).addTo(map);

            // Force map to recalculate size after a short delay
            // This is crucial for proper rendering on large screens
            setTimeout(() => {
              map.invalidateSize();
              logger.log('Map size invalidated for proper rendering');
            }, 100);

            // Create a custom marker icon
            const customIcon = window.L.divIcon({
              className: 'custom-marker',
              html: '<div style="background-color: #0B91D6; width: 20px; height: 20px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3); transition: transform 0.2s ease;"></div>',
              iconSize: [20, 20],
              iconAnchor: [10, 10]
            });

            // Add marker for current location if available
            let marker = null;
            if (pickedLatLng) {
              marker = window.L.marker([pickedLatLng.lat, pickedLatLng.lng], { icon: customIcon }).addTo(map);
              map.setView([pickedLatLng.lat, pickedLatLng.lng], 15);
            }

            // Handle map clicks
            map.on('click', async (e) => {
              const { lat, lng } = e.latlng;
              setPickedLatLng({ lat, lng });

              // Remove existing marker and add new one
              if (marker) {
                map.removeLayer(marker);
              }
              marker = window.L.marker([lat, lng], { icon: customIcon }).addTo(map);

              // Add click event to marker for better UX
              marker.on('click', () => {
                logger.log('Marker clicked at:', lat, lng);
                // Add a small animation to the marker
                marker.getElement().style.transform = 'scale(1.2)';
                setTimeout(() => {
                  marker.getElement().style.transform = 'scale(1)';
                }, 200);
              });

              // Automatically reverse geocode and update the business location field
              try {
                const address = await reverseGeocode(lat, lng);

                // Find and update the business location input field
                const businessLocationInput = document.querySelector('input[name="businessLocation"]');
                logger.log('Looking for business location input:', businessLocationInput);

                if (businessLocationInput) {
                  businessLocationInput.value = address;
                  businessLocationInput.dispatchEvent(new Event('input', { bubbles: true }));
                  businessLocationInput.dispatchEvent(new Event('change', { bubbles: true }));
                  logger.log('Successfully updated business location field with:', address);
                  setAddressUpdated(true);
                  setHasFormChanges(true); // Mark form as changed
                  setTimeout(() => setAddressUpdated(false), 3000); // Hide after 3 seconds
                } else {
                  logger.warn('Business location input field not found!');
                  // Try alternative selectors
                  const altInput = document.querySelector('input[placeholder*="Business location"], input[placeholder*="Address"]');
                  if (altInput) {
                    altInput.value = address;
                    altInput.dispatchEvent(new Event('input', { bubbles: true }));
                    altInput.dispatchEvent(new Event('change', { bubbles: true }));
                    logger.log('Updated using alternative selector:', address);
                    setAddressUpdated(true);
                    setHasFormChanges(true); // Mark form as changed
                    setTimeout(() => setAddressUpdated(false), 3000);
                  }
                }

                logger.log('Updated business location:', address);
              } catch (error) {
                logger.warn('Failed to update business location field:', error);
              }
            });

            // Store map instance for cleanup
            box._mapInstance = map;

          } catch (error) {
            logger.error('Error initializing map:', error);
            box.innerHTML = '<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #ef4444; font-size: 14px;">Failed to load map. Please try again.</div>';
          }
        } else {
          // Fallback: Simple click handler
          box.innerHTML = '<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #6b7280; font-size: 14px; text-align: center; padding: 20px;">Click anywhere on this area to set coordinates<br><small>Map loading failed, using fallback mode</small></div>';

          const clickHandler = async (e) => {
            const rect = box.getBoundingClientRect();
            const x = (e.clientX - rect.left) / rect.width;
            const y = (e.clientY - rect.top) / rect.height;

            // Convert to approximate coordinates (Punjab, India area)
            const lat = 30.6 + (0.4 * (1 - y));
            const lng = 76.6 + (0.4 * x);

            setPickedLatLng({ lat, lng });

            // Show selected coordinates
            box.innerHTML = `<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #0B91D6; font-size: 14px; text-align: center; padding: 20px;">
              Selected: ${lat.toFixed(4)}, ${lng.toFixed(4)}<br>
              <small>Click again to change location</small>
            </div>`;

            // Automatically reverse geocode and update the business location field
            try {
              const address = await reverseGeocode(lat, lng);

              // Find and update the business location input field
              const businessLocationInput = document.querySelector('input[name="businessLocation"]');
              if (businessLocationInput) {
                businessLocationInput.value = address;
                businessLocationInput.dispatchEvent(new Event('input', { bubbles: true }));
                businessLocationInput.dispatchEvent(new Event('change', { bubbles: true }));
                setAddressUpdated(true);
                setHasFormChanges(true); // Mark form as changed
                setTimeout(() => setAddressUpdated(false), 3000);
              }

              logger.log('Updated business location:', address);
            } catch (error) {
              logger.warn('Failed to update business location field:', error);
            }
          };

          box.addEventListener('click', clickHandler);
          box._clickHandler = clickHandler;
        }
      });
    };

    // Longer delay to ensure Dialog is fully rendered, especially on large screens
    // Large screens may need more time for the Dialog animation to complete
    const timer = setTimeout(initializeMap, 300);

    return () => {
      clearTimeout(timer);
      const box = document.getElementById('mapPickerBox');
      if (box) {
        if (box._mapInstance) {
          try {
            // Remove all markers from the map before removing the map
            box._mapInstance.eachLayer((layer) => {
              if (layer instanceof window.L.Marker) {
                box._mapInstance.removeLayer(layer);
              }
            });
            box._mapInstance.remove();
            box._mapInstance = null;
            logger.log('Map instance cleaned up successfully');
          } catch (error) {
            logger.error('Error cleaning up map instance:', error);
          }
        }
        if (box._clickHandler) {
          box.removeEventListener('click', box._clickHandler);
          box._clickHandler = null;
        }
      }
    };
  }, [mapPickerOpen, pickedLatLng]);

  const open = Boolean(anchorEl);

  // Calendar helper functions
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const getMonthName = (date) => {
    return date.toLocaleString('default', { month: 'long', year: 'numeric' });
  };

  const isSameDay = (date1, date2) => {
    return date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear();
  };

  // Calculate dynamic profile score based on business data
  const calculateProfileScore = useCallback((businessData) => {
    if (!businessData) return 0;

    let score = 0;
    const maxPossibleScore = 300;

    // Basic Information (60 points)
    if (businessData.title || businessData.name) score += 20;
    if (businessData.storefrontAddress) score += 20;
    if (businessData.phoneNumbers?.length > 0) score += 20;

    // Business Details (60 points)
    if (businessData.categories?.primaryCategory) score += 15;
    if (businessData.categories?.additionalCategories?.length > 0) score += 15;
    if (businessData.regularHours?.periods?.length > 0) score += 15;
    if (businessData.websiteUri) score += 15;

    // Visual Content (60 points)
    const photoCount = businessData.photos?.length || 0;
    if (photoCount > 0) score += 20;
    if (photoCount >= 5) score += 20;
    if (photoCount >= 10) score += 20;

    // Reviews & Reputation (60 points)
    const reviewCount = businessData.metadata?.reviewCount || 0;
    const rating = businessData.metadata?.averageRating || 0;
    if (reviewCount > 0) score += 20;
    if (reviewCount >= 10) score += 20;
    if (rating >= 4.0) score += 20;

    // Additional Features (40 points)
    if (businessData.attributes?.length > 0) score += 10;
    if (businessData.profile?.description) score += 10;
    if (businessData.serviceItems?.length > 0) score += 10;
    if (businessData.posts?.length > 0) score += 10;

    // Business Performance (20 points)
    if (businessData.metadata?.newReviewCount > 0) score += 10;
    if (businessData.latlng) score += 10;

    return Math.min(score, maxPossibleScore);
  }, []);

  // Calculate gauge rotation based on score (0-180 degrees for semi-circle)
  const calculateGaugeRotation = useCallback((score, maxScore) => {
    const percentage = (score / maxScore) * 100;
    // Convert percentage to degrees (0-180 for semi-circle)
    return (percentage / 100) * 180;
  }, []);

  // Load competitor analysis data from localStorage
  const searchAndLoadCompetitorData = useCallback(() => {
    try {
      const currentBusinessName = locationData?.title || locationData?.name;
      if (!currentBusinessName) {
        logger.log('�s��,? [loadCompetitorData] No business name available yet');
        setCompetitorData(null);
        setKeywordResults({});
        setSelectedKeyword(null);
        setGmbScore(null);
        setDataLoadedFromCache(false);
        return false;
      }

      logger.log('dY", [loadCompetitorData] Business name:', currentBusinessName);
      const storageKey = `visibeen_competitor_analysis_${currentBusinessName.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`;
      const savedMultiKeywordData = localStorage.getItem(`${storageKey}_multi_keywords`);

      if (!savedMultiKeywordData) {
        logger.log('�s��,? [Competitor Data] No cached data for this business yet');
        setCompetitorData(null);
        setKeywordResults({});
        setSelectedKeyword(null);
        setGmbScore(null);
        setDataLoadedFromCache(false);
        return false;
      }

      const parsedMultiKeyword = JSON.parse(savedMultiKeywordData);
      setKeywordResults(parsedMultiKeyword);

      const keywords = Object.keys(parsedMultiKeyword);
      if (keywords.length === 0) {
        logger.log('�s��,? [Competitor Data] Cached data missing keywords - clearing');
        setCompetitorData(null);
        setKeywordResults({});
        setSelectedKeyword(null);
        setGmbScore(null);
        setDataLoadedFromCache(false);
        return false;
      }

      const mostRecentKeyword = keywords.reduce((latest, current) => {
        const latestTime = new Date(parsedMultiKeyword[latest].timestamp);
        const currentTime = new Date(parsedMultiKeyword[current].timestamp);
        return currentTime > latestTime ? current : latest;
      });

      setSelectedKeyword(mostRecentKeyword);
      setCompetitorData(parsedMultiKeyword[mostRecentKeyword]);
      setDataLoadedFromCache(true);

      const recentEntry = parsedMultiKeyword[mostRecentKeyword];
      if (recentEntry.myBusinessScore && typeof recentEntry.myBusinessScore === 'number') {
        setGmbScore(recentEntry.myBusinessScore);
      }

      if (recentEntry.myBusinessRank && recentEntry.myBusinessScore) {
        try {
          // Analyze competitor categories for category suggestion task
          const userCategory = locationData?.categories?.primaryCategory ||
            locationData?.primaryCategory ||
            locationData?.category;
          const categoryInsights = analyzeCompetitorCategories(
            recentEntry.competitors || [],
            userCategory
          );

          saveCompetitorTrackingSnapshot({
            rank: recentEntry.myBusinessRank,
            score: recentEntry.myBusinessScore,
            keyword: mostRecentKeyword,
            businessName: locationData?.title || locationData?.name || currentBusinessName || '',
            profileId: locationId || locationData?.location_id || null,
            // Add grid point data for performance page display
            greenGridPoints: recentEntry.greenGridPoints || 0,
            yellowGridPoints: recentEntry.yellowGridPoints || 0,
            redGridPoints: recentEntry.redGridPoints || 0,
            grayGridPoints: recentEntry.grayGridPoints || 0,
            totalGridPoints: recentEntry.totalGridPoints || 0,
            averageRank: recentEntry.myBusinessRank || 0,
            // Include category insights for Critical Tasks
            competitorInsights: categoryInsights ? {
              suggestedPrimaryCategory: categoryInsights.suggestedCategory,
              matchesCompetitors: categoryInsights.matchesCompetitors,
              confidence: categoryInsights.confidence,
              competitorStats: categoryInsights.competitorStats,
              message: categoryInsights.message
            } : null
          });
          logger.log('[Performance Tracker] Cached multi-keyword data saved with category insights');
        } catch (error) {
          logger.error('[Performance Tracker] Error saving cached multi-keyword data:', error);
        }
      }

      return true;
    } catch (err) {
      logger.error('�?O [Competitor Data] Failed to load:', err);
      return false;
    }
  }, [locationData, locationId]);

  const fetchCompetitorDataFromServer = useCallback(async () => {
    const currentBusinessName = locationData?.title || locationData?.name;
    if (!currentBusinessName) {
      logger.log('[Competitor Data] No business name available for server fetch');
      return false;
    }

    // Check if admin is viewing client - use client's user ID instead of admin's
    let userId;
    try {
      const adminAccessData = sessionStorage.getItem('adminAccessData');
      if (adminAccessData) {
        const accessInfo = JSON.parse(adminAccessData);
        userId = accessInfo.targetUserId;
        logger.log('[Competitor Data] Admin viewing mode - using client user ID:', userId);
      }
    } catch (error) {
      logger.warn('[Competitor Data] Error checking admin access data:', error);
    }

    // Fallback to session user ID if not admin viewing
    if (!userId) {
      const session = getSession();
      userId = session?.id || session?.user?.id;
    }

    if (!userId) {
      logger.warn('[Competitor Data] Missing user ID, cannot fetch competitor history from server');
      return false;
    }

    try {
      const historyParams = {
        business_name: currentBusinessName,
        limit: 25
      };
      const locationFromData = locationId || locationData?.location_id;
      if (locationFromData) {
        historyParams.location_id = locationFromData;
      }

      logger.log('[Competitor Data] Fetching server-side analyses for:', currentBusinessName);
      const historyResponse = await CompetitorAnalysisService.getHistory(userId, historyParams);

      let analyses = [];
      if (Array.isArray(historyResponse?.data?.analyses)) {
        analyses = historyResponse.data.analyses;
      } else if (Array.isArray(historyResponse?.data)) {
        analyses = historyResponse.data;
      } else if (Array.isArray(historyResponse?.analyses)) {
        analyses = historyResponse.analyses;
      }

      if (!Array.isArray(analyses) || analyses.length === 0) {
        logger.log('[Competitor Data] No server history found for this business');
        return false;
      }

      const keywordMap = {};
      analyses.forEach((analysis) => {
        if (!analysis?.keyword) return;

        const timestampIso = analysis.analysis_date
          ? new Date(analysis.analysis_date).toISOString()
          : new Date().toISOString();

        const rawCompetitors = analysis.competitors_data ?? analysis.competitorsData ?? [];
        let competitorsList = [];
        if (Array.isArray(rawCompetitors)) {
          competitorsList = rawCompetitors;
        } else if (typeof rawCompetitors === 'string') {
          try {
            const parsed = JSON.parse(rawCompetitors);
            if (Array.isArray(parsed)) {
              competitorsList = parsed;
            } else if (parsed && Array.isArray(parsed.competitors)) {
              competitorsList = parsed.competitors;
            }
          } catch (parseError) {
            logger.warn('[Competitor Data] Unable to parse competitors_data from server', parseError);
          }
        } else if (rawCompetitors && Array.isArray(rawCompetitors.competitors)) {
          competitorsList = rawCompetitors.competitors;
        }

        const rawScore =
          typeof analysis.my_business_score === 'number'
            ? analysis.my_business_score
            : parseFloat(analysis.my_business_score);
        const safeScore = Number.isFinite(rawScore) ? rawScore : null;

        const rawRank =
          analysis.my_business_rank !== undefined && analysis.my_business_rank !== null
            ? Number(analysis.my_business_rank)
            : null;
        const safeRank = Number.isFinite(rawRank) ? rawRank : null;

        const normalizedEntry = {
          keyword: analysis.keyword,
          competitors: competitorsList,
          myBusinessRank: safeRank,
          myBusinessScore: safeScore,
          myBusinessName: analysis.business_name || currentBusinessName,
          totalFound: analysis.total_competitors || competitorsList.length || 0,
          timestamp: timestampIso,
          savedAt: timestampIso,
          status: 'completed',
          source: 'database',
          analysisId: analysis.id
        };

        const existing = keywordMap[analysis.keyword];
        if (!existing || new Date(timestampIso) > new Date(existing.timestamp || existing.savedAt || 0)) {
          keywordMap[analysis.keyword] = normalizedEntry;
        }
      });

      const keywordKeys = Object.keys(keywordMap);
      if (keywordKeys.length === 0) {
        logger.log('[Competitor Data] Server history did not include usable keyword entries');
        return false;
      }

      const latestKeyword = keywordKeys.reduce((latest, current) => {
        if (!latest) return current;
        const latestTime = new Date(keywordMap[latest].timestamp);
        const currentTime = new Date(keywordMap[current].timestamp);
        return currentTime > latestTime ? current : latest;
      }, null);

      setKeywordResults(keywordMap);
      setDataLoadedFromCache(true);

      if (latestKeyword) {
        const activeEntry = keywordMap[latestKeyword];
        setSelectedKeyword(latestKeyword);
        setCompetitorData(activeEntry);
        if (activeEntry.myBusinessScore) {
          setGmbScore(activeEntry.myBusinessScore);
        }
        if (activeEntry.myBusinessRank && activeEntry.myBusinessScore) {
          try {
            // Analyze competitor categories for category suggestion task
            const userCategory = locationData?.categories?.primaryCategory ||
              locationData?.primaryCategory;
            const categoryInsights = analyzeCompetitorCategories(
              activeEntry.competitors || [],
              userCategory
            );

            saveCompetitorTrackingSnapshot({
              rank: activeEntry.myBusinessRank,
              score: activeEntry.myBusinessScore,
              keyword: latestKeyword,
              businessName: currentBusinessName,
              profileId: locationId || locationData?.location_id || null,
              // Add grid point data for performance page display
              greenGridPoints: activeEntry.greenGridPoints || 0,
              yellowGridPoints: activeEntry.yellowGridPoints || 0,
              redGridPoints: activeEntry.redGridPoints || 0,
              grayGridPoints: activeEntry.grayGridPoints || 0,
              totalGridPoints: activeEntry.totalGridPoints || 0,
              averageRank: activeEntry.myBusinessRank || 0,
              competitorInsights: categoryInsights ? {
                suggestedPrimaryCategory: categoryInsights.suggestedCategory,
                matchesCompetitors: categoryInsights.matchesCompetitors,
                confidence: categoryInsights.confidence,
                competitorStats: categoryInsights.competitorStats,
                message: categoryInsights.message
              } : null
            });
          } catch (error) {
            logger.error('[Competitor Data] Failed to persist server snapshot to tracker:', error);
          }
        }
      }

      const storageKey = `visibeen_competitor_analysis_${currentBusinessName.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`;
      try {
        localStorage.setItem(`${storageKey}_multi_keywords`, JSON.stringify(keywordMap));
      } catch (storageError) {
        logger.warn('[Competitor Data] Unable to cache server data locally:', storageError);
      }

      return true;
    } catch (error) {
      logger.error('[Competitor Data] Error fetching history from server:', error);
      return false;
    }
  }, [locationData, locationId]);

  useEffect(() => {
    // Check if admin is viewing - always fetch from server for admin access
    let isAdminAccess = false;
    try {
      const adminAccessData = sessionStorage.getItem('adminAccessData');
      if (adminAccessData) {
        isAdminAccess = true;
      }
    } catch (e) {
      // ignore
    }

    if (isAdminAccess) {
      // For admin access, skip localStorage and fetch directly from server
      logger.log('[Competitor Data] Admin access detected - fetching directly from server');
      fetchCompetitorDataFromServer();
    } else {
      // For regular users, try localStorage first, then server
      const hasLocalData = searchAndLoadCompetitorData();
      if (!hasLocalData) {
        fetchCompetitorDataFromServer();
      }
    }
  }, [searchAndLoadCompetitorData, fetchCompetitorDataFromServer]);

  // Dynamic Profile Gauge Component
  const DynamicProfileGauge = ({ score, maxScore, width = 280, height = 160 }) => {
    const percentage = Math.min(100, Math.max(0, (score / maxScore) * 100));

    const getScoreStatus = (pct) => {
      if (pct < 20) return { text: 'Poor', color: '#dc2626', bg: '#fef2f2', border: '#fecaca' };
      if (pct < 40) return { text: 'Fair', color: '#ea580c', bg: '#fff7ed', border: '#fed7aa' };
      if (pct < 60) return { text: 'Good', color: '#ca8a04', bg: '#fefce8', border: '#fef08a' };
      if (pct < 80) return { text: 'Great', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' };
      return { text: 'Excellent', color: '#059669', bg: '#ecfdf5', border: '#a7f3d0' };
    };

    const getScoreColor = (pct) => {
      if (pct < 20) return '#ef4444';
      if (pct < 40) return '#f97316';
      if (pct < 60) return '#eab308';
      if (pct < 80) return '#22c55e';
      return '#10b981';
    };

    const status = getScoreStatus(percentage);
    const cx = 140;
    const cy = 140;
    const radius = 100;
    const strokeWidth = 18;
    const arcLength = Math.PI * radius;
    const progressOffset = arcLength * (1 - percentage / 100);

    const needleAngle = 180 + (180 * percentage) / 100;
    const needleRad = (needleAngle * Math.PI) / 180;
    const needleLength = radius - 22;
    const needleTip = {
      x: cx + needleLength * Math.cos(needleRad),
      y: cy + needleLength * Math.sin(needleRad)
    };

    const tickMarks = [];
    for (let i = 0; i <= 10; i++) {
      const angle = 180 + (i * 180) / 10;
      const rad = (angle * Math.PI) / 180;
      const innerR = radius + strokeWidth / 2 + 3;
      const outerR = radius + strokeWidth / 2 + (i % 5 === 0 ? 10 : 6);
      tickMarks.push({
        x1: cx + innerR * Math.cos(rad),
        y1: cy + innerR * Math.sin(rad),
        x2: cx + outerR * Math.cos(rad),
        y2: cy + outerR * Math.sin(rad),
        major: i % 5 === 0
      });
    }

    const gradientId = `profile-gauge-gradient-${Math.random().toString(36).substr(2, 9)}`;

    return (
      <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <svg width={width} height={height} viewBox="0 0 280 160" style={{ overflow: 'visible' }}>
          <defs>
            <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#ef4444" />
              <stop offset="25%" stopColor="#f97316" />
              <stop offset="50%" stopColor="#eab308" />
              <stop offset="75%" stopColor="#22c55e" />
              <stop offset="100%" stopColor="#10b981" />
            </linearGradient>
            <filter id="needleGlow">
              <feGaussianBlur stdDeviation="2" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          <path
            d={`M ${cx - radius} ${cy} A ${radius} ${radius} 0 0 1 ${cx + radius} ${cy}`}
            fill="none"
            stroke="#e5e7eb"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />

          <path
            d={`M ${cx - radius} ${cy} A ${radius} ${radius} 0 0 1 ${cx + radius} ${cy}`}
            fill="none"
            stroke={`url(#${gradientId})`}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={arcLength}
            strokeDashoffset={progressOffset}
            style={{ transition: 'stroke-dashoffset 1.2s ease-out' }}
          />

          {tickMarks.map((tick, i) => (
            <line
              key={i}
              x1={tick.x1}
              y1={tick.y1}
              x2={tick.x2}
              y2={tick.y2}
              stroke={tick.major ? '#9ca3af' : '#d1d5db'}
              strokeWidth={tick.major ? 2 : 1}
              strokeLinecap="round"
            />
          ))}

          <line
            x1={cx}
            y1={cy}
            x2={needleTip.x}
            y2={needleTip.y}
            stroke="#1f2937"
            strokeWidth={4}
            strokeLinecap="round"
            filter="url(#needleGlow)"
            style={{ transition: 'all 1.2s ease-out' }}
          />

          <circle cx={cx} cy={cy} r={10} fill="#1f2937" />
          <circle cx={cx} cy={cy} r={5} fill={getScoreColor(percentage)} />

          <text x="35" y={cy + 18} fontSize="11" fill="#9ca3af" fontFamily="Inter, sans-serif" fontWeight="500">0</text>
          <text x="240" y={cy + 18} fontSize="11" fill="#9ca3af" fontFamily="Inter, sans-serif" fontWeight="500">{maxScore}</text>
        </svg>

        <Box sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '5px 12px',
          borderRadius: '16px',
          backgroundColor: status.bg,
          border: `1px solid ${status.border}`,
          marginTop: '-8px'
        }}>
          <Typography sx={{
            fontSize: '12px',
            fontWeight: 600,
            color: status.color,
            fontFamily: 'Inter, sans-serif'
          }}>
            {status.text}
          </Typography>
        </Box>
      </Box>
    );
  };

  const hasEvent = (day) => {
    const checkDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    return calendarEvents.some(event => isSameDay(event.date, checkDate));
  };

  const isToday = (day) => {
    const checkDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    return isSameDay(checkDate, new Date());
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const generateCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  };

  // Mock data for services (products removed)

  const mockServices = [
    {
      id: 1,
      title: 'Consultation Service',
      price: '$50',
      image: '/api/placeholder/200/120'
    },
    {
      id: 2,
      title: 'Premium Service',
      price: '$100',
      image: '/api/placeholder/200/120'
    },
    {
      id: 3,
      title: 'Basic Service',
      price: '$30',
      image: '/api/placeholder/200/120'
    },
    {
      id: 4,
      title: 'Advanced Service',
      price: '$150',
      image: '/api/placeholder/200/120'
    },
    {
      id: 5,
      title: 'Express Service',
      price: '$75',
      image: '/api/placeholder/200/120'
    }
  ];

  // Notification handler
  const handleNotifications = useCallback((newNotifications, allNotifications) => {
    logger.log('Received notifications:', newNotifications.length, 'new,', allNotifications.length, 'total');
    setNotifications(allNotifications);
    if (newNotifications.length > 0) {
      setNewNotificationCount(prev => prev + newNotifications.length);
    }
  }, []);

  // Fetch local posts for GMB Feed
  const fetchLocalPosts = useCallback(async () => {
    logger.log('[BusinessProfile] fetchLocalPosts called with locationId:', locationId);
    if (!locationId) {
      logger.log('[BusinessProfile] No locationId, returning early');
      return;
    }

    setLocalPostsLoading(true);
    try {
      // Direct API call without using notification service for account detection
      const accessToken = await GMBService.getAccessToken();
      logger.log('[BusinessProfile] Got access token, fetching accounts...');

      const accounts = await GMBService.getAccounts(accessToken);
      logger.log('[BusinessProfile] Available accounts:', accounts);

      let foundAccountId = null;

      // Search through accounts to find the one that owns this location
      for (const account of accounts) {
        try {
          const locations = await GMBService.getLocations(accessToken, account.name);
          const matchingLocation = locations.find(loc => {
            const locId = loc.name?.split('/').pop();
            return locId === locationId;
          });

          if (matchingLocation) {
            foundAccountId = account.name.split('/').pop();
            logger.log('[BusinessProfile] Found account for location:', foundAccountId);
            break;
          }
        } catch (err) {
          logger.warn('[BusinessProfile] Error checking account:', account.name, err);
        }
      }

      if (foundAccountId) {
        logger.log('[BusinessProfile] Fetching local posts for account:', foundAccountId, 'location:', locationId);
        const posts = await GMBService.getLocalPosts(accessToken, foundAccountId, locationId);
        logger.log('[BusinessProfile] Local posts API response:', posts);
        logger.log('[BusinessProfile] Setting localPosts state with:', posts?.length || 0, 'posts');
        setLocalPosts(posts || []);
      } else {
        logger.warn('[BusinessProfile] No account found that owns location:', locationId);
        setLocalPosts([]);
      }
    } catch (error) {
      logger.error('[BusinessProfile] Error fetching local posts:', error);
      setLocalPosts([]);
    } finally {
      setLocalPostsLoading(false);
    }
  }, [locationId]);

  // Persist a Business Profile snapshot for "Your Data"
  useEffect(() => {
    try {
      if (!locationData) return;
      const loc = locationData;

      const name = loc?.title || loc?.locationName || '';
      const address = loc?.storefrontAddress?.addressLines?.join(', ') || '';
      const number = loc?.phoneNumbers?.primaryPhone || '';
      const primaryCategory = loc?.categories?.primaryCategory;
      const additionalCategories = Array.isArray(loc?.categories?.additionalCategories) ? loc.categories.additionalCategories : [];
      const category = [primaryCategory?.displayName || primaryCategory?.name, ...additionalCategories.map(c => c?.displayName || c?.name)].filter(Boolean).join(' | ');
      const serviceArea = loc?.serviceArea?.places || loc?.serviceArea?.region || loc?.serviceArea?.placeInfos || undefined;
      const city = loc?.storefrontAddress?.locality || '';
      const hasPrimaryCategory = !!(primaryCategory?.displayName || primaryCategory?.name);
      const hasServiceArea = !!(
        loc?.serviceArea && (
          (Array.isArray(loc?.serviceArea?.places) && loc.serviceArea.places.length > 0) ||
          loc?.serviceArea?.region ||
          loc?.serviceArea?.placeInfos
        )
      );
      // City in Category: strictly true only if structured locality is present
      const cityInCategory = !!city;
      logger.log('[BusinessProfile] CityInCategory check (strict locality rule):', {
        cityValue: city,
        hasLocality: !!city,
        cityInCategory
      });
      const description = loc?.profile?.description || '';
      const websiteLink = loc?.websiteUri || '';
      const bookAppointment = loc?.appointmentUrl || loc?.profile?.appointmentLink || '';
      const timings = (loc?.regularHours?.periods?.length || loc?.moreHours?.length) ? 'Available' : '';
      const photos = Array.isArray(mediaItems) ? mediaItems.length : undefined;
      const attributes = Array.isArray(loc?.attributes) ? loc.attributes.map(a => a?.name || a?.displayName).filter(Boolean).join(' | ') : '';
      const labels = Array.isArray(loc?.labels) ? loc.labels.join(' | ') : '';
      const pinToMapLocation = (loc?.latlng?.latitude != null && loc?.latlng?.longitude != null)
        ? `${loc.latlng.latitude}, ${loc.latlng.longitude}` : '';
      const gmbPosts = Array.isArray(localPosts) ? localPosts.length : undefined;
      // Derive verification status from location metadata
      const verified = (
        loc?.metadata?.verified === true ||
        String(loc?.metadata?.verificationState || '').toUpperCase() === 'VERIFIED'
      );

      const servicesStr = Array.isArray(services) && services.length ? services.map(s => s.title).join(' | ') : '';

      saveBusinessProfileSnapshot({
        name,
        address,
        number,
        category,
        products: '', // Products removed - only showing services
        services: servicesStr,
        serviceArea,
        cityInCategory,
        description,
        websiteLink,
        bookAppointment,
        timings,
        photos,
        attributes,
        labels,
        pinToMapLocation,
        gmbPosts,
        verified,
      });
    } catch (_) {
      // non-blocking
    }
  }, [locationData, services, localPosts, mediaItems]);

  // Start notification polling when component mounts
  useEffect(() => {
    if (locationId) {
      const startNotifications = async () => {
        try {
          setNotificationLoading(true);
          const accessToken = await GMBService.getAccessToken();

          // Add notification listener
          GMBNotificationService.addListener(handleNotifications);

          // Get initial notifications
          const initialNotifications = await GMBNotificationService.getRecentNotifications(accessToken, locationId);
          setNotifications(initialNotifications);

          // Start polling for new notifications (every 5 minutes)
          GMBNotificationService.startPolling(accessToken, locationId, 300000);

          logger.log('Notification system started for location:', locationId);
        } catch (error) {
          logger.error('Error starting notification system:', error);
        } finally {
          setNotificationLoading(false);
        }
      };

      // Fetch local posts for GMB Feed
      logger.log('[BusinessProfile] About to call fetchLocalPosts for locationId:', locationId);
      fetchLocalPosts();

      startNotifications();
    }

    // Cleanup on unmount
    return () => {
      GMBNotificationService.removeListener(handleNotifications);
      GMBNotificationService.stopPolling();
    };
  }, [locationId, handleNotifications, fetchLocalPosts]);

  // Fetch products and services from GMB API
  const fetchProductsAndServices = async () => {
    if (!locationId) return;

    try {
      const accessToken = localStorage.getItem('googleAccessToken') || sessionStorage.getItem('googleAccessToken');

      // Products fetching removed - only showing services
      // setProductsLoading(true);
      // try {
      //   logger.log('[BusinessProfile] Fetching products for location:', locationId);
      //   const productsData = await GMBService.getProducts(locationId, accessToken);
      //   logger.log('[BusinessProfile] Products fetched:', productsData);
      //   const transformedProducts = productsData.map((product, index) => ({
      //     id: product.name?.split('/').pop() || index + 1,
      //     title: product.productId || product.name || `Product ${index + 1}`,
      //     price: product.price?.amount ? `$${product.price.amount}` : 'Price on request',
      //     image: product.media?.[0]?.sourceUrl || '/api/placeholder/200/120',
      //     description: product.description || ''
      //   }));
      //   setProducts(transformedProducts);
      // } catch (error) {
      //   logger.warn('Error fetching products:', error);
      //   setProducts([]);
      // } finally {
      //   setProductsLoading(false);
      // }

      // Fetch services
      setServicesLoading(true);
      try {
        logger.log('[BusinessProfile] Fetching services for location:', locationId);
        const servicesData = await GMBService.getServiceItems(locationId, accessToken);
        logger.log('[BusinessProfile] Services fetched:', servicesData);

        // Transform API data to match our component structure
        const transformedServices = servicesData.map((service, index) => ({
          id: service.name?.split('/').pop() || index + 1,
          title: (service.serviceId || service.name || `Service ${index + 1}`).replace(/_/g, ' ')
        }));

        setServices(transformedServices);
      } catch (error) {
        logger.warn('Error fetching services:', error);
        setServices([]);
      } finally {
        setServicesLoading(false);
      }

    } catch (error) {
      logger.error('Error fetching services:', error);
      setServicesLoading(false);
    }
  };

  const fetchAvailableProfiles = async () => {
    setProfilesLoading(true);
    try {
      const accessToken = localStorage.getItem('googleAccessToken') || sessionStorage.getItem('googleAccessToken');
      logger.log('[Switch Account] Starting to fetch profiles...');
      const accounts = await GMBService.getAccounts(accessToken);
      logger.log('[Switch Account] Found accounts:', accounts.length);

      if (accounts.length === 0) {
        setAvailableProfiles([]);
        return;
      }

      // Fetch locations for ALL accounts (same as dashboard logic)
      const allLocations = [];
      for (const acct of accounts) {
        try {
          if (!acct?.name) continue;
          logger.log('[Switch Account] Fetching locations for account:', acct.name);
          const locs = await GMBService.getLocations(accessToken, acct.name);
          logger.log('[Switch Account] Found locations for account:', acct.name, locs?.length || 0);
          if (Array.isArray(locs) && locs.length) {
            // Attach account context to each location
            locs.forEach(l => allLocations.push({ account: acct, loc: l }));
          }
        } catch (e) {
          logger.warn('Failed to fetch locations for account', acct?.name, e?.message || e);
        }
      }
      logger.log('[Switch Account] Total locations found:', allLocations.length);

      if (allLocations.length === 0) {
        // Fallback: present accounts as rows if no locations are returned (same as dashboard)
        const fallback = accounts.map((account) => {
          let mappedStatus = 'unverified';
          const verificationState = account.verificationState;
          if (verificationState === 'VERIFIED') mappedStatus = 'verified';
          else if (verificationState === 'PENDING_VERIFICATION' || verificationState === 'PENDING') mappedStatus = 'pending_verification';
          else if (verificationState === 'SUSPENDED') mappedStatus = 'suspended';

          return {
            id: account.name?.split('/').pop(),
            name: account.accountName || 'Business Account',
            address: 'Account level - no location data',
            status: mappedStatus,
            locationId: account.name?.split('/').pop(),
            isCurrentLocation: account.name?.split('/').pop() === locationId
          };
        });
        setAvailableProfiles(fallback);
        return;
      }

      // Normalize locations to same format as dashboard
      const normalized = await Promise.all(allLocations.map(async ({ account, loc }) => {
        const id = loc.name?.split('/').pop();
        const address = loc.storefrontAddress?.addressLines?.join(', ') ||
          loc.storefrontAddress?.locality ||
          loc.storefrontAddress?.administrativeArea ||
          'Address not available';

        // Fetch verification via VoiceOfMerchantState API (same as dashboard)
        let simplifiedStatus = 'unknown';
        let hasVOM = false;
        try {
          const vom = await GMBService.getVoiceOfMerchantState(id, accessToken);
          simplifiedStatus = vom?.simplifiedStatus || 'unknown';
          hasVOM = vom?.raw?.hasVoiceOfMerchant === true;
        } catch (e) {
          logger.warn('VOM fetch failed for', id, e?.message || e);
        }

        // Map to UI status (same logic as dashboard)
        const status = (
          (hasVOM || simplifiedStatus === 'verified') ? 'verified' :
            simplifiedStatus === 'suspended' ? 'suspended' :
              simplifiedStatus === 'pending_verification' ? 'pending_verification' :
                'unverified'
        );

        return {
          id,
          name: loc.title || account?.accountName || 'Business Location',
          address,
          status,
          locationId: id,
          isCurrentLocation: id === locationId
        };
      }));

      logger.log('[Switch Account] Final normalized profiles:', normalized);
      setAvailableProfiles(normalized);
    } catch (err) {
      logger.error('Error fetching available profiles:', err);
    } finally {
      setProfilesLoading(false);
    }
  };

  // Define fetchLocationData outside useEffect so it can be called from other handlers
  const fetchLocationData = async () => {
    if (!locationId) {
      setError('No location ID provided');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Try to get from cache first
      const cacheKey = `business_profile_${locationId}`;
      const { data: cachedData, fromCache } = await cacheManager.getOrFetch(
        cacheKey,
        async () => {
          logger.log('🔄 Fetching business profile from API...');

          // First get accounts directly from Google API (token managed by GMBService)
          const accounts = await GMBService.getAccounts();

          if (accounts.length === 0) {
            throw new Error('No GMB accounts found');
          }

          // Find the account by ID or use the first one
          const targetAccount = accounts.find(acc =>
            acc.name.includes(locationId)
          ) || accounts[0];

          const accountId = targetAccount.name.split('/')[1];

          // Fetch locations for this account directly from Google API
          const locations = await GMBService.getLocations(undefined, targetAccount.name);

          if (locations.length === 0) {
            throw new Error('No locations found for this account');
          }

          // Find the specific location by ID or use the first one
          const targetLocation = locations.find(loc =>
            loc.name.includes(locationId) ||
            loc.name.split('/').pop() === locationId
          ) || locations[0];

          return {
            targetLocation,
            targetAccount,
            accounts
          };
        },
        20 * 60 * 1000 // 20 minutes cache for business profile
      );

      if (fromCache) {
        logger.log('✅ Loaded business profile from cache - instant!');
        setLoading(false); // Set loading to false immediately when using cache
      } else {
        logger.log('🔄 Fetched business profile from API - cached for next time');
      }

      const { targetLocation, targetAccount, accounts } = cachedData;

      // Add review count to location data if available from GMB locations API
      if (targetLocation && !targetLocation.reviewCount && !targetLocation.profile?.reviewCount) {
        // The location from GMB locations API might not have review count, we'll get it from reviews API
        logger.log('Location data missing review count, will rely on reviews API');
      }
      setLocationData(targetLocation);
      setEditStatus(deriveEditStatus(targetLocation));
      setCurrentAccount(targetAccount);

      // Save to localStorage for heatmap and other features
      if (targetLocation && targetAccount) {
        saveSelectedBusiness(targetLocation, targetAccount.name);
      }

      // Store all available accounts for profile switching (same as dashboard)
      const allProfiles = accounts.map((account) => {
        // Map GMB verification states to our status format
        let mappedStatus = 'unverified';
        const verificationState = account.verificationState;

        if (verificationState === 'VERIFIED') {
          mappedStatus = 'verified';
        } else if (verificationState === 'PENDING_VERIFICATION' || verificationState === 'PENDING') {
          mappedStatus = 'pending_verification';
        } else if (verificationState === 'SUSPENDED') {
          mappedStatus = 'suspended';
        } else if (verificationState === 'UNVERIFIED' || !verificationState) {
          mappedStatus = 'unverified';
        }

        return {
          id: account.name?.split('/').pop(),
          name: account.accountName || 'Business Account',
          address: 'Account level - no location data',
          status: mappedStatus,
          optimizationScore: account.vettedState || 'N/A',
          locationId: account.name?.split('/').pop(),
          accountType: account.type || 'UNKNOWN',
          verificationState: account.verificationState || 'UNKNOWN',
          vettedState: account.vettedState || 'UNKNOWN',
          isCurrentLocation: account.name?.split('/').pop() === locationId
        };
      });
      setAvailableProfiles(allProfiles);

      // Extract the location ID and account ID once for downstream calls
      const actualLocationId = targetLocation.name.split('/').pop();
      const accountId = targetAccount.name.split('/')[1];

      // Fetch VoiceOfMerchantState for verification status
      try {
        logger.log('[BusinessProfile] Fetching VoiceOfMerchantState for location:', actualLocationId);
        const { raw, simplifiedStatus } = await GMBService.getVoiceOfMerchantState(actualLocationId);
        const hasVOM = raw?.hasVoiceOfMerchant === true;
        const hasBA = raw?.hasBusinessAuthority === true;
        // Only treat hasVoiceOfMerchant OR explicit 'verified' as verified
        const derivedVerified = hasVOM || simplifiedStatus === 'verified';
        setVerificationState({
          loading: false,
          error: null,
          simplified: simplifiedStatus || 'unknown',
          raw,
          isVerified: derivedVerified,
        });
        logger.log('[BusinessProfile] VoiceOfMerchantState result:', { simplifiedStatus, hasVOM, hasBA, derivedVerified, raw });
      } catch (vomErr) {
        logger.warn('[BusinessProfile] Error fetching VoiceOfMerchantState:', vomErr);
        setVerificationState((prev) => ({
          ...prev,
          loading: false,
          error: vomErr?.message || 'Failed to fetch VoiceOfMerchantState',
        }));
      }

      // Fetch reviews data for this location directly
      try {
        const reviews = await GMBService.getReviews(undefined, targetAccount.name, targetLocation.name);
        setReviewsData(reviews);
        logger.log('Reviews data fetched successfully:', { count: reviews.length });
        // Persist to Your Data store
        const arr = Array.isArray(reviews?.reviews) ? reviews.reviews : (Array.isArray(reviews) ? reviews : []);
        const count = (typeof reviews?.totalReviewCount === 'number' && reviews.totalReviewCount >= 0) ? reviews.totalReviewCount : arr.length;
        const ratingNums = arr
          .map(r => (typeof r?.rating === 'number' ? r.rating : (typeof r?.starRating === 'number' ? r.starRating : undefined)))
          .filter(n => typeof n === 'number');
        const avg = (typeof reviews?.averageRating === 'number' && reviews.averageRating > 0)
          ? reviews.averageRating
          : (ratingNums.length ? (ratingNums.reduce((s, n) => s + n, 0) / ratingNums.length) : undefined);
        saveReviewsSnapshot({ totalReviewCount: count, averageRating: avg });

        // Compute response rates from Business Profile page as well
        try {
          const now = Date.now();
          const THIRTY_D_MS = 30 * 24 * 60 * 60 * 1000;
          const NINETY_D_MS = 90 * 24 * 60 * 60 * 1000;
          const getTime = (r) => {
            const t = r?.updateTime || r?.createTime;
            const ms = t ? Date.parse(t) : NaN;
            return Number.isFinite(ms) ? ms : undefined;
          };
          const hasReply = (r) => !!(r?.reviewReply && (r.reviewReply.comment || r.reviewReply.updateTime));

          const recent30 = arr.filter(r => {
            const ms = getTime(r);
            return ms !== undefined && (now - ms <= THIRTY_D_MS);
          });
          const rate30 = recent30.length ? (recent30.filter(hasReply).length / recent30.length) * 100 : 0;

          const recent90 = arr.filter(r => {
            const ms = getTime(r);
            return ms !== undefined && (now - ms <= NINETY_D_MS);
          });
          const rate90 = recent90.length ? (recent90.filter(hasReply).length / recent90.length) * 100 : 0;

          // Volatility: coefficient of variation of weekly review counts over last 90d
          const weeks = 13; // ~90 days
          const weekMs = 7 * 24 * 60 * 60 * 1000;
          const buckets = new Array(weeks).fill(0);
          recent90.forEach(r => {
            const ms = getTime(r);
            if (ms === undefined) return;
            const diff = now - ms;
            const idx = Math.floor(diff / weekMs);
            if (idx >= 0 && idx < weeks) buckets[idx] += 1;
          });
          const mean = buckets.reduce((s, n) => s + n, 0) / (buckets.length || 1);
          const variance = buckets.length ? buckets.reduce((s, n) => s + Math.pow(n - mean, 2), 0) / buckets.length : 0;
          const stdDev = Math.sqrt(variance);
          const volatility = mean > 0 ? (stdDev / mean) * 100 : 0; // percent

          saveBusinessProfileSnapshot({ responseRate30d: rate30, responseRate90d: rate90, volatility });
        } catch (_) { /* non-blocking */ }
      } catch (reviewsError) {
        logger.warn('Error fetching reviews:', reviewsError);
        setReviewsData({ reviews: [] });
      }

      // Fetch media (photos and videos) for this location directly
      try {
        logger.log('Fetching media for account/location:', accountId, actualLocationId);
        let items = await GMBService.getMedia(undefined, accountId, actualLocationId);

        // Separate videos from photos
        const videos = items.filter(i => i.mediaFormat === 'VIDEO' && (i.googleUrl || i.thumbnailUrl || i.sourceUrl));
        const photos = items.filter(i => (i.mediaFormat === 'PHOTO' || !i.mediaFormat) && (i.googleUrl || i.thumbnailUrl || i.sourceUrl));

        // Process videos
        const processedVideos = videos.map(video => ({
          id: video.name?.split('/').pop() || Math.random().toString(),
          name: video.description || 'Business Video',
          url: video.googleUrl || video.sourceUrl || video.thumbnailUrl,
          thumbnail: video.thumbnailUrl || video.googleUrl || video.sourceUrl,
          duration: parseDuration(video.duration),
          uploadDate: video.createTime || new Date().toISOString()
        }));

        setVideos(processedVideos);
        logger.log('Videos fetched:', processedVideos.length);

        // Process photos - separate cover photos from regular photos
        const coverPhotos = photos.filter(i => i.category === 'COVER' || i.category === 'PROFILE');
        const regularPhotos = photos.filter(i => i.category !== 'COVER' && i.category !== 'PROFILE');

        const sortByTime = (a, b) => {
          const ta = a.createTime ? Date.parse(a.createTime) : 0;
          const tb = b.createTime ? Date.parse(b.createTime) : 0;
          return tb - ta;
        };

        coverPhotos.sort(sortByTime);
        regularPhotos.sort(sortByTime);

        const sortedItems = [...coverPhotos, ...regularPhotos];
        setMediaItems(sortedItems);

        logger.log('Media items (photos) after filter/sort:', sortedItems.length, 'Cover photos:', coverPhotos.length);
      } catch (mediaErr) {
        logger.warn('Error fetching media:', mediaErr);
        setMediaItems([]);
        setVideos([]);
      }

      // Fetch performance metrics for this location
      try {
        logger.log('Fetching performance metrics for location:', actualLocationId);
        const metrics = await GMBService.getPerformanceMetrics(undefined, actualLocationId);
        logger.log('Performance metrics fetched successfully:', metrics);
        setPerformanceData(metrics);
      } catch (performanceErr) {
        logger.warn('Error fetching performance metrics:', performanceErr);
        setPerformanceData(null);
      }

      // Fetch place action links (appointment booking, etc.) for this location
      try {
        logger.log('Fetching place action links for location:', actualLocationId);
        const placeActionLinks = await GMBService.getPlaceActionLinks(actualLocationId);
        logger.log('Place action links fetched successfully:', placeActionLinks);

        // Store place action links in locationData
        setLocationData(prev => ({
          ...prev,
          placeActionLinks: placeActionLinks
        }));
      } catch (placeActionErr) {
        logger.warn('Error fetching place action links:', placeActionErr);
      }

      setLoading(false);
    } catch (error) {
      logger.error('Error in main data fetch:', error);
      setError('Failed to load business profile data');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocationData();

    // Fetch available profiles for the dropdown
    fetchAvailableProfiles();

    // Fetch products and services
    fetchProductsAndServices();
  }, [locationId]);

  // Calculate real reviews data - handle new GMBService format (moved before early returns)
  const reviews = useMemo(() => {
    return Array.isArray(reviewsData?.reviews) ? reviewsData.reviews : (reviewsData?.reviews?.reviews || []);
  }, [reviewsData]);

  // Map Google's enum star ratings (e.g., 'FIVE') to numeric values
  const toNumericRating = useCallback((star) => {
    if (typeof star === 'number') return star;
    const map = { ONE: 1, TWO: 2, THREE: 3, FOUR: 4, FIVE: 5 };
    const key = String(star || '').toUpperCase();
    return map[key] || 0;
  }, []);

  const numericRatings = useMemo(() => {
    return reviews.map(r => toNumericRating(r.starRating));
  }, [reviews, toNumericRating]);

  const averageRating = useMemo(() => {
    return numericRatings.length > 0 ?
      numericRatings.reduce((sum, n) => sum + n, 0) / numericRatings.length : 0;
  }, [numericRatings]);

  const ratingDistribution = useMemo(() => {
    return [5, 4, 3, 2, 1].map(rating => {
      const count = numericRatings.filter(n => Math.round(n) === rating).length;
      let percentage;
      if (numericRatings.length > 0) {
        percentage = (count / numericRatings.length) * 100;
      } else if (averageRating > 0) {
        const roundedAvg = Math.round(averageRating);
        percentage = rating === roundedAvg ? 100 : 0;
      } else {
        percentage = 0;
      }
      return { rating, count, percentage };
    });
  }, [numericRatings, averageRating]);

  const ratingCounts = useMemo(() => {
    const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    ratingDistribution.forEach(item => {
      counts[item.rating] = item.count;
    });
    return counts;
  }, [ratingDistribution]);

  const reviewsLast7Days = useMemo(() => {
    const threshold = new Date();
    threshold.setDate(threshold.getDate() - 7);
    const thresholdTime = threshold.getTime();
    return reviews.filter(review => {
      const createdTime = review?.createTime ? new Date(review.createTime).getTime() : NaN;
      return Number.isFinite(createdTime) && createdTime >= thresholdTime;
    });
  }, [reviews]);

  const reviewsLast30Days = useMemo(() => {
    const threshold = new Date();
    threshold.setDate(threshold.getDate() - 30);
    const thresholdTime = threshold.getTime();
    return reviews.filter(review => {
      const createdTime = review?.createTime ? new Date(review.createTime).getTime() : NaN;
      return Number.isFinite(createdTime) && createdTime >= thresholdTime;
    });
  }, [reviews]);

  const reviewsLast90Days = useMemo(() => {
    const threshold = new Date();
    threshold.setDate(threshold.getDate() - 90);
    const thresholdTime = threshold.getTime();
    return reviews.filter(review => {
      const createdTime = review?.createTime ? new Date(review.createTime).getTime() : NaN;
      return Number.isFinite(createdTime) && createdTime >= thresholdTime;
    });
  }, [reviews]);

  const commentedReviewsCount = useMemo(() => (
    reviews.filter(review => (review.comment || '').trim().length > 0).length
  ), [reviews]);

  const reviewHasReply = (review) => {
    const reply = review?.reviewReply;
    return !!(reply && (reply.comment || reply.updateTime));
  };

  const responseRate30 = useMemo(() => {
    if (!reviewsLast30Days.length) return 0;
    const replied = reviewsLast30Days.filter(reviewHasReply).length;
    return Math.round((replied / reviewsLast30Days.length) * 100);
  }, [reviewsLast30Days]);

  const responseRate90 = useMemo(() => {
    if (!reviewsLast90Days.length) return 0;
    const replied = reviewsLast90Days.filter(reviewHasReply).length;
    return Math.round((replied / reviewsLast90Days.length) * 100);
  }, [reviewsLast90Days]);

  const sentimentBreakdown = useMemo(() => {
    const total = numericRatings.length || 1;
    const breakdown = { positive: 0, neutral: 0, negative: 0 };
    numericRatings.forEach(rating => {
      if (rating >= 4) breakdown.positive += 1;
      else if (rating <= 2 && rating > 0) breakdown.negative += 1;
      else if (rating === 3) breakdown.neutral += 1;
    });
    return {
      ...breakdown,
      positivePct: total ? Math.round((breakdown.positive / total) * 100) : 0,
      neutralPct: total ? Math.round((breakdown.neutral / total) * 100) : 0,
      negativePct: total ? Math.round((breakdown.negative / total) * 100) : 0
    };
  }, [numericRatings]);

  const sortedReviews = useMemo(() => (
    [...reviews].sort((a, b) => {
      const dateA = a?.createTime ? new Date(a.createTime) : new Date(0);
      const dateB = b?.createTime ? new Date(b.createTime) : new Date(0);
      return dateB - dateA;
    })
  ), [reviews]);

  const filteredReviews = useMemo(() => (
    sortedReviews.filter(review => {
      if (reviewFilter === 'all') return true;
      if (reviewFilter === 'withComment') {
        return (review.comment || '').trim().length > 0;
      }
      const rating = toNumericRating(review.starRating);
      return rating === Number(reviewFilter);
    })
  ), [sortedReviews, reviewFilter, toNumericRating]);

  const displayedReviews = filteredReviews.slice(0, 4);
  const displayedReviewCount = displayedReviews.length;
  const filteredReviewCount = filteredReviews.length;

  // Memoize expensive operations to prevent scheduler violations
  const displayedNotifications = useMemo(() => {
    if (!notifications.length) return [];
    const notificationsToShow = showFeedDetails ? notifications : notifications.slice(0, 3);
    return notificationsToShow.map(notification => ({
      ...notification,
      formatted: GMBNotificationService.formatNotification(notification)
    }));
  }, [notifications, showFeedDetails]);

  const displayedLocalPosts = useMemo(() => {
    return localPosts ? localPosts.slice(0, 6) : [];
  }, [localPosts]);

  const getReviewerInitials = useCallback((reviewerName) => {
    if (!reviewerName) return 'AR';
    return reviewerName.split(' ').filter(Boolean).map(part => part[0]).join('').slice(0, 2).toUpperCase() || 'AR';
  }, []);

  const reviewFilterOptions = useMemo(() => (
    REVIEW_FILTERS.map(option => {
      let count = 0;
      if (option.value === 'all') {
        count = reviews.length;
      } else if (option.value === 'withComment') {
        count = commentedReviewsCount;
      } else {
        count = ratingCounts[option.value] || 0;
      }
      return { ...option, count };
    })
  ), [ratingCounts, reviews.length, commentedReviewsCount]);

  const activeFilterLabel = useMemo(() => (
    reviewFilterOptions.find(option => option.value === reviewFilter)?.label || 'All Reviews'
  ), [reviewFilterOptions, reviewFilter]);

  // Paginated reviews for modal
  const paginatedReviews = useMemo(() => {
    const startIndex = (reviewsPage - 1) * reviewsPerPage;
    const endIndex = startIndex + reviewsPerPage;
    return reviews.slice(startIndex, endIndex);
  }, [reviews, reviewsPage, reviewsPerPage]);

  const totalReviewPages = useMemo(() => {
    return Math.ceil(reviews.length / reviewsPerPage);
  }, [reviews.length, reviewsPerPage]);

  // Reset to first page when opening reviews modal
  const handleOpenReviewsModal = () => {
    setReviewsPage(1);
    setReviewsModalOpen(true);
  };

  // Scroll to top of modal content when changing pages
  useEffect(() => {
    if (reviewsModalOpen) {
      const modalContent = document.querySelector('[data-reviews-modal-content]');
      if (modalContent) {
        modalContent.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  }, [reviewsPage, reviewsModalOpen]);

  if (loading) {
    return (
      <DashboardLayout>
        <PageContainer>
          <ContentWrapper>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <CircularProgress />
            </Box>
          </ContentWrapper>
        </PageContainer>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <PageContainer>
          <ContentWrapper>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          </ContentWrapper>
        </PageContainer>
      </DashboardLayout>
    );
  }

  // Extract data from the location object
  const businessTitle = locationData?.title || 'Business Name';
  const businessAddress = locationData?.storefrontAddress ?
    `${locationData.storefrontAddress.addressLines?.join(', ') || ''}, ${locationData.storefrontAddress.locality || ''}, ${locationData.storefrontAddress.administrativeArea || ''} ${locationData.storefrontAddress.postalCode || ''}`.trim() :
    'Address not available';
  const primaryPhone = locationData?.phoneNumbers?.primaryPhone || 'Not available';
  const websiteUrl = locationData?.websiteUri || 'Not available';
  const createMapsUrl = () => {
    const placeId = locationData?.metadata?.placeId || locationData?.placeId || '';
    if (placeId && String(placeId).trim()) {
      return `https://www.google.com/maps/place/?q=place_id:${placeId}`;
    }
    const encodedAddress = encodeURIComponent(businessAddress || '');
    return encodedAddress
      ? `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`
      : '';
  };
  const mapsUri = createMapsUrl();
  const isVerified = verificationState.isVerified;

  const currentPlaceId = locationData?.metadata?.placeId || locationData?.placeId || '';

  // Log Place ID for debugging
  console.log('[BusinessProfile] Place ID:', currentPlaceId);
  console.log('[BusinessProfile] Location metadata:', locationData?.metadata);
  console.log('[BusinessProfile] Location placeId field:', locationData?.placeId);

  // Compute an authoritative profileKey to use for plan lookup
  const profileKey = locationData?.profileKey
    || locationData?.fullLocationName
    || locationData?.locationId
    || (locationData?.accountData && locationData?.locationId ? `${(locationData.accountData.name || '').toString().split('/').pop()}:${locationData.locationId}` : null)
    || businessTitle;

  const goToHeatmap = () => {
    const qs = new URLSearchParams();
    if (businessTitle) qs.set('businessName', businessTitle);
    if (locationId) qs.set('locationId', locationId);

    // Get coordinates for heatmap
    const lat = locationData?.latlng?.latitude || locationData?.coordinates?.lat;
    const lng = locationData?.latlng?.longitude || locationData?.coordinates?.lng;
    if (lat) qs.set('lat', lat);
    if (lng) qs.set('lng', lng);

    // Check if admin is viewing client - add admin view params
    try {
      const adminAccessData = sessionStorage.getItem('adminAccessData');
      if (adminAccessData) {
        const accessInfo = JSON.parse(adminAccessData);
        qs.set('userId', accessInfo.targetUserId);
        qs.set('adminView', 'true');
        logger.log('[goToHeatmap] Admin viewing mode - adding admin params');
      }
    } catch (error) {
      logger.warn('[goToHeatmap] Error checking admin access data:', error);
    }

    // Navigate to heatmap results page
    navigate(`/heatmap-results?${qs.toString()}`);
  };

  // Build review link for QR code with robust fallbacks
  const safeReviewLink = (() => {
    // PRIORITY 1: Use freshly fetched newReviewUri (g.page/r/... format - most reliable)
    if (freshNewReviewUri) {
      console.log('[Review QR] Using fresh newReviewUri (g.page format):', freshNewReviewUri);
      return freshNewReviewUri;
    }

    // PRIORITY 2: Use newReviewUri from locationData metadata
    const newReviewUri = locationData?.metadata?.newReviewUri || locationData?.newReviewUri;
    if (newReviewUri) {
      console.log('[Review QR] Using newReviewUri from locationData:', newReviewUri);
      return newReviewUri;
    }

    // PRIORITY 3: Use Place ID for direct review link
    if (currentPlaceId) {
      console.log('[Review QR] Using Place ID for review link:', currentPlaceId);
      return `https://search.google.com/local/writereview?placeid=${currentPlaceId}`;
    }

    // PRIORITY 4: Use Maps URI if available
    if (mapsUri) {
      console.log('[Review QR] Using Maps URI for review link:', mapsUri);
      return mapsUri;
    }

    // PRIORITY 5: Fallback to address-based search
    const encodedAddress = encodeURIComponent(businessAddress || '');
    if (encodedAddress) {
      console.log('[Review QR] Using address for review link:', businessAddress);
      return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
    }

    console.warn('[Review QR] No valid data for review link generation');
    return '';
  })();

  const qrProviders = [
    (url) => `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(url)}`,
    (url) => `https://quickchart.io/qr?text=${encodeURIComponent(url)}&size=220`
  ];
  const qrSrc = safeReviewLink ? qrProviders[qrProviderIndex](safeReviewLink) : ''
  const shareUrl = mapsUri || window.location.href;
  const shareText = `${businessTitle} on Google`;
  const openShareMenu = (event) => setShareAnchorEl(event.currentTarget);
  const closeShareMenu = () => setShareAnchorEl(null);
  const handleNativeShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: businessTitle, text: shareText, url: shareUrl });
      } else {
        window.open(shareUrl, '_blank');
      }
    } catch (_) { }
    closeShareMenu();
  };
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      alert('Link copied to clipboard');
    } catch (_) {
      // fallback
    }
    closeShareMenu();
  };
  const handleSharePlatform = (platform) => {
    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedText = encodeURIComponent(shareText);
    let url = '';
    if (platform === 'whatsapp') url = `https://api.whatsapp.com/send?text=${encodedText}%20${encodedUrl}`;
    if (platform === 'facebook') url = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
    if (platform === 'twitter') url = `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`;
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
    closeShareMenu();
  };

  // Reverse geocoding function to convert coordinates to address
  const reverseGeocode = async (lat, lng) => {
    setReverseGeocoding(true);

    try {
      // Try using a free reverse geocoding service (Nominatim)
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1&accept-language=en`
      );

      if (!response.ok) {
        throw new Error('Geocoding service unavailable');
      }

      const data = await response.json();

      if (data && data.display_name) {
        // Clean up the address format
        let address = data.display_name;

        // Remove country if it's at the end
        address = address.replace(/, [A-Za-z\s]+$/, '');

        // For Punjab, India area, add some context
        if (lat > 30 && lat < 31 && lng > 76 && lng < 77) {
          if (!address.includes('Punjab')) {
            address += ', Punjab, India';
          }
        }

        return address;
      }
    } catch (error) {
      logger.warn('Reverse geocoding failed:', error);
    } finally {
      setReverseGeocoding(false);
    }

    // Fallback: Create a descriptive address based on coordinates
    const region = lat > 30 && lat < 31 && lng > 76 && lng < 77 ? 'Punjab, India' : 'Unknown Region';
    return `Location at ${lat.toFixed(4)}, ${lng.toFixed(4)}, ${region}`;
  };

  // Function to extract appointment booking links from GMB data
  const getAppointmentLinks = () => {
    if (!locationData) return [];

    // Log the entire locationData to see what we have
    console.log('[getAppointmentLinks] Full locationData:', locationData);
    console.log('[getAppointmentLinks] placeActionLinks:', locationData?.placeActionLinks);
    console.log('[getAppointmentLinks] relationshipData:', locationData?.relationshipData);
    console.log('[getAppointmentLinks] adWordsLocationExtensions:', locationData?.adWordsLocationExtensions);
    console.log('[getAppointmentLinks] moreHours:', locationData?.moreHours);
    console.log('[getAppointmentLinks] websiteUri:', locationData?.websiteUri);
    console.log('[getAppointmentLinks] profile:', locationData?.profile);

    const appointmentLinks = [];

    // PRIORITY 1: Check for direct appointment URL fields
    if (locationData?.appointmentUrl) {
      appointmentLinks.push({
        url: locationData.appointmentUrl,
        type: 'APPOINTMENT_URL',
        isPreferred: true,
        source: 'Direct Field'
      });
    }

    if (locationData?.profile?.appointmentLink) {
      const alreadyExists = appointmentLinks.some(link => link.url === locationData.profile.appointmentLink);
      if (!alreadyExists) {
        appointmentLinks.push({
          url: locationData.profile.appointmentLink,
          type: 'PROFILE_APPOINTMENT',
          isPreferred: true,
          source: 'Profile'
        });
      }
    }

    // PRIORITY 2: Check for Place Action Links (official GMB appointment booking links)
    const placeActionLinks = locationData?.placeActionLinks || [];
    console.log('[getAppointmentLinks] Processing place action links:', placeActionLinks);

    placeActionLinks.forEach(link => {
      console.log('[getAppointmentLinks] Place action link:', link);
      const actionType = link.placeActionType || '';

      // Check for any action type that might be appointment-related
      if (link.uri) {
        const isAppointmentType = actionType === 'APPOINTMENT' ||
          actionType === 'ONLINE_APPOINTMENT' ||
          actionType === 'DINING_RESERVATION';

        const alreadyExists = appointmentLinks.some(l => l.url === link.uri);
        if (!alreadyExists) {
          appointmentLinks.push({
            url: link.uri,
            type: actionType || 'PLACE_ACTION',
            isPreferred: link.isPreferred || false,
            source: 'Place Action Link',
            isAppointmentType: isAppointmentType
          });
        }
      }
    });

    // PRIORITY 3: Check attributes for appointment-related data
    const attributes = locationData?.attributesApi || [];
    console.log('[getAppointmentLinks] Checking attributes:', attributes);

    const appointmentAttributes = attributes.filter(attr => {
      const attrName = (attr.attributeId || attr.name || '').toLowerCase();
      return attrName.includes('appointment') || attrName.includes('booking') || attrName.includes('reservation');
    });

    // Add attribute-based appointment links
    appointmentAttributes.forEach(attr => {
      if (attr.values && attr.values.length > 0) {
        attr.values.forEach(value => {
          if (value && typeof value === 'string' && (value.includes('http') || value.includes('www'))) {
            const alreadyExists = appointmentLinks.some(link => link.url === value);
            if (!alreadyExists) {
              appointmentLinks.push({
                url: value,
                type: 'ATTRIBUTE',
                source: 'Attribute'
              });
            }
          }
        });
      }
    });

    // PRIORITY 4: Check website URI - ALWAYS include it as Google does
    const websiteUri = locationData?.websiteUri || '';
    console.log('[getAppointmentLinks] Website URI:', websiteUri);

    if (websiteUri) {
      const alreadyExists = appointmentLinks.some(link => link.url === websiteUri);
      if (!alreadyExists) {
        appointmentLinks.push({
          url: websiteUri,
          type: 'WEBSITE',
          source: 'Website'
        });
      }
    }

    // PRIORITY 5: Check for social media links in relationshipData or other fields
    // Google often shows LinkedIn, Facebook, etc. as appointment booking options
    const socialMediaUrls = [];

    // Check if there are any social media fields
    if (locationData?.socialProfiles) {
      console.log('[getAppointmentLinks] Social profiles:', locationData.socialProfiles);
      Object.values(locationData.socialProfiles).forEach(url => {
        if (url && typeof url === 'string') {
          socialMediaUrls.push(url);
        }
      });
    }

    // Check profile for social links
    if (locationData?.profile) {
      const profileFields = ['linkedin', 'facebook', 'twitter', 'instagram', 'youtube'];
      profileFields.forEach(field => {
        if (locationData.profile[field]) {
          socialMediaUrls.push(locationData.profile[field]);
        }
      });
    }

    // Add social media URLs
    socialMediaUrls.forEach(url => {
      const alreadyExists = appointmentLinks.some(link => link.url === url);
      if (!alreadyExists) {
        appointmentLinks.push({
          url: url,
          type: 'SOCIAL_MEDIA',
          source: 'Social Media'
        });
      }
    });

    // PRIORITY 6: Check description for appointment URLs
    const appointmentKeywords = ['book', 'appointment', 'schedule', 'reserve', 'booking'];
    const checkForAppointmentUrls = (text) => {
      if (!text) return [];
      const urlRegex = /(https?:\/\/[^\s]+)/g;
      const urls = text.match(urlRegex) || [];
      return urls;
    };

    const description = locationData?.profile?.description || '';
    const descriptionUrls = checkForAppointmentUrls(description);
    console.log('[getAppointmentLinks] Description URLs:', descriptionUrls);

    descriptionUrls.forEach(url => {
      const alreadyExists = appointmentLinks.some(link => link.url === url);
      if (!alreadyExists) {
        appointmentLinks.push({
          url: url,
          type: 'DESCRIPTION',
          source: 'Description'
        });
      }
    });

    console.log('[getAppointmentLinks] Total appointment links found:', appointmentLinks.length);
    console.log('[getAppointmentLinks] Appointment links:', appointmentLinks);

    // If no appointment links found, return empty array
    if (appointmentLinks.length === 0) {
      return [];
    }

    // Sort by priority: Direct fields first, then Place Action Links (preferred ones at the top), then website, then social media, then others
    appointmentLinks.sort((a, b) => {
      // Preferred links first
      if (a.isPreferred && !b.isPreferred) return -1;
      if (!a.isPreferred && b.isPreferred) return 1;

      // Direct appointment fields
      if ((a.type === 'APPOINTMENT_URL' || a.type === 'PROFILE_APPOINTMENT') &&
        !(b.type === 'APPOINTMENT_URL' || b.type === 'PROFILE_APPOINTMENT')) return -1;
      if (!(a.type === 'APPOINTMENT_URL' || a.type === 'PROFILE_APPOINTMENT') &&
        (b.type === 'APPOINTMENT_URL' || b.type === 'PROFILE_APPOINTMENT')) return 1;

      // Official appointment action types
      if (a.isAppointmentType && !b.isAppointmentType) return -1;
      if (!a.isAppointmentType && b.isAppointmentType) return 1;

      // Website before social media
      if (a.type === 'WEBSITE' && b.type !== 'WEBSITE') return -1;
      if (a.type !== 'WEBSITE' && b.type === 'WEBSITE') return 1;

      return 0;
    });

    // Return the array of appointment link objects with formatted display info
    return appointmentLinks.map((linkObj, index) => {
      try {
        const url = new URL(linkObj.url);
        const domain = url.hostname.replace('www.', '');
        const typeLabel = linkObj.type === 'APPOINTMENT' || linkObj.type === 'ONLINE_APPOINTMENT'
          ? 'Official'
          : linkObj.source;

        return {
          ...linkObj,
          id: `appointment-${index}`,
          domain: domain,
          typeLabel: typeLabel,
          label: linkObj.label || domain
        };
      } catch (e) {
        return {
          ...linkObj,
          id: `appointment-${index}`,
          domain: linkObj.url,
          typeLabel: linkObj.source,
          label: linkObj.label || linkObj.url
        };
      }
    });
  };

  // Get the actual total review count from the reviews API response
  const actualTotalFromAPI = reviewsData?.totalReviewCount || reviewsData?.total || null;

  // Since locationData comes from GMB locations API (not reviews API), it won't have totalReviewCount
  // We need to use the totalReviewCount from the reviews API response directly
  const totalReviews = actualTotalFromAPI || numericRatings.length;


  // Get metrics data based on performance API response
  const metricsData = getMetricsData(performanceData);

  const handleProfileSwitch = (profile) => {
    navigate(`/business-profile?id=${profile.locationId}`);
    setAnchorEl(null);
  };

  // Review details handlers
  const handleReviewClick = (review) => {
    setSelectedReview(review);
    setReplyText(review.reviewReply?.comment || '');
    setReviewDetailsOpen(true);
  };

  const handleCloseReviewDetails = () => {
    setReviewDetailsOpen(false);
    setSelectedReview(null);
    setReplyText('');
  };

  // Helper function to generate AI reply text synchronously
  const generateAIReplyText = (review) => {
    try {
      logger.log('generateAIReplyText called with review:', review);

      const reviewerName = review?.reviewer?.displayName || 'Customer';
      const rating = toNumericRating(review?.starRating);
      const comment = review?.comment || '';
      const businessName = locationData?.title || locationData?.name || 'our business';

      logger.log('Review details:', { reviewerName, rating, comment, businessName });

      // Generate AI reply based on rating and comment
      let aiReply = '';

      if (rating >= 4) {
        // Positive review
        aiReply = `Thank you so much for your wonderful ${rating}-star review, ${reviewerName}! We're thrilled to hear you had a great experience with ${businessName}. ${comment ? 'Your feedback about our service means a lot to us.' : ''} We look forward to serving you again soon!`;
      } else if (rating === 3) {
        // Neutral review
        aiReply = `Thank you for your feedback, ${reviewerName}. We appreciate you taking the time to share your experience with ${businessName}. ${comment ? 'We\'ve noted your comments and are working to improve.' : ''} We'd love the opportunity to provide you with a better experience next time. Please feel free to reach out if you have any concerns.`;
      } else {
        // Negative review
        aiReply = `We sincerely apologize for your experience, ${reviewerName}. Your feedback is very important to us at ${businessName}. ${comment ? 'We take your concerns seriously and would like to make things right.' : ''} Please contact us directly so we can address this issue and ensure it doesn't happen again. We value your business and hope to have the opportunity to serve you better.`;
      }

      logger.log('Generated AI reply text:', aiReply);
      return aiReply;
    } catch (error) {
      logger.error('Error generating AI reply text:', error);
      return 'Thank you for your review! We appreciate your feedback.';
    }
  };

  const generateAIReply = async () => {
    if (!selectedReview) {
      logger.error('No review selected for AI reply generation');
      alert('Please select a review first');
      return;
    }

    logger.log('Generating AI reply for review:', selectedReview);
    setGeneratingReply(true);

    try {
      // Add small delay to show loading state
      await new Promise(resolve => setTimeout(resolve, 500));

      const aiReply = generateAIReplyText(selectedReview);
      logger.log('Generated AI reply:', aiReply);

      if (!aiReply || aiReply.trim() === '') {
        throw new Error('Generated reply is empty');
      }

      setReplyText(aiReply);
      logger.log('AI reply set successfully');
    } catch (error) {
      logger.error('Error generating AI reply:', error);
      alert('Failed to generate AI reply. Please try again.');
    } finally {
      setGeneratingReply(false);
    }
  };

  const submitReviewReply = async () => {
    if (!selectedReview || !replyText.trim()) {
      alert('Please enter a reply message');
      return;
    }

    setSubmittingReply(true);
    try {
      const accessToken = await GMBService.getAccessToken();
      const reviewName = selectedReview.name;

      // Submit reply to GMB
      await GMBService.replyToReview(accessToken, reviewName, replyText);

      alert('Reply submitted successfully!');
      handleCloseReviewDetails();

      // Refresh reviews to show the new reply
      if (locationId) {
        const accountId = currentAccount?.name?.split('/').pop();
        if (accountId) {
          const updatedReviews = await GMBService.getReviews(accessToken, `accounts/${accountId}`, `locations/${locationId}`);
          setReviewsData(updatedReviews);
        }
      }
    } catch (error) {
      logger.error('Error submitting reply:', error);
      alert('Failed to submit reply. Please try again.');
    } finally {
      setSubmittingReply(false);
    }
  };

  // Submit reply from modal (for "View All Reviews" popup)
  const submitModalReviewReply = async (review, reviewIndex) => {
    const replyText = modalReplyTexts[reviewIndex];

    if (!replyText || !replyText.trim()) {
      alert('Please enter a reply message');
      return;
    }

    setSubmittingModalReply(prev => ({ ...prev, [reviewIndex]: true }));
    try {
      const accessToken = await GMBService.getAccessToken();
      const reviewName = review.name;

      // Submit reply to GMB
      await GMBService.replyToReview(accessToken, reviewName, replyText);

      alert('Reply submitted successfully!');

      // Clear the reply text for this review
      setModalReplyTexts(prev => ({ ...prev, [reviewIndex]: '' }));

      // Refresh reviews to show the new reply
      if (locationId) {
        const accountId = currentAccount?.name?.split('/').pop();
        if (accountId) {
          const updatedReviews = await GMBService.getReviews(accessToken, `accounts/${accountId}`, `locations/${locationId}`);
          setReviewsData(updatedReviews);
        }
      }
    } catch (error) {
      logger.error('Error submitting reply:', error);
      alert('Failed to submit reply. Please try again.');
    } finally {
      setSubmittingModalReply(prev => ({ ...prev, [reviewIndex]: false }));
    }
  };

  // Handle edit reply - enable edit mode
  const handleEditReply = (reviewIndex, currentReplyText) => {
    setEditingReplyIndex(reviewIndex);
    setEditReplyText(currentReplyText);
  };

  // Handle cancel edit
  const handleCancelEditReply = () => {
    setEditingReplyIndex(null);
    setEditReplyText('');
  };

  // Handle update reply
  const handleUpdateReply = async (review, reviewIndex) => {
    if (!editReplyText || !editReplyText.trim()) {
      alert('Please enter a reply message');
      return;
    }

    setSubmittingModalReply(prev => ({ ...prev, [reviewIndex]: true }));
    try {
      const accessToken = await GMBService.getAccessToken();
      const reviewName = review.name;

      logger.log('[BusinessProfile] Updating reply for review:', reviewName);

      // Update reply to GMB (GMB uses same endpoint for create/update)
      await GMBService.replyToReview(accessToken, reviewName, editReplyText);

      alert('Reply updated successfully!');

      // Exit edit mode
      setEditingReplyIndex(null);
      setEditReplyText('');

      // Refresh reviews to show the updated reply
      if (locationId) {
        const accountId = currentAccount?.name?.split('/').pop();
        if (accountId) {
          const updatedReviews = await GMBService.getReviews(accessToken, `accounts/${accountId}`, `locations/${locationId}`);
          setReviewsData(updatedReviews);
        }
      }
    } catch (error) {
      logger.error('Error updating reply:', error);
      alert('Failed to update reply. Please try again.');
    } finally {
      setSubmittingModalReply(prev => ({ ...prev, [reviewIndex]: false }));
    }
  };

  // Handle delete reply
  const handleDeleteReply = async (review, reviewIndex) => {
    if (!window.confirm('Are you sure you want to delete this reply? This action cannot be undone.')) {
      return;
    }

    setDeletingReply(prev => ({ ...prev, [reviewIndex]: true }));
    try {
      const accessToken = await GMBService.getAccessToken();
      const reviewName = review.name;

      logger.log('[BusinessProfile] Deleting reply for review:', reviewName);

      // Delete reply from GMB
      await GMBService.deleteReviewReply(accessToken, reviewName);

      alert('Reply deleted successfully!');

      // Refresh reviews to show the change
      if (locationId) {
        const accountId = currentAccount?.name?.split('/').pop();
        if (accountId) {
          const updatedReviews = await GMBService.getReviews(accessToken, `accounts/${accountId}`, `locations/${locationId}`);
          setReviewsData(updatedReviews);
        }
      }
    } catch (error) {
      logger.error('Error deleting reply:', error);
      alert('Failed to delete reply. Please try again.');
    } finally {
      setDeletingReply(prev => ({ ...prev, [reviewIndex]: false }));
    }
  };

  // Handler functions for button clicks
  const handleViewAllTasks = () => {
    // Navigate to Task Management page to view all tasks
    navigate('/task-management');
  };

  const handleViewPerformanceDetails = () => {
    setShowPerformanceDetails(!showPerformanceDetails);
  };

  const handleViewFeedDetails = () => {
    setShowFeedDetails(!showFeedDetails);
  };

  // Handle form input changes to detect modifications
  const handleFormInputChange = (event) => {
    const fieldName = event.target.name;
    const fieldValue = event.target.value;

    // Check if the value has changed from initial
    const hasChanged = initialFormValues[fieldName] !== fieldValue;

    // Update the changedFields state for this specific field
    setChangedFields(prev => ({
      ...prev,
      [fieldName]: hasChanged
    }));

    // Also update the global hasFormChanges flag
    if (hasChanged) {
      setHasFormChanges(true);
    } else {
      // Check if any other fields are still changed
      const otherFieldsChanged = Object.entries(changedFields).some(
        ([key, value]) => key !== fieldName && value === true
      );
      setHasFormChanges(otherFieldsChanged);
    }
  };

  const updateAdditionalCategoryEntry = (index, updates) => {
    setAdditionalCategories(prev => prev.map((entry, idx) => {
      if (idx !== index) return entry;
      return { ...entry, ...updates };
    }));
  };

  const handlePrimaryCategorySelect = (event, newValue) => {
    const label = newValue?.label || '';
    const selection = label ? { id: newValue?.id || '', label } : null;
    setPrimaryCategorySelection(selection);
    setPrimaryCategoryInputValue(label);
    setCategorySearchText('');
    setCategorySuggestions([]);
    handleFormInputChange({ target: { name: 'businessCategory', value: label } });
  };

  const handlePrimaryCategoryInputChange = (event, newInputValue, reason) => {
    setPrimaryCategoryInputValue(newInputValue);
    if (reason === 'input') {
      setPrimaryCategorySelection(prev => (prev?.label === newInputValue ? prev : null));
      setCategorySearchText(newInputValue);
    } else if (reason === 'clear') {
      setPrimaryCategorySelection(null);
      setCategorySearchText('');
    }
  };

  const handleAdditionalCategorySelect = (index) => (event, newValue) => {
    const label = newValue?.label || '';
    const selection = label ? { id: newValue?.id || '', label } : null;
    updateAdditionalCategoryEntry(index, { selected: selection, inputValue: label });
    handleFormInputChange({ target: { name: `additionalCategory${index + 1}`, value: label } });
    setCategorySearchText('');
    setCategorySuggestions([]);
  };

  const handleAdditionalCategoryInputChange = (index) => (event, newInputValue, reason) => {
    setAdditionalCategories(prev => prev.map((entry, idx) => {
      if (idx !== index) return entry;
      const updates = { inputValue: newInputValue };
      if (reason === 'input' || reason === 'clear') {
        updates.selected = null;
      }
      return { ...entry, ...updates };
    }));
    if (reason === 'input') {
      setCategorySearchText(newInputValue);
    } else if (reason === 'clear') {
      setCategorySearchText('');
    }
    handleFormInputChange({ target: { name: `additionalCategory${index + 1}`, value: newInputValue } });
  };

  // Handle submit for individual field
  const handleFieldSubmit = async (fieldName) => {
    logger.log(`Submitting field: ${fieldName}`);
    // You can add individual field update logic here if needed
    // For now, we'll just clear the changed state for this field
    setChangedFields(prev => ({
      ...prev,
      [fieldName]: false
    }));
  };

  const handleCategorySubmit = async () => {
    if (categorySubmitting) return;
    const categoriesPayload = buildCategoryPayload();
    if (!categoriesPayload) {
      setCategorySubmitError('No new category selections detected.');
      return;
    }

    setCategorySubmitting(true);
    setCategorySubmitError('');
    try {
      const accessToken = await GMBService.getAccessToken();
      await GMBService.updateBusinessProfile(locationId, { categories: categoriesPayload }, accessToken);
      setCategorySubmitSuccess(true);
      setTimeout(() => setCategorySubmitSuccess(false), 4000);
      await fetchLocationData();
      setChangedFields(prev => {
        const updated = { ...prev, businessCategory: false };
        additionalCategories.forEach((_, idx) => {
          updated[`additionalCategory${idx + 1}`] = false;
        });
        const stillChanged = Object.values(updated).some(Boolean);
        setHasFormChanges(stillChanged);
        return updated;
      });
    } catch (error) {
      const errorMessage = error?.message || 'Failed to sync categories with Google.';
      setCategorySubmitError(errorMessage);
      logger.error('[BusinessProfile] Error syncing categories:', error);
    } finally {
      setCategorySubmitting(false);
    }
  };

  // Handle AI-powered description generation
  const handleGenerateAIDescription = async () => {
    try {
      setGeneratingAIDescription(true);
      logger.log('🤖 [AI Description] Generating for:', locationData?.title || businessTitle);

      const businessData = {
        name: locationData?.title || businessTitle || 'Business',
        category: locationData?.primaryCategory?.displayName || locationData?.categories?.[0]?.displayName || 'business',
        services: [],
        location: locationData?.storefrontAddress?.locality || locationData?.storefrontAddress?.administrativeArea || '',
        yearEstablished: locationData?.openInfo?.openingDate || '',
        uniqueSellingPoints: []
      };

      const aiDescription = await OpenAI.generateDescription(businessData.name, businessData.category);

      // Update the description field
      const descriptionField = document.querySelector('#editProfileForm textarea[name="description"]');
      if (descriptionField) {
        descriptionField.value = aiDescription;
        // Trigger change event to update the form state
        descriptionField.dispatchEvent(new Event('input', { bubbles: true }));
        // Mark the field as changed
        setChangedFields(prev => ({
          ...prev,
          description: true
        }));
        setHasFormChanges(true);
      }

      logger.log('✅ [AI Description] Generated successfully');

    } catch (error) {
      logger.error('❌ Error generating AI description:', error);
      // Fallback to simple template
      const el = document.querySelector('#editProfileForm textarea[name="description"]');
      const current = el ? el.value : (locationData?.profile?.description || '');
      const suggestion = `${businessTitle} provides trusted services${locationData?.cityInCategory ? ` in ${locationData.cityInCategory}` : ''}. ${current || 'We focus on great customer experience, professional staff, and timely service.'}`.slice(0, 740);
      if (el) {
        el.value = suggestion;
        el.dispatchEvent(new Event('input', { bubbles: true }));
      }
    } finally {
      setGeneratingAIDescription(false);
    }
  };

  // Handle adding a new phone number field
  const handleAddPhoneNumber = () => {
    const newId = phoneNumbers.length > 0 ? Math.max(...phoneNumbers.map(p => p.id)) + 1 : 1;
    setPhoneNumbers(prev => [...prev, { id: newId, value: '' }]);
  };

  // Handle removing a phone number field
  const handleRemovePhoneNumber = (id) => {
    if (phoneNumbers.length > 1) {
      setPhoneNumbers(prev => prev.filter(phone => phone.id !== id));
      // Mark as changed
      setChangedFields(prev => ({
        ...prev,
        [`phoneNumber_${id}`]: false
      }));
    }
  };

  // Handle phone number input change
  const handlePhoneNumberChange = (id, value) => {
    setPhoneNumbers(prev => prev.map(phone =>
      phone.id === id ? { ...phone, value } : phone
    ));

    // Check if the value has changed from initial
    const initialValue = initialFormValues[`phoneNumber_${id}`] || '';
    const hasChanged = initialValue !== value;

    setChangedFields(prev => ({
      ...prev,
      [`phoneNumber_${id}`]: hasChanged
    }));

    if (hasChanged) {
      setHasFormChanges(true);
    }
  };

  // Helper function to extract phone number from WhatsApp URL
  const extractPhoneFromWhatsAppUrl = (url) => {
    if (!url) return '';
    // Extract phone number from https://wa.me/1234567890 format
    const match = url.match(/wa\.me\/(\+?\d+)/);
    return match ? match[1] : url;
  };

  // Helper function to generate WhatsApp URL from phone number
  const generateWhatsAppUrl = (phoneNumber) => {
    if (!phoneNumber) return '';
    // Remove all non-digit characters except leading +
    const cleanPhone = phoneNumber.replace(/[^\d+]/g, '');
    // Remove + from the beginning if present for the URL
    const urlPhone = cleanPhone.startsWith('+') ? cleanPhone.substring(1) : cleanPhone;
    return urlPhone ? `https://wa.me/${urlPhone}` : '';
  };

  // Handle WhatsApp phone number change
  const handleWhatsAppPhoneChange = (value) => {
    setWhatsappPhoneNumber(value);

    // Check if the value has changed from initial
    const initialPhone = extractPhoneFromWhatsAppUrl(initialFormValues.chat || '');
    const hasChanged = initialPhone !== value;

    setChangedFields(prev => ({
      ...prev,
      chat: hasChanged
    }));

    if (hasChanged) {
      setHasFormChanges(true);
    }
  };

  // Handle adding a new service area field
  const handleAddServiceArea = () => {
    const newId = serviceAreas.length > 0 ? Math.max(...serviceAreas.map(s => s.id)) + 1 : 1;
    setServiceAreas(prev => [...prev, { id: newId, value: '', placeId: '' }]);
  };

  // Handle removing a service area field
  const handleRemoveServiceArea = (id) => {
    if (serviceAreas.length > 1) {
      setServiceAreas(prev => prev.filter(area => area.id !== id));
      // Clear suggestions for this field
      setServiceAreaSuggestions(prev => {
        const updated = { ...prev };
        delete updated[id];
        return updated;
      });
      // Mark as changed
      setChangedFields(prev => ({
        ...prev,
        [`serviceArea_${id}`]: false
      }));
    }
  };

  // Fetch location suggestions using Google Places Text Search API
  const fetchLocationSuggestions = async (query, serviceAreaId) => {
    if (!query || query.length < 3) {
      setServiceAreaSuggestions(prev => ({ ...prev, [serviceAreaId]: [] }));
      return;
    }

    setLoadingSuggestions(prev => ({ ...prev, [serviceAreaId]: true }));

    try {
      // Use Google Places Text Search API to get place IDs
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || process.env.REACT_APP_GOOGLE_API_KEY;

      console.log('[BusinessProfile] Fetching service area suggestions for:', query);
      console.log('[BusinessProfile] Google Maps API available:', !!(window.google && window.google.maps && window.google.maps.places));
      console.log('[BusinessProfile] API Key available:', !!apiKey);

      if (apiKey && window.google && window.google.maps && window.google.maps.places) {
        // Use Google Places Autocomplete Service
        console.log('[BusinessProfile] Using Google Places Autocomplete Service');
        const autocompleteService = new window.google.maps.places.AutocompleteService();

        autocompleteService.getPlacePredictions(
          {
            input: query,
            types: ['(regions)'], // Only get regions (cities, states, countries)
          },
          (predictions, status) => {
            console.log('[BusinessProfile] Google Places API status:', status);
            if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
              const suggestions = predictions.map(prediction => ({
                name: prediction.description,
                placeId: prediction.place_id,
                type: 'region'
              }));
              console.log('[BusinessProfile] Got suggestions with placeIds:', suggestions.map(s => ({ name: s.name, placeId: s.placeId })));
              setServiceAreaSuggestions(prev => ({ ...prev, [serviceAreaId]: suggestions }));
            } else {
              console.warn('[BusinessProfile] No predictions returned, status:', status);
              setServiceAreaSuggestions(prev => ({ ...prev, [serviceAreaId]: [] }));
            }
            setLoadingSuggestions(prev => ({ ...prev, [serviceAreaId]: false }));
          }
        );
        return;
      }

      // Fallback to Nominatim if Google API is not available
      console.warn('[BusinessProfile] Google Places API not available, falling back to Nominatim (no placeId support!)');
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&addressdetails=1&limit=5`,
        {
          headers: {
            'User-Agent': 'Visibeen-Business-Profile/1.0'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch location suggestions');
      }

      const data = await response.json();

      // Format suggestions - note: Nominatim doesn't provide Google Place IDs
      const suggestions = data.map(item => ({
        name: item.display_name,
        placeId: '', // Nominatim doesn't provide Google Place IDs
        lat: item.lat,
        lon: item.lon,
        type: item.type,
        address: item.address
      }));

      setServiceAreaSuggestions(prev => ({ ...prev, [serviceAreaId]: suggestions }));
    } catch (error) {
      logger.error('Error fetching location suggestions:', error);
      setServiceAreaSuggestions(prev => ({ ...prev, [serviceAreaId]: [] }));
    } finally {
      setLoadingSuggestions(prev => ({ ...prev, [serviceAreaId]: false }));
    }
  };

  // Handle service area input change with debounced autocomplete
  const handleServiceAreaChange = (id, value) => {
    setServiceAreas(prev => prev.map(area =>
      area.id === id ? { ...area, value } : area
    ));

    // Check if the value has changed from initial
    const initialValue = initialFormValues[`serviceArea_${id}`] || '';
    const hasChanged = initialValue !== value;

    setChangedFields(prev => ({
      ...prev,
      [`serviceArea_${id}`]: hasChanged
    }));

    if (hasChanged) {
      setHasFormChanges(true);
    }

    // Debounce the autocomplete API call
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      fetchLocationSuggestions(value, id);
    }, 500); // 500ms debounce
  };

  // Handle selecting a suggestion from autocomplete
  const handleSelectSuggestion = (id, suggestion) => {
    console.log('[BusinessProfile] Selected suggestion:', { id, name: suggestion.name, placeId: suggestion.placeId });

    setServiceAreas(prev => {
      const updated = prev.map(area =>
        area.id === id ? { ...area, value: suggestion.name, placeId: suggestion.placeId || '' } : area
      );
      console.log('[BusinessProfile] Updated serviceAreas state:', updated);
      return updated;
    });

    // Mark as changed
    const initialValue = initialFormValues[`serviceArea_${id}`] || '';
    const hasChanged = initialValue !== suggestion.name;

    setChangedFields(prev => ({
      ...prev,
      [`serviceArea_${id}`]: hasChanged
    }));

    if (hasChanged) {
      setHasFormChanges(true);
    }

    // Clear suggestions after selection
    setServiceAreaSuggestions(prev => ({ ...prev, [id]: [] }));
  };

  // ==================== REGULAR HOURS HANDLERS ====================

  // Generate time slots for dropdown (00:00 to 23:30 in 30-minute intervals)
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeStr = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        slots.push(timeStr);
      }
    }
    return slots;
  };

  // Toggle day open/closed
  const handleDayToggle = (day) => {
    setRegularHours(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        isOpen: !prev[day].isOpen
      }
    }));

    // Mark as changed
    setChangedFields(prev => ({
      ...prev,
      [`regularHours_${day}`]: true
    }));
    setHasFormChanges(true);
  };

  // Handle time change for a specific period
  const handleTimeChange = (day, periodId, field, value) => {
    setRegularHours(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        periods: prev[day].periods.map(period =>
          period.id === periodId ? { ...period, [field]: value } : period
        )
      }
    }));

    // Mark as changed
    setChangedFields(prev => ({
      ...prev,
      [`regularHours_${day}`]: true
    }));
    setHasFormChanges(true);
  };

  // Add another time period for a day (split hours)
  const handleAddTimePeriod = (day) => {
    setRegularHours(prev => {
      const currentPeriods = prev[day].periods;
      const newId = currentPeriods.length > 0 ? Math.max(...currentPeriods.map(p => p.id)) + 1 : 1;
      return {
        ...prev,
        [day]: {
          ...prev[day],
          periods: [...currentPeriods, { id: newId, openTime: '09:00', closeTime: '17:00' }]
        }
      };
    });

    // Mark as changed
    setChangedFields(prev => ({
      ...prev,
      [`regularHours_${day}`]: true
    }));
    setHasFormChanges(true);
  };

  // Remove a time period for a day
  const handleRemoveTimePeriod = (day, periodId) => {
    setRegularHours(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        periods: prev[day].periods.filter(period => period.id !== periodId)
      }
    }));

    // Mark as changed
    setChangedFields(prev => ({
      ...prev,
      [`regularHours_${day}`]: true
    }));
    setHasFormChanges(true);
  };

  // ==================== SPECIAL HOURS HANDLERS ====================

  // Add a new special hours entry
  const handleAddSpecialHours = () => {
    const newId = specialHours.length > 0 ? Math.max(...specialHours.map(s => s.id)) + 1 : 1;
    setSpecialHours(prev => [...prev, {
      id: newId,
      date: '',
      description: '',
      isClosed: false,
      openTime: '09:00',
      closeTime: '17:00'
    }]);
  };

  // Remove a special hours entry
  const handleRemoveSpecialHours = (id) => {
    setSpecialHours(prev => prev.filter(sh => sh.id !== id));
    setChangedFields(prev => ({
      ...prev,
      [`specialHours_${id}`]: false
    }));
  };

  // Handle special hours field change
  const handleSpecialHoursChange = (id, field, value) => {
    setSpecialHours(prev => prev.map(sh =>
      sh.id === id ? { ...sh, [field]: value } : sh
    ));

    // Mark as changed
    setChangedFields(prev => ({
      ...prev,
      [`specialHours_${id}`]: true
    }));
    setHasFormChanges(true);
  };

  const handleEditProfile = async () => {
    // ⚠️ Don't show upgrade modal while permissions are still loading
    if (permissionsLoading) {
      return;
    }
    // Check plan-based access - admins always allowed
    if (!isAdmin && !hasPlanAccess) {
      setSelectedFeature('Edit Profile');
      setUpgradeModalOpen(true);
      return;
    }

    // Load Google Maps API with Places library for service area autocomplete
    try {
      await loadGoogleMapsApi({ libraries: ['places'] });
      console.log('[BusinessProfile] Google Maps API loaded successfully');
    } catch (err) {
      console.warn('[BusinessProfile] Failed to load Google Maps API:', err.message);
      // Continue anyway - will use Nominatim fallback (without placeId)
    }

    const primaryCategory = locationData?.categories?.primaryCategory;
    const primaryCategoryId = primaryCategory?.categoryId || primaryCategory?.name || '';
    const primaryCategoryLabel = primaryCategory?.displayName || primaryCategory?.name || '';
    setPrimaryCategorySelection(primaryCategoryLabel ? { id: primaryCategoryId || '', label: primaryCategoryLabel } : null);
    setPrimaryCategoryInputValue(primaryCategoryLabel);
    setCategorySearchText('');
    setCategorySuggestions([]);
    setCategorySuggestionLoading(false);

    const existingAdditionalCategories = locationData?.categories?.additionalCategories || [];
    const additionalCategoryEntries = existingAdditionalCategories.map(category => {
      const id = category?.categoryId || category?.name || '';
      const label = category?.displayName || category?.name || '';
      return {
        selected: label ? { id, label } : null,
        inputValue: label
      };
    });
    setAdditionalCategories(additionalCategoryEntries);

    // Initialize phone numbers from locationData
    const primaryPhone = locationData?.phoneNumbers?.primaryPhone || '';
    const additionalPhones = locationData?.phoneNumbers?.additionalPhones || [];
    const allPhones = [primaryPhone, ...additionalPhones].filter(Boolean);

    if (allPhones.length > 0) {
      setPhoneNumbers(allPhones.map((phone, index) => ({ id: index + 1, value: phone })));
    } else {
      setPhoneNumbers([{ id: 1, value: '' }]);
    }

    // Initialize WhatsApp phone number from chat URL (if it exists)
    // Assuming chat field might contain WhatsApp URL like https://wa.me/1234567890
    const chatUrl = ''; // You can get this from locationData if available
    const extractedPhone = extractPhoneFromWhatsAppUrl(chatUrl);
    setWhatsappPhoneNumber(extractedPhone);

    // Initialize service areas from locationData
    const placeInfos = locationData?.serviceArea?.places?.placeInfos || [];
    const serviceAreaData = placeInfos.map((p, index) => ({
      id: index + 1,
      value: p.placeName || '',
      placeId: p.placeId || ''
    })).filter(area => area.value);

    if (serviceAreaData.length > 0) {
      setServiceAreas(serviceAreaData);
    } else {
      setServiceAreas([{ id: 1, value: '', placeId: '' }]);
    }

    // Set service area business type from locationData
    if (locationData?.serviceArea?.businessType) {
      setServiceAreaBusinessType(locationData.serviceArea.businessType);
    }

    // Create allServiceAreas array for initial form values tracking
    const allServiceAreas = serviceAreaData.length > 0
      ? serviceAreaData.map(area => area.value)
      : [];

    // Initialize regular hours from locationData
    const convertGMBHoursToState = (regularHoursData) => {
      if (!regularHoursData?.periods) return null;

      const hoursMap = {
        monday: { isOpen: false, periods: [{ id: 1, openTime: '09:00', closeTime: '17:00' }] },
        tuesday: { isOpen: false, periods: [{ id: 1, openTime: '09:00', closeTime: '17:00' }] },
        wednesday: { isOpen: false, periods: [{ id: 1, openTime: '09:00', closeTime: '17:00' }] },
        thursday: { isOpen: false, periods: [{ id: 1, openTime: '09:00', closeTime: '17:00' }] },
        friday: { isOpen: false, periods: [{ id: 1, openTime: '09:00', closeTime: '17:00' }] },
        saturday: { isOpen: false, periods: [{ id: 1, openTime: '09:00', closeTime: '17:00' }] },
        sunday: { isOpen: false, periods: [{ id: 1, openTime: '09:00', closeTime: '17:00' }] }
      };

      // Helper function to convert TimeOfDay object to "HH:MM" string
      const timeOfDayToString = (timeObj) => {
        if (!timeObj) return null;
        // Handle both object format and string format
        if (typeof timeObj === 'string') return timeObj;
        const hours = String(timeObj.hours || 0).padStart(2, '0');
        const minutes = String(timeObj.minutes || 0).padStart(2, '0');
        return `${hours}:${minutes}`;
      };

      // Group periods by day
      const dayPeriods = {};
      regularHoursData.periods.forEach(period => {
        const openDay = period.openDay?.toLowerCase();
        if (!openDay) return;

        if (!dayPeriods[openDay]) {
          dayPeriods[openDay] = [];
        }

        const openTimeStr = timeOfDayToString(period.openTime) || '09:00';
        const closeTimeStr = timeOfDayToString(period.closeTime) || '17:00';

        dayPeriods[openDay].push({
          id: dayPeriods[openDay].length + 1,
          openTime: openTimeStr,
          closeTime: closeTimeStr
        });
      });

      // Update hoursMap with actual data
      Object.keys(dayPeriods).forEach(day => {
        if (hoursMap[day]) {
          hoursMap[day] = {
            isOpen: true,
            periods: dayPeriods[day]
          };
        }
      });

      return hoursMap;
    };

    const gmbHours = convertGMBHoursToState(locationData?.regularHours);
    if (gmbHours) {
      setRegularHours(gmbHours);
    }

    // Initialize special hours from locationData
    const convertGMBSpecialHours = (specialHoursData) => {
      if (!specialHoursData?.specialHourPeriods) return [];

      // Helper function to convert TimeOfDay object to "HH:MM" string
      const timeOfDayToString = (timeObj) => {
        if (!timeObj) return null;
        // Handle both object format and string format
        if (typeof timeObj === 'string') return timeObj;
        const hours = String(timeObj.hours || 0).padStart(2, '0');
        const minutes = String(timeObj.minutes || 0).padStart(2, '0');
        return `${hours}:${minutes}`;
      };

      return specialHoursData.specialHourPeriods.map((period, index) => ({
        id: index + 1,
        date: period.startDate ? `${period.startDate.year}-${String(period.startDate.month).padStart(2, '0')}-${String(period.startDate.day).padStart(2, '0')}` : '',
        description: period.description || '',
        isClosed: period.isClosed || false,
        openTime: timeOfDayToString(period.openTime) || '09:00',
        closeTime: timeOfDayToString(period.closeTime) || '17:00'
      }));
    };

    const gmbSpecialHours = convertGMBSpecialHours(locationData?.specialHours);
    if (gmbSpecialHours.length > 0) {
      setSpecialHours(gmbSpecialHours);
    }

    // Capture initial form values
    const initialAdditionalCategoryLabels = additionalCategoryEntries.map(entry => entry.inputValue).filter(Boolean);
    const initialValues = {
      businessName: locationData?.title || businessTitle || '',
      businessCategory: primaryCategoryLabel || 'Hospital',
      businessCategoryId: primaryCategoryId,
      description: locationData?.profile?.description || '',
      openingDate: locationData?.openInfo?.openingDate ?
        `${locationData.openInfo.openingDate.year}-${String(locationData.openInfo.openingDate.month).padStart(2, '0')}-${String(locationData.openInfo.openingDate.day).padStart(2, '0')}` : '',
      phoneNumber: locationData?.phoneNumbers?.primaryPhone || '',
      chat: chatUrl, // Store the full WhatsApp URL
      website: locationData?.websiteUri || '',
      businessLocation: locationData?.storefrontAddress ?
        [
          locationData.storefrontAddress.addressLines?.[0] || '',
          locationData.storefrontAddress.locality || '',
          locationData.storefrontAddress.administrativeArea || '',
          locationData.storefrontAddress.postalCode || ''
        ].filter(Boolean).join(', ') : '',
      serviceArea: locationData?.serviceArea?.places?.placeInfos?.[0]?.placeName ||
        (locationData?.serviceArea?.places?.placeInfos?.map(p => p.placeName).join(', ')) || '',
      additionalCategories: initialAdditionalCategoryLabels
    };

    additionalCategoryEntries.forEach((entry, index) => {
      initialValues[`additionalCategory${index + 1}`] = entry.inputValue;
      initialValues[`additionalCategoryId_${index + 1}`] = entry.selected?.id || '';
    });

    // Add initial values for each phone number
    allPhones.forEach((phone, index) => {
      initialValues[`phoneNumber_${index + 1}`] = phone;
    });

    // Add initial values for each service area
    allServiceAreas.forEach((area, index) => {
      initialValues[`serviceArea_${index + 1}`] = area;
    });

    setInitialFormValues(initialValues);
    setHasFormChanges(false);
    setChangedFields({}); // Reset all field change tracking
    setEditProfileOpen(true);
  };

  const handleAddEvent = () => {
    // ⚠️ Don't show upgrade modal while permissions are still loading
    if (permissionsLoading) {
      return;
    }
    if (!isAdmin && !hasPlanAccess) {
      setSelectedFeature('Add Event');
      setUpgradeModalOpen(true);
      return;
    }
    setAddEventModalOpen(true);
  };

  const handleCreatePostFromTestimonial = () => {
    // ⚠️ Don't show upgrade modal while permissions are still loading
    if (permissionsLoading) {
      return;
    }
    if (!isAdmin && !canAccessFeature('createPosts')) {
      setSelectedFeature('Create Post from Testimonial');
      setUpgradeModalOpen(true);
      return;
    }
    navigate('/create-post');
  };

  const handlePhotoUpload = () => {
    setPhotoUploadOpen(true);
  };

  const uploadPhotoToGMB = async (file) => {
    try {
      // 1. Get robust access token (check multiple common keys)
      const accessToken = sessionStorage.getItem('google_access_token') ||
        localStorage.getItem('google_access_token') ||
        sessionStorage.getItem('googleAccessToken') ||
        localStorage.getItem('googleAccessToken');

      // 2. Extract accountId robustly
      let accountId = currentAccount?.name?.split('/').pop();

      if (!accountId) {
        // Fallback to session/localStorage like PhotoUploadPopup
        const sessionStr = sessionStorage.getItem('session') || localStorage.getItem('session');
        if (sessionStr) {
          try {
            const session = JSON.parse(sessionStr);
            if (session.gmbAccounts?.[0]?.name) {
              accountId = session.gmbAccounts[0].name.split('/').pop();
            }
          } catch (e) {
            logger.warn('Failed to parse session for accountId', e);
          }
        }
      }

      // If still not found, try direct keys
      if (!accountId) {
        accountId = sessionStorage.getItem('gmb_account_id') ||
          localStorage.getItem('gmb_account_id') ||
          sessionStorage.getItem('google_account_id') ||
          localStorage.getItem('google_account_id');
      }

      // 3. Ensure ids are clean
      const cleanAccountId = accountId?.replace('accounts/', '');
      const cleanLocationId = locationId?.replace('locations/', '');

      logger.log(`[BusinessProfile] Preparing upload: accountId=${cleanAccountId}, locationId=${cleanLocationId}`);

      if (!cleanAccountId || !cleanLocationId) {
        throw new Error('Missing account or location information. Please try refreshing or re-selecting your business.');
      }

      if (!accessToken) {
        throw new Error('Google access token not found. Please re-authenticate.');
      }

      logger.log('📤 [BusinessProfile] Starting photo upload to GMB via backend API...');

      // ============ AUTO-APPLY GEOTAG ============
      // Process image with automatic geotag using business location coordinates
      let fileToUpload = file;
      let geotagApplied = false;
      let finalCoordinates = null;

      try {
        logger.log('📍 [BusinessProfile] Applying geotag to photo...');
        const geotagResult = await ImageUploadService.processImageForUpload(file, {
          businessData: locationData,
          forceGeotag: true
        });

        if (geotagResult.geotagApplied && geotagResult.file) {
          fileToUpload = geotagResult.file;
          geotagApplied = true;
          finalCoordinates = geotagResult.coordinates;
          logger.log('✅ [BusinessProfile] Geotag applied successfully:', geotagResult.coordinates);
        } else {
          logger.warn('⚠️ [BusinessProfile] Geotag not applied:', geotagResult.error || 'Unknown reason');
        }
      } catch (geotagError) {
        logger.error('❌ [BusinessProfile] Error applying geotag (continuing without):', geotagError);
      }
      // ============================================

      logger.log('📋 [BusinessProfile] Upload details:', {
        accountId: cleanAccountId,
        locationId: cleanLocationId,
        fileName: fileToUpload.name,
        fileSize: fileToUpload.size,
        fileType: fileToUpload.type,
        geotagApplied,
        hasAccessToken: !!accessToken,
        tokenPreview: accessToken ? `${accessToken.substring(0, 20)}...` : 'NO TOKEN'
      });

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('photo', fileToUpload, file.name); // Using original name
      formData.append('accountId', cleanAccountId);
      formData.append('locationId', cleanLocationId);
      formData.append('category', 'ADDITIONAL');

      // Also send metadata as a field for the backend to use/log
      if (geotagApplied && finalCoordinates) {
        formData.append('geotag', JSON.stringify({
          latitude: finalCoordinates.latitude,
          longitude: finalCoordinates.longitude,
          businessName: locationData?.title || 'Business'
        }));
      }

      // Debug: Log FormData contents
      logger.log('🔍 [BusinessProfile] FormData contents:');
      for (let [key, value] of formData.entries()) {
        if (value instanceof File) {
          logger.log(`  ${key}: File(${value.name}, ${value.size} bytes, ${value.type})`);
        } else {
          logger.log(`  ${key}: ${value}`);
        }
      }

      // Verify required fields
      if (!formData.has('photo')) {
        throw new Error('FormData missing "photo" field');
      }
      if (!formData.has('locationId')) {
        throw new Error('FormData missing "locationId" field');
      }
      if (!formData.has('accountId')) {
        throw new Error('FormData missing "accountId" field');
      }

      // Upload photo to GMB via backend API
      // IMPORTANT: Send Google OAuth token, not application JWT token
      const response = await ApiService.request('/gmb/upload-photo', {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (!response.success) {
        throw new Error(response.message || 'Upload failed');
      }

      const result = response.data.mediaItem;
      logger.log('✅ [BusinessProfile] Photo uploaded successfully to GMB:', result);

      // Refresh media items to show the new photo
      const items = await GMBService.getMedia(undefined, accountId, locationId);
      const filteredItems = items.filter(i => (i.mediaFormat === 'PHOTO' || !i.mediaFormat) && (i.googleUrl || i.thumbnailUrl || i.sourceUrl));

      // Separate cover photos from regular photos
      const coverPhotos = filteredItems.filter(i => i.category === 'COVER' || i.category === 'PROFILE');
      const regularPhotos = filteredItems.filter(i => i.category !== 'COVER' && i.category !== 'PROFILE');

      const sortByTime = (a, b) => {
        const ta = a.createTime ? Date.parse(a.createTime) : 0;
        const tb = b.createTime ? Date.parse(b.createTime) : 0;
        return tb - ta;
      };

      coverPhotos.sort(sortByTime);
      regularPhotos.sort(sortByTime);

      const sortedItems = [...coverPhotos, ...regularPhotos];
      setMediaItems(sortedItems);

      return result;
    } catch (error) {
      logger.error('❌ [BusinessProfile] Error uploading photo to GMB:', error);
      logger.error('❌ [BusinessProfile] Error details:', {
        message: error.message,
        status: error.status,
        body: error.body
      });
      throw error;
    }
  };

  const handleViewAllReviews = () => {
    setShowAllReviews(!showAllReviews);
  };

  const handleViewAllGMBFeed = () => {
    setShowAllGMBFeed(!showAllGMBFeed);
  };

  const handleViewAllSocialFeed = () => {
    setShowAllSocialFeed(!showAllSocialFeed);
  };

  const handleCompetitorAnalysis = () => {
    logger.log('🎯 [Competitor Analysis] Button clicked');
    logger.log('🎯 [Competitor Analysis] canRunAnalysis:', canRunAnalysis);
    logger.log('🎯 [Competitor Analysis] daysUntilNextAnalysis:', daysUntilNextAnalysis);
    logger.log('🎯 [Competitor Analysis] lastAnalysisTime:', lastAnalysisTime);

    // ⚠️ Don't show upgrade modal while permissions are still loading
    if (permissionsLoading || accessChecking) {
      return;
    }

    // Check if user has plan OR is whitelisted - block if neither
    const hasPlanAssigned = hasSubscription && profilePlanCode;
    const hasPlatformAccess = isAdmin || isWhitelisted || hasPlanAssigned;

    if (!hasPlatformAccess) {
      logger.log('❌ [Competitor Analysis] BLOCKED - No whitelist AND no plan');
      // If user arrived via trial link, show compact trial offer instead of upgrade modal
      try {
        let inviteRaw = null;
        try { inviteRaw = sessionStorage.getItem('trial_invite'); } catch (_) { inviteRaw = null; }
        if (!inviteRaw) { try { inviteRaw = localStorage.getItem('trial_invite'); } catch (_) { inviteRaw = null; } }
        if (inviteRaw) {
          setTrialOfferOpen(true);
          return;
        }
      } catch (_) { }

      setSelectedFeature('Competitor Analysis');
      setUpgradeModalOpen(true);
      return;
    }

    if (!isAdmin && !canAccessFeature('competitorAnalysis')) {
      setSelectedFeature('Competitor Analysis');
      setUpgradeModalOpen(true);
      return;
    }

    // Check 7-day restriction
    if (!canRunAnalysis) {
      logger.log('⏳ [Competitor Analysis] BLOCKED - Restriction active');

      const lastRunDate = new Date(lastAnalysisTime).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });

      setSnackbar({
        open: true,
        message: `⏳ Competitor Analysis can only be run once every 7 days. Last analysis: ${lastRunDate}. Please wait ${daysUntilNextAnalysis} more day${daysUntilNextAnalysis > 1 ? 's' : ''}.`,
        severity: 'warning'
      });

      logger.log('⏳ [Competitor Analysis] Blocked - User must wait', daysUntilNextAnalysis, 'more days');
      setCompetitorAnalysisOpen(true);
      return;
    }

    logger.log('✅ [Competitor Analysis] ALLOWED - Opening modal');
    setCompetitorAnalysisOpen(true);
  };

  const handleAutoRunComplete = () => {
    setAutoRunKeyword(null);
    autoRunScheduledRef.current = false;
  };

  const handleAddCampaign = () => {
    if (permissionsLoading || accessChecking) {
      return;
    }

    // Check if user has plan OR is whitelisted - block if neither
    const hasPlanAssigned = hasSubscription && profilePlanCode;
    const hasPlatformAccess = isAdmin || isWhitelisted || hasPlanAssigned;

    if (!hasPlatformAccess) {
      setSelectedFeature('Competitor Analysis');
      setUpgradeModalOpen(true);
      return;
    }

    if (!isAdmin && !canAccessFeature('competitorAnalysis')) {
      setSelectedFeature('Competitor Analysis');
      setUpgradeModalOpen(true);
      return;
    }
    // Open Competitor Analysis modal (same as Competitor Analysis button)
    setCompetitorAnalysisOpen(true);
  };

  const handleSchedulePost = () => {
    // ⚠️ Don't show upgrade modal while permissions are still loading
    if (permissionsLoading) {
      return;
    }
    if (!isAdmin && !canAccessFeature('schedulePosts')) {
      setSelectedFeature('Schedule Post');
      setUpgradeModalOpen(true);
      return;
    }
    setSchedulePostModalOpen(true);
  };

  const handleOpenHeatmap = async () => {
    // ⚠️ Don't show upgrade modal while permissions are still loading
    if (permissionsLoading || accessChecking) {
      return;
    }

    // Check if user has plan OR is whitelisted - block if neither
    // NOTE: Admins always have access, so skip this check for admins
    if (!isAdmin) {
      const hasPlanAssigned = hasSubscription && profilePlanCode;
      const hasPlatformAccess = isWhitelisted || hasPlanAssigned;

      if (!hasPlatformAccess) {
        logger.log('❌ [Open Heatmap] BLOCKED - No whitelist AND no plan');
        setSelectedFeature('Heatmap');
        setUpgradeModalOpen(true);
        return;
      }
    }

    // Check if we have competitor analysis data with grid results in memory
    const currentKeywordData = selectedKeyword ? keywordResults[selectedKeyword] : null;
    if (currentKeywordData && currentKeywordData.gridData && currentKeywordData.gridData.results) {
      // Show heatmap modal with the grid data from competitor analysis
      setHeatmapModalOpen(true);
      return;
    }

    // Try to fetch the most recent heatmap from heatmap_data table in database
    try {
      // Check if admin is viewing client - use client's user ID instead of admin's
      let userId = parseInt(getSession()?.id);
      try {
        const adminAccessData = sessionStorage.getItem('adminAccessData');
        if (adminAccessData) {
          const accessInfo = JSON.parse(adminAccessData);
          userId = accessInfo.targetUserId;
          logger.log('[Open Heatmap] Admin viewing mode - using client user ID:', userId);
        }
      } catch (error) {
        logger.warn('[Open Heatmap] Error checking admin access data:', error);
      }

      if (!userId) {
        logger.log('[BusinessProfile] Missing userId, redirecting to heatmap page');
        goToHeatmap();
        return;
      }

      logger.log('[BusinessProfile] Fetching heatmap data from database for userId:', userId);

      // Build query params
      const queryParams = {};
      if (locationId) {
        queryParams.location_id = locationId;
      }
      const businessName = businessTitle || locationData?.title || locationData?.name;
      if (businessName) {
        queryParams.business_name = businessName;
      }

      logger.log('[BusinessProfile] Heatmap query params:', queryParams);

      // Fetch from heatmap_data table
      const response = await HeatmapDataService.getLatest(userId, queryParams);

      if (response?.success && response?.data) {
        const heatmapRecord = response.data;
        logger.log('[BusinessProfile] Found heatmap data in database:', heatmapRecord.id);

        // Parse grid_results - handle both array and JSON string formats
        let gridResults = heatmapRecord.grid_results;
        if (typeof gridResults === 'string') {
          try {
            gridResults = JSON.parse(gridResults);
          } catch (e) {
            logger.warn('[BusinessProfile] Failed to parse grid_results JSON:', e);
            gridResults = null;
          }
        }

        if (gridResults && Array.isArray(gridResults) && gridResults.length > 0) {
          logger.log('[BusinessProfile] Found grid data with', gridResults.length, 'points, showing heatmap modal');

          // Build the data structure expected by the heatmap modal
          const heatmapData = {
            gridData: {
              results: gridResults,
              gridSize: heatmapRecord.grid_size || Math.round(Math.sqrt(gridResults.length)) || 5,
              searchRadius: parseFloat(heatmapRecord.radius) || 2
            },
            keyword: heatmapRecord.keyword,
            myBusinessName: heatmapRecord.business_name || businessName,
            greenGridPoints: heatmapRecord.green_points || 0,
            yellowGridPoints: heatmapRecord.yellow_points || 0,
            redGridPoints: heatmapRecord.red_points || 0,
            grayGridPoints: heatmapRecord.gray_points || 0,
            totalGridPoints: heatmapRecord.total_points || gridResults.length,
            searchLocation: {
              lat: parseFloat(heatmapRecord.center_lat),
              lng: parseFloat(heatmapRecord.center_lng)
            },
            searchRadius: parseFloat(heatmapRecord.radius) || 2,
            gridSize: heatmapRecord.grid_size || 5,
            timestamp: heatmapRecord.analysis_date
          };

          // Set the keyword and data to display in modal
          setSelectedKeyword(heatmapRecord.keyword);
          setKeywordResults({
            [heatmapRecord.keyword]: heatmapData
          });
          setCompetitorData(heatmapData);

          // Show the heatmap modal
          setHeatmapModalOpen(true);
        } else {
          logger.log('[BusinessProfile] No grid_results in heatmap data, redirecting to heatmap page');
          goToHeatmap();
        }
      } else {
        logger.log('[BusinessProfile] No heatmap data found in database, redirecting to heatmap page');
        goToHeatmap();
      }
    } catch (error) {
      logger.error('[BusinessProfile] Error fetching heatmap data:', error);
      // On error, just redirect to the full heatmap page
      goToHeatmap();
    }
  };

  const handlePostScheduled = (postData, isEdit = false) => {
    try {
      logger.log('dYZ_ [handlePostScheduled] Called with:', { postData, isEdit, locationId });

      const existingPosts = localStorage.getItem(`scheduledPosts_${locationId}`);
      const posts = existingPosts ? normalizeScheduledPosts(JSON.parse(existingPosts)) : [];
      const normalizedPostData = normalizeScheduledPost(postData);
      const scheduledDate = getValidScheduledDate(normalizedPostData);
      const scheduledTimestamp = getScheduledTimestamp(normalizedPostData);
      const selectedScheduledTimestamp = getScheduledTimestamp(selectedScheduledPost);

      if (!normalizedPostData || !scheduledDate || !scheduledTimestamp) {
        throw new Error('Scheduled time is missing or invalid for this post');
      }

      logger.log('dY"? [handlePostScheduled] Existing posts:', posts.length);

      if (isEdit && selectedScheduledPost && selectedScheduledTimestamp) {
        // Update existing post
        const postIndex = posts.findIndex(post =>
          getScheduledTimestamp(post) === selectedScheduledTimestamp
        );

        if (postIndex !== -1) {
          posts[postIndex] = normalizedPostData;
          logger.log('[BusinessProfile] Post updated:', normalizedPostData);
        }

        // Update calendar events
        const updatedEvents = calendarEvents.map(event => {
          if (event.type === 'scheduled_post' &&
            getScheduledTimestamp(event.postData) === selectedScheduledTimestamp) {
            return {
              date: scheduledDate,
              title: `dY". ${normalizedPostData.summary.substring(0, 30)}${normalizedPostData.summary.length > 30 ? '...' : ''}`,
              type: 'scheduled_post',
              postData: normalizedPostData
            };
          }
          return event;
        });
        setCalendarEvents(updatedEvents);

        // Show success message
        setSnackbar({
          open: true,
          message: 'Scheduled post updated successfully!',
          severity: 'success'
        });
      } else {
        // Add new post
        posts.push(normalizedPostData);

        // Add to calendar events
        const newEvent = {
          date: scheduledDate,
          title: `dY". ${normalizedPostData.summary.substring(0, 30)}${normalizedPostData.summary.length > 30 ? '...' : ''}`,
          type: 'scheduled_post',
          postData: normalizedPostData
        };

        logger.log('?o" [handlePostScheduled] Creating new event:', newEvent);
        logger.log('dY". [handlePostScheduled] Event date:', scheduledDate.toISOString());
        logger.log('dY"? [handlePostScheduled] Event title:', newEvent.title);

        setCalendarEvents(prev => {
          const updated = [...prev, newEvent];
          logger.log('dY"S [handlePostScheduled] Updated calendar events:', updated.length);
          return updated;
        });

        logger.log('[BusinessProfile] Post scheduled and added to calendar:', newEvent);

        // Show success message
        setSnackbar({
          open: true,
          message: 'Post scheduled successfully! Check the calendar for your scheduled post.',
          severity: 'success'
        });
      }

      localStorage.setItem(`scheduledPosts_${locationId}`, JSON.stringify(posts));
      logger.log('dY', '[handlePostScheduled] Saved to localStorage:', posts.length, 'posts');

      // Verify it was saved
      const verification = localStorage.getItem(`scheduledPosts_${locationId}`);
      logger.log('?o. [handlePostScheduled] Verification - localStorage contains:', verification ? JSON.parse(verification).length : 0, 'posts');

    } catch (error) {
      logger.error('[BusinessProfile] Error saving scheduled post:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save scheduled post',
        severity: 'error'
      });
    }
  };

  const handleCalendarDayClick = (day) => {
    if (!day) return;

    const dayDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    const dayEvents = calendarEvents.filter(event => isSameDay(event.date, dayDate));
    const scheduledPosts = dayEvents
      .filter(e => e.type === 'scheduled_post')
      .map(e => normalizeScheduledPost(e.postData))
      .filter(Boolean);

    if (scheduledPosts.length > 0) {
      // If multiple posts, show the first one (can be enhanced to show a list)
      setSelectedScheduledPost({
        ...scheduledPosts[0],
        date: dayDate,
        allPostsForDay: scheduledPosts // Store all posts for this day
      });
      setScheduledPostDetailsOpen(true);
    }
  };

  const handleCloseScheduledPostDetails = () => {
    setScheduledPostDetailsOpen(false);
    setSelectedScheduledPost(null);
  };

  const handleEditScheduledPost = () => {
    if (!selectedScheduledPost) return;

    // Close the details modal
    setScheduledPostDetailsOpen(false);

    // Open the schedule post modal with the selected post data for editing
    setSchedulePostModalOpen(true);

    // The SchedulePostModal will need to handle editing mode
    // We'll pass the selectedScheduledPost data to it
  };

  const handleDeleteScheduledPost = () => {
    if (!selectedScheduledPost || !locationId) return;

    try {
      // Get existing scheduled posts
      const savedPosts = localStorage.getItem(`scheduledPosts_${locationId}`);
      if (savedPosts) {
        const posts = normalizeScheduledPosts(JSON.parse(savedPosts));
        const selectedTimestamp = getScheduledTimestamp(selectedScheduledPost);

        // Filter out the post to delete (match by scheduled timestamp)
        const updatedPosts = posts.filter(post =>
          getScheduledTimestamp(post) !== selectedTimestamp
        );

        // Save updated posts back to localStorage
        localStorage.setItem(`scheduledPosts_${locationId}`, JSON.stringify(updatedPosts));

        // Update calendar events
        const updatedEvents = calendarEvents.filter(event => {
          if (event.type !== 'scheduled_post') return true;
          return getScheduledTimestamp(event.postData) !== selectedTimestamp;
        });
        setCalendarEvents(updatedEvents);

        // Show success message
        setSnackbar({
          open: true,
          message: 'Scheduled post deleted successfully!',
          severity: 'success'
        });

        // Close the modal
        setScheduledPostDetailsOpen(false);
        setSelectedScheduledPost(null);
      }
    } catch (error) {
      logger.error('[BusinessProfile] Error deleting scheduled post:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete scheduled post',
        severity: 'error'
      });
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const fetchServiceDetails = async (serviceName) => {
    if (!locationId) return null;

    setServiceDetailsLoading(true);
    try {
      logger.log('[BusinessProfile] Fetching service details for:', serviceName);
      const accessToken = localStorage.getItem('googleAccessToken') || sessionStorage.getItem('googleAccessToken');
      const serviceItems = await GMBService.getServiceItems(locationId, accessToken);

      logger.log('[BusinessProfile] Service items received:', serviceItems);

      // Find the specific service by name, serviceTypeId, or other identifiers
      const serviceDetail = serviceItems.find(item => {
        const itemName = item.name || item.serviceId || '';
        const itemTypeId = item.serviceTypeId || '';
        const itemDescription = item.description || '';

        return itemName.toLowerCase().includes(serviceName.toLowerCase()) ||
          itemTypeId.toLowerCase().includes(serviceName.toLowerCase()) ||
          serviceName.toLowerCase().includes(itemName.toLowerCase()) ||
          itemDescription.toLowerCase().includes(serviceName.toLowerCase());
      });

      if (serviceDetail) {
        logger.log('[BusinessProfile] Found matching service:', serviceDetail);
        return {
          id: serviceDetail.serviceTypeId || serviceDetail.serviceId || serviceDetail.name || serviceName.toLowerCase().replace(/\s+/g, '_'),
          name: serviceName, // Use the display name from the service list
          description: serviceDetail.description || 'Service description not available. Please contact the business for more details about this service.',
          price: serviceDetail.price ? `${serviceDetail.price.currencyCode || ''} ${serviceDetail.price.units || serviceDetail.price.amount || ''}`.trim() : null,
          category: serviceDetail.category || null,
          serviceTypeId: serviceDetail.serviceTypeId || null
        };
      } else {
        logger.log('[BusinessProfile] No matching service found, using fallback');
        // Return a basic service object if not found in service items
        return {
          id: serviceName.toLowerCase().replace(/\s+/g, '_'),
          name: serviceName,
          description: 'Service description not available. Please contact the business for more details about this service.',
          price: null,
          category: null
        };
      }
    } catch (error) {
      logger.warn('Error fetching service details:', error);
      return {
        id: serviceName.toLowerCase().replace(/\s+/g, '_'),
        name: serviceName,
        description: 'Unable to load service description at this time. Please try again later or contact the business directly.',
        price: null,
        category: null
      };
    } finally {
      setServiceDetailsLoading(false);
    }
  };

  const handleServiceClick = async (service) => {
    logger.log('[BusinessProfile] handleServiceClick called with service:', service);
    try {
      const serviceDetails = await fetchServiceDetails(service.title);
      logger.log('[BusinessProfile] Service details fetched:', serviceDetails);

      if (serviceDetails) {
        logger.log('[BusinessProfile] Setting selected service and opening modal');
        setSelectedService(serviceDetails);
        setServiceDetailsModalOpen(true);
      } else {
        logger.warn('[BusinessProfile] No service details returned');
      }
    } catch (error) {
      logger.error('[BusinessProfile] Error in handleServiceClick:', error);
    }
  };

  // Handle opening edit service modal
  const handleEditService = async (service, event) => {
    // Stop propagation to prevent triggering the service click handler
    if (event) {
      event.stopPropagation();
    }

    logger.log('[BusinessProfile] Opening edit modal for service:', service);

    try {
      // Fetch full service details
      const serviceDetails = await fetchServiceDetails(service.title);

      if (serviceDetails) {
        setEditingService(serviceDetails);
        const categoryLabel =
          serviceCategoryOptions.find(option => option.id === serviceDetails.category)?.label ||
          serviceDetails.category ||
          '';
        setServiceFormData({
          name: serviceDetails.name || '',
          description: serviceDetails.description || '',
          category: categoryLabel,
          serviceTypeId: serviceDetails.serviceTypeId || serviceDetails.id || ''
        });
        setServiceEditModalOpen(true);
      }
    } catch (error) {
      logger.error('[BusinessProfile] Error opening edit service modal:', error);
    }
  };

  // Handle opening add service modal
  const handleAddService = () => {
    logger.log('[BusinessProfile] Opening add service modal');
    setEditingService(null);
    const defaultCategory = locationData?.categories?.primaryCategory?.displayName || '';
    const defaultCategoryId = locationData?.categories?.primaryCategory?.categoryId || '';
    setServiceFormData({
      name: '',
      description: '',
      category: defaultCategory,
      categoryId: defaultCategoryId,
      serviceTypeId: ''
    });
    setServiceAddModalOpen(true);
  };

  // Handle service form field changes
  const handleServiceFormChange = (field, value) => {
    setServiceFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSuggestionFill = (suggestion) => {
    const name = suggestion.displayName || suggestion.serviceTypeId || '';
    const description = suggestion.description || serviceFormData.description;
    setServiceFormData(prev => ({
      ...prev,
      name,
      description,
      serviceTypeId: suggestion.serviceTypeId || prev.serviceTypeId
    }));
  };

  // Handle saving edited service
  const handleSaveService = async () => {
    try {
      setServiceSaving(true);
      const accessToken = localStorage.getItem('googleAccessToken') || sessionStorage.getItem('googleAccessToken');

      if (!editingService) {
        throw new Error('No service selected for editing');
      }

      const name = serviceFormData.name?.trim();
      const description = serviceFormData.description?.trim();
      if (!name || !description) {
        alert('Service name and description are required to save changes.');
        return;
      }

      logger.log('[BusinessProfile] Saving service:', editingService.id, serviceFormData);

      const categoryValue =
        serviceFormData.categoryId?.trim() ||
        serviceFormData.category?.trim() ||
        locationData?.categories?.primaryCategory?.categoryId ||
        '';
      const preparedServiceTypeId =
        serviceFormData.serviceTypeId?.trim() ||
        editingService.serviceTypeId ||
        editingService.id ||
        name.toLowerCase().replace(/\s+/g, '_');

      const updatePayload = {
        serviceTypeId: preparedServiceTypeId,
        description,
        ...(categoryValue ? { category: categoryValue } : {})
      };
      logger.log('[BusinessProfile] Service update payload:', updatePayload);
      const updateResponse = await GMBService.updateServiceItem(
        locationId,
        editingService.id,
        updatePayload,
        accessToken
      );
      logger.log('[BusinessProfile] Service update response:', updateResponse);

      await fetchProductsAndServices();

      setServiceEditModalOpen(false);
      setSaveSuccess(true);
      logger.log('[BusinessProfile] Service updated successfully');
    } catch (error) {
      logger.error('[BusinessProfile] Error saving service:', error);
      alert(`Failed to update service: ${error.message}`);
    } finally {
      setServiceSaving(false);
    }
  };

  // Handle adding new service
  const handleCreateService = async () => {
    try {
      setServiceSaving(true);
      const accessToken = localStorage.getItem('googleAccessToken') || sessionStorage.getItem('googleAccessToken');

      const name = serviceFormData.name?.trim();
      const description = serviceFormData.description?.trim();
      if (!name || !description) {
        alert('Service name and description are required');
        return;
      }

      const structuredId = serviceFormData.serviceTypeId?.trim();
      const categoryIdValue = serviceFormData.categoryId?.trim();
      const categoryLabelValue = serviceFormData.category?.trim();
      if (!structuredId && !categoryIdValue && !categoryLabelValue) {
        alert('Provide a category or structured service ID to add this service.');
        return;
      }

      logger.log('[BusinessProfile] Creating new service:', serviceFormData);

      const availableFreeFormCategories = new Set(
        serviceCategoryOptions
          .map(option => (option.label || option.id || '').trim())
          .filter(Boolean)
      );

      if (
        !structuredId &&
        (categoryIdValue || categoryLabelValue) &&
        !availableFreeFormCategories.has(categoryIdValue || categoryLabelValue)
      ) {
        alert(
          `Category "${categoryIdValue || categoryLabelValue}" is not listed on this Google Business Profile. Choose one of: ${[
            ...availableFreeFormCategories
          ].join(', ')}`
        );
        return;
      }

      const categoryForPayload = categoryIdValue || categoryLabelValue;
      const payload = {
        name,
        description,
        ...(structuredId ? { serviceTypeId: structuredId } : {}),
        ...(categoryForPayload ? { category: categoryForPayload } : {})
      };

      logger.log('[BusinessProfile] Service create payload:', payload);
      const createResponse = await GMBService.addServiceItem(
        locationId,
        payload,
        accessToken
      );
      logger.log('[BusinessProfile] Service create response:', createResponse);

      await fetchProductsAndServices();

      setServiceAddModalOpen(false);
      setServiceFormData({
        name: '',
        description: '',
        category: '',
        serviceTypeId: ''
      });
      setSaveSuccess(true);
      logger.log('[BusinessProfile] Service created successfully');
    } catch (error) {
      logger.error('[BusinessProfile] Error creating service:', error);
      alert(`Failed to create service: ${error.message}`);
    } finally {
      setServiceSaving(false);
    }
  };

  // Handle deleting a service
  const handleDeleteService = async (serviceId) => {
    try {
      const confirmed = window.confirm('Are you sure you want to delete this service?');
      if (!confirmed) return;

      setServiceSaving(true);
      const accessToken = localStorage.getItem('googleAccessToken') || sessionStorage.getItem('googleAccessToken');

      logger.log('[BusinessProfile] Deleting service:', serviceId);

      // Delete the service via GMB API
      await GMBService.deleteServiceItem(locationId, serviceId, accessToken);

      // Refresh services list
      await fetchProductsAndServices();

      // Close modal if open and show success
      setServiceEditModalOpen(false);
      setSaveSuccess(true);
      logger.log('[BusinessProfile] Service deleted successfully');
    } catch (error) {
      logger.error('[BusinessProfile] Error deleting service:', error);
      alert(`Failed to delete service: ${error.message}`);
    } finally {
      setServiceSaving(false);
    }
  };

  // ==================== APPOINTMENT LINK HANDLERS ====================

  // Handle opening add appointment link modal
  const handleAddAppointmentLink = () => {
    logger.log('[BusinessProfile] Opening add appointment link modal');
    setEditingAppointment(null);
    setAppointmentFormData({
      linkType: 'APPOINTMENT',
      url: '',
      label: '',
      description: ''
    });
    setAppointmentAddModalOpen(true);
  };

  // Handle opening edit appointment link modal
  const handleEditAppointmentLink = (appointmentLink, event) => {
    // Stop propagation to prevent triggering the link click
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    logger.log('[BusinessProfile] Opening edit modal for appointment link:', appointmentLink);

    setEditingAppointment(appointmentLink);
    setAppointmentFormData({
      linkType: appointmentLink.type || 'APPOINTMENT',
      url: appointmentLink.url || '',
      label: appointmentLink.label || '',
      description: appointmentLink.description || ''
    });
    setAppointmentEditModalOpen(true);
  };

  // Handle appointment form field changes
  const handleAppointmentFormChange = (field, value) => {
    setAppointmentFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Validate URL format
  const isValidUrl = (urlString) => {
    try {
      const url = new URL(urlString);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch (e) {
      return false;
    }
  };

  // Handle adding new appointment link
  const handleCreateAppointmentLink = async () => {
    try {
      // Validate URL
      if (!appointmentFormData.url) {
        alert('URL is required');
        return;
      }

      if (!isValidUrl(appointmentFormData.url)) {
        alert('Please enter a valid URL (must start with http:// or https://)');
        return;
      }

      setAppointmentSaving(true);
      const accessToken = localStorage.getItem('googleAccessToken') || sessionStorage.getItem('googleAccessToken');

      logger.log('[BusinessProfile] Creating new appointment link:', appointmentFormData);

      // Add the appointment link via GMB API
      await GMBService.addPlaceActionLink(
        locationId,
        {
          placeActionType: appointmentFormData.linkType,
          uri: appointmentFormData.url,
          label: appointmentFormData.label,
          description: appointmentFormData.description
        },
        accessToken
      );

      // Refresh location data to get updated appointment links
      await fetchLocationData();

      // Close modal and show success
      setAppointmentAddModalOpen(false);
      setSaveSuccess(true);
      logger.log('[BusinessProfile] Appointment link created successfully');
    } catch (error) {
      logger.error('[BusinessProfile] Error creating appointment link:', error);
      alert(`Failed to create appointment link: ${error.message}`);
    } finally {
      setAppointmentSaving(false);
    }
  };

  // Handle saving edited appointment link
  const handleSaveAppointmentLink = async () => {
    try {
      // Validate URL
      if (!appointmentFormData.url) {
        alert('URL is required');
        return;
      }

      if (!isValidUrl(appointmentFormData.url)) {
        alert('Please enter a valid URL (must start with http:// or https://)');
        return;
      }

      if (!editingAppointment) {
        throw new Error('No appointment link selected for editing');
      }

      setAppointmentSaving(true);
      const accessToken = localStorage.getItem('googleAccessToken') || sessionStorage.getItem('googleAccessToken');

      logger.log('[BusinessProfile] Saving appointment link:', editingAppointment, appointmentFormData);

      // Update the appointment link via GMB API
      await GMBService.updatePlaceActionLink(
        locationId,
        editingAppointment.url, // Use original URL as identifier
        {
          placeActionType: appointmentFormData.linkType,
          uri: appointmentFormData.url,
          label: appointmentFormData.label,
          description: appointmentFormData.description
        },
        accessToken
      );

      // Refresh location data to get updated appointment links
      await fetchLocationData();

      // Close modal and show success
      setAppointmentEditModalOpen(false);
      setSaveSuccess(true);
      logger.log('[BusinessProfile] Appointment link updated successfully');
    } catch (error) {
      logger.error('[BusinessProfile] Error saving appointment link:', error);
      alert(`Failed to update appointment link: ${error.message}`);
    } finally {
      setAppointmentSaving(false);
    }
  };

  // Handle deleting an appointment link
  const handleDeleteAppointmentLink = async (appointmentUrl) => {
    try {
      const confirmed = window.confirm('Are you sure you want to delete this appointment link?');
      if (!confirmed) return;

      setAppointmentSaving(true);
      const accessToken = localStorage.getItem('googleAccessToken') || sessionStorage.getItem('googleAccessToken');

      logger.log('[BusinessProfile] Deleting appointment link:', appointmentUrl);

      // Delete the appointment link via GMB API
      await GMBService.deletePlaceActionLink(locationId, appointmentUrl, accessToken);

      // Refresh location data to get updated appointment links
      await fetchLocationData();

      // Close modal if open and show success
      setAppointmentEditModalOpen(false);
      setSaveSuccess(true);
      logger.log('[BusinessProfile] Appointment link deleted successfully');
    } catch (error) {
      logger.error('[BusinessProfile] Error deleting appointment link:', error);
      alert(`Failed to delete appointment link: ${error.message}`);
    } finally {
      setAppointmentSaving(false);
    }
  };

  // ==================== GMB POST HANDLERS ====================

  // Handle opening edit post modal
  const handleEditPost = (post, event) => {
    // Stop propagation to prevent triggering the post click
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    logger.log('[BusinessProfile] Opening edit modal for post:', post);

    setEditingPost(post);

    // Pre-fill form data with existing post data
    setPostFormData({
      summary: post.summary || '',
      callToAction: {
        actionType: post.callToAction?.actionType || 'LEARN_MORE',
        url: post.callToAction?.url || ''
      },
      event: {
        title: post.event?.title || '',
        schedule: {
          startDate: post.event?.schedule?.startDate?.year && post.event?.schedule?.startDate?.month && post.event?.schedule?.startDate?.day
            ? `${post.event.schedule.startDate.year}-${String(post.event.schedule.startDate.month).padStart(2, '0')}-${String(post.event.schedule.startDate.day).padStart(2, '0')}`
            : '',
          startTime: post.event?.schedule?.startTime?.hours !== undefined && post.event?.schedule?.startTime?.minutes !== undefined
            ? `${String(post.event.schedule.startTime.hours).padStart(2, '0')}:${String(post.event.schedule.startTime.minutes).padStart(2, '0')}`
            : '',
          endDate: post.event?.schedule?.endDate?.year && post.event?.schedule?.endDate?.month && post.event?.schedule?.endDate?.day
            ? `${post.event.schedule.endDate.year}-${String(post.event.schedule.endDate.month).padStart(2, '0')}-${String(post.event.schedule.endDate.day).padStart(2, '0')}`
            : '',
          endTime: post.event?.schedule?.endTime?.hours !== undefined && post.event?.schedule?.endTime?.minutes !== undefined
            ? `${String(post.event.schedule.endTime.hours).padStart(2, '0')}:${String(post.event.schedule.endTime.minutes).padStart(2, '0')}`
            : ''
        }
      },
      offer: {
        couponCode: post.offer?.couponCode || '',
        redeemOnlineUrl: post.offer?.redeemOnlineUrl || '',
        termsConditions: post.offer?.termsConditions || ''
      }
    });

    setPostEditModalOpen(true);
  };

  // Handle post form field changes
  const handlePostFormChange = (field, value) => {
    if (field.includes('.')) {
      // Handle nested fields like 'callToAction.url'
      const parts = field.split('.');
      setPostFormData(prev => {
        const updated = { ...prev };
        let current = updated;
        for (let i = 0; i < parts.length - 1; i++) {
          current = current[parts[i]];
        }
        current[parts[parts.length - 1]] = value;
        return updated;
      });
    } else {
      setPostFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  // Handle saving edited post
  const handleSavePost = async () => {
    try {
      // Validate required fields
      if (!postFormData.summary) {
        alert('Post content/summary is required');
        return;
      }

      if (!editingPost || !editingPost.name) {
        throw new Error('No post selected for editing');
      }

      setPostSaving(true);
      const accessToken = localStorage.getItem('googleAccessToken') || sessionStorage.getItem('googleAccessToken');

      logger.log('[BusinessProfile] Saving post:', editingPost.name, postFormData);

      // Extract account ID and location ID from post name
      // Post name format: accounts/{accountId}/locations/{locationId}/localPosts/{postId}
      const nameParts = editingPost.name.split('/');
      const accountId = nameParts[1];
      const locationIdFromPost = nameParts[3];
      const postId = nameParts[5];

      // Prepare update data
      const updateData = {
        summary: postFormData.summary
      };

      // Add call to action if URL is provided
      if (postFormData.callToAction.url) {
        updateData.callToAction = {
          actionType: postFormData.callToAction.actionType,
          url: postFormData.callToAction.url
        };
      }

      // Add event data if title is provided
      if (postFormData.event.title) {
        updateData.event = {
          title: postFormData.event.title
        };

        // Add schedule if dates are provided
        if (postFormData.event.schedule.startDate) {
          const startDateParts = postFormData.event.schedule.startDate.split('-');
          const endDateParts = postFormData.event.schedule.endDate ? postFormData.event.schedule.endDate.split('-') : startDateParts;

          updateData.event.schedule = {
            startDate: {
              year: parseInt(startDateParts[0]),
              month: parseInt(startDateParts[1]),
              day: parseInt(startDateParts[2])
            },
            endDate: {
              year: parseInt(endDateParts[0]),
              month: parseInt(endDateParts[1]),
              day: parseInt(endDateParts[2])
            }
          };

          // Add times if provided
          if (postFormData.event.schedule.startTime) {
            const startTimeParts = postFormData.event.schedule.startTime.split(':');
            updateData.event.schedule.startTime = {
              hours: parseInt(startTimeParts[0]),
              minutes: parseInt(startTimeParts[1])
            };
          }

          if (postFormData.event.schedule.endTime) {
            const endTimeParts = postFormData.event.schedule.endTime.split(':');
            updateData.event.schedule.endTime = {
              hours: parseInt(endTimeParts[0]),
              minutes: parseInt(endTimeParts[1])
            };
          }
        }
      }

      // Add offer data if coupon code is provided
      if (postFormData.offer.couponCode) {
        updateData.offer = {
          couponCode: postFormData.offer.couponCode
        };

        if (postFormData.offer.redeemOnlineUrl) {
          updateData.offer.redeemOnlineUrl = postFormData.offer.redeemOnlineUrl;
        }

        if (postFormData.offer.termsConditions) {
          updateData.offer.termsConditions = postFormData.offer.termsConditions;
        }
      }

      // Update the post via GMB API
      await GMBService.updatePost(accountId, locationIdFromPost, postId, updateData, accessToken);

      // Refresh posts list
      await fetchLocalPosts();

      // Close modal and show success
      setPostEditModalOpen(false);
      setSaveSuccess(true);
      logger.log('[BusinessProfile] Post updated successfully');
    } catch (error) {
      logger.error('[BusinessProfile] Error saving post:', error);
      alert(`Failed to update post: ${error.message}`);
    } finally {
      setPostSaving(false);
    }
  };

  // Handle deleting a post
  const handleDeletePost = async (postName) => {
    try {
      const confirmed = window.confirm('Are you sure you want to delete this post? This action cannot be undone.');
      if (!confirmed) return;

      setPostSaving(true);
      const accessToken = localStorage.getItem('googleAccessToken') || sessionStorage.getItem('googleAccessToken');

      logger.log('[BusinessProfile] Deleting post:', postName);

      // Extract account ID, location ID, and post ID from post name
      // Post name format: accounts/{accountId}/locations/{locationId}/localPosts/{postId}
      const nameParts = postName.split('/');
      const accountId = nameParts[1];
      const locationIdFromPost = nameParts[3];
      const postId = nameParts[5];

      // Delete the post via GMB API
      await GMBService.deletePost(accountId, locationIdFromPost, postId, accessToken);

      // Refresh posts list
      await fetchLocalPosts();

      // Close modal if open and show success
      setPostEditModalOpen(false);
      setSaveSuccess(true);
      logger.log('[BusinessProfile] Post deleted successfully');
    } catch (error) {
      logger.error('[BusinessProfile] Error deleting post:', error);
      alert(`Failed to delete post: ${error.message}`);
    } finally {
      setPostSaving(false);
    }
  };

  return (
    <DashboardLayout>
      {/* Success message */}
      <Snackbar open={saveSuccess} autoHideDuration={6000} onClose={() => setSaveSuccess(false)}>
        <Alert onClose={() => setSaveSuccess(false)} severity="success" sx={{ width: '100%' }}>
          Business profile updated successfully!
        </Alert>
      </Snackbar>

      {/* Error message */}
      <Snackbar open={!!saveError} autoHideDuration={6000} onClose={() => setSaveError('')}>
        <Alert onClose={() => setSaveError('')} severity="error" sx={{ width: '100%' }}>
          {saveError}
        </Alert>
      </Snackbar>

      {/* General notification snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <PageContainer>
        <ContentWrapper>
          {/* Page Header */}
          <PageHeader>
            <HeaderLeft>
              <HeaderTitle>{businessTitle}</HeaderTitle>
              <HeaderSubtitle>
                {isAdminViewingClient
                  ? "🔐 Admin View - Viewing client profile with full access to all features including competitor analysis and leaderboard"
                  : "View and manage your Google My Business profile information and performance metrics."}
              </HeaderSubtitle>

            </HeaderLeft>
            <SwitchAccountButton
              onClick={(event) => {
                setAnchorEl(event.currentTarget);
              }}
            >
              <SwitchAccountIcon width={17} height={15} color="#121927" />
              Switch Profile
            </SwitchAccountButton>
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={() => setAnchorEl(null)}
              PaperProps={{
                sx: {
                  maxHeight: 400,
                  width: 320,
                  mt: 1,
                  borderRadius: '8px',
                  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)'
                }
              }}
            >
              <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#121927' }}>
                  Switch to Another Profile
                </Typography>
                <Typography sx={{ fontSize: '12px', color: '#6b7280', mt: 0.5 }}>
                  Select a business profile to view
                </Typography>
              </Box>
              {availableProfiles.length > 0 ? (
                availableProfiles.map((profile, index) => (
                  <MenuItem
                    key={`${profile.id}-${profile.locationId}`}
                    onClick={() => handleProfileSwitch(profile)}
                    disabled={profile.isCurrentLocation}
                    sx={{
                      py: 1.5,
                      px: 2,
                      '&:hover': {
                        backgroundColor: profile.isCurrentLocation ? 'transparent' : '#f8f9fa'
                      },
                      opacity: profile.isCurrentLocation ? 0.6 : 1
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          backgroundColor: profile.isCurrentLocation ? '#0B91D6' : '#e5e7eb',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {profile.isCurrentLocation && (
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: 'white'
                            }}
                          />
                        )}
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#121927' }}>
                          {profile.name}
                          {profile.isCurrentLocation && (
                            <Typography component="span" sx={{ fontSize: '12px', color: '#0B91D6', ml: 1 }}>
                              (Current)
                            </Typography>
                          )}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>
                            {profile.address}
                          </Typography>
                          <Typography sx={{ fontSize: '11px', color: profile.status === 'verified' ? '#34A853' : profile.status === 'pending_verification' ? '#F59E0B' : '#EF4444', textTransform: 'capitalize' }}>
                            {profile.status.replace('_', ' ')}
                          </Typography>
                        </Box>
                      }
                    />
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>
                  <ListItemText
                    primary={
                      <Typography sx={{ fontSize: '14px', color: '#6b7280', textAlign: 'center' }}>
                        {profilesLoading ? 'Loading profiles...' : 'No other profiles available'}
                      </Typography>
                    }
                  />
                </MenuItem>
              )}
            </Menu>
          </PageHeader>

          {/* Admin Viewing Banner */}
          {isAdminViewingClient && (
            <Alert
              severity="info"
              sx={{
                mb: 3,
                backgroundColor: '#E3F2FD',
                border: '1px solid #0B91D6',
                '& .MuiAlert-icon': {
                  color: '#0B91D6'
                }
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Admin Viewing Mode Active
              </Typography>
              <Typography variant="caption" component="div">
                You are viewing this profile as an administrator with full access to all client features including:
                • Competitor Analysis & Rankings • Leaderboard • Profile Scores • All Premium Features
              </Typography>
            </Alert>
          )}

          {/* Main Grid - Business Profile + Profile Strength */}
          <MainGrid>
            <BusinessProfileCard>
              <Dialog open={qrOpen} onClose={() => setQrOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
                  Review us on Google
                </DialogTitle>
                <DialogContent>
                  {qrSrc ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 2 }}>
                      <Typography sx={{ fontSize: '14px', color: '#6b7280', textAlign: 'center', mb: 1 }}>
                        Scan this QR code to leave a review
                      </Typography>
                      <Box sx={{
                        p: 2,
                        bgcolor: '#fff',
                        borderRadius: 2,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        border: '1px solid #e5e7eb'
                      }}>
                        <img
                          src={qrSrc}
                          alt="Review QR Code"
                          width={220}
                          height={220}
                          onError={() => {
                            console.error('[Review QR] QR code image failed to load, trying alternate provider');
                            setQrProviderIndex((i) => (i + 1) % 2);
                          }}
                          style={{ display: 'block' }}
                        />
                      </Box>
                      <Box sx={{
                        mt: 1,
                        p: 2,
                        bgcolor: '#f9fafb',
                        borderRadius: 1,
                        width: '100%',
                        wordBreak: 'break-all'
                      }}>
                        <Typography sx={{ fontSize: '11px', color: '#6b7280', fontFamily: 'monospace' }}>
                          {safeReviewLink}
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                      <Typography sx={{ fontSize: '14px', color: '#ef4444', mb: 2 }}>
                        ⚠ Review link unavailable
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>
                        Unable to generate review link. Please ensure the business has a valid Place ID or address.
                      </Typography>
                    </Box>
                  )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
                  <Button
                    onClick={() => setQrOpen(false)}
                    sx={{ color: '#6b7280' }}
                  >
                    Close
                  </Button>
                  {safeReviewLink && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        onClick={() => {
                          navigator.clipboard.writeText(safeReviewLink);
                          // You could add a toast notification here
                          console.log('[Review QR] Link copied to clipboard');
                        }}
                        variant="outlined"
                        sx={{ textTransform: 'none' }}
                      >
                        Copy Link
                      </Button>
                      <Button
                        onClick={() => window.open(safeReviewLink, '_blank', 'noopener,noreferrer')}
                        variant="contained"
                        sx={{ textTransform: 'none' }}
                      >
                        Test Link
                      </Button>
                    </Box>
                  )}
                </DialogActions>
              </Dialog>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '8px',
                flexWrap: 'wrap',
                gap: { xs: '8px', sm: '0' }
              }}>
                <Box sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: { xs: '6px', sm: '0' },
                  flex: 1
                }}>
                  <BusinessTitle>
                    {businessTitle}
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      ml: { xs: 0, sm: 1 },
                      mt: { xs: '4px', sm: 0 }
                    }}>
                      {isVerified ? (
                        <>
                          <VerifiedCheckIcon width={11} height={12} color="#34A853" />
                          <Typography sx={{
                            fontSize: { xs: '11px', sm: '12px' },
                            fontWeight: 500,
                            color: '#34A853',
                            backgroundColor: '#F0F9F4',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            whiteSpace: 'nowrap'
                          }}>
                            Verified
                          </Typography>
                        </>
                      ) : (
                        <Typography sx={{
                          fontSize: { xs: '11px', sm: '12px' },
                          fontWeight: 500,
                          color: '#EF4444',
                          backgroundColor: '#FEF2F2',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          whiteSpace: 'nowrap'
                        }}>
                          Not Verified
                        </Typography>
                      )}
                    </Box>
                  </BusinessTitle>
                </Box>
                <EditProfileLink onClick={handleEditProfile}>(Edit Profile)</EditProfileLink>
              </Box>

              <AddressText>
                <LocationIcon width={12} height={15} color="#121927" />
                {businessAddress}
              </AddressText>

              <ReviewsRow>
                <ReviewsLabel>Reviews :</ReviewsLabel>
                <Rating
                  value={averageRating}
                  readOnly
                  precision={0.1}
                  size="small"
                  sx={{
                    color: '#F59E0B',
                    '& .MuiRating-iconFilled': { color: '#F59E0B' },
                    '& .MuiRating-iconEmpty': { color: '#E5E7EB' },
                    '& .MuiRating-iconHover': { color: '#F59E0B' },
                    '& .MuiRating-icon': { fontSize: '16px' }
                  }}
                />
                <ReviewsRating>{averageRating.toFixed(2)} ({totalReviews} reviews)</ReviewsRating>
              </ReviewsRow>

              <ContactGrid>
                <ContactItem>
                  <ContactLabel>Primary Number:</ContactLabel>
                  <ContactValue>
                    {primaryPhone}
                  </ContactValue>
                </ContactItem>
                <ContactItem>
                  <ContactLabel>Secondary Number:</ContactLabel>
                  <ContactValue>
                    {locationData?.secondaryPhone || 'Not available'}
                  </ContactValue>
                </ContactItem>
                <ContactItem>
                  <ContactLabel>Manager Number:</ContactLabel>
                  <ContactValue>
                    {locationData?.managerPhone || 'Not available'}
                  </ContactValue>
                </ContactItem>
              </ContactGrid>

              <HoursText>Hours:</HoursText>
              <HoursValue>Open ⋅ Closes 7 pm</HoursValue>

              <PhotosSection>
                <PhotosTitle>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>Photos</span>
                  </Box>
                </PhotosTitle>

                {/* Cover Photo Display */}
                {mediaItems && mediaItems.length > 0 && (
                  (() => {
                    const coverPhoto = mediaItems.find(item => item.category === 'COVER' || item.category === 'PROFILE');
                    if (coverPhoto) {
                      const originalSrc = coverPhoto.googleUrl || coverPhoto.thumbnailUrl || coverPhoto.sourceUrl || '';
                      const proxied = originalSrc ? buildProxyUrl(originalSrc) : null;
                      const initialSrc = proxied || originalSrc;

                      return (
                        <CoverPhotoContainer
                          onClick={() => handlePhotoClick(originalSrc, proxied)}
                          role="button"
                          aria-label="Open cover photo"
                        >
                          <CoverPhotoLabel>
                            {coverPhoto.category === 'COVER' ? 'Cover Photo' : 'Profile Photo'}
                          </CoverPhotoLabel>
                          <img
                            src={initialSrc}
                            alt="GMB Cover Photo"
                            referrerPolicy="no-referrer"
                            crossOrigin="anonymous"
                            data-original={originalSrc}
                            data-proxy={proxied || ''}
                            data-retry="0"
                            onError={(e) => {
                              const img = e.target;
                              const retry = parseInt(img.dataset.retry || '0');
                              const og = img.dataset.original;
                              const proxy = img.dataset.proxy;
                              if (retry === 0 && proxy && img.src === proxy && og) {
                                logger.warn('Cover photo failed via proxy, retrying original:', og);
                                img.dataset.retry = '1';
                                img.src = og;
                              } else if (retry <= 1 && proxy && img.src === og) {
                                logger.warn('Cover photo failed via original, falling back to placeholder');
                                img.dataset.retry = '2';
                                img.src = placeholderSmall;
                              } else {
                                img.src = placeholderSmall;
                              }
                            }}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        </CoverPhotoContainer>
                      );
                    }
                    return null;
                  })()
                )}

                <PhotosGrid>
                  {mediaItems && mediaItems.length > 0 ? (
                    mediaItems
                      .filter(item => item.category !== 'COVER' && item.category !== 'PROFILE')
                      .slice(0, 2)
                      .map((item, index) => {
                        const originalSrc = item.googleUrl || item.thumbnailUrl || item.sourceUrl || '';
                        const proxied = originalSrc ? buildProxyUrl(originalSrc) : null;
                        const initialSrc = proxied || originalSrc;
                        return (
                          <PhotoItem
                            key={item.name || `photo-${index}`}
                            sx={{ cursor: initialSrc ? 'pointer' : 'default' }}
                            onClick={() => handlePhotoClick(originalSrc, proxied)}
                            role={initialSrc ? 'button' : undefined}
                            aria-label={initialSrc ? 'Open photo' : undefined}
                          >
                            {initialSrc ? (
                              <img
                                src={initialSrc}
                                alt={`GMB Photo ${index + 1}`}
                                referrerPolicy="no-referrer"
                                crossOrigin="anonymous"
                                data-original={originalSrc}
                                data-proxy={proxied || ''}
                                data-retry="0"
                                onError={(e) => {
                                  const img = e.target;
                                  const retry = parseInt(img.dataset.retry || '0');
                                  const og = img.dataset.original;
                                  const proxy = img.dataset.proxy;
                                  if (retry === 0 && proxy && img.src === proxy && og) {
                                    logger.warn('Photo failed via proxy, retrying original:', og);
                                    img.dataset.retry = '1';
                                    img.src = og;
                                  } else if (retry <= 1 && proxy && img.src === og) {
                                    logger.warn('Photo failed via original, falling back to placeholder');
                                    img.dataset.retry = '2';
                                    img.src = placeholderSmall;
                                  } else {
                                    img.src = placeholderSmall;
                                  }
                                }}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            ) : (
                              <img
                                src={placeholderSmall}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                alt="No image available"
                              />
                            )}
                          </PhotoItem>
                        );
                      })
                  ) : (
                    <>
                      <PhotoItem>
                        <img
                          src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjEzMyIgdmlld0JveD0iMCAwIDEyMCAxMzMiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMjAiIGhlaWdodD0iMTMzIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik00MCA0MEg4MFY5M0g0MFY0MFoiIGZpbGw9IiM5Q0EzQUYiLz4KPC9zdmc+Cg=="
                          alt="No photos available"
                        />
                      </PhotoItem>
                      <PhotoItem>
                        <img
                          src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjEzMyIgdmlld0JveD0iMCAwIDEyMCAxMzMiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMjAiIGhlaWdodD0iMTMzIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik00MCA0MEg4MFY5M0g0MFY0MFoiIGZpbGw9IiM5Q0EzQUYiLz4KPC9zdmc+Cg=="
                          alt="No photos available"
                        />
                      </PhotoItem>
                    </>
                  )}
                  <AddPhotoButton onClick={handlePhotoUpload}>
                    <AddPhotoIcon width={24} height={24} color="#0B91D6" />
                    <AddPhotoText>Add Photo</AddPhotoText>
                  </AddPhotoButton>
                </PhotosGrid>
                {mediaItems && mediaItems.length > 0 && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Button
                      variant="text"
                      onClick={() => setPhotosModalOpen(true)}
                      sx={{
                        color: '#0B91D6',
                        fontSize: '14px',
                        fontWeight: 500,
                        textTransform: 'none',
                        '&:hover': {
                          backgroundColor: 'transparent',
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      View All Photos ({mediaItems.length})
                    </Button>
                  </Box>
                )}
              </PhotosSection>

              {/* Videos Section */}
              <VideosSection>
                <VideosTitle>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Videos
                      {videos.length > 0 && (
                        <Badge
                          badgeContent={videos.length}
                          color="primary"
                          sx={{
                            '& .MuiBadge-badge': {
                              fontSize: '10px',
                              minWidth: '18px',
                              height: '18px',
                              backgroundColor: '#0B91D6'
                            }
                          }}
                        />
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconButton
                        onClick={refreshVideos}
                        size="small"
                        disabled={videosRefreshing}
                        sx={{
                          color: '#0B91D6',
                          '&:hover': {
                            backgroundColor: 'rgba(11, 145, 214, 0.1)'
                          }
                        }}
                        title="Refresh videos"
                      >
                        {videosRefreshing ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <RefreshIcon />
                        )}
                      </IconButton>
                    </Box>
                  </Box>
                </VideosTitle>
                <VideosGrid>
                  {loading ? (
                    <>
                      <VideoItem>
                        <Box sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          color: '#9ca3af'
                        }}>
                          <CircularProgress size={20} color="inherit" />
                          <Typography sx={{ fontSize: '10px', textAlign: 'center' }}>
                            Loading videos...
                          </Typography>
                        </Box>
                      </VideoItem>
                      <VideoItem>
                        <Box sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          color: '#9ca3af'
                        }}>
                          <CircularProgress size={20} color="inherit" />
                          <Typography sx={{ fontSize: '10px', textAlign: 'center' }}>
                            Loading videos...
                          </Typography>
                        </Box>
                      </VideoItem>
                    </>
                  ) : videos && videos.length > 0 ? (
                    videos.slice(0, 2).map((video, index) => (
                      <VideoItem
                        key={video.id || `video-${index}`}
                        onClick={() => handleVideoClick(video)}
                        role="button"
                        aria-label="Open video"
                      >
                        {video.thumbnail ? (
                          <VideoThumbnail
                            src={video.thumbnail}
                            alt={`Video ${index + 1}`}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : (
                          <Box sx={{
                            width: '100%',
                            height: '100%',
                            backgroundColor: '#f3f4f6',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <VideoIcon width={32} height={32} color="#9ca3af" />
                          </Box>
                        )}
                        <VideoPlayButton>
                          <Box component="span" sx={{ fontSize: '20px' }}>▶</Box>
                        </VideoPlayButton>
                        {video.duration && <VideoDuration>{video.duration}</VideoDuration>}
                      </VideoItem>
                    ))
                  ) : (
                    <>
                      <VideoItem>
                        <Box sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          color: '#9ca3af'
                        }}>
                          <VideoIcon width={32} height={32} />
                          <Typography sx={{ fontSize: '10px', textAlign: 'center' }}>
                            No videos yet
                          </Typography>
                          <Typography sx={{ fontSize: '8px', textAlign: 'center', opacity: 0.7 }}>
                            Videos from GMB profile
                          </Typography>
                        </Box>
                      </VideoItem>
                      <VideoItem>
                        <Box sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          color: '#9ca3af'
                        }}>
                          <VideoIcon width={32} height={32} />
                          <Typography sx={{ fontSize: '10px', textAlign: 'center' }}>
                            No videos yet
                          </Typography>
                          <Typography sx={{ fontSize: '8px', textAlign: 'center', opacity: 0.7 }}>
                            Videos from GMB profile
                          </Typography>
                        </Box>
                      </VideoItem>
                    </>
                  )}
                  <AddVideoButton
                    onClick={handleVideoUpload}
                    title="Note: Direct video uploads have limitations. Consider using YouTube or Vimeo."
                  >
                    <VideoIcon width={24} height={24} color="#0B91D6" />
                    <AddVideoText>Add Video to GMB</AddVideoText>
                    <Typography variant="caption" sx={{ fontSize: '9px', color: '#F59E0B', mt: 0.5 }}>
                      (Limited support)
                    </Typography>
                  </AddVideoButton>
                </VideosGrid>
                {videos.length > 0 && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Button
                      variant="text"
                      onClick={handleOpenVideosModal}
                      sx={{
                        color: '#0B91D6',
                        fontSize: '14px',
                        fontWeight: 500,
                        textTransform: 'none',
                        '&:hover': {
                          backgroundColor: 'transparent',
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      View All Videos ({videos.length})
                    </Button>
                  </Box>
                )}
                {!loading && videos.length === 0 && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    {/* <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                      No videos found in your Google My Business profile
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Upload a video to get started, or check your GMB account for existing videos
                    </Typography> */}
                  </Box>
                )}
              </VideosSection>

              {/* Video Upload Success Message */}
              {videoUploadSuccess && (
                <Alert
                  severity="success"
                  sx={{
                    mt: 2,
                    mb: 2,
                    '& .MuiAlert-message': {
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }
                  }}
                >
                  <VideoIcon width={20} height={20} />
                  Video uploaded successfully to Google My Business! It will appear in your business profile shortly.
                </Alert>
              )}

              {/* Photo Modal */}
              <Modal open={photoModalOpen} onClose={closePhotoModal} aria-labelledby="photo-modal">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                  <Box sx={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh', outline: 'none' }}>
                    {/* Top close button */}
                    <IconButton
                      aria-label="Close"
                      onClick={closePhotoModal}
                      sx={{
                        position: 'absolute',
                        top: { xs: 12, sm: -50 },
                        right: { xs: 12, sm: 'auto' },
                        left: { xs: 'auto', sm: '50%' },
                        transform: { xs: 'none', sm: 'translateX(-50%)' },
                        color: '#EF232A',
                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                        border: '2px solid #EF232A',
                        zIndex: 1000,
                        boxShadow: { xs: '0 6px 16px rgba(0,0,0,0.12)', sm: 'none' },
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 1)',
                          color: '#d32f2f',
                          transform: { xs: 'none', sm: 'translateX(-50%) scale(1.1)' }
                        }
                      }}
                      size="large"
                    >
                      <CloseIcon />
                    </IconButton>
                    {selectedPhoto && (
                      <img
                        src={selectedPhoto.proxy || selectedPhoto.original}
                        alt="Selected"
                        style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain', display: 'block' }}
                        referrerPolicy="no-referrer"
                        crossOrigin="anonymous"
                        data-original={selectedPhoto.original || ''}
                        data-proxy={selectedPhoto.proxy || ''}
                        data-retry="0"
                        onError={(e) => {
                          const img = e.target;
                          const retry = parseInt(img.dataset.retry || '0');
                          const og = img.dataset.original;
                          const proxy = img.dataset.proxy;
                          if (retry === 0 && proxy && img.src === proxy && og) {
                            logger.warn('Photo modal failed via proxy, retrying original:', og);
                            img.dataset.retry = '1';
                            img.src = og;
                          } else if (retry <= 1 && proxy && img.src === og) {
                            logger.warn('Photo modal failed via original, falling back to placeholder');
                            img.dataset.retry = '2';
                            img.src = placeholderSmall;
                          } else {
                            img.src = placeholderSmall;
                          }
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </Modal>

              {/* Video Modal */}
              <Modal open={videoModalOpen} onClose={closeVideoModal} aria-labelledby="video-modal">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                  <Box sx={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh', outline: 'none' }}>
                    {/* Top close button */}
                    <IconButton
                      aria-label="Close"
                      onClick={closeVideoModal}
                      sx={{
                        position: 'absolute',
                        top: { xs: 12, sm: -50 },
                        right: { xs: 12, sm: 'auto' },
                        left: { xs: 'auto', sm: '50%' },
                        transform: { xs: 'none', sm: 'translateX(-50%)' },
                        color: '#EF232A',
                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                        border: '2px solid #EF232A',
                        zIndex: 1000,
                        boxShadow: { xs: '0 6px 16px rgba(0,0,0,0.12)', sm: 'none' },
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 1)',
                          color: '#d32f2f',
                          transform: { xs: 'none', sm: 'translateX(-50%) scale(1.1)' }
                        }
                      }}
                      size="large"
                    >
                      <CloseIcon />
                    </IconButton>
                    {selectedVideo && (
                      <video
                        key={selectedVideo.id || selectedVideo.url}
                        controls
                        autoPlay
                        playsInline
                        crossOrigin="anonymous"
                        style={{
                          maxWidth: '90vw',
                          maxHeight: '90vh',
                          objectFit: 'contain',
                          display: 'block',
                          backgroundColor: '#000'
                        }}
                        onError={(e) => {
                          logger.error('Video playback error:', e);
                          logger.error('Video URL:', selectedVideo.url);
                        }}
                        onLoadedMetadata={(e) => {
                          logger.log('Video loaded successfully, duration:', e.target.duration);
                        }}
                      >
                        <source src={selectedVideo.url} type="video/mp4" />
                        <source src={selectedVideo.url} type="video/webm" />
                        Your browser does not support the video tag.
                      </video>
                    )}
                  </Box>
                </Box>
              </Modal>

              {/* Edit Profile Modal (comprehensive GMB form) */}
              <ModalContainer open={editProfileOpen} onClose={() => setEditProfileOpen(false)}>
                <ModalContent sx={{ maxWidth: '700px', maxHeight: '90vh', overflow: 'auto' }}>
                  <ModalHeader>
                    <ModalTitle>Edit Business Profile</ModalTitle>
                    <CloseButton onClick={() => setEditProfileOpen(false)} aria-label="Close">
                      <CloseIcon />
                    </CloseButton>
                  </ModalHeader>
                  {editStatus && (
                    <Alert
                      severity={editStatus.type === 'rejected' ? 'error' : 'info'}
                      sx={{ mt: 1, mb: 2 }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {editStatus.message}
                      </Typography>
                      {editStatus.detail && (
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                          {editStatus.detail}
                        </Typography>
                      )}
                    </Alert>
                  )}
                  <Box id="editProfileForm" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Section 1: Basic Information */}
                    <Box>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#121927' }}>
                        Basic Information
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box>
                          <TextField
                            name="businessName"
                            label="Business name"
                            defaultValue={locationData?.title || businessTitle}
                            onChange={handleFormInputChange}
                            fullWidth
                            variant="outlined"
                            size="small"
                          />
                          {changedFields.businessName && (
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleFieldSubmit('businessName')}
                              sx={{
                                mt: 1,
                                backgroundColor: '#0B91D6',
                                textTransform: 'capitalize',
                                '&:hover': { backgroundColor: '#0980c2' }
                              }}
                            >
                              Submit
                            </Button>
                          )}
                        </Box>
                        <Box>
                          <Typography variant="body2" sx={{ mb: 1, color: '#6b7280', fontSize: '12px' }}>
                            Business category
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1, color: '#6b7280', fontSize: '11px' }}>
                            Help customers find your business by industry. <a href="#" style={{ color: '#0B91D6' }}>Learn more</a>
                          </Typography>
                          <Box>
                            <Autocomplete
                              fullWidth
                              options={categoryOptions}
                              value={primaryCategorySelection}
                              inputValue={primaryCategoryInputValue}
                              onChange={handlePrimaryCategorySelect}
                              onInputChange={handlePrimaryCategoryInputChange}
                              getOptionLabel={(option) => option?.label || option?.id || ''}
                              isOptionEqualToValue={(option, value) => {
                                const optionKey = option?.id || option?.label || '';
                                const valueKey = value?.id || value?.label || '';
                                return optionKey === valueKey;
                              }}
                              loading={categorySuggestionLoading}
                              freeSolo={false}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  name="businessCategory"
                                  label="Primary category"
                                  variant="outlined"
                                  size="small"
                                  sx={{ mb: 1 }}
                                  InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                      <>
                                        {categorySuggestionLoading ? (
                                          <CircularProgress color="inherit" size={16} sx={{ mr: 1 }} />
                                        ) : null}
                                        {params.InputProps.endAdornment}
                                      </>
                                    )
                                  }}
                                />
                              )}
                            />
                            <input type="hidden" name="businessCategoryId" value={primaryCategorySelection?.id || ''} />
                            {changedFields.businessCategory && (
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => handleFieldSubmit('businessCategory')}
                                sx={{
                                  mt: 1,
                                  mb: 1,
                                  backgroundColor: '#0B91D6',
                                  textTransform: 'capitalize',
                                  '&:hover': { backgroundColor: '#0980c2' }
                                }}
                              >
                                Submit
                              </Button>
                            )}
                          </Box>
                          {additionalCategories.map((categoryEntry, index) => (
                            <Box key={'additional-category-' + index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                              <Autocomplete
                                sx={{ flex: 1 }}
                                options={categoryOptions}
                                value={categoryEntry.selected}
                                inputValue={categoryEntry.inputValue}
                                onChange={handleAdditionalCategorySelect(index)}
                                onInputChange={handleAdditionalCategoryInputChange(index)}
                                getOptionLabel={(option) => option?.label || option?.id || ''}
                                isOptionEqualToValue={(option, value) => {
                                  const optionKey = option?.id || option?.label || '';
                                  const valueKey = value?.id || value?.label || '';
                                  return optionKey === valueKey;
                                }}
                                loading={categorySuggestionLoading}
                                freeSolo={false}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    name={'additionalCategory' + (index + 1)}
                                    label={'Additional category ' + (index + 1)}
                                    variant='outlined'
                                    size='small'
                                    fullWidth
                                    InputProps={{
                                      ...params.InputProps,
                                      endAdornment: (
                                        <>
                                          {categorySuggestionLoading ? (
                                            <CircularProgress color='inherit' size={16} sx={{ mr: 1 }} />
                                          ) : null}
                                          {params.InputProps.endAdornment}
                                        </>
                                      )
                                    }}
                                  />
                                )}
                              />
                              <input
                                type='hidden'
                                name={'additionalCategoryId_' + (index + 1)}
                                value={categoryEntry.selected?.id || ''}
                              />
                              <Button
                                onClick={() => {
                                  const newCategories = additionalCategories.filter((_, i) => i !== index);
                                  setAdditionalCategories(newCategories);
                                  setHasFormChanges(true); // Mark form as changed
                                }}
                                sx={{ minWidth: '40px', color: '#ef4444' }}
                              >
                                <DeleteIcon fontSize='small' />
                              </Button>
                            </Box>
                          ))}
                          <Button
                            startIcon={<span style={{ fontSize: '18px' }}>+</span>}
                            onClick={() => {
                              setAdditionalCategories([...additionalCategories, { selected: null, inputValue: '' }]);
                              setHasFormChanges(true); // Mark form as changed
                            }}
                            sx={{
                              textTransform: 'none',
                              color: '#0B91D6',
                              fontSize: '14px',
                              fontWeight: 500,
                              padding: '4px 8px',
                              '&:hover': {
                                backgroundColor: 'transparent',
                                textDecoration: 'underline'
                              }
                            }}
                          >
                            Add another category
                          </Button>
                          {categoryPayload && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                              <Button
                                variant="contained"
                                onClick={handleCategorySubmit}
                                disabled={categorySubmitting}
                                sx={{ textTransform: 'capitalize', backgroundColor: '#0B91D6', '&:hover': { backgroundColor: '#0980c2' } }}
                              >
                                {categorySubmitting ? 'Submitting...' : 'Submit categories'}
                              </Button>
                              {categorySubmitting && <CircularProgress size={16} />}
                            </Box>
                          )}
                          {categorySubmitSuccess && (
                            <Alert severity="success" sx={{ mt: 1 }}>
                              Categories synced with Google Business Profile.
                            </Alert>
                          )}
                          {categorySubmitError && (
                            <Alert severity="error" sx={{ mt: 1 }}>
                              {categorySubmitError}
                            </Alert>
                          )}
                        </Box>

                        <Box>
                          <TextField
                            name="description"
                            label="Description"
                            defaultValue={locationData?.profile?.description || 'Welcome at Mehar Hospital, located in Zirakpur, which is one of the best hospitals in Tricity.The management & ownership of the institute consists of 10 specialized doctors belonging to one family.We ensure that you and your loved ones receive the best possible treatment at our hospital.Let\'s have a look n the services which we provide includes emergency services, ICU, Trauma, Cath Lab, Dialysis, Orthopaedics,Ophthalmology, Obstetrics, Pediatrics, Skin and Cosmetology, Urology, ENT Services, Psychiatry and Psychology, Dental Care, Gastroenterology, Physiotherapy, Blood Bank, Plastic and Pediatric Surgery, Spine Surgery, Gynecology and many more.We aim to be one of the best healthcare providers and focused to provide accessible services'}
                            onChange={handleFormInputChange}
                            fullWidth
                            multiline
                            rows={4}
                            variant="outlined"
                            size="small"
                          />
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                            {changedFields.description && (
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => handleFieldSubmit('description')}
                                sx={{
                                  backgroundColor: '#0B91D6',
                                  textTransform: 'capitalize',
                                  '&:hover': { backgroundColor: '#0980c2' }
                                }}
                              >
                                Submit
                              </Button>
                            )}
                            <Button
                              type="button"
                              onClick={handleGenerateAIDescription}
                              disabled={generatingAIDescription}
                              sx={{
                                textTransform: 'none',
                                ml: 'auto',
                                backgroundColor: '#0B91D6',
                                color: '#fff',
                                '&:hover': { backgroundColor: '#0980c2' },
                                '&:disabled': { backgroundColor: '#ccc', color: '#666' }
                              }}
                            >
                              {generatingAIDescription ? (
                                <>
                                  <CircularProgress size={16} sx={{ mr: 1, color: '#fff' }} />
                                  Generating...
                                </>
                              ) : (
                                '🤖 AI Suggest'
                              )}
                            </Button>
                          </Box>
                        </Box>
                        <Box>
                          <TextField
                            name="openingDate"
                            label="Opening date"
                            defaultValue={locationData?.openInfo?.openingDate ?
                              `${locationData.openInfo.openingDate.year}-${String(locationData.openInfo.openingDate.month).padStart(2, '0')}-${String(locationData.openInfo.openingDate.day).padStart(2, '0')}` : ''}
                            onChange={handleFormInputChange}
                            fullWidth
                            variant="outlined"
                            size="small"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                          />
                          {changedFields.openingDate && (
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleFieldSubmit('openingDate')}
                              sx={{
                                mt: 1,
                                backgroundColor: '#0B91D6',
                                textTransform: 'capitalize',
                                '&:hover': { backgroundColor: '#0980c2' }
                              }}
                            >
                              Submit
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </Box>

                    {/* Section 2: Contact Information */}
                    <Box>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#121927' }}>
                        Contact Information
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* Phone Numbers Section */}
                        <Box>
                          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#6b7280' }}>
                            Phone Numbers
                          </Typography>
                          {phoneNumbers.map((phone, index) => (
                            <Box key={phone.id} sx={{ mb: 2 }}>
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                <TextField
                                  name={`phoneNumber_${phone.id}`}
                                  label={index === 0 ? "Primary Phone" : `Phone ${index + 1}`}
                                  value={phone.value}
                                  onChange={(e) => handlePhoneNumberChange(phone.id, e.target.value)}
                                  fullWidth
                                  variant="outlined"
                                  size="small"
                                  placeholder="+1 (555) 123-4567"
                                />
                                {phoneNumbers.length > 1 && (
                                  <IconButton
                                    onClick={() => handleRemovePhoneNumber(phone.id)}
                                    size="small"
                                    sx={{
                                      color: '#ef4444',
                                      '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.1)' }
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </Box>
                              {changedFields[`phoneNumber_${phone.id}`] && (
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={() => handleFieldSubmit(`phoneNumber_${phone.id}`)}
                                  sx={{
                                    mt: 1,
                                    backgroundColor: '#0B91D6',
                                    textTransform: 'capitalize',
                                    '&:hover': { backgroundColor: '#0980c2' }
                                  }}
                                >
                                  Submit
                                </Button>
                              )}
                            </Box>
                          ))}
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={handleAddPhoneNumber}
                            sx={{
                              textTransform: 'none',
                              borderColor: '#0B91D6',
                              color: '#0B91D6',
                              '&:hover': {
                                borderColor: '#0980c2',
                                backgroundColor: 'rgba(11, 145, 214, 0.1)'
                              }
                            }}
                          >
                            + Add Another Number
                          </Button>
                        </Box>
                        {/* WhatsApp Chat Section */}
                        <Box>
                          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#6b7280' }}>
                            WhatsApp Chat
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <TextField
                              name="whatsappPhone"
                              label="WhatsApp Phone Number"
                              value={whatsappPhoneNumber}
                              onChange={(e) => handleWhatsAppPhoneChange(e.target.value)}
                              fullWidth
                              variant="outlined"
                              size="small"
                              placeholder="Enter phone number (e.g., +1234567890)"
                              helperText="Enter phone number to generate WhatsApp chat link"
                              InputProps={{
                                startAdornment: (
                                  <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                                    <WhatsAppIcon sx={{ fontSize: 20, color: '#25D366' }} />
                                  </Box>
                                )
                              }}
                            />
                            {whatsappPhoneNumber && (
                              <TextField
                                label="Generated WhatsApp URL"
                                value={generateWhatsAppUrl(whatsappPhoneNumber)}
                                fullWidth
                                variant="outlined"
                                size="small"
                                InputProps={{
                                  readOnly: true,
                                  endAdornment: (
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        navigator.clipboard.writeText(generateWhatsAppUrl(whatsappPhoneNumber));
                                        // You can add a toast notification here
                                      }}
                                      sx={{ color: '#0B91D6' }}
                                    >
                                      <Box component="span" sx={{ fontSize: '14px' }}>📋</Box>
                                    </IconButton>
                                  )
                                }}
                                sx={{
                                  '& .MuiInputBase-input': {
                                    color: '#059669',
                                    fontWeight: 500,
                                    fontSize: '13px'
                                  }
                                }}
                              />
                            )}
                          </Box>
                          {changedFields.chat && (
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleFieldSubmit('chat')}
                              sx={{
                                mt: 1,
                                backgroundColor: '#0B91D6',
                                textTransform: 'capitalize',
                                '&:hover': { backgroundColor: '#0980c2' }
                              }}
                            >
                              Submit
                            </Button>
                          )}
                        </Box>
                        <Box>
                          <TextField
                            name="website"
                            label="Website"
                            defaultValue={locationData?.websiteUri || ''}
                            onChange={handleFormInputChange}
                            fullWidth
                            variant="outlined"
                            size="small"
                          />
                          {changedFields.website && (
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleFieldSubmit('website')}
                              sx={{
                                mt: 1,
                                backgroundColor: '#0B91D6',
                                textTransform: 'capitalize',
                                '&:hover': { backgroundColor: '#0980c2' }
                              }}
                            >
                              Submit
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </Box>

                    {/* Section 3: Location and Areas */}
                    <Box>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#121927' }}>
                        Location and Areas
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box>
                          <TextField
                            name="businessLocation"
                            label="Business location"
                            defaultValue={locationData?.storefrontAddress ?
                              [
                                locationData.storefrontAddress.addressLines?.[0] || '',
                                locationData.storefrontAddress.locality || '',
                                locationData.storefrontAddress.administrativeArea || '',
                                locationData.storefrontAddress.postalCode || ''
                              ].filter(Boolean).join(', ') : ''}
                            onChange={handleFormInputChange}
                            fullWidth
                            variant="outlined"
                            size="small"
                            helperText={
                              reverseGeocoding
                                ? "Getting address from map..."
                                : addressUpdated
                                  ? "✅ Address updated from map selection!"
                                  : "Click 'Pick on Map' to select location"
                            }
                          />
                          {changedFields.businessLocation && (
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleFieldSubmit('businessLocation')}
                              sx={{
                                mt: 1,
                                backgroundColor: '#0B91D6',
                                textTransform: 'capitalize',
                                '&:hover': { backgroundColor: '#0980c2' }
                              }}
                            >
                              Submit
                            </Button>
                          )}
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setMapPickerOpen(true)}
                            sx={{ alignSelf: 'flex-start', textTransform: 'none', mt: 1 }}
                          >
                            Pick on Map
                          </Button>
                        </Box>
                        <Box>
                          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#6b7280' }}>
                            Service Areas
                          </Typography>
                          {serviceAreas.map((area, index) => (
                            <Box key={area.id} sx={{ mb: 2, position: 'relative' }}>
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                <Box sx={{ flex: 1, position: 'relative' }}>
                                  <TextField
                                    name={`serviceArea_${area.id}`}
                                    label={index === 0 ? "Primary Service Area" : `Service Area ${index + 1}`}
                                    value={area.value}
                                    onChange={(e) => handleServiceAreaChange(area.id, e.target.value)}
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    placeholder="Type to search locations..."
                                    helperText="Start typing to see location suggestions"
                                    InputProps={{
                                      endAdornment: loadingSuggestions[area.id] && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                                          <CircularProgress size={16} />
                                        </Box>
                                      )
                                    }}
                                  />

                                  {/* Autocomplete Suggestions Dropdown */}
                                  {serviceAreaSuggestions[area.id] && serviceAreaSuggestions[area.id].length > 0 && (
                                    <Paper
                                      sx={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: 0,
                                        right: 0,
                                        zIndex: 1000,
                                        maxHeight: 200,
                                        overflowY: 'auto',
                                        mt: 0.5,
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                        border: '1px solid #e5e7eb'
                                      }}
                                    >
                                      {serviceAreaSuggestions[area.id].map((suggestion, idx) => (
                                        <Box
                                          key={idx}
                                          onClick={() => handleSelectSuggestion(area.id, suggestion)}
                                          sx={{
                                            p: 1.5,
                                            cursor: 'pointer',
                                            borderBottom: idx < serviceAreaSuggestions[area.id].length - 1 ? '1px solid #f3f4f6' : 'none',
                                            '&:hover': {
                                              backgroundColor: '#f0f9ff'
                                            }
                                          }}
                                        >
                                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#1f2937' }}>
                                            {suggestion.name}
                                          </Typography>
                                          {suggestion.type && (
                                            <Typography variant="caption" sx={{ color: '#6b7280' }}>
                                              {suggestion.type}
                                            </Typography>
                                          )}
                                        </Box>
                                      ))}
                                    </Paper>
                                  )}
                                </Box>

                                {serviceAreas.length > 1 && (
                                  <IconButton
                                    onClick={() => handleRemoveServiceArea(area.id)}
                                    sx={{
                                      mt: 0.5,
                                      color: '#ef4444',
                                      '&:hover': { backgroundColor: '#fee2e2' }
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </Box>

                              {changedFields[`serviceArea_${area.id}`] && (
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={() => handleFieldSubmit(`serviceArea_${area.id}`)}
                                  sx={{
                                    mt: 1,
                                    backgroundColor: '#0B91D6',
                                    textTransform: 'capitalize',
                                    '&:hover': { backgroundColor: '#0980c2' }
                                  }}
                                >
                                  Submit
                                </Button>
                              )}
                            </Box>
                          ))}

                          <Button
                            variant="outlined"
                            size="small"
                            onClick={handleAddServiceArea}
                            startIcon={<AddIcon />}
                            sx={{
                              textTransform: 'none',
                              borderColor: '#0B91D6',
                              color: '#0B91D6',
                              '&:hover': {
                                borderColor: '#0980c2',
                                backgroundColor: '#f0f9ff'
                              }
                            }}
                          >
                            Add Another Service Area
                          </Button>
                        </Box>
                      </Box>
                    </Box>

                    {/* Section 4: Opening Hours - Redesigned */}
                    <Box>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#121927' }}>
                        Regular Hours
                      </Typography>

                      {/* Regular Hours for each day */}
                      {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                        <Box key={day} sx={{ mb: 2, p: 2, border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: regularHours[day]?.isOpen ? '#f0f9ff' : '#f9fafb' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={regularHours[day]?.isOpen || false}
                                  onChange={() => handleDayToggle(day)}
                                  sx={{ color: '#0B91D6', '&.Mui-checked': { color: '#0B91D6' } }}
                                />
                              }
                              label={
                                <Typography sx={{ fontWeight: 600, fontSize: '15px', textTransform: 'capitalize' }}>
                                  {day}
                                </Typography>
                              }
                            />
                            {!regularHours[day]?.isOpen && (
                              <Chip label="Closed" size="small" sx={{ backgroundColor: '#fee2e2', color: '#991b1b' }} />
                            )}
                          </Box>

                          {regularHours[day]?.isOpen && (
                            <Box>
                              {regularHours[day].periods.map((period, index) => (
                                <Box key={period.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                  <FormControl size="small" sx={{ minWidth: 120 }}>
                                    <InputLabel>Opens at</InputLabel>
                                    <Select
                                      value={period.openTime}
                                      onChange={(e) => handleTimeChange(day, period.id, 'openTime', e.target.value)}
                                      label="Opens at"
                                    >
                                      {generateTimeSlots().map((time) => (
                                        <MenuItem key={time} value={time}>
                                          {time}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>

                                  <Typography variant="body2" color="textSecondary">to</Typography>

                                  <FormControl size="small" sx={{ minWidth: 120 }}>
                                    <InputLabel>Closes at</InputLabel>
                                    <Select
                                      value={period.closeTime}
                                      onChange={(e) => handleTimeChange(day, period.id, 'closeTime', e.target.value)}
                                      label="Closes at"
                                    >
                                      {generateTimeSlots().map((time) => (
                                        <MenuItem key={time} value={time}>
                                          {time}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>

                                  {regularHours[day].periods.length > 1 && (
                                    <IconButton
                                      size="small"
                                      onClick={() => handleRemoveTimePeriod(day, period.id)}
                                      sx={{ color: '#ef4444', '&:hover': { backgroundColor: '#fee2e2' } }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                </Box>
                              ))}

                              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handleAddTimePeriod(day)}
                                  startIcon={<AddIcon />}
                                  sx={{
                                    textTransform: 'none',
                                    fontSize: '12px',
                                    borderColor: '#0B91D6',
                                    color: '#0B91D6',
                                    '&:hover': {
                                      borderColor: '#0980c2',
                                      backgroundColor: '#f0f9ff'
                                    }
                                  }}
                                >
                                  Add Hours
                                </Button>

                                {changedFields[`regularHours_${day}`] && (
                                  <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => handleFieldSubmit(`regularHours_${day}`)}
                                    sx={{
                                      backgroundColor: '#0B91D6',
                                      textTransform: 'capitalize',
                                      fontSize: '12px',
                                      '&:hover': { backgroundColor: '#0980c2' }
                                    }}
                                  >
                                    Submit
                                  </Button>
                                )}
                              </Box>
                            </Box>
                          )}

                          {changedFields[`regularHours_${day}`] && !regularHours[day]?.isOpen && (
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleFieldSubmit(`regularHours_${day}`)}
                              sx={{
                                mt: 1,
                                backgroundColor: '#0B91D6',
                                textTransform: 'capitalize',
                                fontSize: '12px',
                                '&:hover': { backgroundColor: '#0980c2' }
                              }}
                            >
                              Submit
                            </Button>
                          )}
                        </Box>
                      ))}

                      {/* Special Hours Section */}
                      <Box sx={{ mt: 4 }}>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#121927' }}>
                          Special Hours
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                          Add special hours for holidays, events, or other exceptions
                        </Typography>

                        {specialHours.map((sh) => (
                          <Box key={sh.id} sx={{ mb: 2, p: 2, border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: '#fefce8' }}>
                            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                              <TextField
                                type="date"
                                label="Date"
                                value={sh.date}
                                onChange={(e) => handleSpecialHoursChange(sh.id, 'date', e.target.value)}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                sx={{ flex: 1, minWidth: 150 }}
                              />
                              <TextField
                                label="Description"
                                value={sh.description}
                                onChange={(e) => handleSpecialHoursChange(sh.id, 'description', e.target.value)}
                                size="small"
                                placeholder="e.g., Christmas Day, New Year's Eve"
                                sx={{ flex: 2, minWidth: 200 }}
                              />
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveSpecialHours(sh.id)}
                                sx={{ color: '#ef4444', '&:hover': { backgroundColor: '#fee2e2' } }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={sh.isClosed}
                                    onChange={(e) => handleSpecialHoursChange(sh.id, 'isClosed', e.target.checked)}
                                    sx={{ color: '#0B91D6', '&.Mui-checked': { color: '#0B91D6' } }}
                                  />
                                }
                                label="Closed on this day"
                              />
                            </Box>

                            {!sh.isClosed && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                  <InputLabel>Opens at</InputLabel>
                                  <Select
                                    value={sh.openTime}
                                    onChange={(e) => handleSpecialHoursChange(sh.id, 'openTime', e.target.value)}
                                    label="Opens at"
                                  >
                                    {generateTimeSlots().map((time) => (
                                      <MenuItem key={time} value={time}>
                                        {time}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>

                                <Typography variant="body2" color="textSecondary">to</Typography>

                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                  <InputLabel>Closes at</InputLabel>
                                  <Select
                                    value={sh.closeTime}
                                    onChange={(e) => handleSpecialHoursChange(sh.id, 'closeTime', e.target.value)}
                                    label="Closes at"
                                  >
                                    {generateTimeSlots().map((time) => (
                                      <MenuItem key={time} value={time}>
                                        {time}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Box>
                            )}

                            {changedFields[`specialHours_${sh.id}`] && (
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => handleFieldSubmit(`specialHours_${sh.id}`)}
                                sx={{
                                  mt: 2,
                                  backgroundColor: '#0B91D6',
                                  textTransform: 'capitalize',
                                  fontSize: '12px',
                                  '&:hover': { backgroundColor: '#0980c2' }
                                }}
                              >
                                Submit
                              </Button>
                            )}
                          </Box>
                        ))}

                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleAddSpecialHours}
                          startIcon={<AddIcon />}
                          sx={{
                            textTransform: 'none',
                            borderColor: '#0B91D6',
                            color: '#0B91D6',
                            '&:hover': {
                              borderColor: '#0980c2',
                              backgroundColor: '#f0f9ff'
                            }
                          }}
                        >
                          Add Special Hours
                        </Button>
                      </Box>
                    </Box>

                    {/* Section 5: More Information */}
                    <Box>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#121927' }}>
                        More
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box>
                          <TextField
                            name="fromBusiness"
                            label="From the business"
                            placeholder="Add information from the business"
                            onChange={handleFormInputChange}
                            fullWidth
                            variant="outlined"
                            size="small"
                          />
                          {changedFields.fromBusiness && (
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleFieldSubmit('fromBusiness')}
                              sx={{
                                mt: 1,
                                backgroundColor: '#0B91D6',
                                textTransform: 'capitalize',
                                '&:hover': { backgroundColor: '#0980c2' }
                              }}
                            >
                              Submit
                            </Button>
                          )}
                        </Box>
                        <Box>
                          <TextField
                            name="accessibility"
                            label="Accessibility"
                            defaultValue="Has wheelchair-accessible entrance"
                            onChange={handleFormInputChange}
                            fullWidth
                            variant="outlined"
                            size="small"
                          />
                          {changedFields.accessibility && (
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleFieldSubmit('accessibility')}
                              sx={{
                                mt: 1,
                                backgroundColor: '#0B91D6',
                                textTransform: 'capitalize',
                                '&:hover': { backgroundColor: '#0980c2' }
                              }}
                            >
                              Submit
                            </Button>
                          )}
                        </Box>
                        <Box>
                          <TextField
                            name="amenities"
                            label="Amenities"
                            placeholder="Add amenities"
                            onChange={handleFormInputChange}
                            fullWidth
                            variant="outlined"
                            size="small"
                          />
                          {changedFields.amenities && (
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleFieldSubmit('amenities')}
                              sx={{
                                mt: 1,
                                backgroundColor: '#0B91D6',
                                textTransform: 'capitalize',
                                '&:hover': { backgroundColor: '#0980c2' }
                              }}
                            >
                              Submit
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </Box>

                    {/* Section 6: Additional Options */}
                    <Box>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#121927' }}>
                        Additional Options
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box>
                          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Children</Typography>
                          <TextField
                            name="children"
                            placeholder="Add children-related information"
                            onChange={handleFormInputChange}
                            fullWidth
                            variant="outlined"
                            size="small"
                          />
                          {changedFields.children && (
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleFieldSubmit('children')}
                              sx={{
                                mt: 1,
                                backgroundColor: '#0B91D6',
                                textTransform: 'capitalize',
                                '&:hover': { backgroundColor: '#0980c2' }
                              }}
                            >
                              Submit
                            </Button>
                          )}
                        </Box>
                        <Box>
                          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Crowd</Typography>
                          <TextField
                            name="crowd"
                            defaultValue="LGBTQ+ friendly"
                            onChange={handleFormInputChange}
                            fullWidth
                            variant="outlined"
                            size="small"
                          />
                          {changedFields.crowd && (
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleFieldSubmit('crowd')}
                              sx={{
                                mt: 1,
                                backgroundColor: '#0B91D6',
                                textTransform: 'capitalize',
                                '&:hover': { backgroundColor: '#0980c2' }
                              }}
                            >
                              Submit
                            </Button>
                          )}
                        </Box>
                        <Box>
                          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Parking</Typography>
                          <TextField
                            name="parking"
                            placeholder="Add parking information"
                            onChange={handleFormInputChange}
                            fullWidth
                            variant="outlined"
                            size="small"
                          />
                          {changedFields.parking && (
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleFieldSubmit('parking')}
                              sx={{
                                mt: 1,
                                backgroundColor: '#0B91D6',
                                textTransform: 'capitalize',
                                '&:hover': { backgroundColor: '#0980c2' }
                              }}
                            >
                              Submit
                            </Button>
                          )}
                        </Box>
                        <Box>
                          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Payments</Typography>
                          <TextField
                            name="payments"
                            placeholder="Add payment options"
                            onChange={handleFormInputChange}
                            fullWidth
                            variant="outlined"
                            size="small"
                          />
                          {changedFields.payments && (
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleFieldSubmit('payments')}
                              sx={{
                                mt: 1,
                                backgroundColor: '#0B91D6',
                                textTransform: 'capitalize',
                                '&:hover': { backgroundColor: '#0980c2' }
                              }}
                            >
                              Submit
                            </Button>
                          )}
                        </Box>
                        <Box>
                          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Service options</Typography>
                          <TextField
                            name="serviceOptions"
                            placeholder="Add service options"
                            onChange={handleFormInputChange}
                            fullWidth
                            variant="outlined"
                            size="small"
                          />
                          {changedFields.serviceOptions && (
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleFieldSubmit('serviceOptions')}
                              sx={{
                                mt: 1,
                                backgroundColor: '#0B91D6',
                                textTransform: 'capitalize',
                                '&:hover': { backgroundColor: '#0980c2' }
                              }}
                            >
                              Submit
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </Box>

                    <Typography variant="body2" color="textSecondary" sx={{ mt: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                      Note: Changes will be submitted to Google My Business for review and may take time to appear in your business profile.
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3, pt: 2, borderTop: '1px solid #e5e7eb' }}>
                    <Button onClick={() => setEditProfileOpen(false)} sx={{ textTransform: 'capitalize' }}>Cancel</Button>
                    {hasFormChanges && (
                      <Button
                        variant="contained"
                        disabled={updatingProfile}
                        sx={{ backgroundColor: '#0B91D6', textTransform: 'capitalize' }}
                        onClick={async () => {
                          if (updatingProfile) return; // Prevent multiple clicks
                          setUpdatingProfile(true);
                          setSaveError(''); // Clear previous errors
                          try {
                            // Get form data from the modal
                            const formElements = document.querySelectorAll('#editProfileForm input, #editProfileForm textarea');
                            const formData = {};

                            // Store original values to detect changes
                            const originalValues = {
                              businessName: locationData?.title || '',
                              phoneNumber: locationData?.phoneNumbers?.primaryPhone || '',
                              website: locationData?.websiteUri || '',
                              description: locationData?.profile?.description || '',
                              businessCategory: locationData?.categories?.primaryCategory?.displayName || '',
                              businessLocation: locationData?.storefrontAddress ?
                                [
                                  locationData.storefrontAddress.addressLines?.[0] || '',
                                  locationData.storefrontAddress.locality || '',
                                  locationData.storefrontAddress.administrativeArea || '',
                                  locationData.storefrontAddress.postalCode || ''
                                ].filter(p => p).join(', ') : '',
                              storeCode: locationData?.storeCode || '',
                              openingDate: locationData?.openInfo?.openingDate ?
                                `${locationData.openInfo.openingDate.year}-${String(locationData.openInfo.openingDate.month).padStart(2, '0')}-${String(locationData.openInfo.openingDate.day).padStart(2, '0')}` : '',
                              serviceArea: locationData?.serviceArea?.places?.placeInfos?.[0]?.placeName || ''
                            };

                            // Extract values from form fields - only include if changed
                            formElements.forEach(element => {
                              if (element.name && element.value) {
                                const trimmedValue = element.value.trim();
                                const originalValue = (originalValues[element.name] || '').trim();
                                // Only include if value has changed from original
                                if (trimmedValue !== originalValue) {
                                  formData[element.name] = element.value;
                                }
                              }
                            });

                            // Structure the data according to the GMB API requirements
                            const updatedProfileData = {};

                            // 1. Business Title
                            if (formData.businessName && formData.businessName.trim()) {
                              updatedProfileData.title = formData.businessName.trim();
                            }

                            // 2. Phone Numbers
                            if (formData.phoneNumber && formData.phoneNumber.trim()) {
                              const phoneNumber = formData.phoneNumber.trim();
                              if (/^[\d\s\+\-\(\)]+$/.test(phoneNumber)) {
                                updatedProfileData.phoneNumbers = {
                                  primaryPhone: phoneNumber
                                };
                              } else {
                                setSaveError('Please enter a valid phone number');
                                return;
                              }
                            }

                            // 3. Website URI
                            if (formData.website && formData.website.trim()) {
                              const website = formData.website.trim();
                              if (website.startsWith('http://') || website.startsWith('https://') || website.includes('.')) {
                                updatedProfileData.websiteUri = website;
                              } else {
                                setSaveError('Please enter a valid website URL (e.g., https://example.com)');
                                return;
                              }
                            }

                            // 4. Profile (description)
                            if (formData.description && formData.description.trim()) {
                              updatedProfileData.profile = {
                                description: formData.description.trim()
                              };
                            }

                            const categoriesPayload = buildCategoryPayload();
                            if (categoriesPayload) {
                              updatedProfileData.categories = categoriesPayload;
                            }

                            // 6. Storefront Address
                            // IMPORTANT: When updating storefrontAddress, latlng coordinates are REQUIRED by GMB API
                            // Only include address in update if it was explicitly changed AND is complete
                            if (formData.businessLocation && formData.businessLocation.trim()) {
                              const address = formData.businessLocation.trim();
                              // Parse address (simple parsing, can be enhanced)
                              const parts = address.split(',').map(p => p.trim()).filter(p => p);

                              // Check if address is complete (has all required parts)
                              const isAddressComplete = parts.length >= 4;

                              // Check if coordinates are available
                              const hasValidCoordinates = pickedLatLng && typeof pickedLatLng.lat === 'number' && typeof pickedLatLng.lng === 'number';

                              // Only process address if both address and coordinates are complete
                              if (isAddressComplete && hasValidCoordinates) {
                                const addressData = {
                                  regionCode: 'IN', // Default to India, can be made dynamic
                                  addressLines: [parts[0]],
                                  locality: parts[1],
                                  administrativeArea: parts[2], // Must be valid state name (e.g., "Maharashtra", "Delhi", "Karnataka")
                                  postalCode: parts[3]
                                };

                                // Final validation: ensure all fields are non-empty
                                if (addressData.postalCode && addressData.postalCode.length > 0 &&
                                  addressData.administrativeArea && addressData.administrativeArea.length > 0 &&
                                  addressData.locality && addressData.locality.length > 0 &&
                                  addressData.addressLines && addressData.addressLines.length > 0 && addressData.addressLines[0]) {

                                  updatedProfileData.storefrontAddress = addressData;

                                  // MUST include latlng when updating storefrontAddress (for validation)
                                  // Note: latlng is required in request body but NOT in updateMask (GMBService handles this)
                                  updatedProfileData.latlng = {
                                    latitude: pickedLatLng.lat,
                                    longitude: pickedLatLng.lng
                                  };
                                } else {
                                  // Address is incomplete - skip it instead of failing
                                  logger.warn('Skipping address update - incomplete address data');
                                }
                              } else if (!hasValidCoordinates) {
                                // Only show error if user is actively trying to update address but missing coordinates
                                logger.warn('Skipping address update - coordinates not set. Use map to set location.');
                              } else {
                                // Address format is incomplete - skip it
                                logger.warn('Skipping address update - address format incomplete (need: Street, City, State, Postal Code)');
                              }
                            } else if (pickedLatLng && typeof pickedLatLng.lat === 'number' && typeof pickedLatLng.lng === 'number') {
                              // 7. Lat/Lng - Google API does NOT allow direct latlng updates
                              // Coordinates can only be updated as part of storefrontAddress update
                              // So we skip this - user must update the full address to change coordinates
                              logger.warn('Skipping latlng update - Google API requires updating storefrontAddress to change coordinates');
                            }

                            // 8. Store Code
                            if (formData.storeCode && formData.storeCode.trim()) {
                              updatedProfileData.storeCode = formData.storeCode.trim();
                            }

                            // 9. Opening Date (openInfo)
                            if (formData.openingDate && formData.openingDate.trim()) {
                              try {
                                const date = new Date(formData.openingDate);
                                updatedProfileData.openInfo = {
                                  openingDate: {
                                    year: date.getFullYear(),
                                    month: date.getMonth() + 1,
                                    day: date.getDate()
                                  }
                                };
                              } catch (e) {
                                logger.warn('Invalid opening date format:', e);
                              }
                            }

                            // 10. Regular Hours - Convert state to GMB API format
                            const convertRegularHoursToGMB = () => {
                              const periods = [];
                              const dayMap = {
                                monday: 'MONDAY',
                                tuesday: 'TUESDAY',
                                wednesday: 'WEDNESDAY',
                                thursday: 'THURSDAY',
                                friday: 'FRIDAY',
                                saturday: 'SATURDAY',
                                sunday: 'SUNDAY'
                              };

                              // Helper function to convert time string "HH:MM" to TimeOfDay object
                              const timeStringToTimeOfDay = (timeStr) => {
                                if (!timeStr || typeof timeStr !== 'string') return null;
                                const parts = timeStr.split(':');
                                if (parts.length < 2) return null;
                                return {
                                  hours: parseInt(parts[0], 10),
                                  minutes: parseInt(parts[1], 10),
                                  seconds: 0,
                                  nanos: 0
                                };
                              };

                              Object.keys(regularHours).forEach(day => {
                                if (regularHours[day].isOpen) {
                                  regularHours[day].periods.forEach(period => {
                                    const openTime = timeStringToTimeOfDay(period.openTime);
                                    const closeTime = timeStringToTimeOfDay(period.closeTime);
                                    if (openTime && closeTime) {
                                      periods.push({
                                        openDay: dayMap[day],
                                        closeDay: dayMap[day],
                                        openTime: openTime,
                                        closeTime: closeTime
                                      });
                                    }
                                  });
                                }
                              });

                              return periods.length > 0 ? { periods } : null;
                            };

                            const regularHoursData = convertRegularHoursToGMB();
                            if (regularHoursData) {
                              updatedProfileData.regularHours = regularHoursData;
                            }

                            // 10b. Special Hours - Convert state to GMB API format
                            const convertSpecialHoursToGMB = () => {
                              if (specialHours.length === 0) return null;

                              // Helper function to convert time string "HH:MM" to TimeOfDay object
                              const timeStringToTimeOfDay = (timeStr) => {
                                if (!timeStr || typeof timeStr !== 'string') return null;
                                const parts = timeStr.split(':');
                                if (parts.length < 2) return null;
                                return {
                                  hours: parseInt(parts[0], 10),
                                  minutes: parseInt(parts[1], 10),
                                  seconds: 0,
                                  nanos: 0
                                };
                              };

                              const specialHourPeriods = specialHours.map(sh => {
                                const dateParts = sh.date.split('-');
                                const period = {
                                  startDate: {
                                    year: parseInt(dateParts[0]),
                                    month: parseInt(dateParts[1]),
                                    day: parseInt(dateParts[2])
                                  },
                                  endDate: {
                                    year: parseInt(dateParts[0]),
                                    month: parseInt(dateParts[1]),
                                    day: parseInt(dateParts[2])
                                  },
                                  description: sh.description,
                                  isClosed: sh.isClosed
                                };

                                if (!sh.isClosed) {
                                  period.openTime = timeStringToTimeOfDay(sh.openTime);
                                  period.closeTime = timeStringToTimeOfDay(sh.closeTime);
                                }

                                return period;
                              });

                              return { specialHourPeriods };
                            };

                            const specialHoursData = convertSpecialHoursToGMB();
                            if (specialHoursData) {
                              updatedProfileData.specialHours = specialHoursData;
                            }

                            // 11. Service Area - Handle multiple service areas with placeId
                            // Google API requires placeId for each place, and businessType for the service area
                            const validServiceAreas = serviceAreas
                              .filter(area => area.value.trim() !== '');

                            if (validServiceAreas.length > 0) {
                              // Check if any service area is missing placeId
                              const areasWithoutPlaceId = validServiceAreas.filter(area => !area.placeId);
                              if (areasWithoutPlaceId.length > 0) {
                                console.warn('[BusinessProfile] Service areas missing placeId:', areasWithoutPlaceId.map(a => a.value));
                                console.warn('[BusinessProfile] Please select service areas from the dropdown suggestions to get valid Google Place IDs');
                              }

                              updatedProfileData.serviceArea = {
                                businessType: serviceAreaBusinessType || 'CUSTOMER_AND_BUSINESS_LOCATION',
                                places: {
                                  placeInfos: validServiceAreas.map(area => {
                                    const placeInfo = {
                                      placeName: area.value.trim()
                                    };
                                    // Only include placeId if it exists (Google API requires it for proper update)
                                    if (area.placeId) {
                                      placeInfo.placeId = area.placeId;
                                    }
                                    return placeInfo;
                                  })
                                }
                              };
                              console.log('[BusinessProfile] Service area update payload:', JSON.stringify(updatedProfileData.serviceArea, null, 2));
                              console.log('[BusinessProfile] Service areas state:', JSON.stringify(serviceAreas, null, 2));
                            }

                            // 12. Labels (can be added if needed)
                            // updatedProfileData.labels = ['label1', 'label2'];

                            // Validate that we have at least one field to update
                            if (Object.keys(updatedProfileData).length === 0) {
                              setSaveError('Please fill in at least one field to update');
                              return;
                            }

                            logger.log('Updating business profile with data:', updatedProfileData);

                            // Get access token
                            const accessToken = await GMBService.getAccessToken();

                            // Call the GMB API to update the business profile
                            // URL will be: https://mybusinessbusinessinformation.googleapis.com/v1/locations/{locationId}?updateMask=...
                            const result = await GMBService.updateBusinessProfile(locationId, updatedProfileData, accessToken);

                            logger.log('Business profile updated successfully:', result);

                            // Show success message
                            setSaveSuccess(true);
                            setSaveError('');

                            // Close the modal after a short delay
                            setTimeout(() => {
                              setEditProfileOpen(false);
                              // Reset success state after modal closes
                              setTimeout(() => setSaveSuccess(false), 500);
                            }, 1500);
                          } catch (error) {
                            logger.error('Error updating business profile:', error);

                            // Handle specific error cases
                            let errorMessage = 'Error updating business profile';

                            if (error.message.includes('invalid argument')) {
                              errorMessage = 'Invalid data provided. Please check your input and try again.';
                            } else if (error.message.includes('permission')) {
                              errorMessage = 'Permission denied. Please check your Google My Business access.';
                            } else if (error.message.includes('not found')) {
                              errorMessage = 'Business location not found. Please refresh and try again.';
                            } else if (error.message.includes('quota')) {
                              errorMessage = 'API quota exceeded. Please try again later.';
                            } else {
                              errorMessage = `Error updating business profile: ${error.message}`;
                            }

                            setSaveError(errorMessage);
                          } finally {
                            setUpdatingProfile(false);
                          }
                        }}
                      >
                        {updatingProfile ? 'Saving...' : 'Save Changes'}
                      </Button>
                    )}
                  </Box>
                </ModalContent>
              </ModalContainer>

              {/* Map Picker Dialog */}
              <Dialog
                open={mapPickerOpen}
                onClose={() => setMapPickerOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                  sx: {
                    minHeight: '600px',
                    maxHeight: '90vh'
                  }
                }}
              >
                <DialogTitle>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6">Pick Business Location</Typography>
                    {pickedLatLng && (
                      <Box sx={{
                        px: 2,
                        py: 1,
                        backgroundColor: '#f0f9ff',
                        borderRadius: '6px',
                        border: '1px solid #0B91D6'
                      }}>
                        <Typography sx={{ fontSize: '12px', color: '#0B91D6', fontWeight: '600' }}>
                          Selected: {pickedLatLng.lat.toFixed(4)}, {pickedLatLng.lng.toFixed(4)}
                        </Typography>
                        {reverseGeocoding && (
                          <Typography sx={{ fontSize: '10px', color: '#6b7280', mt: 0.5 }}>
                            Getting address...
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                </DialogTitle>
                <DialogContent sx={{ pb: 2 }}>
                  <Box
                    id="mapPickerBox"
                    sx={{
                      width: '100%',
                      height: { xs: 300, sm: 400, md: 450, lg: 500 }, // Responsive height
                      minHeight: 300,
                      borderRadius: '8px',
                      overflow: 'hidden',
                      backgroundColor: '#f3f4f6',
                      border: '2px solid #e5e7eb',
                      position: 'relative',
                      display: 'block', // Ensure it's displayed
                      '& .leaflet-container': {
                        width: '100% !important',
                        height: '100% !important',
                        zIndex: 1
                      }
                    }}
                  />
                  <Box sx={{ mt: 2, p: 2, backgroundColor: '#f9fafb', borderRadius: '6px' }}>
                    <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                      <strong>Instructions:</strong>
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                      • Click anywhere on the map to set your business location
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                      • Use mouse wheel or zoom controls to navigate
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                      • The marker will show your selected location
                    </Typography>
                  </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                  <Button
                    onClick={() => {
                      setPickedLatLng(null);
                      setMapPickerOpen(false);
                    }}
                    sx={{ textTransform: 'none' }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={async () => {
                      if (pickedLatLng) {
                        try {
                          // Get the address from coordinates
                          const address = await reverseGeocode(pickedLatLng.lat, pickedLatLng.lng);

                          // Find and update the business location input field
                          const businessLocationInput = document.querySelector('input[name="businessLocation"]');
                          if (businessLocationInput) {
                            businessLocationInput.value = address;
                            businessLocationInput.dispatchEvent(new Event('input', { bubbles: true }));
                            businessLocationInput.dispatchEvent(new Event('change', { bubbles: true }));
                            setAddressUpdated(true);
                            setHasFormChanges(true); // Mark form as changed
                            setTimeout(() => setAddressUpdated(false), 3000);
                          }

                          logger.log('Selected coordinates:', pickedLatLng);
                          logger.log('Updated business location:', address);

                          // Show success message
                          alert(`Location selected and updated: ${address}`);
                        } catch (error) {
                          logger.warn('Failed to update business location:', error);
                          alert(`Location selected: ${pickedLatLng.lat.toFixed(4)}, ${pickedLatLng.lng.toFixed(4)}`);
                        }
                      } else {
                        alert('Please select a location on the map first');
                        return;
                      }
                      setMapPickerOpen(false);
                    }}
                    sx={{
                      backgroundColor: '#0B91D6',
                      textTransform: 'none',
                      '&:hover': { backgroundColor: '#0980c2' }
                    }}
                    disabled={!pickedLatLng}
                  >
                    Use Selected Location
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Photo Upload Dialog */}
              <Dialog
                open={photoUploadOpen}
                onClose={() => { setPhotoUploadOpen(false); setSelectedPhotoFile(null); setPhotoUploadError(''); }}
                maxWidth="sm"
                fullWidth
              >
                <DialogTitle>Add Photo to Google My Business</DialogTitle>
                <DialogContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                      Upload a photo to add to your Google My Business profile. Photos help customers learn about your business.
                    </Typography>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files && e.target.files[0];
                        setSelectedPhotoFile(file || null);
                        setPhotoUploadError('');
                      }}
                      sx={{ mt: 2 }}
                    />
                    {selectedPhotoFile && (
                      <Typography variant="caption" color="textSecondary">
                        Selected: {selectedPhotoFile.name}
                      </Typography>
                    )}
                    <Typography variant="caption" color="textSecondary">
                      Supported formats: JPG, PNG, GIF. Max size: 10MB
                    </Typography>

                    {/* Geotag Status Indicator - Premium Design */}
                    <Box sx={{
                      mt: 3,
                      p: '1px', // Border gradient trick
                      borderRadius: 3,
                      background: locationData?.latlng?.latitude
                        ? 'linear-gradient(135deg, #10B981 0%, #3B82F6 100%)'
                        : 'linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                    }}>
                      <Box sx={{
                        p: 2,
                        borderRadius: '11px', // Slightly less than parent
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(4px)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2
                      }}>
                        <Box sx={{
                          width: 48,
                          height: 48,
                          borderRadius: '12px',
                          background: locationData?.latlng?.latitude
                            ? 'linear-gradient(135deg, #D1FAE5 0%, #EFF6FF 100%)'
                            : 'linear-gradient(135deg, #FEF3C7 0%, #FEF2F2 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '24px',
                          boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.05)'
                        }}>
                          {locationData?.latlng?.latitude ? '📍' : '❓'}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle2" sx={{
                            fontWeight: 700,
                            color: '#1F2937',
                            fontSize: '15px',
                            mb: 0.5
                          }}>
                            {locationData?.latlng?.latitude ? 'Auto-Geotagging Active' : 'Geotagging UnAvailable'}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '13px', lineHeight: 1.4 }}>
                            {locationData?.latlng?.latitude
                              ? `We'll automatically embed coordinates (${locationData.latlng.latitude.toFixed(4)}, ${locationData.latlng.longitude.toFixed(4)}) into this photo's EXIF data to boost local SEO.`
                              : 'Business coordinates not found. Please verify your business address to enable automatic SEO geotagging.'}
                          </Typography>
                        </Box>
                        {locationData?.latlng?.latitude && (
                          <Box sx={{
                            px: 1.5,
                            py: 0.5,
                            borderRadius: '20px',
                            backgroundColor: '#D1FAE5',
                            color: '#065F46',
                            fontSize: '11px',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                          }}>
                            SEO Ready
                          </Box>
                        )}
                      </Box>
                    </Box>
                    {photoUploadSuccess && (
                      <Alert
                        severity="success"
                        icon={<Box component="span" sx={{ fontSize: '20px' }}>✓</Box>}
                        sx={{
                          backgroundColor: '#D1FAE5',
                          color: '#065F46',
                          border: '1px solid #10B981',
                          '& .MuiAlert-icon': {
                            color: '#10B981'
                          }
                        }}
                      >
                        <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
                          Photo uploaded to GMB successfully!
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                          Your photo will appear in your Google Business Profile shortly.
                        </Typography>
                      </Alert>
                    )}
                    {photoUploadError && (
                      <Alert severity="error">{photoUploadError}</Alert>
                    )}
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => { setPhotoUploadOpen(false); setSelectedPhotoFile(null); setPhotoUploadError(''); setPhotoUploadSuccess(false); }} disabled={photoUploading}>Cancel</Button>
                  <Button
                    variant="contained"
                    onClick={async () => {
                      if (!selectedPhotoFile) return;
                      try {
                        setPhotoUploading(true);
                        setPhotoUploadError('');
                        setPhotoUploadSuccess(false);

                        const result = await uploadPhotoToGMB(selectedPhotoFile);
                        logger.log('Photo uploaded successfully!', result);

                        // Only set success if we got here (no error thrown)
                        setPhotoUploadSuccess(true);
                        setSelectedPhotoFile(null);

                        // Close dialog after 2.5 seconds to show success message
                        setTimeout(() => {
                          setPhotoUploadOpen(false);
                          setPhotoUploadSuccess(false);
                        }, 2500);
                      } catch (error) {
                        logger.error('Failed to upload photo:', error);
                        // Make sure we're not setting the backend success message as an error
                        const errorMessage = error?.message || 'Failed to upload photo';
                        if (!errorMessage.toLowerCase().includes('successfully')) {
                          setPhotoUploadError(errorMessage);
                        } else {
                          // If error message says "successfully", it's actually a success
                          setPhotoUploadSuccess(true);
                          setTimeout(() => {
                            setPhotoUploadOpen(false);
                            setPhotoUploadSuccess(false);
                          }, 2500);
                        }
                      } finally {
                        setPhotoUploading(false);
                      }
                    }}
                    disabled={!selectedPhotoFile || photoUploading}
                  >
                    {photoUploading ? 'Uploading...' : 'Upload Photo'}
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Video Upload Dialog */}
              <Dialog
                open={videoUploadOpen}
                onClose={() => { setVideoUploadOpen(false); setSelectedVideoFile(null); }}
                maxWidth="sm"
                fullWidth
              >
                <DialogTitle>Add Video to Google My Business</DialogTitle>
                <DialogContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <Alert severity="warning" sx={{ mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        Video uploads currently have limitations
                      </Typography>
                      <Typography variant="caption" component="div">
                        Due to Google API restrictions, direct video uploads are not supported.
                        We recommend:
                      </Typography>
                      <Typography variant="caption" component="div" sx={{ mt: 1, ml: 2 }}>
                        • Upload your video to YouTube or Vimeo<br />
                        • Add the video link to your Google Business Profile<br />
                        • Or contact support for assistance
                      </Typography>
                    </Alert>

                    <Typography variant="body2" color="textSecondary">
                      If you still want to try uploading, the system will attempt to process your video.
                    </Typography>

                    {videoUploadError && (
                      <Alert severity="error" sx={{ mt: 1 }}>
                        {videoUploadError}
                      </Alert>
                    )}

                    {videoUploading ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 3 }}>
                        <CircularProgress size={40} />
                        <Typography variant="body2" color="textSecondary">
                          Uploading video... Please wait.
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        <Input
                          type="file"
                          accept="video/*"
                          onChange={(e) => {
                            const file = e.target.files && e.target.files[0];
                            setSelectedVideoFile(file || null);
                          }}
                          sx={{ mt: 2 }}
                        />
                        {selectedVideoFile && (
                          <Typography variant="caption" color="textSecondary">
                            Selected: {selectedVideoFile.name}
                          </Typography>
                        )}
                        <Typography variant="caption" color="textSecondary">
                          Supported formats: MP4, MOV, AVI. Max size: 75MB. Max duration: 30 seconds.
                        </Typography>
                      </>
                    )}
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button
                    onClick={() => { setVideoUploadOpen(false); setSelectedVideoFile(null); }}
                    disabled={videoUploading}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={async () => {
                      if (!selectedVideoFile) return;
                      try {
                        setVideoUploading(true);
                        await uploadVideoToGMB(selectedVideoFile);
                        logger.log('Video uploaded successfully!');
                        setSelectedVideoFile(null);
                        setVideoUploadOpen(false);
                      } catch (error) {
                        logger.error('Failed to upload video:', error);
                      } finally {
                        setVideoUploading(false);
                      }
                    }}
                    disabled={!selectedVideoFile || videoUploading}
                  >
                    {videoUploading ? 'Uploading...' : 'Upload Video'}
                  </Button>
                </DialogActions>
              </Dialog>

              <ActionsGrid>
                <ActionButton onClick={() => websiteUrl !== 'Not available' && window.open(websiteUrl, '_blank')}>
                  <WebsiteGlobeIcon width={17} height={17} color="#121927" />
                  Website
                  <ActionSubtext>
                    ({websiteUrl !== 'Not available' ? websiteUrl : 'Not available'})
                  </ActionSubtext>
                </ActionButton>
                <ActionButton onClick={() => {
                  logger.log('Directions button clicked');
                  logger.log('mapsUri:', mapsUri);
                  logger.log('Full locationData:', JSON.stringify(locationData, null, 2));
                  logger.log('Location name:', locationData?.name);
                  logger.log('Extracted location ID:', locationData?.name?.split('/').pop());

                  if (mapsUri) {
                    logger.log('Opening maps with URI:', mapsUri);
                    window.open(mapsUri, '_blank');
                  } else {
                    // Fallback: create Google Maps URL with address
                    // const encodedAddress = encodeURIComponent(businessAddress);
                    // const fallbackUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
                    // logger.log('Using fallback URL:', fallbackUrl);
                    // window.open(fallbackUrl, '_blank');
                  }
                }}>
                  <DirectionsIcon width={16} height={16} color="#121927" />
                  Directions
                  <ActionSubtext>{mapsUri ? 'Open in Maps' : 'Search Address'}</ActionSubtext>
                </ActionButton>
                <ActionButton onClick={openShareMenu}>
                  <ShareIcon width={15} height={15} color="#121927" />
                  Share
                  <ActionSubtext>Link address</ActionSubtext>
                </ActionButton>
                <Menu
                  anchorEl={shareAnchorEl}
                  open={Boolean(shareAnchorEl)}
                  onClose={closeShareMenu}
                >
                  <MenuItem onClick={handleNativeShare}>
                    {/* Share */}
                  </MenuItem>
                  <MenuItem onClick={() => handleSharePlatform('whatsapp')}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <WhatsAppIcon width={16} height={16} />
                    </ListItemIcon>
                    <ListItemText primary="WhatsApp" />
                  </MenuItem>
                  <MenuItem onClick={() => handleSharePlatform('facebook')}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <FacebookIcon width={16} height={16} />
                    </ListItemIcon>
                    <ListItemText primary="Facebook" />
                  </MenuItem>
                  <MenuItem onClick={() => handleSharePlatform('twitter')}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <TwitterIcon width={16} height={16} />
                    </ListItemIcon>
                    <ListItemText primary="Twitter" />
                  </MenuItem>
                  <MenuItem onClick={handleCopyLink}>Copy link</MenuItem>
                </Menu>
                <ActionButton onClick={() => setQrOpen(true)}>
                  <QRCodeIcon width={15} height={15} color="#121927" />
                  Review QR
                  <ActionSubtext>Shot link</ActionSubtext>
                </ActionButton>
              </ActionsGrid>

              <AppointmentsSection>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <AppointmentsLabel>Appointments:</AppointmentsLabel>
                  <Button
                    onClick={handleAddAppointmentLink}
                    startIcon={<AddIcon />}
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#0B91D6',
                      textTransform: 'none',
                      padding: '8px 16px',
                      border: '1px solid #0B91D6',
                      borderRadius: '6px',
                      backgroundColor: 'transparent',
                      '&:hover': {
                        backgroundColor: '#0B91D6',
                        color: '#ffffff'
                      }
                    }}
                  >
                    Add Appointment Link
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {(() => {
                    const appointmentLinks = getAppointmentLinks();
                    if (appointmentLinks.length === 0) {
                      return (
                        <Typography sx={{ fontSize: '14px', color: '#6b7280', fontStyle: 'italic' }}>
                          No appointment booking links available. Click "Add Appointment Link" to add one.
                        </Typography>
                      );
                    }
                    return appointmentLinks.map((linkObj) => (
                      <Box
                        key={linkObj.id}
                        sx={{
                          position: 'relative',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 1,
                          padding: '8px 12px',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          backgroundColor: '#ffffff',
                          transition: 'all 0.2s',
                          '&:hover': {
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                            borderColor: '#0B91D6',
                            '& .edit-icon': {
                              opacity: 1
                            }
                          }
                        }}
                      >
                        <a
                          href={linkObj.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: '#0B91D6',
                            textDecoration: 'none',
                            fontWeight: linkObj.isPreferred ? 600 : 400,
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          {linkObj.label}
                          {linkObj.isPreferred && <span style={{ fontSize: '16px' }}>⭐</span>}
                        </a>
                        <Typography
                          sx={{
                            fontSize: '11px',
                            color: '#6b7280',
                            fontStyle: 'italic'
                          }}
                        >
                          ({linkObj.typeLabel})
                        </Typography>
                        <IconButton
                          className="edit-icon"
                          onClick={(e) => handleEditAppointmentLink(linkObj, e)}
                          sx={{
                            opacity: 0,
                            transition: 'opacity 0.2s',
                            padding: '4px',
                            marginLeft: '4px',
                            '&:hover': {
                              backgroundColor: '#EFF6FF',
                              color: '#0B91D6'
                            }
                          }}
                          size="small"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ));
                  })()}
                </Box>
              </AppointmentsSection>

              {/* Competitive Ranking Section */}
              {competitorData && competitorData.competitors && competitorData.competitors.length > 0 && (
                <CompetitorRankingSection>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <CompetitorRankingTitle>
                      🏆 Competitive Ranking
                    </CompetitorRankingTitle>
                    <AddCampaignButton
                      size="small"
                      onClick={handleAddCampaign}
                      title="Add a new keyword campaign"
                      sx={{
                        fontSize: '12px',
                        padding: '8px 12px',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      + Add Keyword
                    </AddCampaignButton>
                  </Box>
                  <Typography variant="caption" sx={{ color: '#6B7280', mb: 2, fontSize: '12px' }}>
                    Run a fresh competitor keyword analysis by opening a campaign with a new keyword.
                  </Typography>

                  {/* Keyword Selection */}
                  {Object.keys(keywordResults).length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{
                        mb: 1,
                        color: '#6B7280',
                        fontWeight: 500,
                        fontSize: '12px'
                      }}>
                        📊 Keyword:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {Object.keys(keywordResults).map((keyword) => (
                          <Box
                            key={keyword}
                            onClick={() => {
                              setSelectedKeyword(keyword);
                              setCompetitorData(keywordResults[keyword]);
                              if (keywordResults[keyword].myBusinessScore) {
                                setGmbScore(keywordResults[keyword].myBusinessScore);
                              }
                            }}
                            sx={{
                              px: 2,
                              py: 1,
                              backgroundColor: selectedKeyword === keyword ? '#0B91D6' : '#F3F4F6',
                              color: selectedKeyword === keyword ? 'white' : '#374151',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '13px',
                              fontWeight: selectedKeyword === keyword ? 600 : 500,
                              transition: 'all 0.2s ease',
                              border: selectedKeyword === keyword ? 'none' : '1px solid #E5E7EB',
                              '&:hover': {
                                backgroundColor: selectedKeyword === keyword ? '#0980c2' : '#E5E7EB',
                                transform: 'translateY(-1px)',
                                boxShadow: selectedKeyword === keyword
                                  ? '0 2px 8px rgba(11, 145, 214, 0.3)'
                                  : '0 1px 4px rgba(0,0,0,0.1)'
                              }
                            }}
                          >
                            {keyword}
                            {keywordResults[keyword].myBusinessRank && (
                              <Box component="span" sx={{
                                ml: 1,
                                fontSize: '11px',
                                opacity: 0.9,
                                fontWeight: 600
                              }}>
                                (Rank #{keywordResults[keyword].myBusinessRank})
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                  {(() => {
                    // Get plan-based competitor limit: basic=1, advance=3, pro=5
                    const competitorLimit = getCompetitorGmbsLimit(planCode);
                    const allCompetitors = competitorData.competitors || [];

                    // Find user's business index
                    const myBusinessIndex = allCompetitors.findIndex(c => c.isMyBusiness);

                    // Only show competitors above user + user (no competitors below)
                    let displayList = [];
                    if (myBusinessIndex !== -1) {
                      // Get competitors directly above user (up to limit)
                      const startIdx = Math.max(0, myBusinessIndex - competitorLimit);
                      const aboveCompetitors = allCompetitors.slice(startIdx, myBusinessIndex);
                      const myBusiness = allCompetitors[myBusinessIndex];
                      displayList = [...aboveCompetitors, myBusiness];

                      // Determine which should be blurred (only show 'competitorLimit' above user unblurred)
                      const visibleStartIdx = Math.max(0, myBusinessIndex - competitorLimit);
                      displayList = displayList.map((comp, idx) => ({
                        ...comp,
                        isBlurred: !comp.isMyBusiness && (startIdx + idx) < visibleStartIdx,
                        originalIndex: startIdx + idx
                      }));
                    } else {
                      // User not found - show top N
                      displayList = allCompetitors.slice(0, competitorLimit).map((comp, idx) => ({
                        ...comp,
                        isBlurred: false,
                        originalIndex: idx
                      }));
                    }

                    return displayList.map((competitor, index) => {
                      const actualRank = competitor.rank || competitor.originalIndex + 1;
                      const isMyBusiness = competitor.isMyBusiness;
                      const isBlurred = competitor.isBlurred;

                      return (
                        <CompetitorCard
                          key={competitor.placeId || index}
                          sx={{
                            backgroundColor: isMyBusiness ? '#e8f5e8' : '#f8f9fa',
                            border: isMyBusiness ? '2px solid #10b981' : '1px solid #e5e7eb',
                            boxShadow: isMyBusiness ? '0 2px 8px rgba(16, 185, 129, 0.15)' : 'none',
                            position: 'relative',
                            ...(isBlurred && {
                              filter: 'blur(6px)',
                              userSelect: 'none',
                              pointerEvents: 'none'
                            })
                          }}
                        >
                          <CompetitorInfo>
                            <CompetitorName sx={{
                              color: isMyBusiness ? '#10b981' : '#121927',
                              fontWeight: isMyBusiness ? 600 : 500
                            }}>
                              {competitor.name}
                              {isMyBusiness && (
                                <Box component="span" sx={{
                                  ml: 1,
                                  px: 1,
                                  py: 0.5,
                                  backgroundColor: '#10b981',
                                  color: 'white',
                                  borderRadius: '4px',
                                  fontSize: '10px',
                                  fontWeight: 600
                                }}>
                                  YOU
                                </Box>
                              )}
                            </CompetitorName>
                          </CompetitorInfo>
                          <RankBadge
                            rank={actualRank}
                            sx={{
                              backgroundColor: isMyBusiness ? '#10b981' :
                                actualRank === 1 ? '#34A853' :
                                  actualRank <= 3 ? '#f59e0b' :
                                    actualRank <= 5 ? '#0B91D6' : '#ef4444',
                              fontWeight: isMyBusiness ? 700 : 600,
                              fontSize: isMyBusiness ? '16px' : '14px',
                              width: isMyBusiness ? '36px' : '32px',
                              height: isMyBusiness ? '36px' : '32px'
                            }}
                          >
                            {actualRank}
                          </RankBadge>
                        </CompetitorCard>
                      );
                    });
                  })()}
                </CompetitorRankingSection>
              )}

              {/* Only show Services section if there are services */}
              {!servicesLoading && services.length > 0 && (
                <ProductsServicesSection>
                  <SectionHeader>
                    <ProductsServicesTitle>Services</ProductsServicesTitle>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        onClick={handleAddService}
                        startIcon={<AddIcon />}
                        sx={{
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '14px',
                          fontWeight: 500,
                          color: '#0B91D6',
                          textTransform: 'none',
                          padding: '8px 16px',
                          border: '1px solid #0B91D6',
                          borderRadius: '6px',
                          backgroundColor: 'transparent',
                          marginTop: '16px',
                          '&:hover': {
                            backgroundColor: '#0B91D6',
                            color: '#ffffff'
                          }
                        }}
                      >
                        Add Service
                      </Button>
                      <ProductsViewAllButton onClick={() => setProductsModalOpen(true)}>
                        View All
                      </ProductsViewAllButton>
                    </Box>
                  </SectionHeader>

                  <ProductsGrid>
                    {services.slice(0, 2).map((item) => (
                      <ProductCard
                        key={item.id}
                        onClick={() => handleServiceClick(item)}
                        sx={{
                          cursor: 'pointer',
                          position: 'relative',
                          '&:hover': {
                            boxShadow: '0 6px 16px rgba(0, 0, 0, 0.15)',
                            transform: 'translateY(-2px)'
                          },
                          '&:hover .edit-icon': {
                            opacity: 1
                          }
                        }}
                      >
                        <IconButton
                          className="edit-icon"
                          onClick={(e) => handleEditService(item, e)}
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            opacity: 0,
                            transition: 'opacity 0.2s',
                            backgroundColor: '#fff',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            '&:hover': {
                              backgroundColor: '#EFF6FF',
                              color: '#0B91D6'
                            },
                            zIndex: 1
                          }}
                          size="small"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <ProductContent>
                          <ProductTitle>{item.title}</ProductTitle>
                          <ProductPrice>{item.price}</ProductPrice>
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#0B91D6',
                              fontSize: '11px',
                              fontStyle: 'italic',
                              mt: 0.5
                            }}
                          >
                            Click to view details
                          </Typography>
                        </ProductContent>
                      </ProductCard>
                    ))}
                  </ProductsGrid>
                </ProductsServicesSection>
              )}
            </BusinessProfileCard>

            <ProfileStrengthCard>
              <StrengthTitle>Profile Strength</StrengthTitle>
              <GaugeContainer>
                <DynamicProfileGauge
                  score={gmbScore ? Math.round((gmbScore / 1000) * maxScore) : profileScore}
                  maxScore={maxScore}
                />
                <ScoreText>
                  {gmbScore ? `${Math.round(gmbScore)}/1000` : `${profileScore} / ${maxScore}`}
                </ScoreText>
              </GaugeContainer>
              <ActionButtonsContainer>
                {/* Only show "Boost Your Profile" button if user doesn't have a plan */}
                {!isAdmin && !hasPlanAccess && (
                  <BoostButton onClick={() => navigate('/pricing')}>Boost Your Profile</BoostButton>
                )}
                <SchedulePostButton
                  onClick={handleSchedulePost}
                  title="Schedule a post to be published at a specific date and time"
                >
                  📅 Schedule Post
                </SchedulePostButton>
                <ScoreButton>
                  Get Consolidated Score
                  <Box component="span" sx={{ color: '#EF4444', fontWeight: 600 }}>
                    (Coming Soon)
                  </Box>
                </ScoreButton>
                <CompetitorButton
                  onClick={handleCompetitorAnalysis}
                  sx={{
                    opacity: canRunAnalysis ? 1 : 0.6,
                    cursor: canRunAnalysis ? 'pointer' : 'not-allowed',
                    position: 'relative'
                  }}
                  title={
                    canRunAnalysis
                      ? 'Run competitor analysis'
                      : `Available in ${daysUntilNextAnalysis} day${daysUntilNextAnalysis > 1 ? 's' : ''}`
                  }
                >
                  {canRunAnalysis ? 'Competitor Analysis' : `⏳ Available in ${daysUntilNextAnalysis}d`}
                </CompetitorButton>
                <AddCampaignButton onClick={handleAddCampaign} title="Run a keyword campaign">
                  Run Campaign
                </AddCampaignButton>
                <HeaderLink width={250} onClick={handleOpenHeatmap}>Open Heatmap</HeaderLink>
              </ActionButtonsContainer>
              <Box sx={{ width: '100%' }}>
                <CalendarCard sx={{ mt: 1, width: '100%', maxWidth: '320px' }}>
                  <CalendarTitle>Calendar</CalendarTitle>
                  <CalendarContainer>
                    <CalendarHeader>
                      <CalendarNavButton onClick={() => navigateMonth(-1)}>
                        <ChevronLeftIcon />
                      </CalendarNavButton>
                      <CalendarMonth>{getMonthName(currentDate)}</CalendarMonth>
                      <CalendarNavButton onClick={() => navigateMonth(1)}>
                        <ChevronRightIcon />
                      </CalendarNavButton>
                    </CalendarHeader>
                    <CalendarGrid>
                      {/* Day headers */}
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                        <CalendarDayHeader key={day}>{day}</CalendarDayHeader>
                      ))}
                      {/* Calendar days */}
                      {generateCalendarDays().map((day, index) => {
                        const dayDate = day ? new Date(currentDate.getFullYear(), currentDate.getMonth(), day) : null;
                        const dayEvents = dayDate ? calendarEvents.filter(event => isSameDay(event.date, dayDate)) : [];
                        const scheduledPostsCount = dayEvents.filter(e => e.type === 'scheduled_post').length;
                        const hasScheduledPost = scheduledPostsCount > 0;
                        const hasOtherEvent = dayEvents.some(e => e.type !== 'scheduled_post');

                        return (
                          <CalendarDay
                            key={index}
                            isToday={day && isToday(day)}
                            hasEvent={day && dayEvents.length > 0}
                            isOtherMonth={!day}
                            onClick={() => handleCalendarDayClick(day)}
                            sx={{
                              cursor: day && dayEvents.length > 0 ? 'pointer' : 'default',
                              position: 'relative',
                              '&:hover': day && dayEvents.length > 0 ? {
                                backgroundColor: 'rgba(11, 145, 214, 0.1)',
                                transform: 'scale(1.05)'
                              } : {}
                            }}
                          >
                            {day || ''}
                            {day && dayEvents.length > 0 && (
                              <>
                                <EventDot isScheduledPost={hasScheduledPost} />
                                {scheduledPostsCount > 1 && (
                                  <Box sx={{
                                    position: 'absolute',
                                    top: '2px',
                                    right: '2px',
                                    backgroundColor: '#34A853',
                                    color: '#FFFFFF',
                                    fontSize: '9px',
                                    fontWeight: 700,
                                    borderRadius: '50%',
                                    width: '14px',
                                    height: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                  }}>
                                    {scheduledPostsCount}
                                  </Box>
                                )}
                              </>
                            )}
                          </CalendarDay>
                        );
                      })}
                    </CalendarGrid>
                  </CalendarContainer>

                  {/* Calendar Legend */}
                  <Box sx={{
                    display: 'flex',
                    gap: '16px',
                    justifyContent: 'center',
                    mt: 2,
                    mb: 2,
                    fontSize: '11px',
                    color: '#666666'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Box sx={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        backgroundColor: '#34A853',
                        boxShadow: '0 0 4px rgba(52, 168, 83, 0.5)'
                      }} />
                      <Typography sx={{ fontSize: '11px', color: '#666666' }}>Scheduled Post</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Box sx={{
                        width: '4px',
                        height: '4px',
                        borderRadius: '50%',
                        backgroundColor: '#0B91D6'
                      }} />
                      <Typography sx={{ fontSize: '11px', color: '#666666' }}>Event</Typography>
                    </Box>
                  </Box>

                  <CalendarActions>
                    <CalendarActionButton onClick={handleAddEvent}>Add Event</CalendarActionButton>
                    <CalendarActionButton onClick={() => setFullCalendarModalOpen(true)}>View Full Calendar</CalendarActionButton>
                    <CalendarActionButton onClick={handleCreatePostFromTestimonial}>Create Post from Testimonial</CalendarActionButton>
                  </CalendarActions>
                </CalendarCard>
              </Box>
            </ProfileStrengthCard>


          </MainGrid>

          {/* Action Center */}
          <ActionCenterCard>
            <SectionTitle>Action Center</SectionTitle>
            {/* Stats Grid - Always shown */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', mb: 2 }}>
              {/* Task Stats Row */}
              <Box sx={{ textAlign: 'center', p: 2, borderRadius: '8px', backgroundColor: '#F3F4F6' }}>
                <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#0B91D6', mb: 0.5 }}>
                  {taskStats.totalTasks}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: '#6B7280', fontWeight: 500 }}>
                  Total Tasks
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 2, borderRadius: '8px', backgroundColor: '#ECFDF5' }}>
                <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#10B981', mb: 0.5 }}>
                  {taskStats.completedTasks}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: '#6B7280', fontWeight: 500 }}>
                  Completed
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 2, borderRadius: '8px', backgroundColor: '#FEF3C7' }}>
                <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#F59E0B', mb: 0.5 }}>
                  {taskStats.pendingTasks - taskStats.delayedTasks}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: '#6B7280', fontWeight: 500 }}>
                  Engagement
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 2, borderRadius: '8px', backgroundColor: '#FEE2E2' }}>
                <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#EF4444', mb: 0.5 }}>
                  {taskStats.delayedTasks}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: '#6B7280', fontWeight: 500 }}>
                  Critical
                </Typography>
              </Box>
            </Box>

            {/* AI Replies & Posts Stats Row */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', mb: 3 }}>
              <Box sx={{ textAlign: 'center', p: 2, borderRadius: '8px', backgroundColor: '#EDE9FE', border: '1px solid #DDD6FE' }}>
                <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#7C3AED', mb: 0.5 }}>
                  {aiReplyStats.totalRepliesSent}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: '#6B7280', fontWeight: 500 }}>
                  AI Replies Sent
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 2, borderRadius: '8px', backgroundColor: '#DBEAFE', border: '1px solid #BFDBFE' }}>
                <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#2563EB', mb: 0.5 }}>
                  {postStats.scheduledPosts}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: '#6B7280', fontWeight: 500 }}>
                  Scheduled Posts
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 2, borderRadius: '8px', backgroundColor: '#D1FAE5', border: '1px solid #A7F3D0' }}>
                <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#059669', mb: 0.5 }}>
                  {postStats.completedPosts}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: '#6B7280', fontWeight: 500 }}>
                  Published Posts
                </Typography>
              </Box>
            </Box>

            {taskStats.totalTasks > 0 ? (
              <>
                <TasksGrid>
                  {taskStats.recentTasks.slice(0, 5).map((task, index) => (
                    <TaskCard key={index}>
                      <TaskLabel>{task.name || task.label || 'Task'}</TaskLabel>
                      <Box sx={{
                        width: '52px',
                        height: '52px',
                        borderRadius: '50%',
                        backgroundColor: task.completed ? '#10B981' : task.status === 'delayed' ? '#EF4444' : '#F59E0B',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '10px',
                        color: '#fff',
                        fontSize: '20px',
                        fontWeight: 600
                      }}>
                        {task.completed ? '✓' : '•'}
                      </Box>
                      <TaskStatus sx={{
                        color: task.completed ? '#10B981' : task.status === 'delayed' ? '#EF4444' : '#F59E0B'
                      }}>
                        {task.completed ? 'Completed' : task.status === 'delayed' ? 'Delayed' : 'Pending'}
                      </TaskStatus>
                    </TaskCard>
                  ))}
                </TasksGrid>
                <ViewAllButton onClick={handleViewAllTasks}>
                  View All Tasks
                </ViewAllButton>
              </>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography sx={{ fontSize: '14px', color: '#10B981', mb: 1, fontWeight: 500 }}>
                  All tasks complete!
                </Typography>
                <Typography sx={{ fontSize: '12px', color: '#9CA3AF' }}>
                  Your profile is in great shape. Check back later for new tasks.
                </Typography>
              </Box>
            )}
          </ActionCenterCard>

          {/* Performance + Latest Feed */}
          <PerformanceGrid>
            <PerformanceCard>
              <SectionTitle>Performance</SectionTitle>
              <Typography sx={{ fontSize: '12px', color: '#6b7280', mb: 1 }}>Last 30 days</Typography>
              <MetricsGrid>
                {metricsData.slice(0, 2).map((metric, index) => (
                  <MetricCard key={index} sx={{ backgroundColor: metric.color }}>
                    <MetricLabel sx={{ color: '#ffffff' }}>{metric.label}</MetricLabel>
                    <MetricValue>{metric.value}</MetricValue>
                  </MetricCard>
                ))}
              </MetricsGrid>
              <Box sx={{ textAlign: 'center', marginTop: '26px' }}>
                <ViewAllButton onClick={() => setPerformanceModalOpen(true)}>
                  View All
                </ViewAllButton>
              </Box>
              {/* Additional Insights removed as requested */}
            </PerformanceCard>

            {/* Performance Metrics Modal */}
            <ModalContainer open={performanceModalOpen} onClose={() => setPerformanceModalOpen(false)}>
              <ModalContent>
                <ModalHeader>
                  <ModalTitle>Performance - Last 30 days</ModalTitle>
                  <CloseButton onClick={() => setPerformanceModalOpen(false)} aria-label="Close">
                    <CloseIcon />
                  </CloseButton>
                </ModalHeader>
                <Box>
                  <MetricsGrid>
                    {metricsData.map((metric, index) => (
                      <MetricCard key={`all-metric-${index}`} sx={{ backgroundColor: metric.color }}>
                        <MetricLabel sx={{ color: '#ffffff' }}>{metric.label}</MetricLabel>
                        <MetricValue>{metric.value}</MetricValue>
                      </MetricCard>
                    ))}
                  </MetricsGrid>
                </Box>
              </ModalContent>
            </ModalContainer>

            {/* Single GMB Post Modal */}
            <ModalContainer open={gmbPostModalOpen} onClose={handleCloseGmbPost}>
              <ModalContent>
                <ModalHeader>
                  <ModalTitle>Post Details</ModalTitle>
                  <CloseButton onClick={handleCloseGmbPost}>
                    <CloseIcon />
                  </CloseButton>
                </ModalHeader>
                {selectedGmbPost ? (
                  <Box>
                    {(() => {
                      const post = selectedGmbPost;
                      const postMedia = post.media?.[0];
                      let originalSrc = '';
                      if (postMedia) {
                        originalSrc = postMedia.sourceUrl || postMedia.googleUrl || postMedia.thumbnailUrl || postMedia.url || '';
                      } else if (post.photos && post.photos.length > 0) {
                        const photo = post.photos[0];
                        originalSrc = photo.sourceUrl || photo.googleUrl || photo.thumbnailUrl || photo.url || '';
                      }
                      const imgSrc = originalSrc;
                      const created = post.createTime ? new Date(post.createTime) : null;
                      const dateText = created
                        ? created.toLocaleString(undefined, { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                        : 'Date not available';
                      const heading = post.summary || post.callToAction?.actionType || post.topicType || 'Local Post';
                      const description = post.event?.title || post.offer?.couponCode || '';
                      return (
                        <Box>
                          {imgSrc ? (
                            <ModalImage
                              src={buildProxyUrl(imgSrc) || imgSrc}
                              alt={heading}
                              referrerPolicy="no-referrer"
                              crossOrigin="anonymous"
                              data-original={imgSrc}
                              data-proxy={buildProxyUrl(imgSrc) || ''}
                              data-retry="0"
                              onError={(e) => {
                                const imgEl = e.currentTarget;
                                const retry = parseInt(imgEl.dataset.retry || '0', 10);
                                const og = imgEl.dataset.original;
                                const proxy = imgEl.dataset.proxy;
                                if (retry === 0 && proxy && imgEl.src === proxy && og) {
                                  logger.warn('Image failed via proxy, retrying original:', og);
                                  imgEl.dataset.retry = '1';
                                  imgEl.src = og;
                                } else if (retry <= 1 && proxy && imgEl.src === og) {
                                  logger.warn('Image failed via original, falling back to placeholder');
                                  imgEl.dataset.retry = '2';
                                  imgEl.src = placeholderLarge;
                                } else {
                                  imgEl.src = placeholderLarge;
                                }
                              }}
                            />
                          ) : null}
                          <FeedContent>
                            <FeedDate>{dateText}</FeedDate>
                            <FeedText>{heading}</FeedText>
                            {description && (
                              <Typography sx={{ mt: 1, fontSize: '12px', color: '#6b7280', whiteSpace: 'pre-line', wordBreak: 'break-word' }}>{description}</Typography>
                            )}
                          </FeedContent>
                        </Box>
                      );
                    })()}
                  </Box>
                ) : (
                  <Typography sx={{ color: '#6b7280' }}>No post selected.</Typography>
                )}
              </ModalContent>
            </ModalContainer>

            <PerformanceCard>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <SectionTitle>Latest Feed</SectionTitle>
                <Badge
                  badgeContent={newNotificationCount}
                  color="error"
                  sx={{
                    '& .MuiBadge-badge': {
                      fontSize: '10px',
                      minWidth: '16px',
                      height: '16px'
                    }
                  }}
                >
                  <Button
                    size="small"
                    onClick={() => setNewNotificationCount(0)}
                    sx={{
                      fontSize: '10px',
                      color: '#0B91D6',
                      textTransform: 'none',
                      minWidth: 'auto',
                      p: 0.5
                    }}
                  >
                    {notificationLoading ? 'Loading...' : 'Live Feed'}
                  </Button>
                </Badge>
              </Box>
              <FeedSection>
                {/* Show only real notifications from API */}
                {displayedNotifications.length > 0 ? (
                  displayedNotifications.map((notification, index) => {
                    return (
                      <FeedItem key={notification.id}>
                        <FeedHeader>
                          <Box sx={{
                            width: '18px',
                            height: '18px',
                            borderRadius: '50%',
                            backgroundColor: notification.icon === 'review-red' ? '#EF232A' :
                              notification.icon === 'photo' ? '#0B91D6' :
                                notification.icon === 'post' ? '#9C27B0' : '#34A853'
                          }} />
                          <FeedTitle>{notification.title}</FeedTitle>
                        </FeedHeader>
                        <FeedDescription>{notification.description}</FeedDescription>
                        {showFeedDetails && (
                          <Box sx={{ mt: 1, pl: 3 }}>
                            <Typography sx={{ fontSize: '11px', color: '#0B91D6' }}>
                              {notification.formatted.timeAgo}
                            </Typography>
                          </Box>
                        )}
                      </FeedItem>
                    );
                  })
                ) : (
                  /* Show message when no notifications are available */
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Typography sx={{ fontSize: '14px', color: '#6b7280', mb: 1 }}>
                      No recent notifications
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#9ca3af' }}>
                      {notificationLoading ? 'Loading notifications...' : 'New activity will appear here'}
                    </Typography>
                  </Box>
                )}
              </FeedSection>
              <Box sx={{ textAlign: 'center', marginTop: '16px' }}>
                <ViewAllButton onClick={handleViewFeedDetails}>
                  {showFeedDetails ? 'Show Less' : 'View All'}
                </ViewAllButton>
              </Box>
              {/* Live Feed Analytics removed as requested */}
            </PerformanceCard>
          </PerformanceGrid>

          {/* Reviews Overview */}
          <ReviewsCard data-tutorial="reviews-section">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 3 }}>
              <SectionTitle sx={{ mb: 0 }}>Reviews Overview</SectionTitle>
              <SectionSubtitle>Monitor customer sentiment and respond quickly to feedback.</SectionSubtitle>
            </Box>

            <ReviewsHeader>
              <ScorePanel>
                <Box>
                  <ScoreValue>{averageRating.toFixed(1)}</ScoreValue>
                  <Rating
                    value={averageRating}
                    readOnly
                    precision={0.1}
                    size="large"
                    sx={{
                      '& .MuiRating-iconFilled': { color: '#FCD34D' },
                      '& .MuiRating-iconEmpty': { color: 'rgba(255,255,255,0.3)' }
                    }}
                  />
                </Box>
                <ScoreSubtitle>Based on {totalReviews} public reviews</ScoreSubtitle>
                <ScoreMetaRow>
                  <TrendChip>
                    <TrendingUpIcon sx={{ fontSize: 18 }} />
                    {reviewsLast30Days.length} new this month
                  </TrendChip>
                  <Typography sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.86)' }}>
                    Response rate
                    <Box component="span" sx={{ fontWeight: 700, ml: 0.5 }}>{responseRate30}%</Box>
                    <Box component="span" sx={{ ml: 0.5 }}>(30d)</Box>
                  </Typography>
                </ScoreMetaRow>
              </ScorePanel>

              <ReviewsSummaryGrid>
                <SummaryCard>
                  <SummaryLabel>Total reviews</SummaryLabel>
                  <SummaryValue>{totalReviews}</SummaryValue>
                  <SummaryDelta>Avg rating {averageRating.toFixed(1)}</SummaryDelta>
                </SummaryCard>
                <SummaryCard>
                  <SummaryLabel>New (7 days)</SummaryLabel>
                  <SummaryValue>{reviewsLast7Days.length}</SummaryValue>
                  <SummaryDelta>{reviewsLast30Days.length} this month</SummaryDelta>
                </SummaryCard>
                <SummaryCard>
                  <SummaryLabel>Response rate (30d)</SummaryLabel>
                  <SummaryValue>{responseRate30}%</SummaryValue>
                  <SummaryDelta>{responseRate90}% in 90 days</SummaryDelta>
                </SummaryCard>
                <SummaryCard>
                  <SummaryLabel>Positive share</SummaryLabel>
                  <SummaryValue>{sentimentBreakdown.positivePct}%</SummaryValue>
                  <SummaryDelta>{sentimentBreakdown.positive} reviews</SummaryDelta>
                </SummaryCard>
              </ReviewsSummaryGrid>
            </ReviewsHeader>

            <ReviewsBody>
              <DistributionPanel>
                <PanelHeader>
                  <PanelTitle>Rating distribution</PanelTitle>
                  <PanelSubtitle>Across all reviews</PanelSubtitle>
                </PanelHeader>
                <DistributionList>
                  {ratingDistribution.map(item => (
                    <RatingRow key={item.rating}>
                      <RatingLabel>
                        <Box component="span" sx={{ color: '#fbbf24', fontSize: 14, lineHeight: 1 }}>★</Box>
                        {item.rating}.0
                      </RatingLabel>
                      <RatingBar>
                        <RatingFill ownerState={{ rating: item.rating, percentage: Number.isFinite(item.percentage) ? item.percentage : 0 }} />
                      </RatingBar>
                      <RatingMeta>
                        <RatingCount>{item.count}</RatingCount>
                        <RatingPercent>{Math.round(item.percentage || 0)}%</RatingPercent>
                      </RatingMeta>
                    </RatingRow>
                  ))}
                </DistributionList>
                <SentimentGrid>
                  <SentimentCard ownerState={{ background: 'rgba(34,197,94,0.12)', color: '#15803d' }}>
                    <SentimentLabel>Positive</SentimentLabel>
                    <SentimentValue ownerState={{ color: '#15803d' }}>{sentimentBreakdown.positivePct}%</SentimentValue>
                    <SentimentSubtext>{sentimentBreakdown.positive} reviews</SentimentSubtext>
                  </SentimentCard>
                  <SentimentCard ownerState={{ background: 'rgba(245,158,11,0.12)', color: '#b45309' }}>
                    <SentimentLabel>Neutral</SentimentLabel>
                    <SentimentValue ownerState={{ color: '#b45309' }}>{sentimentBreakdown.neutralPct}%</SentimentValue>
                    <SentimentSubtext>{sentimentBreakdown.neutral} reviews</SentimentSubtext>
                  </SentimentCard>
                  <SentimentCard ownerState={{ background: 'rgba(239,68,68,0.12)', color: '#b91c1c' }}>
                    <SentimentLabel>Negative</SentimentLabel>
                    <SentimentValue ownerState={{ color: '#b91c1c' }}>{sentimentBreakdown.negativePct}%</SentimentValue>
                    <SentimentSubtext>{sentimentBreakdown.negative} reviews</SentimentSubtext>
                  </SentimentCard>
                </SentimentGrid>

                {/* Activate AI Review Reply Button */}
                <Button
                  onClick={() => setAiReplyDialogOpen(true)}
                  startIcon={<AutoAwesomeIcon />}
                  data-tutorial="ai-review-reply"
                  sx={{
                    mt: 2,
                    width: '100%',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: '#ffffff',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '14px',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                      boxShadow: '0 6px 20px rgba(102, 126, 234, 0.5)',
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  Activate AI Review Reply
                </Button>
              </DistributionPanel>

              <ReviewsContentPanel>
                <PanelHeader>
                  <Box>
                    <PanelTitle>Recent reviews</PanelTitle>
                    <PanelSubtitle>
                      Showing {displayedReviewCount} of {filteredReviewCount} {activeFilterLabel.toLowerCase()}
                    </PanelSubtitle>
                  </Box>
                </PanelHeader>

                <ReviewsFilterBar>
                  {reviewFilterOptions.map(option => (
                    <FilterChip
                      key={option.value}
                      label={`${option.label} (${option.count})`}
                      active={reviewFilter === option.value}
                      clickable
                      size="small"
                      onClick={() => setReviewFilter(option.value)}
                    />
                  ))}
                </ReviewsFilterBar>

                {filteredReviewCount > 0 ? (
                  <ReviewsList>
                    {displayedReviews.map((review, index) => {
                      const reviewerName = review.reviewer?.displayName || 'Anonymous Reviewer';
                      const initials = getReviewerInitials(reviewerName);
                      const ratingValue = toNumericRating(review.starRating);
                      const comment = (review.comment || '').trim();
                      const reviewDate = review?.createTime ? new Date(review.createTime) : null;
                      const formattedDate = reviewDate && !Number.isNaN(reviewDate.getTime())
                        ? reviewDate.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
                        : 'Date unavailable';
                      const sentimentStyle = ratingValue >= 4
                        ? { background: 'rgba(34,197,94,0.16)', color: '#15803d', label: 'Positive' }
                        : ratingValue <= 2 && ratingValue > 0
                          ? { background: 'rgba(239,68,68,0.16)', color: '#b91c1c', label: 'Negative' }
                          : { background: 'rgba(245,158,11,0.16)', color: '#b45309', label: 'Neutral' };
                      const hasComment = comment.length > 0;
                      const replied = reviewHasReply(review);

                      return (
                        <ReviewItem
                          key={review.name || index}
                          onClick={() => handleReviewClick(review)}
                          sx={{ cursor: 'pointer', '&:hover': { boxShadow: '0 8px 16px rgba(15, 23, 42, 0.12)' } }}
                        >
                          <ReviewerAvatar>{initials}</ReviewerAvatar>
                          <ReviewContent>
                            <ReviewHeaderRow>
                              <ReviewerName>{reviewerName}</ReviewerName>
                              <ReviewMeta>{formattedDate}</ReviewMeta>
                            </ReviewHeaderRow>
                            <ReviewMetaRow>
                              <Rating
                                value={ratingValue}
                                readOnly
                                precision={0.5}
                                size="small"
                                sx={{
                                  '& .MuiRating-iconFilled': { color: '#F59E0B' },
                                  '& .MuiRating-iconEmpty': { color: '#E5E7EB' }
                                }}
                              />
                              <ReviewTag ownerState={{ background: sentimentStyle.background, color: sentimentStyle.color }}>
                                {sentimentStyle.label}
                              </ReviewTag>
                              {replied && (
                                <ReviewTag ownerState={{ variant: 'outline', color: '#0B91D6' }}>
                                  Replied
                                </ReviewTag>
                              )}
                              {!hasComment && (
                                <ReviewTag ownerState={{ variant: 'outline', color: '#94a3b8' }}>
                                  No comment
                                </ReviewTag>
                              )}
                            </ReviewMetaRow>
                            <ReviewComment muted={!hasComment}>
                              {hasComment ? comment : 'Customer left a rating without a written review.'}
                            </ReviewComment>
                          </ReviewContent>
                        </ReviewItem>
                      );
                    })}
                  </ReviewsList>
                ) : (
                  <EmptyReviewsState>
                    <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#0f172a', mb: 0.5 }}>No reviews match this filter</Typography>
                    <Typography sx={{ fontSize: '12px', color: '#64748b' }}>
                      Try a different filter or encourage customers to share their experience.
                    </Typography>
                  </EmptyReviewsState>
                )}
              </ReviewsContentPanel>
            </ReviewsBody>

            <ReviewsFooter>
              <SecondaryActionText>
                Showing {displayedReviewCount} of {filteredReviewCount} {activeFilterLabel.toLowerCase()}.
              </SecondaryActionText>
              <ViewAllButton onClick={handleOpenReviewsModal}>View All Reviews</ViewAllButton>
            </ReviewsFooter>
          </ReviewsCard>

          {/* GMB Feed */}
          <FeedCard id="gmb-feed">
            <SectionTitle>GMB Feed</SectionTitle>
            {localPostsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <Typography>Loading local posts...</Typography>
              </Box>
            ) : displayedLocalPosts.length > 0 ? (
              <>
                <FeedGrid>
                  {displayedLocalPosts.map((post, index) => {

                    // Extract post data - check multiple possible image sources
                    const postMedia = post.media?.[0]; // Get first media item if available
                    let originalSrc = '';

                    if (postMedia) {
                      // Try different possible image URL fields
                      originalSrc = postMedia.sourceUrl ||
                        postMedia.googleUrl ||
                        postMedia.thumbnailUrl ||
                        postMedia.url || '';
                    } else if (post.photos && post.photos.length > 0) {
                      // Check if photos array exists
                      const photo = post.photos[0];
                      originalSrc = photo.sourceUrl || photo.googleUrl || photo.thumbnailUrl || photo.url || '';
                    }

                    const imgSrc = originalSrc;
                    const created = post.createTime ? new Date(post.createTime) : null;
                    const dateText = created
                      ? `Posted on - ${created.toLocaleString(undefined, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        timeZoneName: 'short'
                      })}`
                      : 'Date not available';

                    // Get post content
                    const heading = post.summary || post.callToAction?.actionType || post.topicType || 'Local Post';
                    const description = post.event?.title || post.offer?.couponCode || '';

                    return (
                      <FeedCardItem
                        key={post.name || `post-${index}`}
                        onClick={() => handleOpenGmbPost(post)}
                        sx={{
                          position: 'relative',
                          '&:hover .edit-icon': {
                            opacity: 1
                          }
                        }}
                      >
                        {imgSrc ? (
                          <FeedImage
                            src={buildProxyUrl(imgSrc) || imgSrc}
                            alt={`Local Post ${index + 1}`}
                            referrerPolicy="no-referrer"
                            crossOrigin="anonymous"
                            data-original={imgSrc}
                            data-proxy={buildProxyUrl(imgSrc) || ''}
                            data-retry="0"
                            onError={(e) => {
                              const imgEl = e.currentTarget;
                              const retry = parseInt(imgEl.dataset.retry || '0', 10);
                              const og = imgEl.dataset.original;
                              const proxy = imgEl.dataset.proxy;
                              if (retry === 0 && proxy && imgEl.src === proxy && og) {
                                logger.warn('Image failed via proxy, retrying original:', og);
                                imgEl.dataset.retry = '1';
                                imgEl.src = og;
                              } else if (retry <= 1 && proxy && imgEl.src === og) {
                                logger.warn('Image failed via original, falling back to placeholder');
                                imgEl.dataset.retry = '2';
                                imgEl.src = placeholderLarge;
                              } else {
                                imgEl.src = placeholderLarge;
                              }
                            }}
                          />
                        ) : null}
                        <FeedContent>
                          <FeedDate>{dateText}</FeedDate>
                          <FeedText>{heading}</FeedText>
                          {description && (
                            <Typography sx={{ mt: 1, fontSize: '12px', color: '#6b7280', whiteSpace: 'pre-line', wordBreak: 'break-word' }}>{description}</Typography>
                          )}
                        </FeedContent>
                        <IconButton
                          className="edit-icon"
                          onClick={(e) => handleEditPost(post, e)}
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            opacity: 0,
                            transition: 'opacity 0.2s',
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            '&:hover': {
                              backgroundColor: '#EFF6FF',
                              color: '#0B91D6'
                            }
                          }}
                          size="small"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </FeedCardItem>
                    );
                  })}
                </FeedGrid>
                <Box sx={{ textAlign: 'center' }}>
                  <ViewAllButton onClick={() => setGmbFeedModalOpen(true)}>View All</ViewAllButton>
                </Box>
              </>
            ) : (
              <Typography sx={{ color: '#6b7280' }}>No local posts found.</Typography>
            )}
          </FeedCard>

          {/* Social Feed */}
          <FeedCard>
            <SectionTitle>Social Feed</SectionTitle>
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '200px',
              py: 4
            }}>
              <Typography sx={{
                fontSize: '48px',
                mb: 2
              }}>
                🚀
              </Typography>
              <Typography sx={{
                fontSize: '18px',
                fontWeight: 600,
                color: '#121927',
                mb: 1
              }}>
                Coming Soon
              </Typography>
              <Typography sx={{
                fontSize: '14px',
                color: '#6B7280',
                textAlign: 'center',
                maxWidth: '400px'
              }}>
                Social media integration is on its way! Soon you'll be able to manage all your social posts from here.
              </Typography>
            </Box>
          </FeedCard>

        </ContentWrapper>
      </PageContainer>

      {/* Products/Services Modal */}
      <ModalContainer
        open={productsModalOpen}
        onClose={() => setProductsModalOpen(false)}
      >
        <ModalContent>
          <ModalHeader>
            <ModalTitle>
              All Services
            </ModalTitle>
            <CloseButton onClick={() => setProductsModalOpen(false)}>
              <CloseIcon />
            </CloseButton>
          </ModalHeader>

          <ModalProductsGrid>
            {services.map((item) => (
              <ProductCard
                key={item.id}
                onClick={() => handleServiceClick(item)}
                sx={{
                  cursor: 'pointer',
                  position: 'relative',
                  '&:hover': {
                    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.15)',
                    transform: 'translateY(-2px)'
                  },
                  '&:hover .edit-icon': {
                    opacity: 1
                  }
                }}
              >
                <IconButton
                  className="edit-icon"
                  onClick={(e) => handleEditService(item, e)}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    opacity: 0,
                    transition: 'opacity 0.2s',
                    backgroundColor: '#fff',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    '&:hover': {
                      backgroundColor: '#EFF6FF',
                      color: '#0B91D6'
                    },
                    zIndex: 1
                  }}
                  size="small"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <ProductContent>
                  <ProductTitle>{item.title}</ProductTitle>
                  <ProductPrice>{item.price}</ProductPrice>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#0B91D6',
                      fontSize: '11px',
                      fontStyle: 'italic',
                      mt: 0.5
                    }}
                  >
                    Click to view details
                  </Typography>
                </ProductContent>
              </ProductCard>
            ))}
          </ModalProductsGrid>
        </ModalContent>
      </ModalContainer>

      {/* Edit Service Modal */}
      <Dialog
        open={serviceEditModalOpen}
        onClose={() => !serviceSaving && setServiceEditModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Service</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="Service Name"
              value={serviceFormData.name}
              onChange={(e) => handleServiceFormChange('name', e.target.value)}
              fullWidth
              required
              disabled={serviceSaving}
            />
            {serviceSuggestions.length > 0 || serviceSuggestionsLoading ? (
              <Box>
                <Typography component="p" variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                  Suggested services from Google
                </Typography>
                {serviceSuggestionsLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={18} />
                    <Typography variant="caption" color="text.secondary">
                      Loading suggestions...
                    </Typography>
                  </Box>
                ) : (
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {serviceSuggestions.map((suggestion) => (
                      <Chip
                        key={suggestion.serviceTypeId || suggestion.displayName}
                        label={suggestion.displayName || suggestion.serviceTypeId}
                        variant="outlined"
                        size="small"
                        onClick={() => handleSuggestionFill(suggestion)}
                      />
                    ))}
                  </Stack>
                )}
              </Box>
            ) : null}
            <TextField
              label="Service Description"
              value={serviceFormData.description}
              onChange={(e) => handleServiceFormChange('description', e.target.value)}
              fullWidth
              multiline
              rows={4}
              required
              disabled={serviceSaving}
              helperText="Describe what this service provides for customers."
              sx={{ mt: serviceSuggestions.length ? 2 : 0 }}
            />
            <TextField
              label="Category (optional)"
              value={serviceFormData.category}
              onChange={(e) => handleServiceFormChange('category', e.target.value)}
              fullWidth
              disabled={serviceSaving}
              helperText={
                serviceCategoryOptions.length
                  ? `Suggested categories: ${serviceCategoryOptions
                    .slice(0, 4)
                    .map(option => option.label || option.id)
                    .filter(Boolean)
                    .join(', ')}`
                  : 'Category is optional when using structured service IDs.'
              }
            />
            <TextField
              label="Structured service ID (optional)"
              value={serviceFormData.serviceTypeId}
              onChange={(e) => handleServiceFormChange('serviceTypeId', e.target.value)}
              fullWidth
              disabled={serviceSaving}
              helperText="Add the Google serviceTypeId to keep this service in sync with structured services."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <Button
            onClick={() => editingService && handleDeleteService(editingService.id)}
            color="error"
            disabled={serviceSaving}
            startIcon={<DeleteIcon />}
          >
            Delete
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => setServiceEditModalOpen(false)} disabled={serviceSaving}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveService}
              variant="contained"
              disabled={
                serviceSaving ||
                !serviceFormData.name.trim() ||
                !serviceFormData.description.trim()
              }
              sx={{ backgroundColor: '#0B91D6', '&:hover': { backgroundColor: '#0A7AB8' } }}
            >
              {serviceSaving ? <CircularProgress size={20} /> : 'Save Changes'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Add Service Modal */}
      <Dialog
        open={serviceAddModalOpen}
        onClose={() => !serviceSaving && setServiceAddModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Service</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="Service Name"
              value={serviceFormData.name}
              onChange={(e) => handleServiceFormChange('name', e.target.value)}
              fullWidth
              required
              disabled={serviceSaving}
            />
            {serviceSuggestions.length > 0 || serviceSuggestionsLoading ? (
              <Box>
                <Typography component="p" variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                  Suggested services from Google
                </Typography>
                {serviceSuggestionsLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={18} />
                    <Typography variant="caption" color="text.secondary">
                      Loading suggestions...
                    </Typography>
                  </Box>
                ) : (
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {serviceSuggestions.map((suggestion) => (
                      <Chip
                        key={suggestion.serviceTypeId || suggestion.displayName}
                        label={suggestion.displayName || suggestion.serviceTypeId}
                        variant="outlined"
                        size="small"
                        onClick={() => handleSuggestionFill(suggestion)}
                      />
                    ))}
                  </Stack>
                )}
              </Box>
            ) : null}
            <TextField
              label="Service Description"
              value={serviceFormData.description}
              onChange={(e) => handleServiceFormChange('description', e.target.value)}
              fullWidth
              multiline
              rows={4}
              required
              disabled={serviceSaving}
              helperText="Describe this service clearly for customers."
              sx={{ mt: serviceSuggestions.length ? 2 : 0 }}
            />
            <TextField
              label="Category (optional)"
              value={serviceFormData.category}
              onChange={(e) => handleServiceFormChange('category', e.target.value)}
              fullWidth
              disabled={serviceSaving}
              helperText={
                serviceCategoryOptions.length
                  ? `Suggested categories: ${serviceCategoryOptions
                    .slice(0, 4)
                    .map(option => option.label || option.id)
                    .filter(Boolean)
                    .join(', ')}`
                  : 'Category is optional when using structured service IDs.'
              }
            />
            <TextField
              label="Structured service ID (optional)"
              value={serviceFormData.serviceTypeId}
              onChange={(e) => handleServiceFormChange('serviceTypeId', e.target.value)}
              fullWidth
              disabled={serviceSaving}
              helperText="Add the Google serviceTypeId to keep this service aligned with structured services."
            />
            <Alert severity="info" variant="outlined" sx={{ fontSize: '13px', py: 1, px: 1 }}>
              Adding a service here pushes it directly to Google Business Profile and syncs it in real time.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setServiceAddModalOpen(false)}
            disabled={serviceSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateService}
            variant="contained"
            disabled={
              serviceSaving ||
              !serviceFormData.name.trim() ||
              !serviceFormData.description.trim()
            }
            sx={{ backgroundColor: '#0B91D6', '&:hover': { backgroundColor: '#0A7AB8' } }}
          >
            {serviceSaving ? <CircularProgress size={20} /> : 'Add Service'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Edit Appointment Link Modal */}
      <Dialog
        open={appointmentEditModalOpen}
        onClose={() => !appointmentSaving && setAppointmentEditModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Edit Appointment Link
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <FormControl fullWidth required>
              <InputLabel>Link Type</InputLabel>
              <Select
                value={appointmentFormData.linkType}
                onChange={(e) => handleAppointmentFormChange('linkType', e.target.value)}
                label="Link Type"
                disabled={appointmentSaving}
              >
                <MenuItem value="APPOINTMENT">Appointment Booking</MenuItem>
                <MenuItem value="ONLINE_APPOINTMENT">Online Appointment</MenuItem>
                <MenuItem value="DINING_RESERVATION">Dining Reservation</MenuItem>
                <MenuItem value="SOCIAL_MEDIA">Social Media</MenuItem>
                <MenuItem value="WEBSITE">Website</MenuItem>
                <MenuItem value="CUSTOM">Custom/Other</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Link URL"
              value={appointmentFormData.url}
              onChange={(e) => handleAppointmentFormChange('url', e.target.value)}
              fullWidth
              required
              disabled={appointmentSaving}
              helperText="Enter the full URL (must start with http:// or https://)"
              placeholder="https://example.com/book"
            />
            <TextField
              label="Link Label/Name (Optional)"
              value={appointmentFormData.label}
              onChange={(e) => handleAppointmentFormChange('label', e.target.value)}
              fullWidth
              disabled={appointmentSaving}
              helperText="Custom display name for the link (e.g., 'Book on Calendly')"
            />
            <TextField
              label="Description (Optional)"
              value={appointmentFormData.description}
              onChange={(e) => handleAppointmentFormChange('description', e.target.value)}
              fullWidth
              multiline
              rows={3}
              disabled={appointmentSaving}
              helperText="Brief description of what this link is for"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <Button
            onClick={() => editingAppointment && handleDeleteAppointmentLink(editingAppointment.url)}
            color="error"
            disabled={appointmentSaving}
            startIcon={<DeleteIcon />}
          >
            Delete
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={() => setAppointmentEditModalOpen(false)}
              disabled={appointmentSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveAppointmentLink}
              variant="contained"
              disabled={appointmentSaving || !appointmentFormData.url}
              sx={{
                backgroundColor: '#0B91D6',
                '&:hover': {
                  backgroundColor: '#0A7AB8'
                }
              }}
            >
              {appointmentSaving ? <CircularProgress size={20} /> : 'Save Changes'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Add Appointment Link Modal */}
      <Dialog
        open={appointmentAddModalOpen}
        onClose={() => !appointmentSaving && setAppointmentAddModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Add Appointment Link
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <FormControl fullWidth required>
              <InputLabel>Link Type</InputLabel>
              <Select
                value={appointmentFormData.linkType}
                onChange={(e) => handleAppointmentFormChange('linkType', e.target.value)}
                label="Link Type"
                disabled={appointmentSaving}
              >
                <MenuItem value="APPOINTMENT">Appointment Booking</MenuItem>
                <MenuItem value="ONLINE_APPOINTMENT">Online Appointment</MenuItem>
                <MenuItem value="DINING_RESERVATION">Dining Reservation</MenuItem>
                <MenuItem value="SOCIAL_MEDIA">Social Media</MenuItem>
                <MenuItem value="WEBSITE">Website</MenuItem>
                <MenuItem value="CUSTOM">Custom/Other</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Link URL"
              value={appointmentFormData.url}
              onChange={(e) => handleAppointmentFormChange('url', e.target.value)}
              fullWidth
              required
              disabled={appointmentSaving}
              helperText="Enter the full URL (must start with http:// or https://)"
              placeholder="https://example.com/book"
            />
            <TextField
              label="Link Label/Name (Optional)"
              value={appointmentFormData.label}
              onChange={(e) => handleAppointmentFormChange('label', e.target.value)}
              fullWidth
              disabled={appointmentSaving}
              helperText="Custom display name for the link (e.g., 'Book on Calendly')"
            />
            <TextField
              label="Description (Optional)"
              value={appointmentFormData.description}
              onChange={(e) => handleAppointmentFormChange('description', e.target.value)}
              fullWidth
              multiline
              rows={3}
              disabled={appointmentSaving}
              helperText="Brief description of what this link is for"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setAppointmentAddModalOpen(false)}
            disabled={appointmentSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateAppointmentLink}
            variant="contained"
            disabled={appointmentSaving || !appointmentFormData.url}
            sx={{
              backgroundColor: '#0B91D6',
              '&:hover': {
                backgroundColor: '#0A7AB8'
              }
            }}
          >
            {appointmentSaving ? <CircularProgress size={20} /> : 'Add Link'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Post Modal */}
      <Dialog
        open={postEditModalOpen}
        onClose={() => !postSaving && setPostEditModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Edit GMB Post
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            {/* Post Summary/Content */}
            <TextField
              label="Post Content"
              value={postFormData.summary}
              onChange={(e) => handlePostFormChange('summary', e.target.value)}
              fullWidth
              required
              multiline
              rows={4}
              disabled={postSaving}
              helperText="Main content of your post (required)"
            />

            {/* Call to Action Section */}
            <Box sx={{ border: '1px solid #e5e7eb', borderRadius: '8px', p: 2 }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: 2, color: '#374151' }}>
                Call to Action (Optional)
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Action Type</InputLabel>
                  <Select
                    value={postFormData.callToAction.actionType}
                    onChange={(e) => handlePostFormChange('callToAction.actionType', e.target.value)}
                    label="Action Type"
                    disabled={postSaving}
                  >
                    <MenuItem value="LEARN_MORE">Learn More</MenuItem>
                    <MenuItem value="BOOK">Book</MenuItem>
                    <MenuItem value="ORDER">Order</MenuItem>
                    <MenuItem value="SHOP">Shop</MenuItem>
                    <MenuItem value="SIGN_UP">Sign Up</MenuItem>
                    <MenuItem value="CALL">Call</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Action URL"
                  value={postFormData.callToAction.url}
                  onChange={(e) => handlePostFormChange('callToAction.url', e.target.value)}
                  fullWidth
                  disabled={postSaving}
                  helperText="URL for the call-to-action button"
                  placeholder="https://example.com"
                />
              </Box>
            </Box>

            {/* Event Section */}
            <Box sx={{ border: '1px solid #e5e7eb', borderRadius: '8px', p: 2 }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: 2, color: '#374151' }}>
                Event Details (Optional)
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Event Title"
                  value={postFormData.event.title}
                  onChange={(e) => handlePostFormChange('event.title', e.target.value)}
                  fullWidth
                  disabled={postSaving}
                  helperText="Title of the event"
                />
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <TextField
                    label="Start Date"
                    type="date"
                    value={postFormData.event.schedule.startDate}
                    onChange={(e) => handlePostFormChange('event.schedule.startDate', e.target.value)}
                    fullWidth
                    disabled={postSaving}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="Start Time"
                    type="time"
                    value={postFormData.event.schedule.startTime}
                    onChange={(e) => handlePostFormChange('event.schedule.startTime', e.target.value)}
                    fullWidth
                    disabled={postSaving}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="End Date"
                    type="date"
                    value={postFormData.event.schedule.endDate}
                    onChange={(e) => handlePostFormChange('event.schedule.endDate', e.target.value)}
                    fullWidth
                    disabled={postSaving}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="End Time"
                    type="time"
                    value={postFormData.event.schedule.endTime}
                    onChange={(e) => handlePostFormChange('event.schedule.endTime', e.target.value)}
                    fullWidth
                    disabled={postSaving}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              </Box>
            </Box>

            {/* Offer Section */}
            <Box sx={{ border: '1px solid #e5e7eb', borderRadius: '8px', p: 2 }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: 2, color: '#374151' }}>
                Offer Details (Optional)
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Coupon Code"
                  value={postFormData.offer.couponCode}
                  onChange={(e) => handlePostFormChange('offer.couponCode', e.target.value)}
                  fullWidth
                  disabled={postSaving}
                  helperText="Promotional code for the offer"
                />
                <TextField
                  label="Redeem Online URL"
                  value={postFormData.offer.redeemOnlineUrl}
                  onChange={(e) => handlePostFormChange('offer.redeemOnlineUrl', e.target.value)}
                  fullWidth
                  disabled={postSaving}
                  helperText="URL where customers can redeem the offer"
                  placeholder="https://example.com/redeem"
                />
                <TextField
                  label="Terms & Conditions"
                  value={postFormData.offer.termsConditions}
                  onChange={(e) => handlePostFormChange('offer.termsConditions', e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                  disabled={postSaving}
                  helperText="Terms and conditions for the offer"
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <Button
            onClick={() => editingPost && handleDeletePost(editingPost.name)}
            color="error"
            disabled={postSaving}
            startIcon={<DeleteIcon />}
          >
            Delete Post
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={() => setPostEditModalOpen(false)}
              disabled={postSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSavePost}
              variant="contained"
              disabled={postSaving || !postFormData.summary}
              sx={{
                backgroundColor: '#0B91D6',
                '&:hover': {
                  backgroundColor: '#0A7AB8'
                }
              }}
            >
              {postSaving ? <CircularProgress size={20} /> : 'Save Changes'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Reviews Overview Modal */}
      <ModalContainer open={reviewsModalOpen} onClose={() => setReviewsModalOpen(false)}>
        <ModalContent data-reviews-modal-content>
          <ModalHeader>
            <ModalTitle>All Reviews ({reviews.length})</ModalTitle>
            <CloseButton onClick={() => setReviewsModalOpen(false)}>
              <CloseIcon />
            </CloseButton>
          </ModalHeader>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: 2,
              py: 1,
              backgroundColor: '#F3F4F6',
              borderRadius: '8px'
            }}>
              <Typography sx={{ fontSize: '14px', color: '#6b7280', fontWeight: 500 }}>
                Page {reviewsPage} of {totalReviewPages} • Showing {paginatedReviews.length} reviews
              </Typography>
              <Typography sx={{ fontSize: '12px', color: '#0B91D6', fontWeight: 600 }}>
                AI-powered reply suggestions
              </Typography>
            </Box>
            {(paginatedReviews && paginatedReviews.length > 0 ? paginatedReviews : []).map((review, paginatedIndex) => {
              // Calculate the actual index in the full reviews array
              const actualIndex = (reviewsPage - 1) * reviewsPerPage + paginatedIndex;

              return (
                <Box data-review-idx={actualIndex} key={`review-${actualIndex}`} sx={{ p: 2, border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: '#ffffff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Rating
                      value={toNumericRating(review.starRating)}
                      readOnly
                      size="small"
                      sx={{
                        color: '#F59E0B',
                        '& .MuiRating-iconFilled': { color: '#F59E0B' },
                        '& .MuiRating-iconEmpty': { color: '#E5E7EB' }
                      }}
                    />
                    <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>
                      {review.createTime ? new Date(review.createTime).toLocaleDateString() : 'Unknown date'}
                    </Typography>
                    <Box sx={{
                      ml: 'auto',
                      px: 1,
                      py: 0.5,
                      backgroundColor: toNumericRating(review.starRating) >= 4 ? '#10b981' : toNumericRating(review.starRating) >= 3 ? '#f59e0b' : '#ef4444',
                      borderRadius: '4px'
                    }}>
                      <Typography sx={{ fontSize: '10px', color: '#ffffff', fontWeight: '600' }}>
                        {toNumericRating(review.starRating)}★
                      </Typography>
                    </Box>
                  </Box>
                  <Typography sx={{ fontSize: '13px', color: '#121927', mb: 1 }}>
                    {review.comment || 'No comment provided'}
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: '#0B91D6' }}>
                    - {review.reviewer?.displayName || 'Anonymous'}
                  </Typography>

                  {/* Show existing reply if available */}
                  {review.reviewReply?.comment ? (
                    editingReplyIndex === actualIndex ? (
                      /* Edit Mode */
                      <Box sx={{ mt: 1.5, p: 2, backgroundColor: '#FEF3C7', borderRadius: '6px', borderLeft: '4px solid #F59E0B' }}>
                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#92400E', mb: 1 }}>
                          ✏️ Editing Reply:
                        </Typography>
                        <TextField
                          fullWidth
                          placeholder="Edit your reply..."
                          size="small"
                          multiline
                          minRows={2}
                          value={editReplyText}
                          onChange={(e) => setEditReplyText(e.target.value)}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: '#ffffff',
                              '&:hover fieldset': { borderColor: '#F59E0B' },
                              '&.Mui-focused fieldset': { borderColor: '#F59E0B' }
                            }
                          }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={handleCancelEditReply}
                            disabled={submittingModalReply[actualIndex]}
                            sx={{
                              textTransform: 'none',
                              borderColor: '#6B7280',
                              color: '#6B7280',
                              '&:hover': {
                                borderColor: '#4B5563',
                                backgroundColor: 'rgba(107, 114, 128, 0.1)'
                              }
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleUpdateReply(review, actualIndex)}
                            disabled={!editReplyText.trim() || submittingModalReply[actualIndex]}
                            startIcon={submittingModalReply[actualIndex] ? <CircularProgress size={16} /> : null}
                            sx={{
                              backgroundColor: '#F59E0B',
                              textTransform: 'none',
                              '&:hover': {
                                backgroundColor: '#D97706'
                              },
                              '&:disabled': {
                                backgroundColor: '#9CA3AF',
                                color: '#ffffff'
                              }
                            }}
                          >
                            {submittingModalReply[actualIndex] ? 'Updating...' : 'Update Reply'}
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      /* Display Mode with Edit/Delete Options */
                      <Box sx={{
                        mt: 1.5,
                        p: 2,
                        backgroundColor: '#EFF6FF',
                        borderRadius: '6px',
                        borderLeft: '4px solid #0B91D6',
                        position: 'relative',
                        '&:hover .reply-actions': {
                          opacity: 1
                        }
                      }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#0B91D6' }}>
                            ✅ Your Reply:
                          </Typography>
                          <Box
                            className="reply-actions"
                            sx={{
                              display: 'flex',
                              gap: 0.5,
                              opacity: 0,
                              transition: 'opacity 0.2s ease'
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={() => handleEditReply(actualIndex, review.reviewReply.comment)}
                              sx={{
                                color: '#0B91D6',
                                padding: '4px',
                                '&:hover': {
                                  backgroundColor: '#DBEAFE',
                                  color: '#0980c2'
                                }
                              }}
                              title="Edit reply"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteReply(review, actualIndex)}
                              disabled={deletingReply[actualIndex]}
                              sx={{
                                color: '#DC2626',
                                padding: '4px',
                                '&:hover': {
                                  backgroundColor: '#FEE2E2',
                                  color: '#B91C1C'
                                },
                                '&:disabled': {
                                  color: '#9CA3AF'
                                }
                              }}
                              title="Delete reply"
                            >
                              {deletingReply[actualIndex] ? (
                                <CircularProgress size={16} sx={{ color: '#DC2626' }} />
                              ) : (
                                <DeleteIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Box>
                        </Box>
                        <Typography sx={{ fontSize: '13px', color: '#1F2937', lineHeight: 1.6 }}>
                          {review.reviewReply.comment}
                        </Typography>
                        {review.reviewReply.updateTime && (
                          <Typography sx={{ fontSize: '11px', color: '#6B7280', mt: 1 }}>
                            Replied on {new Date(review.reviewReply.updateTime).toLocaleDateString()}
                          </Typography>
                        )}
                      </Box>
                    )
                  ) : (
                    /* Enhanced reply box with AI features - Only show if no reply exists */
                    <Box sx={{ position: 'relative', mt: 1.5, p: 2, backgroundColor: '#f9fafb', borderRadius: '6px' }}>
                      <Typography sx={{ fontSize: '12px', fontWeight: '600', color: '#121927', mb: 1 }}>
                        Reply to this review:
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Write a professional reply..."
                        size="small"
                        multiline
                        minRows={2}
                        value={modalReplyTexts[actualIndex] || ''}
                        onChange={(e) => setModalReplyTexts(prev => ({ ...prev, [actualIndex]: e.target.value }))}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: '#ffffff',
                            '&:hover fieldset': { borderColor: '#0B91D6' },
                            '&.Mui-focused fieldset': { borderColor: '#0B91D6' }
                          }
                        }}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                        <Box sx={{ display: 'flex', gap: '8px' }}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              const aiReply = generateAIReplyText(review);
                              setModalReplyTexts(prev => ({ ...prev, [actualIndex]: aiReply }));
                            }}
                            sx={{
                              textTransform: 'none',
                              fontSize: '11px',
                              borderColor: '#0B91D6',
                              color: '#0B91D6',
                              '&:hover': {
                                borderColor: '#0980c2',
                                backgroundColor: 'rgba(11, 145, 214, 0.1)'
                              }
                            }}
                          >
                            🤖 AI Reply
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              const polite = `Thank you for your feedback${review.reviewer?.displayName ? `, ${review.reviewer.displayName}` : ''}! We appreciate your support and look forward to serving you again.`;
                              setModalReplyTexts(prev => ({ ...prev, [actualIndex]: polite }));
                            }}
                            sx={{
                              textTransform: 'none',
                              fontSize: '11px',
                              borderColor: '#6b7280',
                              color: '#6b7280',
                              '&:hover': {
                                borderColor: '#4b5563',
                                backgroundColor: 'rgba(107, 114, 128, 0.1)'
                              }
                            }}
                          >
                            Quick Reply
                          </Button>
                        </Box>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => submitModalReviewReply(review, actualIndex)}
                          disabled={!modalReplyTexts[actualIndex]?.trim() || submittingModalReply[actualIndex]}
                          startIcon={submittingModalReply[actualIndex] ? <CircularProgress size={16} /> : null}
                          sx={{
                            backgroundColor: '#0B91D6',
                            textTransform: 'none',
                            '&:disabled': {
                              backgroundColor: '#9CA3AF',
                              color: '#ffffff'
                            }
                          }}
                        >
                          {submittingModalReply[actualIndex] ? 'Sending...' : 'Send'}
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Box>
              );
            })}
            {(!reviews || reviews.length === 0) && (
              <Typography sx={{ color: '#6b7280' }}>No reviews available.</Typography>
            )}

            {/* Pagination Controls */}
            {reviews && reviews.length > reviewsPerPage && (
              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 2,
                mt: 3,
                pt: 2,
                borderTop: '1px solid #E5E7EB'
              }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setReviewsPage(prev => Math.max(1, prev - 1))}
                  disabled={reviewsPage === 1}
                  startIcon={<ChevronLeftIcon />}
                  sx={{
                    textTransform: 'none',
                    borderColor: '#0B91D6',
                    color: '#0B91D6',
                    '&:hover': {
                      borderColor: '#0980c2',
                      backgroundColor: 'rgba(11, 145, 214, 0.04)'
                    },
                    '&:disabled': {
                      borderColor: '#E5E7EB',
                      color: '#9CA3AF'
                    }
                  }}
                >
                  Previous
                </Button>

                {/* Page Numbers */}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {(() => {
                    const pages = [];
                    const maxPagesToShow = 5;
                    let startPage = Math.max(1, reviewsPage - Math.floor(maxPagesToShow / 2));
                    let endPage = Math.min(totalReviewPages, startPage + maxPagesToShow - 1);

                    // Adjust start page if we're near the end
                    if (endPage - startPage < maxPagesToShow - 1) {
                      startPage = Math.max(1, endPage - maxPagesToShow + 1);
                    }

                    // Show first page if not in range
                    if (startPage > 1) {
                      pages.push(
                        <Button
                          key={1}
                          size="small"
                          onClick={() => setReviewsPage(1)}
                          sx={{
                            minWidth: '36px',
                            height: '36px',
                            borderRadius: '8px',
                            backgroundColor: '#FFFFFF',
                            color: '#374151',
                            border: '1px solid #E5E7EB',
                            fontWeight: 500,
                            '&:hover': {
                              borderColor: '#0B91D6',
                              backgroundColor: 'rgba(11, 145, 214, 0.04)'
                            }
                          }}
                        >
                          1
                        </Button>
                      );
                      if (startPage > 2) {
                        pages.push(
                          <Typography key="ellipsis-start" sx={{ color: '#9CA3AF', px: 1 }}>
                            ...
                          </Typography>
                        );
                      }
                    }

                    // Show page range
                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <Button
                          key={i}
                          size="small"
                          onClick={() => setReviewsPage(i)}
                          sx={{
                            minWidth: '36px',
                            height: '36px',
                            borderRadius: '8px',
                            backgroundColor: reviewsPage === i ? '#0B91D6' : '#FFFFFF',
                            color: reviewsPage === i ? '#FFFFFF' : '#374151',
                            border: reviewsPage === i ? 'none' : '1px solid #E5E7EB',
                            fontWeight: reviewsPage === i ? 600 : 500,
                            '&:hover': {
                              borderColor: reviewsPage === i ? 'none' : '#0B91D6',
                              backgroundColor: reviewsPage === i ? '#0980c2' : 'rgba(11, 145, 214, 0.04)'
                            }
                          }}
                        >
                          {i}
                        </Button>
                      );
                    }

                    // Show last page if not in range
                    if (endPage < totalReviewPages) {
                      if (endPage < totalReviewPages - 1) {
                        pages.push(
                          <Typography key="ellipsis-end" sx={{ color: '#9CA3AF', px: 1 }}>
                            ...
                          </Typography>
                        );
                      }
                      pages.push(
                        <Button
                          key={totalReviewPages}
                          size="small"
                          onClick={() => setReviewsPage(totalReviewPages)}
                          sx={{
                            minWidth: '36px',
                            height: '36px',
                            borderRadius: '8px',
                            backgroundColor: '#FFFFFF',
                            color: '#374151',
                            border: '1px solid #E5E7EB',
                            fontWeight: 500,
                            '&:hover': {
                              borderColor: '#0B91D6',
                              backgroundColor: 'rgba(11, 145, 214, 0.04)'
                            }
                          }}
                        >
                          {totalReviewPages}
                        </Button>
                      );
                    }

                    return pages;
                  })()}
                </Box>

                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setReviewsPage(prev => Math.min(totalReviewPages, prev + 1))}
                  disabled={reviewsPage === totalReviewPages}
                  endIcon={<ChevronRightIcon />}
                  sx={{
                    textTransform: 'none',
                    borderColor: '#0B91D6',
                    color: '#0B91D6',
                    '&:hover': {
                      borderColor: '#0980c2',
                      backgroundColor: 'rgba(11, 145, 214, 0.04)'
                    },
                    '&:disabled': {
                      borderColor: '#E5E7EB',
                      color: '#9CA3AF'
                    }
                  }}
                >
                  Next
                </Button>
              </Box>
            )}
          </Box>
        </ModalContent>
      </ModalContainer>

      {/* GMB Feed Modal */}
      <ModalContainer open={gmbFeedModalOpen} onClose={() => setGmbFeedModalOpen(false)}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>All GMB Posts</ModalTitle>
            <CloseButton onClick={() => setGmbFeedModalOpen(false)}>
              <CloseIcon />
            </CloseButton>
          </ModalHeader>
          <FeedGrid>
            {(localPosts && localPosts.length > 0 ? localPosts : []).map((post, index) => {
              const postMedia = post.media?.[0];
              let originalSrc = '';
              if (postMedia) {
                originalSrc = postMedia.sourceUrl || postMedia.googleUrl || postMedia.thumbnailUrl || postMedia.url || '';
              } else if (post.photos && post.photos.length > 0) {
                const photo = post.photos[0];
                originalSrc = photo.sourceUrl || photo.googleUrl || photo.thumbnailUrl || photo.url || '';
              }
              const imgSrc = originalSrc;
              const created = post.createTime ? new Date(post.createTime) : null;
              const dateText = created
                ? `Posted on - ${created.toLocaleString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}`
                : 'Date not available';
              const heading = post.summary || post.callToAction?.actionType || post.topicType || 'Local Post';
              const description = post.event?.title || post.offer?.couponCode || '';
              return (
                <FeedCardItem
                  key={post.name || `post-all-${index}`}
                  sx={{
                    position: 'relative',
                    '&:hover .edit-icon': {
                      opacity: 1
                    }
                  }}
                >
                  {imgSrc ? (
                    <ModalImage
                      src={buildProxyUrl(imgSrc) || imgSrc}
                      alt={`Local Post ${index + 1}`}
                      referrerPolicy="no-referrer"
                      crossOrigin="anonymous"
                      data-original={imgSrc}
                      data-proxy={buildProxyUrl(imgSrc) || ''}
                      data-retry="0"
                      onError={(e) => {
                        const imgEl = e.currentTarget;
                        const retry = parseInt(imgEl.dataset.retry || '0', 10);
                        const og = imgEl.dataset.original;
                        const proxy = imgEl.dataset.proxy;
                        if (retry === 0 && proxy && imgEl.src === proxy && og) {
                          logger.warn('Image failed via proxy (modal), retrying original:', og);
                          imgEl.dataset.retry = '1';
                          imgEl.src = og;
                        } else if (retry <= 1 && proxy && imgEl.src === og) {
                          logger.warn('Image failed via original (modal), fallback placeholder');
                          imgEl.dataset.retry = '2';
                          imgEl.src = placeholderLarge;
                        } else {
                          imgEl.src = placeholderLarge;
                        }
                      }}
                    />
                  ) : null}
                  <FeedContent>
                    <FeedDate>{dateText}</FeedDate>
                    <FeedText>{heading}</FeedText>
                    {description && (
                      <Typography sx={{ mt: 1, fontSize: '12px', color: '#6b7280', whiteSpace: 'pre-line', wordBreak: 'break-word' }}>{description}</Typography>
                    )}
                  </FeedContent>
                  <IconButton
                    className="edit-icon"
                    onClick={(e) => handleEditPost(post, e)}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      opacity: 0,
                      transition: 'opacity 0.2s',
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      '&:hover': {
                        backgroundColor: '#EFF6FF',
                        color: '#0B91D6'
                      }
                    }}
                    size="small"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </FeedCardItem>
              );
            })}
            {(!localPosts || localPosts.length === 0) && (
              <Typography sx={{ color: '#6b7280' }}>No local posts found.</Typography>
            )}
          </FeedGrid>
        </ModalContent>
      </ModalContainer>

      {/* Photos Modal */}
      <ModalContainer open={photosModalOpen} onClose={() => setPhotosModalOpen(false)}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>All Photos ({mediaItems.length})</ModalTitle>
            <CloseButton onClick={() => setPhotosModalOpen(false)}>
              <CloseIcon />
            </CloseButton>
          </ModalHeader>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 3 }}>
            {mediaItems && mediaItems.length > 0 ? (
              mediaItems.map((photo, index) => {
                const originalSrc = photo.googleUrl || photo.thumbnailUrl || photo.sourceUrl || '';
                const proxied = originalSrc ? buildProxyUrl(originalSrc) : null;
                const initialSrc = proxied || originalSrc;

                return (
                  <Box key={photo.name || index} sx={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    position: 'relative',
                    transition: 'box-shadow 0.2s ease',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                    },
                    '&:hover .delete-button': {
                      opacity: 1
                    }
                  }}>
                    <Box
                      sx={{
                        position: 'relative',
                        width: '100%',
                        height: '200px',
                        backgroundColor: '#f3f4f6',
                        cursor: 'pointer'
                      }}
                      onClick={() => handlePhotoClick(originalSrc, proxied)}
                    >
                      <ModalImage
                        src={initialSrc}
                        alt={`Photo ${index + 1}`}
                        referrerPolicy="no-referrer"
                        crossOrigin="anonymous"
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        data-original={originalSrc}
                        data-proxy={proxied || ''}
                        data-retry="0"
                        onError={(e) => {
                          const img = e.target;
                          const retry = parseInt(img.dataset.retry || '0');
                          const og = img.dataset.original;
                          const proxy = img.dataset.proxy;
                          if (retry === 0 && proxy && img.src === proxy && og) {
                            img.dataset.retry = '1';
                            img.src = og;
                          } else {
                            img.src = placeholderLarge;
                          }
                        }}
                      />
                      {photo.category && (
                        <Box sx={{
                          position: 'absolute',
                          top: 8,
                          left: 8,
                          backgroundColor: 'rgba(0, 0, 0, 0.7)',
                          color: '#ffffff',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: 500
                        }}>
                          {photo.category === 'COVER' ? 'Cover' : photo.category === 'PROFILE' ? 'Profile' : 'Photo'}
                        </Box>
                      )}
                    </Box>
                    <IconButton
                      className="delete-button"
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (window.confirm('Are you sure you want to delete this photo from GMB?')) {
                          try {
                            setDeletingMediaId(photo.name);
                            const googleAccessToken = localStorage.getItem('googleAccessToken');
                            const accountId = currentAccount?.name?.split('/').pop();

                            await ApiService.request('/gmb/delete-media', {
                              method: 'DELETE',
                              headers: {
                                'Authorization': `Bearer ${googleAccessToken}`,
                                'Content-Type': 'application/json'
                              },
                              body: JSON.stringify({
                                accountId,
                                locationId,
                                mediaName: photo.name
                              })
                            });

                            // Refresh media items
                            const items = await GMBService.getMedia(undefined, accountId, locationId);
                            const filteredItems = items.filter(i => (i.mediaFormat === 'PHOTO' || !i.mediaFormat) && (i.googleUrl || i.thumbnailUrl || i.sourceUrl));
                            setMediaItems(filteredItems);

                            logger.log('Photo deleted successfully');
                          } catch (error) {
                            logger.error('Failed to delete photo:', error);
                            alert('Failed to delete photo: ' + (error.message || 'Unknown error'));
                          } finally {
                            setDeletingMediaId(null);
                          }
                        }
                      }}
                      disabled={deletingMediaId === photo.name}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        backgroundColor: 'rgba(239, 68, 68, 0.9)',
                        color: '#ffffff',
                        opacity: 0,
                        transition: 'opacity 0.2s ease',
                        '&:hover': {
                          backgroundColor: '#dc2626'
                        },
                        '&:disabled': {
                          backgroundColor: '#9ca3af'
                        }
                      }}
                    >
                      {deletingMediaId === photo.name ? (
                        <CircularProgress size={20} sx={{ color: '#ffffff' }} />
                      ) : (
                        <DeleteIcon fontSize="small" />
                      )}
                    </IconButton>
                    <Box sx={{ p: 2 }}>
                      <Typography sx={{
                        fontSize: '12px',
                        color: '#6b7280'
                      }}>
                        {photo.createTime ? new Date(photo.createTime).toLocaleDateString() : 'Unknown date'}
                      </Typography>
                    </Box>
                  </Box>
                );
              })
            ) : (
              <Box sx={{ gridColumn: '1 / -1', textAlign: 'center', py: 4, color: '#6b7280' }}>
                <Typography>No photos available</Typography>
              </Box>
            )}
          </Box>
        </ModalContent>
      </ModalContainer>

      {/* Videos Modal */}
      <ModalContainer open={videosModalOpen} onClose={handleCloseVideosModal}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>All Videos ({videos.length})</ModalTitle>
            <CloseButton onClick={handleCloseVideosModal}>
              <CloseIcon />
            </CloseButton>
          </ModalHeader>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 3 }}>
            {videos.length > 0 ? (
              videos.map((video) => (
                <Box key={video.id} sx={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  position: 'relative',
                  cursor: 'pointer',
                  transition: 'box-shadow 0.2s ease',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                  },
                  '&:hover .delete-button': {
                    opacity: 1
                  }
                }}>
                  <Box
                    sx={{
                      position: 'relative',
                      width: '100%',
                      height: '140px',
                      backgroundColor: '#f3f4f6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onClick={() => handleVideoClick(video)}
                  >
                    {video.thumbnail ? (
                      <ModalImage
                        src={buildProxyUrl(video.thumbnail) || video.thumbnail}
                        alt={video.name}
                        referrerPolicy="no-referrer"
                        crossOrigin="anonymous"
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={(e) => {
                          e.target.src = placeholderLarge;
                        }}
                      />
                    ) : (
                      <ModalImage src={placeholderLarge} alt="No thumbnail" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    )}
                    <Box sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      borderRadius: '50%',
                      width: '48px',
                      height: '48px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Box sx={{
                        width: 0,
                        height: 0,
                        borderLeft: '12px solid #ffffff',
                        borderTop: '8px solid transparent',
                        borderBottom: '8px solid transparent',
                        marginLeft: '3px'
                      }} />
                    </Box>
                    <IconButton
                      className="delete-button"
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (window.confirm('Are you sure you want to delete this video from GMB?')) {
                          try {
                            setDeletingMediaId(video.id);
                            const googleAccessToken = localStorage.getItem('googleAccessToken');
                            const accountId = currentAccount?.name?.split('/').pop();

                            // Find the full media name from the video
                            const allMedia = await GMBService.getMedia(undefined, accountId, locationId);
                            const videoMedia = allMedia.find(m => m.name && m.name.includes(video.id));

                            if (videoMedia) {
                              await ApiService.request('/gmb/delete-media', {
                                method: 'DELETE',
                                headers: {
                                  'Authorization': `Bearer ${googleAccessToken}`,
                                  'Content-Type': 'application/json'
                                },
                                body: JSON.stringify({
                                  accountId,
                                  locationId,
                                  mediaName: videoMedia.name
                                })
                              });

                              // Refresh videos
                              await refreshVideos();
                              logger.log('Video deleted successfully');
                            } else {
                              throw new Error('Video not found');
                            }
                          } catch (error) {
                            logger.error('Failed to delete video:', error);
                            alert('Failed to delete video: ' + (error.message || 'Unknown error'));
                          } finally {
                            setDeletingMediaId(null);
                          }
                        }
                      }}
                      disabled={deletingMediaId === video.id}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        backgroundColor: 'rgba(239, 68, 68, 0.9)',
                        color: '#ffffff',
                        opacity: 0,
                        transition: 'opacity 0.2s ease',
                        '&:hover': {
                          backgroundColor: '#dc2626'
                        },
                        '&:disabled': {
                          backgroundColor: '#9ca3af'
                        }
                      }}
                    >
                      {deletingMediaId === video.id ? (
                        <CircularProgress size={20} sx={{ color: '#ffffff' }} />
                      ) : (
                        <DeleteIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Box>
                  <Box sx={{ p: 2 }}>
                    <Typography sx={{
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#121927',
                      mb: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {video.name}
                    </Typography>
                    <Typography sx={{
                      fontSize: '12px',
                      color: '#6b7280',
                      mb: 0.5
                    }}>
                      Duration: {video.duration}
                    </Typography>
                    <Typography sx={{
                      fontSize: '12px',
                      color: '#6b7280'
                    }}>
                      {video.uploadDate ? new Date(video.uploadDate).toLocaleDateString() : 'Unknown date'}
                    </Typography>
                  </Box>
                </Box>
              ))
            ) : (
              <Box sx={{
                gridColumn: '1 / -1',
                textAlign: 'center',
                py: 4,
                color: '#6b7280'
              }}>
                <Typography>No videos available</Typography>
              </Box>
            )}
          </Box>
        </ModalContent>
      </ModalContainer>

      {/* Social Feed Modal */}
      <ModalContainer open={socialFeedModalOpen} onClose={() => setSocialFeedModalOpen(false)}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>All Social Posts</ModalTitle>
            <CloseButton onClick={() => setSocialFeedModalOpen(false)}>
              <CloseIcon />
            </CloseButton>
          </ModalHeader>
          <FeedGrid>
            {[...mockSocialFeed, ...mockSocialFeed, ...mockSocialFeed].map((item, index) => (
              <FeedCardItem key={`social-all-${index}`}>
                <FeedImage src={item.image} alt={`Social Feed ${index + 1}`} />
                <FeedContent>
                  <FeedDate>{item.date}</FeedDate>
                  <FeedText>{item.text}</FeedText>
                </FeedContent>
              </FeedCardItem>
            ))}
          </FeedGrid>
        </ModalContent>
      </ModalContainer>

      {/* Service Details Modal */}
      <ModalContainer open={serviceDetailsModalOpen} onClose={() => setServiceDetailsModalOpen(false)}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Service Details</ModalTitle>
            <CloseButton onClick={() => setServiceDetailsModalOpen(false)}>
              <CloseIcon />
            </CloseButton>
          </ModalHeader>

          {serviceDetailsLoading ? (
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '200px'
            }}>
              <CircularProgress size={32} />
            </Box>
          ) : selectedService ? (
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" sx={{
                fontWeight: 600,
                color: '#121927',
                mb: 2
              }}>
                {selectedService.name}
              </Typography>

              {selectedService.category && (
                <Box sx={{ mb: 2 }}>
                  <Chip
                    label={selectedService.category}
                    size="small"
                    sx={{
                      backgroundColor: '#e3f2fd',
                      color: '#1976d2',
                      fontWeight: 500
                    }}
                  />
                </Box>
              )}

              <Typography sx={{
                fontSize: '14px',
                color: '#374151',
                lineHeight: 1.6,
                mb: 2
              }}>
                {selectedService.description}
              </Typography>

              {selectedService.price && (
                <Box sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0'
                }}>
                  <Typography sx={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#059669'
                  }}>
                    Price: {selectedService.price}
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{
              p: 3,
              textAlign: 'center',
              color: '#6b7280'
            }}>
              <Typography>No service details available</Typography>
            </Box>
          )}
        </ModalContent>
      </ModalContainer>

      {/* Add Event Modal */}
      <EventModalContainer open={addEventModalOpen} onClose={() => setAddEventModalOpen(false)}>
        <EventModalContent>
          <EventModalHeader>
            <EventModalTitle>Add post</EventModalTitle>
            <CloseButton onClick={() => setAddEventModalOpen(false)} aria-label="Close">
              <CloseIcon />
            </CloseButton>
          </EventModalHeader>

          {/* Event Type Tabs */}
          <Box sx={{ display: 'flex', marginBottom: '20px', borderBottom: '1px solid #e5e7eb' }}>
            <EventTypeTab
              active={eventForm.type === 'Update'}
              onClick={() => setEventForm({ ...eventForm, type: 'Update' })}
            >
              Update
            </EventTypeTab>
            <EventTypeTab
              active={eventForm.type === 'Offer'}
              onClick={() => setEventForm({ ...eventForm, type: 'Offer' })}
            >
              Offer
            </EventTypeTab>
            <EventTypeTab
              active={eventForm.type === 'Event'}
              onClick={() => setEventForm({ ...eventForm, type: 'Event' })}
            >
              Event
            </EventTypeTab>
          </Box>

          <EventFormContainer>
            {/* Title Field - Only for Offer and Event types */}
            {(eventForm.type === 'Offer' || eventForm.type === 'Event') && (
              <EventDescriptionField
                placeholder="Title*"
                value={eventForm.title}
                onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                variant="outlined"
                fullWidth
                InputProps={{
                  endAdornment: (
                    <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '12px' }}>
                      {eventForm.title.length}/58
                    </Typography>
                  )
                }}
              />
            )}

            {/* Description Field */}
            <EventDescriptionField
              multiline
              rows={2}
              placeholder="Description"
              value={eventForm.description}
              onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
              variant="outlined"
              fullWidth
              InputProps={{
                endAdornment: (
                  <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '12px' }}>
                    {eventForm.description.length}/1,500
                  </Typography>
                )
              }}
            />

            {/* Date and Time Fields - Only for Offer and Event types */}
            {(eventForm.type === 'Offer' || eventForm.type === 'Event') && (
              <>
                <Box sx={{ display: 'flex', gap: '16px' }}>
                  <EventDescriptionField
                    type="date"
                    label="Start date*"
                    value={eventForm.startDate}
                    onChange={(e) => setEventForm({ ...eventForm, startDate: e.target.value })}
                    variant="outlined"
                    sx={{ flex: 1 }}
                    InputLabelProps={{ shrink: true }}
                  />
                  <EventDescriptionField
                    type="time"
                    label="Start time"
                    value={eventForm.startTime}
                    onChange={(e) => setEventForm({ ...eventForm, startTime: e.target.value })}
                    variant="outlined"
                    sx={{ flex: 1 }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: '16px' }}>
                  <EventDescriptionField
                    type="date"
                    label="End Date*"
                    value={eventForm.endDate}
                    onChange={(e) => setEventForm({ ...eventForm, endDate: e.target.value })}
                    variant="outlined"
                    sx={{ flex: 1 }}
                    InputLabelProps={{ shrink: true }}
                  />
                  <EventDescriptionField
                    type="time"
                    label="End time"
                    value={eventForm.endTime}
                    onChange={(e) => setEventForm({ ...eventForm, endTime: e.target.value })}
                    variant="outlined"
                    sx={{ flex: 1 }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              </>
            )}

            {/* Add more details section */}
            <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
              Add more details
            </Typography>

            {/* Conditional buttons based on type */}
            <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {eventForm.type === 'Offer' && (
                <>
                  <Button
                    variant="outlined"
                    startIcon={activeOfferFields.terms ?
                      <Box sx={{ width: 16, height: 16, backgroundColor: '#0B91D6', borderRadius: '2px', position: 'relative' }}>
                        <Box sx={{ position: 'absolute', top: '2px', right: '2px', width: '8px', height: '8px', color: 'white', fontSize: '10px', lineHeight: '8px', textAlign: 'center' }}>×</Box>
                      </Box> :
                      <Box sx={{ width: 16, height: 16, backgroundColor: '#0B91D6', borderRadius: '2px' }} />
                    }
                    onClick={() => {
                      setActiveOfferFields(prev => ({
                        ...prev,
                        terms: !prev.terms
                      }));
                      if (activeOfferFields.terms) {
                        setEventForm(prev => ({ ...prev, terms: '' }));
                      }
                    }}
                    sx={{
                      color: '#0B91D6',
                      borderColor: '#0B91D6',
                      textTransform: 'none',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '14px',
                      '&:hover': {
                        borderColor: '#0980c2',
                        backgroundColor: 'rgba(11, 145, 214, 0.04)'
                      }
                    }}
                  >
                    Terms
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={activeOfferFields.couponCode ?
                      <Box sx={{ width: 16, height: 16, backgroundColor: '#0B91D6', borderRadius: '2px', position: 'relative' }}>
                        <Box sx={{ position: 'absolute', top: '2px', right: '2px', width: '8px', height: '8px', color: 'white', fontSize: '10px', lineHeight: '8px', textAlign: 'center' }}>×</Box>
                      </Box> :
                      <Box sx={{ width: 16, height: 16, backgroundColor: '#0B91D6', borderRadius: '2px' }} />
                    }
                    onClick={() => {
                      setActiveOfferFields(prev => ({
                        ...prev,
                        couponCode: !prev.couponCode
                      }));
                      if (activeOfferFields.couponCode) {
                        setEventForm(prev => ({ ...prev, couponCode: '' }));
                      }
                    }}
                    sx={{
                      color: '#0B91D6',
                      borderColor: '#0B91D6',
                      textTransform: 'none',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '14px',
                      '&:hover': {
                        borderColor: '#0980c2',
                        backgroundColor: 'rgba(11, 145, 214, 0.04)'
                      }
                    }}
                  >
                    Coupon code
                  </Button>
                </>
              )}
              {eventForm.type === 'Event' && (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<Box sx={{ width: 16, height: 16, backgroundColor: '#0B91D6', borderRadius: '2px' }} />}
                    endIcon={<Box sx={{ width: 0, height: 0, borderLeft: '4px solid transparent', borderRight: '4px solid transparent', borderTop: '4px solid #0B91D6' }} />}
                    onClick={(e) => {
                      setButtonAnchorEl(e.currentTarget);
                      setButtonDropdownOpen(true);
                    }}
                    sx={{
                      color: '#0B91D6',
                      borderColor: '#0B91D6',
                      textTransform: 'none',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '14px',
                      '&:hover': {
                        borderColor: '#0980c2',
                        backgroundColor: 'rgba(11, 145, 214, 0.04)'
                      }
                    }}
                  >
                    {eventForm.selectedButton === 'None' ? 'Button' : eventForm.selectedButton}
                  </Button>
                  <Menu
                    anchorEl={buttonAnchorEl}
                    open={buttonDropdownOpen}
                    onClose={() => {
                      setButtonDropdownOpen(false);
                      setButtonAnchorEl(null);
                    }}
                    PaperProps={{
                      sx: {
                        mt: 1,
                        minWidth: 200,
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                        border: '1px solid #e5e7eb'
                      }
                    }}
                  >
                    <Box sx={{ p: 2, borderBottom: '1px solid #e5e7eb' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151' }}>
                        Add a button (optional)
                      </Typography>
                    </Box>
                    <MenuItem
                      onClick={() => {
                        setEventForm(prev => ({ ...prev, selectedButton: 'None' }));
                        setButtonDropdownOpen(false);
                        setButtonAnchorEl(null);
                      }}
                      sx={{
                        py: 1.5,
                        px: 2,
                        color: '#374151',
                        '&:hover': { backgroundColor: '#f9fafb' }
                      }}
                    >
                      None
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setEventForm(prev => ({ ...prev, selectedButton: 'Book' }));
                        setButtonDropdownOpen(false);
                        setButtonAnchorEl(null);
                      }}
                      sx={{
                        py: 1.5,
                        px: 2,
                        color: '#374151',
                        '&:hover': { backgroundColor: '#f9fafb' }
                      }}
                    >
                      Book
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setEventForm(prev => ({ ...prev, selectedButton: 'Order online' }));
                        setButtonDropdownOpen(false);
                        setButtonAnchorEl(null);
                      }}
                      sx={{
                        py: 1.5,
                        px: 2,
                        color: '#374151',
                        '&:hover': { backgroundColor: '#f9fafb' }
                      }}
                    >
                      Order online
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setEventForm(prev => ({ ...prev, selectedButton: 'Buy' }));
                        setButtonDropdownOpen(false);
                        setButtonAnchorEl(null);
                      }}
                      sx={{
                        py: 1.5,
                        px: 2,
                        color: '#374151',
                        '&:hover': { backgroundColor: '#f9fafb' }
                      }}
                    >
                      Buy
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setEventForm(prev => ({ ...prev, selectedButton: 'Learn more' }));
                        setButtonDropdownOpen(false);
                        setButtonAnchorEl(null);
                      }}
                      sx={{
                        py: 1.5,
                        px: 2,
                        color: '#374151',
                        '&:hover': { backgroundColor: '#f9fafb' }
                      }}
                    >
                      Learn more
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setEventForm(prev => ({ ...prev, selectedButton: 'Sign up' }));
                        setButtonDropdownOpen(false);
                        setButtonAnchorEl(null);
                      }}
                      sx={{
                        py: 1.5,
                        px: 2,
                        color: '#374151',
                        '&:hover': { backgroundColor: '#f9fafb' }
                      }}
                    >
                      Sign up
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setEventForm(prev => ({ ...prev, selectedButton: 'Call now' }));
                        setButtonDropdownOpen(false);
                        setButtonAnchorEl(null);
                      }}
                      sx={{
                        py: 1.5,
                        px: 2,
                        color: '#374151',
                        '&:hover': { backgroundColor: '#f9fafb' }
                      }}
                    >
                      Call now
                    </MenuItem>
                  </Menu>

                  {/* Link input field - Only show when a button other than None is selected */}
                  {eventForm.selectedButton !== 'None' && (
                    <Box sx={{ mt: 2, width: '100%' }}>
                      <EventDescriptionField
                        placeholder="Link for your button*"
                        value={eventForm.buttonLink}
                        onChange={(e) => setEventForm({ ...eventForm, buttonLink: e.target.value })}
                        variant="outlined"
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: '#0B91D6',
                            },
                            '&:hover fieldset': {
                              borderColor: '#0980c2',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#0B91D6',
                            },
                          },
                          '& .MuiInputBase-input': {
                            color: '#0B91D6',
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '14px'
                          },
                          '& .MuiInputBase-input::placeholder': {
                            color: '#0B91D6',
                            opacity: 1
                          }
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#6b7280',
                          fontSize: '12px',
                          mt: 0.5,
                          display: 'block'
                        }}
                      >
                        (Example: https://google.com)
                      </Typography>
                    </Box>
                  )}
                </>
              )}
              {eventForm.type === 'Update' && (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<Box sx={{ width: 16, height: 16, backgroundColor: '#0B91D6', borderRadius: '2px' }} />}
                    endIcon={<Box sx={{ width: 0, height: 0, borderLeft: '4px solid transparent', borderRight: '4px solid transparent', borderTop: '4px solid #0B91D6' }} />}
                    onClick={(e) => {
                      setButtonAnchorEl(e.currentTarget);
                      setButtonDropdownOpen(true);
                    }}
                    sx={{
                      color: '#0B91D6',
                      borderColor: '#0B91D6',
                      textTransform: 'none',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '14px',
                      '&:hover': {
                        borderColor: '#0980c2',
                        backgroundColor: 'rgba(11, 145, 214, 0.04)'
                      }
                    }}
                  >
                    {eventForm.selectedButton === 'None' ? 'Button' : eventForm.selectedButton}
                  </Button>
                  <Menu
                    anchorEl={buttonAnchorEl}
                    open={buttonDropdownOpen}
                    onClose={() => {
                      setButtonDropdownOpen(false);
                      setButtonAnchorEl(null);
                    }}
                    PaperProps={{
                      sx: {
                        mt: 1,
                        minWidth: 200,
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                        border: '1px solid #e5e7eb'
                      }
                    }}
                  >
                    <Box sx={{ p: 2, borderBottom: '1px solid #e5e7eb' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151' }}>
                        Add a button (optional)
                      </Typography>
                    </Box>
                    <MenuItem
                      onClick={() => {
                        setEventForm(prev => ({ ...prev, selectedButton: 'None' }));
                        setButtonDropdownOpen(false);
                        setButtonAnchorEl(null);
                      }}
                      sx={{
                        py: 1.5,
                        px: 2,
                        color: '#374151',
                        '&:hover': { backgroundColor: '#f9fafb' }
                      }}
                    >
                      None
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setEventForm(prev => ({ ...prev, selectedButton: 'Book' }));
                        setButtonDropdownOpen(false);
                        setButtonAnchorEl(null);
                      }}
                      sx={{
                        py: 1.5,
                        px: 2,
                        color: '#374151',
                        '&:hover': { backgroundColor: '#f9fafb' }
                      }}
                    >
                      Book
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setEventForm(prev => ({ ...prev, selectedButton: 'Order online' }));
                        setButtonDropdownOpen(false);
                        setButtonAnchorEl(null);
                      }}
                      sx={{
                        py: 1.5,
                        px: 2,
                        color: '#374151',
                        '&:hover': { backgroundColor: '#f9fafb' }
                      }}
                    >
                      Order online
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setEventForm(prev => ({ ...prev, selectedButton: 'Buy' }));
                        setButtonDropdownOpen(false);
                        setButtonAnchorEl(null);
                      }}
                      sx={{
                        py: 1.5,
                        px: 2,
                        color: '#374151',
                        '&:hover': { backgroundColor: '#f9fafb' }
                      }}
                    >
                      Buy
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setEventForm(prev => ({ ...prev, selectedButton: 'Learn more' }));
                        setButtonDropdownOpen(false);
                        setButtonAnchorEl(null);
                      }}
                      sx={{
                        py: 1.5,
                        px: 2,
                        color: '#374151',
                        '&:hover': { backgroundColor: '#f9fafb' }
                      }}
                    >
                      Learn more
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setEventForm(prev => ({ ...prev, selectedButton: 'Sign up' }));
                        setButtonDropdownOpen(false);
                        setButtonAnchorEl(null);
                      }}
                      sx={{
                        py: 1.5,
                        px: 2,
                        color: '#374151',
                        '&:hover': { backgroundColor: '#f9fafb' }
                      }}
                    >
                      Sign up
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setEventForm(prev => ({ ...prev, selectedButton: 'Call now' }));
                        setButtonDropdownOpen(false);
                        setButtonAnchorEl(null);
                      }}
                      sx={{
                        py: 1.5,
                        px: 2,
                        color: '#374151',
                        '&:hover': { backgroundColor: '#f9fafb' }
                      }}
                    >
                      Call now
                    </MenuItem>
                  </Menu>

                  {/* Link input field - Only show when a button other than None is selected */}
                  {eventForm.selectedButton !== 'None' && (
                    <Box sx={{ mt: 2, width: '100%' }}>
                      <EventDescriptionField
                        placeholder="Link for your button*"
                        value={eventForm.buttonLink}
                        onChange={(e) => setEventForm({ ...eventForm, buttonLink: e.target.value })}
                        variant="outlined"
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: '#0B91D6',
                            },
                            '&:hover fieldset': {
                              borderColor: '#0980c2',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#0B91D6',
                            },
                          },
                          '& .MuiInputBase-input': {
                            color: '#0B91D6',
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '14px'
                          },
                          '& .MuiInputBase-input::placeholder': {
                            color: '#0B91D6',
                            opacity: 1
                          }
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#6b7280',
                          fontSize: '12px',
                          mt: 0.5,
                          display: 'block'
                        }}
                      >
                        (Example: https://google.com)
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </Box>

            {/* Link to redeem offer - Only for Offer type */}
            {eventForm.type === 'Offer' && (
              <Button
                variant="outlined"
                startIcon={activeOfferFields.redeemLink ?
                  <Box sx={{ width: 16, height: 16, backgroundColor: '#0B91D6', borderRadius: '2px', position: 'relative' }}>
                    <Box sx={{ position: 'absolute', top: '2px', right: '2px', width: '8px', height: '8px', color: 'white', fontSize: '10px', lineHeight: '8px', textAlign: 'center' }}>×</Box>
                  </Box> :
                  <Box sx={{ width: 16, height: 16, backgroundColor: '#0B91D6', borderRadius: '2px' }} />
                }
                onClick={() => {
                  setActiveOfferFields(prev => ({
                    ...prev,
                    redeemLink: !prev.redeemLink
                  }));
                  if (activeOfferFields.redeemLink) {
                    setEventForm(prev => ({ ...prev, redeemLink: '' }));
                  }
                }}
                sx={{
                  color: '#0B91D6',
                  borderColor: '#0B91D6',
                  textTransform: 'none',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  alignSelf: 'flex-start',
                  '&:hover': {
                    borderColor: '#0980c2',
                    backgroundColor: 'rgba(11, 145, 214, 0.04)'
                  }
                }}
              >
                Link to redeem offer
              </Button>
            )}

            {/* Input fields for active Offer options */}
            {eventForm.type === 'Offer' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
                {activeOfferFields.terms && (
                  <EventDescriptionField
                    multiline
                    rows={2}
                    placeholder="Terms"
                    value={eventForm.terms}
                    onChange={(e) => setEventForm({ ...eventForm, terms: e.target.value })}
                    variant="outlined"
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '12px' }}>
                          {eventForm.terms.length}/5,000
                        </Typography>
                      )
                    }}
                  />
                )}
                {activeOfferFields.couponCode && (
                  <EventDescriptionField
                    placeholder="Coupon code"
                    value={eventForm.couponCode}
                    onChange={(e) => setEventForm({ ...eventForm, couponCode: e.target.value })}
                    variant="outlined"
                    fullWidth
                  />
                )}
                {activeOfferFields.redeemLink && (
                  <Box>
                    <EventDescriptionField
                      placeholder="Link to redeem offer"
                      value={eventForm.redeemLink}
                      onChange={(e) => setEventForm({ ...eventForm, redeemLink: e.target.value })}
                      variant="outlined"
                      fullWidth
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6b7280',
                        fontSize: '12px',
                        mt: 0.5,
                        display: 'block'
                      }}
                    >
                      (Example: https://google.com)
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Image Upload Area */}
            <EventImageUploadArea
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                setDragOver(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                const files = Array.from(e.dataTransfer.files);
                const imageFiles = files.filter(file =>
                  file.type.startsWith('image/') || file.type.startsWith('video/')
                );
                setEventForm(prev => ({
                  ...prev,
                  images: [...prev.images, ...imageFiles]
                }));
              }}
              sx={{
                backgroundColor: dragOver ? 'rgba(11, 145, 214, 0.04)' : 'transparent',
                borderColor: dragOver ? '#0B91D6' : '#e5e7eb',
                transition: 'all 0.2s ease-in-out'
              }}
            >
              <EventImageUploadText>
                Drag images and videos here
              </EventImageUploadText>
              <EventImageUploadText sx={{ marginBottom: '16px' }}>
                or
              </EventImageUploadText>
              <EventImageUploadButton
                component="label"
                sx={{ cursor: 'pointer' }}
              >
                Select images and videos
                <input
                  type="file"
                  multiple
                  accept="image/*,video/*"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const files = Array.from(e.target.files);
                    setEventForm(prev => ({
                      ...prev,
                      images: [...prev.images, ...files]
                    }));
                  }}
                />
              </EventImageUploadButton>

              {/* Display uploaded files */}
              {eventForm.images.length > 0 && (
                <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {eventForm.images.map((file, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        backgroundColor: '#f3f4f6',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        color: '#374151'
                      }}
                    >
                      <span>{file.name}</span>
                      <Box
                        component="button"
                        onClick={() => {
                          setEventForm(prev => ({
                            ...prev,
                            images: prev.images.filter((_, i) => i !== index)
                          }));
                        }}
                        sx={{
                          ml: 1,
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#6b7280',
                          fontSize: '14px',
                          '&:hover': { color: '#374151' }
                        }}
                      >
                        ×
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </EventImageUploadArea>

            {/* Post Button */}
            <EventPostButton
              disabled={!eventForm.description.trim()}
              onClick={() => {
                // Handle post submission here
                logger.log('Event form submitted:', eventForm);
                setAddEventModalOpen(false);
                // Reset form
                setEventForm({
                  title: '',
                  description: '',
                  startDate: '',
                  startTime: '',
                  endDate: '',
                  endTime: '',
                  type: 'Update',
                  images: [],
                  selectedButton: 'None',
                  buttonLink: '',
                  terms: '',
                  couponCode: '',
                  redeemLink: ''
                });
                setActiveOfferFields({
                  terms: false,
                  couponCode: false,
                  redeemLink: false
                });
              }}
            >
              Post
            </EventPostButton>
          </EventFormContainer>
        </EventModalContent>
      </EventModalContainer>

      {/* Full Calendar Modal */}
      <FullCalendarModal
        open={fullCalendarModalOpen}
        onClose={() => setFullCalendarModalOpen(false)}
      >
        <FullCalendarContent>
          <FullCalendarHeader>
            <FullCalendarTitle>Full Calendar View</FullCalendarTitle>
            <IconButton
              onClick={() => setFullCalendarModalOpen(false)}
              sx={{
                color: '#6b7280',
                '&:hover': {
                  backgroundColor: 'rgba(107, 114, 128, 0.1)'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </FullCalendarHeader>

          <FullCalendarContainer>
            <CalendarHeader>
              <CalendarNavButton onClick={() => navigateMonth(-1)}>
                <ChevronLeftIcon />
              </CalendarNavButton>
              <CalendarMonth>{getMonthName(currentDate)}</CalendarMonth>
              <CalendarNavButton onClick={() => navigateMonth(1)}>
                <ChevronRightIcon />
              </CalendarNavButton>
            </CalendarHeader>
            <FullCalendarGrid>
              {/* Day headers */}
              {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map(day => (
                <CalendarDayHeader key={day} sx={{ fontSize: '12px', fontWeight: 600 }}>{day}</CalendarDayHeader>
              ))}
              {/* Calendar days */}
              {generateCalendarDays().map((day, index) => {
                const dayDate = day ? new Date(currentDate.getFullYear(), currentDate.getMonth(), day) : null;
                const dayEvents = dayDate ? calendarEvents.filter(event => isSameDay(event.date, dayDate)) : [];
                const scheduledPostsCount = dayEvents.filter(e => e.type === 'scheduled_post').length;
                const hasScheduledPost = scheduledPostsCount > 0;
                const firstEvent = dayEvents[0];

                return (
                  <FullCalendarDay
                    key={index}
                    isToday={day && isToday(day)}
                    hasEvent={day && dayEvents.length > 0}
                    isOtherMonth={!day}
                    onClick={() => handleCalendarDayClick(day)}
                    sx={{
                      cursor: day && dayEvents.length > 0 ? 'pointer' : 'default',
                      position: 'relative',
                      '&:hover': day && dayEvents.length > 0 ? {
                        backgroundColor: 'rgba(11, 145, 214, 0.1)',
                        transform: 'scale(1.02)'
                      } : {}
                    }}
                  >
                    {day || ''}
                    {day && dayEvents.length > 0 && (
                      <>
                        <EventDot isScheduledPost={hasScheduledPost} />
                        {scheduledPostsCount > 1 && (
                          <Box sx={{
                            position: 'absolute',
                            top: '4px',
                            right: '4px',
                            backgroundColor: '#34A853',
                            color: '#FFFFFF',
                            fontSize: '10px',
                            fontWeight: 700,
                            borderRadius: '50%',
                            width: '16px',
                            height: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                          }}>
                            {scheduledPostsCount}
                          </Box>
                        )}
                        <Box sx={{
                          position: 'absolute',
                          bottom: '8px',
                          left: '4px',
                          right: '4px',
                          fontSize: '10px',
                          color: hasScheduledPost ? '#34A853' : '#0B91D6',
                          fontWeight: 500,
                          textAlign: 'center'
                        }}>
                          {scheduledPostsCount > 1 ? `${scheduledPostsCount} posts` : firstEvent?.title}
                        </Box>
                      </>
                    )}
                  </FullCalendarDay>
                );
              })}
            </FullCalendarGrid>
          </FullCalendarContainer>

          {/* Calendar Legend */}
          <Box sx={{
            display: 'flex',
            gap: '24px',
            justifyContent: 'center',
            mt: 3,
            mb: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Box sx={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                backgroundColor: '#34A853',
                boxShadow: '0 0 4px rgba(52, 168, 83, 0.5)'
              }} />
              <Typography sx={{ fontSize: '13px', color: '#666666' }}>Scheduled Post</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Box sx={{
                width: '4px',
                height: '4px',
                borderRadius: '50%',
                backgroundColor: '#0B91D6'
              }} />
              <Typography sx={{ fontSize: '13px', color: '#666666' }}>Event</Typography>
            </Box>
          </Box>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
            <CalendarActionButton onClick={() => {
              setFullCalendarModalOpen(false);
              handleAddEvent();
            }}>
              Add New Event
            </CalendarActionButton>
          </Box>
        </FullCalendarContent>
      </FullCalendarModal>

      {/* Enhanced Competitor Analysis Modal */}
      <CompetitorAnalysisWithPopup
        open={competitorAnalysisOpen}
        onClose={() => {
          setCompetitorAnalysisOpen(false);
          // Force reload data after modal closes
          setTimeout(() => {
            logger.log('🔄 [Modal Close] Reloading competitor data...');
            searchAndLoadCompetitorData();
          }, 500);
        }}
        businessData={{
          ...locationData,
          name: businessTitle // Use businessTitle as the name for consistency
        }}
        businessLocation={{
          lat: locationData?.latlng?.latitude || pickedLatLng?.lat,
          lng: locationData?.latlng?.longitude || pickedLatLng?.lng
        }}
        businessName={businessTitle}
        locationId={locationId}
        analysisLocked={!canRunAnalysis}
        daysUntilNextAnalysis={daysUntilNextAnalysis}
        currentPlan={planCode || 'basic'}
        autoRunKeyword={autoRunKeyword}
        onAutoRunComplete={handleAutoRunComplete}
        autoOpenKeywordInput={autoOpenKeywordInput}
        onKeywordInputReady={() => setAutoOpenKeywordInput(false)}
        onAnalysisSaved={(data) => {
          logger.log('🎉 [Competitor Analysis] onAnalysisSaved callback triggered!');
          logger.log('🎉 [Competitor Analysis] Data received:', {
            hasCompetitors: !!data.competitors,
            competitorsLength: data.competitors?.length,
            myBusinessScore: data.myBusinessScore,
            keyword: data.keyword,
            timestamp: data.timestamp,
            isNewAnalysis: data.isNewAnalysis,
            greenGridPoints: data.greenGridPoints,
            yellowGridPoints: data.yellowGridPoints,
            redGridPoints: data.redGridPoints,
            grayGridPoints: data.grayGridPoints,
            totalGridPoints: data.totalGridPoints
          });

          // Update the competitor data immediately
          setCompetitorData(data);
          setDataLoadedFromCache(!data.isNewAnalysis); // Track if data is from cache
          if (data.myBusinessScore) {
            setGmbScore(data.myBusinessScore);
            logger.log('✅ [Competitor Analysis] GMB Score set to:', data.myBusinessScore);
          }

          // ✅ Save to Performance Tracker ALWAYS (for both new analysis and cache)
          // This ensures the Performance page always has the latest data
          if (data.myBusinessRank && data.myBusinessScore) {
            try {
              const trackingData = {
                rank: data.myBusinessRank,
                score: data.myBusinessScore,
                keyword: data.keyword || '',
                businessName: locationData?.title || locationData?.name || businessTitle || '',
                profileId: locationId || locationData?.location_id || null,
                // Add grid point data for performance page display
                greenGridPoints: data.greenGridPoints || 0,
                yellowGridPoints: data.yellowGridPoints || 0,
                redGridPoints: data.redGridPoints || 0,
                grayGridPoints: data.grayGridPoints || 0,
                totalGridPoints: data.totalGridPoints || 0,
                averageRank: data.myBusinessRank || 0
              };

              saveCompetitorTrackingSnapshot(trackingData);

              logger.log('✅ [Performance Tracker] Competitor data saved for tracking:', trackingData);
              logger.log('%c📊 Performance Tracker will now show this data!', 'color: blue; font-weight: bold; font-size: 14px;');
            } catch (error) {
              logger.error('❌ [Performance Tracker] Failed to save competitor tracking data:', error);
            }
          } else {
            logger.warn('⚠️ [Performance Tracker] Skipping save - missing rank or score:', {
              hasRank: !!data.myBusinessRank,
              hasScore: !!data.myBusinessScore
            });
          }

          // ✅ Only save to DB if this is a NEW analysis (not loaded from cache)
          if (!data.isNewAnalysis) {
            logger.log('ℹ️ [Competitor Analysis] Data loaded from cache, skipping database save');
            return;
          }

          // ✅ Save to Database
          const saveToDatabase = async () => {
            try {
              // Check if admin is viewing client - use client's user ID
              let userId;
              try {
                const adminAccessData = sessionStorage.getItem('adminAccessData');
                if (adminAccessData) {
                  const accessInfo = JSON.parse(adminAccessData);
                  userId = accessInfo.targetUserId;
                  logger.log('[Database Save] Admin viewing mode - using client user ID:', userId);
                }
              } catch (error) {
                logger.warn('[Database Save] Error checking admin access data:', error);
              }

              // Fallback to session user ID if not admin viewing
              if (!userId) {
                const session = getSession();
                userId = session?.id || session?.user?.id;
              }

              // Ensure userId is a number
              if (userId) {
                userId = parseInt(userId, 10);
              }

              if (!userId || isNaN(userId)) {
                logger.warn('⚠️ [Database Save] No valid user ID found, skipping database save');
                return;
              }

              logger.log('💾 [Database Save] Saving competitor analysis for user_id:', userId);

              const dbData = {
                user_id: userId,
                gmb_account_id: null, // Backend will lookup using location_id
                business_name: locationData?.title || locationData?.name || businessTitle || '',
                location_id: locationId || null,
                keyword: data.keyword || '',
                my_business_rank: data.myBusinessRank ?? 0,
                my_business_score: data.myBusinessScore ?? 0,
                total_competitors: data.competitors?.length || 0,
                // Save full data structure (same format as auto-run for consistency)
                // This allows HeatmapResultsPage to read gridData.results for heatmap display
                competitors_data: {
                  competitors: data.competitors || [],
                  gridData: data.gridData || null,
                  aroundMe: data.aroundMe || [],
                  searchLocation: data.searchLocation || null,
                  searchRadius: data.searchRadius || null,
                  gridSize: data.gridSize || null,
                  config: data.config || null,
                  timestamp: data.timestamp || new Date().toISOString()
                },
                latitude: locationData?.latlng?.latitude || pickedLatLng?.lat || null,
                longitude: locationData?.latlng?.longitude || pickedLatLng?.lng || null,
                search_location: locationData?.storefrontAddress?.locality || null,
                targetUserId: userId !== (getSession()?.id) ? userId : null, // Send targetUserId if different from session user (Admin Override)
                notes: null
              };

              // Log what we're about to send for debugging
              logger.log('💾 [Database Save] Data being sent:', {
                user_id: dbData.user_id,
                business_name: dbData.business_name,
                location_id: dbData.location_id,
                keyword: dbData.keyword,
                my_business_rank: dbData.my_business_rank,
                my_business_score: dbData.my_business_score,
                total_competitors: dbData.total_competitors
              });

              const response = await CompetitorAnalysisService.saveAnalysis(dbData);

              if (response.success) {
                logger.log('✅ [Database] Competitor analysis saved to database:', response.data);
                logger.log('%c💾 Data saved to database successfully!', 'color: green; font-weight: bold; font-size: 14px;');
              } else {
                logger.error('❌ [Database] Save failed with response:', response);
              }
            } catch (error) {
              logger.error('❌ [Database] Failed to save competitor analysis:', error);
              logger.error('❌ [Database] Error details:', error.response?.data || error.message);
              // Don't throw error - localStorage save already succeeded
            }
          };

          // Save to database asynchronously
          saveToDatabase();

          // ✅ Save heatmap/grid point data to database
          const saveHeatmapData = async () => {
            try {
              // Only save if we have grid data
              if (!data.gridData || !data.gridData.results) {
                logger.log('ℹ️ [Heatmap Data] No grid data available, skipping heatmap save');
                return;
              }

              // Get user ID (same logic as saveToDatabase)
              let heatmapUserId;
              try {
                const adminAccessData = sessionStorage.getItem('adminAccessData');
                if (adminAccessData) {
                  const accessInfo = JSON.parse(adminAccessData);
                  heatmapUserId = accessInfo.targetUserId;
                }
              } catch (_) { }

              if (!heatmapUserId) {
                const session = getSession();
                heatmapUserId = session?.id || session?.user?.id;
              }

              if (heatmapUserId) {
                heatmapUserId = parseInt(heatmapUserId, 10);
              }

              if (!heatmapUserId || isNaN(heatmapUserId)) {
                logger.warn('⚠️ [Heatmap Data] No valid user ID found, skipping heatmap save');
                return;
              }

              const gridResults = data.gridData.results;
              const greenPoints = data.greenGridPoints || gridResults.filter(r => r.targetBusinessRank !== null && r.targetBusinessRank <= 3).length;
              const yellowPoints = data.yellowGridPoints || gridResults.filter(r => r.targetBusinessRank !== null && r.targetBusinessRank >= 4 && r.targetBusinessRank <= 7).length;
              const redPoints = data.redGridPoints || gridResults.filter(r => r.targetBusinessRank !== null && r.targetBusinessRank >= 8 && r.targetBusinessRank <= 19).length;
              const grayPoints = data.grayGridPoints || gridResults.filter(r => r.targetBusinessRank === null || r.targetBusinessRank >= 20).length;
              const totalPoints = data.totalGridPoints || gridResults.length;

              const heatmapDbData = {
                user_id: heatmapUserId,
                location_id: locationId || null,
                business_name: locationData?.title || locationData?.name || businessTitle || '',
                keyword: data.keyword || '',
                grid_size: data.gridSize || data.gridData?.gridSize || Math.round(Math.sqrt(gridResults.length)) || 5,
                radius: data.searchRadius || data.gridData?.searchRadius || null,
                center_lat: data.searchLocation?.lat || locationData?.latlng?.latitude || null,
                center_lng: data.searchLocation?.lng || locationData?.latlng?.longitude || null,
                green_points: greenPoints,
                yellow_points: yellowPoints,
                red_points: redPoints,
                gray_points: grayPoints,
                total_points: totalPoints,
                grid_results: gridResults
              };

              logger.log('💾 [Heatmap Data] Saving grid points to database:', {
                keyword: heatmapDbData.keyword,
                total_points: heatmapDbData.total_points,
                green_points: heatmapDbData.green_points
              });

              const heatmapResponse = await HeatmapDataService.saveHeatmapData(heatmapDbData);

              if (heatmapResponse.success) {
                logger.log('✅ [Heatmap Data] Grid points saved to database:', heatmapResponse.data?.id);
              } else {
                logger.error('❌ [Heatmap Data] Save failed:', heatmapResponse);
              }
            } catch (error) {
              logger.error('❌ [Heatmap Data] Error saving grid points:', error);
              // Don't throw - this is a secondary save
            }
          };

          // Save heatmap data asynchronously
          saveHeatmapData();

          // ✅ Save timestamp and set restriction based on plan's cooldown days
          const businessName = locationData?.title || locationData?.name || businessTitle || '';
          const storageKey = `competitor_analysis_last_run_${businessName.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`;
          localStorage.setItem(storageKey, Date.now().toString());
          setLastAnalysisTime(Date.now());
          setCanRunAnalysis(false);

          // Get plan-based cooldown days
          const cooldownDays = getCompetitorCooldownDays(planCode);
          setDaysUntilNextAnalysis(cooldownDays);
          logger.log('⏰ [Competitor Analysis] Timestamp saved - Next analysis available in', cooldownDays, 'days (Plan:', planCode + ')');

          // Update keyword results
          if (data.keyword) {
            setKeywordResults(prev => ({
              ...prev,
              [data.keyword]: data
            }));
            setSelectedKeyword(data.keyword);
            logger.log('✅ [Competitor Analysis] Keyword result saved:', data.keyword);
          }

          // Verify data was saved to localStorage - check ALL possible keys
          logger.log('🔍 [Competitor Analysis] Business name for storage:', businessName);

          // List all possible keys that might be used
          const possibleKeys = [
            `visibeen_competitor_analysis_${businessName.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`,
            `visibeen_competitor_analysis_${(locationData?.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '_')}`,
            `visibeen_competitor_analysis_${(locationData?.title || '').toLowerCase().replace(/[^a-z0-9]+/g, '_')}`
          ].filter(k => k && k !== 'visibeen_competitor_analysis_');

          logger.log('🔍 [Competitor Analysis] Checking these possible keys:', possibleKeys);

          let foundKey = null;
          for (const key of possibleKeys) {
            if (localStorage.getItem(key)) {
              foundKey = key;
              break;
            }
          }

          if (foundKey) {
            logger.log('✅ [Competitor Analysis] Data verified in localStorage with key:', foundKey);
            logger.log('%c✅ DATA SAVED WITH KEY:', 'color: green; font-weight: bold;', foundKey);
          } else {
            // Check if ANY competitor analysis key exists
            const allCompetitorKeys = [];
            for (let i = 0; i < localStorage.length; i++) {
              const key = localStorage.key(i);
              if (key && key.startsWith('visibeen_competitor_analysis_')) {
                allCompetitorKeys.push(key);
              }
            }

            if (allCompetitorKeys.length > 0) {
              logger.log('✅ [Competitor Analysis] Found competitor data with different key:', allCompetitorKeys[0]);
              logger.log('%c✅ DATA SAVED WITH KEY:', 'color: green; font-weight: bold;', allCompetitorKeys[0]);
            } else {
              logger.error('❌ [Competitor Analysis] Data NOT found in localStorage!');
              logger.error('❌ [Competitor Analysis] Tried these keys:', possibleKeys);
              logger.log('%c❌ DATA NOT SAVED!', 'color: red; font-weight: bold;');
            }
          }

          // Show success message
          logger.log('%c🎉 COMPETITOR ANALYSIS SAVED!', 'color: green; font-size: 16px; font-weight: bold;');
          logger.log('%cData is now available and will persist on page refresh', 'color: blue; font-size: 12px;');
        }}
      />

      {/* Run Campaign modal removed — Add Campaign now redirects directly to analysis page */}

      {/* Heatmap Grid Modal with Keyword Selection */}
      <Dialog
        open={heatmapModalOpen}
        onClose={() => setHeatmapModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            maxHeight: '95vh',
            overflow: 'hidden'
          }
        }}
      >
        {/* Keyword Selection Header */}
        <Box sx={{
          background: 'linear-gradient(135deg, #0B91D6 0%, #0A7BC4 100%)',
          color: '#ffffff',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                🗺️ Heatmap Grid Data
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {businessTitle || 'Business'} • View ranking positions across the grid
              </Typography>
            </Box>
            <IconButton
              onClick={() => setHeatmapModalOpen(false)}
              sx={{ color: '#ffffff', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' } }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Keyword Tabs */}
          {Object.keys(keywordResults).length > 0 && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1, opacity: 0.9, fontWeight: 500 }}>
                📊 Select Keyword:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {Object.keys(keywordResults).map((keyword) => {
                  const hasGridData = keywordResults[keyword]?.gridData?.results?.length > 0;
                  return (
                    <Box
                      key={keyword}
                      onClick={() => {
                        if (hasGridData) {
                          setSelectedKeyword(keyword);
                          setCompetitorData(keywordResults[keyword]);
                          if (keywordResults[keyword].myBusinessScore) {
                            setGmbScore(keywordResults[keyword].myBusinessScore);
                          }
                        }
                      }}
                      sx={{
                        px: 2,
                        py: 1,
                        backgroundColor: selectedKeyword === keyword ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.2)',
                        color: selectedKeyword === keyword ? '#0B91D6' : 'white',
                        borderRadius: '8px',
                        cursor: hasGridData ? 'pointer' : 'not-allowed',
                        fontSize: '13px',
                        fontWeight: selectedKeyword === keyword ? 700 : 500,
                        transition: 'all 0.2s ease',
                        opacity: hasGridData ? 1 : 0.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        '&:hover': hasGridData ? {
                          backgroundColor: selectedKeyword === keyword ? 'rgba(255, 255, 255, 1)' : 'rgba(255, 255, 255, 0.3)',
                          transform: 'translateY(-1px)'
                        } : {}
                      }}
                    >
                      {keyword}
                      {keywordResults[keyword].myBusinessRank && (
                        <Box component="span" sx={{
                          fontSize: '11px',
                          opacity: 0.9,
                          fontWeight: 600,
                          backgroundColor: selectedKeyword === keyword ? 'rgba(11, 145, 214, 0.1)' : 'rgba(255, 255, 255, 0.2)',
                          px: 1,
                          py: 0.25,
                          borderRadius: '4px'
                        }}>
                          Rank #{keywordResults[keyword].myBusinessRank}
                        </Box>
                      )}
                      {!hasGridData && (
                        <Box component="span" sx={{ fontSize: '10px' }}>
                          (No grid data)
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>

        {/* Grid Modal Content */}
        <Box sx={{ p: 0 }}>
          {(() => {
            const currentKeywordData = selectedKeyword ? keywordResults[selectedKeyword] : null;
            if (!currentKeywordData || !currentKeywordData.gridData) {
              return (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    No heatmap data available. Run a competitor analysis first to generate grid data.
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => {
                      setHeatmapModalOpen(false);
                      setCompetitorAnalysisOpen(true);
                    }}
                    sx={{ mt: 2 }}
                  >
                    Run Competitor Analysis
                  </Button>
                </Box>
              );
            }

            const gridData = currentKeywordData.gridData;
            const results = gridData.results || [];

            // Convert grid results to pins format expected by GridDataModal
            const pins = results.map((result) => {
              const rank = result.targetBusinessRank;
              return {
                rank: rank === null || rank === undefined || rank > 20 ? '20+' : rank,
                lat: result.gridPoint?.lat,
                lng: result.gridPoint?.lng
              };
            });

            const heatmapData = {
              pins,
              results,
              businessName: businessTitle || currentKeywordData.myBusinessName || 'Business',
              keyword: selectedKeyword || currentKeywordData.keyword || 'Keyword',
              gridSize: gridData.gridSize || Math.round(Math.sqrt(results.length)),
              totalGridPoints: results.length,
              centerLocation: currentKeywordData.searchLocation || locationData?.latlng ? {
                lat: locationData?.latlng?.latitude || pickedLatLng?.lat || 30.7333,
                lng: locationData?.latlng?.longitude || pickedLatLng?.lng || 76.7794
              } : { lat: 30.7333, lng: 76.7794 },
              radius: gridData.searchRadius || currentKeywordData.searchRadius || 2
            };

            // Render inline heatmap content instead of nested dialog
            const greenCount = pins.filter(p => p.rank !== '20+' && typeof p.rank === 'number' && p.rank >= 1 && p.rank <= 3).length;
            const yellowCount = pins.filter(p => typeof p.rank === 'number' && p.rank > 3 && p.rank <= 7).length;
            const redCount = pins.filter(p => typeof p.rank === 'number' && p.rank > 7 && p.rank <= 20).length;
            const grayCount = pins.filter(p => p.rank === '20+' || p.rank === null).length;
            const numericRanks = pins.map(p => p.rank).filter(r => typeof r === 'number' && r >= 1 && r <= 20);
            const avgRank = numericRanks.length > 0
              ? (numericRanks.reduce((sum, r) => sum + r, 0) / numericRanks.length).toFixed(1)
              : 'N/A';

            return (
              <Box sx={{ p: 3, backgroundColor: '#F8F9FA' }}>
                {/* Statistics Cards */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mb: 3 }}>
                  <Paper sx={{ p: 2, textAlign: 'center', borderRadius: '12px' }}>
                    <Typography sx={{ fontSize: '13px', color: '#666', mb: 1 }}>🟢 Green (1-3)</Typography>
                    <Typography sx={{ fontSize: '28px', fontWeight: 700, color: '#34A853' }}>{greenCount}</Typography>
                    <Typography variant="caption" color="text.secondary">Excellent</Typography>
                  </Paper>
                  <Paper sx={{ p: 2, textAlign: 'center', borderRadius: '12px' }}>
                    <Typography sx={{ fontSize: '13px', color: '#666', mb: 1 }}>🟡 Yellow (4-7)</Typography>
                    <Typography sx={{ fontSize: '28px', fontWeight: 700, color: '#FBBC05' }}>{yellowCount}</Typography>
                    <Typography variant="caption" color="text.secondary">Good</Typography>
                  </Paper>
                  <Paper sx={{ p: 2, textAlign: 'center', borderRadius: '12px' }}>
                    <Typography sx={{ fontSize: '13px', color: '#666', mb: 1 }}>🔴 Red (8-20)</Typography>
                    <Typography sx={{ fontSize: '28px', fontWeight: 700, color: '#EA4335' }}>{redCount}</Typography>
                    <Typography variant="caption" color="text.secondary">Needs Work</Typography>
                  </Paper>
                  <Paper sx={{ p: 2, textAlign: 'center', borderRadius: '12px' }}>
                    <Typography sx={{ fontSize: '13px', color: '#666', mb: 1 }}>⚪ Out of Top 20</Typography>
                    <Typography sx={{ fontSize: '28px', fontWeight: 700, color: '#9E9E9E' }}>{grayCount}</Typography>
                    <Typography variant="caption" color="text.secondary">Not Ranked</Typography>
                  </Paper>
                </Box>

                {/* Quick Info */}
                <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
                  <Chip label={`Grid: ${heatmapData.gridSize} × ${heatmapData.gridSize}`} sx={{ backgroundColor: '#E3F2FD', color: '#0B91D6', fontWeight: 600 }} />
                  <Chip label={`Total Points: ${heatmapData.totalGridPoints}`} sx={{ backgroundColor: '#E3F2FD', color: '#0B91D6', fontWeight: 600 }} />
                  <Chip label={`Average Rank: ${avgRank}`} sx={{ backgroundColor: '#E3F2FD', color: '#0B91D6', fontWeight: 600 }} />
                  <Chip label={`Keyword: ${selectedKeyword}`} sx={{ backgroundColor: '#E8F5E9', color: '#2E7D32', fontWeight: 600 }} />
                </Stack>

                {/* Interactive Heatmap Map */}
                <Paper sx={{ p: 2, borderRadius: '12px' }}>
                  <Box sx={{
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                    border: '1px solid #E0E0E0'
                  }}>
                    {(() => {
                      // Generate grid points using the same function as HeatmapMapView
                      const gridPoints = generateGridPoints(
                        heatmapData.centerLocation,
                        heatmapData.radius || 2,
                        heatmapData.gridSize
                      );

                      // Map pins to grid points with their ranks
                      const mapResults = pins.map((pin, index) => {
                        const gridPoint = gridPoints[index];
                        if (!gridPoint) return null;

                        let targetBusinessRank;
                        if (pin.rank === '20+' || pin.rank === null) {
                          targetBusinessRank = null;
                        } else if (typeof pin.rank === 'number') {
                          targetBusinessRank = pin.rank;
                        } else {
                          targetBusinessRank = null;
                        }

                        return {
                          gridPoint: {
                            id: gridPoint.id,
                            lat: gridPoint.lat,
                            lng: gridPoint.lng,
                            address: `Grid ${gridPoint.id} - Rank: ${pin.rank || '20+'}`,
                            gridX: gridPoint.gridX,
                            gridY: gridPoint.gridY
                          },
                          targetBusinessRank
                        };
                      }).filter(Boolean);

                      // Calculate zoom level based on grid size
                      let mapZoom;
                      if (heatmapData.gridSize <= 3) {
                        mapZoom = 13;
                      } else if (heatmapData.gridSize <= 5) {
                        mapZoom = 12;
                      } else if (heatmapData.gridSize <= 7) {
                        mapZoom = 11;
                      } else {
                        mapZoom = 10;
                      }

                      return (
                        <HeatmapMap
                          center={heatmapData.centerLocation}
                          results={mapResults}
                          height={450}
                          zoom={mapZoom}
                        />
                      );
                    })()}
                  </Box>

                  {/* Legend */}
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 20, height: 20, borderRadius: '50%', backgroundColor: '#22C55E' }} />
                      <Typography variant="caption">Rank 1-3</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 20, height: 20, borderRadius: '50%', backgroundColor: '#FACC15' }} />
                      <Typography variant="caption">Rank 4-7</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 20, height: 20, borderRadius: '50%', backgroundColor: '#EF4444' }} />
                      <Typography variant="caption">Rank 8-20</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 20, height: 20, borderRadius: '50%', backgroundColor: '#9CA3AF' }} />
                      <Typography variant="caption">Not in Top 20</Typography>
                    </Box>
                  </Box>

                  {/* Action Button */}
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Button
                      variant="outlined"
                      onClick={() => goToHeatmap()}
                      sx={{ borderRadius: '8px' }}
                    >
                      Run New Heatmap Analysis
                    </Button>
                  </Box>
                </Paper>
              </Box>
            );
          })()}
        </Box>
      </Dialog>

      {/* Schedule Post Modal */}
      <SchedulePostModal
        open={schedulePostModalOpen}
        onClose={() => {
          setSchedulePostModalOpen(false);
          setSelectedScheduledPost(null);
        }}
        accountId={currentAccount?.name?.split('/').pop()}
        locationId={locationId}
        businessCategory={locationData?.categories?.primaryCategory?.displayName || locationData?.category || ''}
        businessName={locationData?.title || ''}
        businessAddress={businessAddress}
        businessKeywords={selectedKeyword ? [selectedKeyword] : []}
        businessCoordinates={locationData?.latlng || null}
        onPostScheduled={handlePostScheduled}
        editMode={!!selectedScheduledPost}
        editData={selectedScheduledPost}
      />

      {/* Video Upload Modal */}
      <VideoUploadModal
        open={videoUploadOpen}
        onClose={() => setVideoUploadOpen(false)}
        accountId={currentAccount?.name?.split('/').pop()}
        locationId={locationId}
        onSuccess={(mediaItem) => {
          logger.log('[BusinessProfile] Video uploaded successfully:', mediaItem);
          // Refresh videos list
          refreshVideos();
          setVideoUploadOpen(false);
        }}
      />

      {/* Scheduled Post Details Modal */}
      <Dialog
        open={scheduledPostDetailsOpen}
        onClose={handleCloseScheduledPostDetails}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{
          borderBottom: '1px solid #E5E7EB',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2,
          backgroundColor: '#F9FAFB'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Box sx={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              backgroundColor: '#34A853',
              boxShadow: '0 0 8px rgba(52, 168, 83, 0.5)'
            }} />
            <Typography sx={{ fontSize: '20px', fontWeight: 600, color: '#1F2937' }}>
              Scheduled Post
            </Typography>
          </Box>
          <IconButton onClick={handleCloseScheduledPostDetails} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3, mt: 2 }}>
          {selectedScheduledPost && (
            <Stack spacing={3}>
              {/* Success Message */}
              <Alert severity="success" sx={{ borderRadius: '8px' }}>
                <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                  Your post is scheduled! 🎉
                </Typography>
                <Typography variant="body2">
                  This post will be automatically published to your Google Business Profile on the date and time below.
                </Typography>
              </Alert>

              {/* Multiple Posts Indicator */}
              {selectedScheduledPost.allPostsForDay && selectedScheduledPost.allPostsForDay.length > 1 && (
                <Alert severity="info" sx={{ borderRadius: '8px' }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Multiple Posts Scheduled:</strong> You have {selectedScheduledPost.allPostsForDay.length} posts scheduled for this date.
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                    {selectedScheduledPost.allPostsForDay.map((post, index) => {
                      const postTime = new Date(post.scheduledTime);
                      const isCurrentPost = post.scheduledTime === selectedScheduledPost.scheduledTime;
                      return (
                        <Box
                          key={index}
                          onClick={() => {
                            if (!isCurrentPost) {
                              setSelectedScheduledPost({
                                ...post,
                                date: selectedScheduledPost.date,
                                allPostsForDay: selectedScheduledPost.allPostsForDay
                              });
                            }
                          }}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            p: 1.5,
                            borderRadius: '6px',
                            backgroundColor: isCurrentPost ? '#DBEAFE' : '#F9FAFB',
                            border: isCurrentPost ? '2px solid #0B91D6' : '1px solid #E5E7EB',
                            cursor: isCurrentPost ? 'default' : 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': !isCurrentPost ? {
                              backgroundColor: '#EBF5FB',
                              transform: 'translateX(4px)'
                            } : {}
                          }}
                        >
                          <Box sx={{
                            minWidth: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            backgroundColor: isCurrentPost ? '#0B91D6' : '#6B7280'
                          }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1F2937' }}>
                              {postTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                            <Typography sx={{ fontSize: '12px', color: '#6B7280', mt: 0.5 }}>
                              {post.summary.substring(0, 50)}{post.summary.length > 50 ? '...' : ''}
                            </Typography>
                          </Box>
                          {isCurrentPost && (
                            <Chip label="Viewing" size="small" sx={{
                              backgroundColor: '#0B91D6',
                              color: '#FFFFFF',
                              fontSize: '11px',
                              height: '20px'
                            }} />
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Alert>
              )}

              {/* Scheduled Date & Time */}
              <Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#6B7280', mb: 1 }}>
                  Scheduled Date & Time
                </Typography>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  backgroundColor: '#F3F4F6',
                  padding: '16px',
                  borderRadius: '8px'
                }}>
                  <CalendarTodayIcon sx={{ color: '#34A853', fontSize: '24px' }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#1F2937' }}>
                      {formatDateTime(selectedScheduledPost.scheduledTime)}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#6B7280', mt: 0.5 }}>
                      {(() => {
                        const now = new Date();
                        const scheduledTime = new Date(selectedScheduledPost.scheduledTime);
                        const diff = scheduledTime - now;

                        if (diff < 0) {
                          return '⏰ Publishing should happen soon...';
                        } else if (diff < 60000) {
                          return '⏰ Publishing in less than 1 minute';
                        } else if (diff < 3600000) {
                          const minutes = Math.floor(diff / 60000);
                          return `⏰ Publishing in ${minutes} minute${minutes > 1 ? 's' : ''}`;
                        } else if (diff < 86400000) {
                          const hours = Math.floor(diff / 3600000);
                          return `⏰ Publishing in ${hours} hour${hours > 1 ? 's' : ''}`;
                        } else {
                          const days = Math.floor(diff / 86400000);
                          return `⏰ Publishing in ${days} day${days > 1 ? 's' : ''}`;
                        }
                      })()}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Post Content */}
              <Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#6B7280', mb: 1 }}>
                  Post Content
                </Typography>
                <Box sx={{
                  backgroundColor: '#F9FAFB',
                  padding: '16px',
                  borderRadius: '8px',
                  border: '1px solid #E5E7EB'
                }}>
                  <Typography sx={{
                    fontSize: '14px',
                    color: '#374151',
                    lineHeight: 1.6,
                    whiteSpace: 'pre-wrap'
                  }}>
                    {selectedScheduledPost.summary}
                  </Typography>
                </Box>
              </Box>

              {/* Post Type */}
              <Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#6B7280', mb: 1 }}>
                  Post Type
                </Typography>
                <Chip
                  label={selectedScheduledPost.topicType || 'STANDARD'}
                  sx={{
                    backgroundColor: '#DBEAFE',
                    color: '#1E40AF',
                    fontWeight: 600,
                    fontSize: '12px'
                  }}
                />
              </Box>

              {/* Media */}
              {selectedScheduledPost.mediaUrl && (
                <Box>
                  <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#6B7280', mb: 1 }}>
                    Attached Media
                  </Typography>
                  <Box
                    component="img"
                    src={selectedScheduledPost.mediaUrl}
                    alt="Scheduled post media"
                    sx={{
                      maxWidth: '100%',
                      maxHeight: '200px',
                      borderRadius: '8px',
                      border: '1px solid #E5E7EB',
                      objectFit: 'contain'
                    }}
                  />
                </Box>
              )}

              {/* Call to Action */}
              {selectedScheduledPost.actionType && (
                <Box>
                  <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#6B7280', mb: 1 }}>
                    Call to Action
                  </Typography>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    backgroundColor: '#FEF3C7',
                    padding: '12px 16px',
                    borderRadius: '8px'
                  }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#92400E' }}>
                      {selectedScheduledPost.actionType}
                    </Typography>
                    {selectedScheduledPost.actionUrl && (
                      <>
                        <Typography sx={{ color: '#92400E' }}>→</Typography>
                        <Typography
                          sx={{
                            fontSize: '13px',
                            color: '#0B91D6',
                            textDecoration: 'underline',
                            cursor: 'pointer'
                          }}
                          onClick={() => window.open(selectedScheduledPost.actionUrl, '_blank')}
                        >
                          {selectedScheduledPost.actionUrl}
                        </Typography>
                      </>
                    )}
                  </Box>
                </Box>
              )}

              {/* Info Note */}
              <Alert severity="info" sx={{ borderRadius: '8px' }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Important:</strong> This post is scheduled but not yet published.
                </Typography>
                <Typography variant="body2">
                  The post will be automatically published to your Google Business Profile at the scheduled time.
                  Make sure to keep the browser tab open around the scheduled time, or return to this page to trigger the publishing.
                </Typography>
              </Alert>
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 2, borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between' }}>
          <Button
            onClick={handleDeleteScheduledPost}
            startIcon={<DeleteIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1,
              borderRadius: '8px',
              color: '#DC2626',
              border: '1px solid #DC2626',
              '&:hover': {
                backgroundColor: '#FEE2E2',
                borderColor: '#B91C1C'
              }
            }}
          >
            Delete
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={handleEditScheduledPost}
              startIcon={<EditIcon />}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1,
                borderRadius: '8px',
                color: '#0B91D6',
                border: '1px solid #0B91D6',
                '&:hover': {
                  backgroundColor: '#EBF5FB'
                }
              }}
            >
              Edit
            </Button>
            <Button
              onClick={handleCloseScheduledPostDetails}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1,
                borderRadius: '8px',
                backgroundColor: '#0B91D6',
                color: '#FFFFFF',
                '&:hover': {
                  backgroundColor: '#0A7AB8'
                }
              }}
            >
              Close
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Review Details Modal with AI Reply */}
      <Dialog
        open={reviewDetailsOpen}
        onClose={handleCloseReviewDetails}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{
          borderBottom: '1px solid #E5E7EB',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2
        }}>
          <Typography sx={{ fontSize: '20px', fontWeight: 600, color: '#1F2937' }}>
            Review Details
          </Typography>
          <IconButton onClick={handleCloseReviewDetails} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          {selectedReview && (
            <Box>
              {/* Reviewer Info */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Avatar sx={{
                  width: 56,
                  height: 56,
                  background: 'linear-gradient(135deg, #0B91D6 0%, #0B91D6 100%)',
                  fontSize: '20px',
                  fontWeight: 700
                }}>
                  {getReviewerInitials(selectedReview.reviewer?.displayName || 'Anonymous')}
                </Avatar>
                <Box>
                  <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#1F2937' }}>
                    {selectedReview.reviewer?.displayName || 'Anonymous Reviewer'}
                  </Typography>
                  <Typography sx={{ fontSize: '13px', color: '#6B7280' }}>
                    {selectedReview.createTime ? new Date(selectedReview.createTime).toLocaleDateString(undefined, {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    }) : 'Date unavailable'}
                  </Typography>
                </Box>
              </Box>

              {/* Rating */}
              <Box sx={{ mb: 3 }}>
                <Rating
                  value={toNumericRating(selectedReview.starRating)}
                  readOnly
                  precision={0.5}
                  size="large"
                  sx={{
                    '& .MuiRating-iconFilled': { color: '#F59E0B' },
                    '& .MuiRating-iconEmpty': { color: '#E5E7EB' }
                  }}
                />
              </Box>

              {/* Review Comment */}
              <Box sx={{ mb: 3, p: 2, backgroundColor: '#F9FAFB', borderRadius: '8px' }}>
                <Typography sx={{ fontSize: '14px', color: '#4B5563', lineHeight: 1.6 }}>
                  {selectedReview.comment || 'Customer left a rating without a written review.'}
                </Typography>
              </Box>

              {/* Existing Reply (if any) */}
              {selectedReview.reviewReply?.comment && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{
                    p: 2,
                    backgroundColor: '#EFF6FF',
                    borderRadius: '8px',
                    borderLeft: '4px solid #0B91D6',
                    position: 'relative',
                    '&:hover .reply-actions-single': {
                      opacity: 1
                    }
                  }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#0B91D6' }}>
                        Your Reply:
                      </Typography>
                      <Box
                        className="reply-actions-single"
                        sx={{
                          display: 'flex',
                          gap: 0.5,
                          opacity: 0,
                          transition: 'opacity 0.2s ease'
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => {
                            // Switch to edit mode
                            setReplyText(selectedReview.reviewReply.comment);
                            // Create a temporary marker to show we're editing
                            const tempReview = { ...selectedReview };
                            delete tempReview.reviewReply;
                            setSelectedReview(tempReview);
                          }}
                          sx={{
                            color: '#0B91D6',
                            padding: '4px',
                            '&:hover': {
                              backgroundColor: '#DBEAFE',
                              color: '#0980c2'
                            }
                          }}
                          title="Edit reply"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={async () => {
                            if (!window.confirm('Are you sure you want to delete this reply? This action cannot be undone.')) {
                              return;
                            }

                            try {
                              const accessToken = await GMBService.getAccessToken();
                              await GMBService.deleteReviewReply(accessToken, selectedReview.name);

                              alert('Reply deleted successfully!');
                              handleCloseReviewDetails();

                              // Refresh reviews
                              if (locationId) {
                                const accountId = currentAccount?.name?.split('/').pop();
                                if (accountId) {
                                  const updatedReviews = await GMBService.getReviews(accessToken, `accounts/${accountId}`, `locations/${locationId}`);
                                  setReviewsData(updatedReviews);
                                }
                              }
                            } catch (error) {
                              logger.error('Error deleting reply:', error);
                              alert('Failed to delete reply. Please try again.');
                            }
                          }}
                          disabled={submittingReply}
                          sx={{
                            color: '#DC2626',
                            padding: '4px',
                            '&:hover': {
                              backgroundColor: '#FEE2E2',
                              color: '#B91C1C'
                            },
                            '&:disabled': {
                              color: '#9CA3AF'
                            }
                          }}
                          title="Delete reply"
                        >
                          {submittingReply ? (
                            <CircularProgress size={16} sx={{ color: '#DC2626' }} />
                          ) : (
                            <DeleteIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Box>
                    </Box>
                    <Typography sx={{ fontSize: '14px', color: '#1F2937', lineHeight: 1.6 }}>
                      {selectedReview.reviewReply.comment}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#6B7280', mt: 1 }}>
                      {selectedReview.reviewReply.updateTime ?
                        `Replied on ${new Date(selectedReview.reviewReply.updateTime).toLocaleDateString()}` :
                        'Reply submitted'}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Reply Section (only if no existing reply) */}
              {!selectedReview.reviewReply?.comment && (
                <Box>
                  <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1F2937', mb: 2 }}>
                    Reply to this review:
                  </Typography>

                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Write your reply here..."
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        fontSize: '14px',
                        '&:hover fieldset': { borderColor: '#0B91D6' },
                        '&.Mui-focused fieldset': { borderColor: '#0B91D6' }
                      }
                    }}
                  />

                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant="outlined"
                      startIcon={generatingReply ? <CircularProgress size={16} /> : null}
                      onClick={generateAIReply}
                      disabled={generatingReply}
                      sx={{
                        textTransform: 'none',
                        borderColor: '#0B91D6',
                        color: '#0B91D6',
                        '&:hover': { borderColor: '#0980c2', backgroundColor: 'rgba(11, 145, 214, 0.04)' }
                      }}
                    >
                      {generatingReply ? 'Generating...' : '✨ Generate AI Reply'}
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: '1px solid #E5E7EB' }}>
          <Button
            onClick={handleCloseReviewDetails}
            sx={{ textTransform: 'none', color: '#6B7280' }}
          >
            Close
          </Button>
          {!selectedReview?.reviewReply?.comment && replyText && (
            <Button
              variant="contained"
              onClick={submitReviewReply}
              disabled={!replyText.trim() || submittingReply}
              startIcon={submittingReply ? <CircularProgress size={16} /> : null}
              sx={{
                textTransform: 'none',
                backgroundColor: '#0B91D6',
                '&:hover': { backgroundColor: '#0980c2' },
                '&:disabled': { backgroundColor: '#E5E7EB' }
              }}
            >
              {submittingReply ? 'Submitting...' : 'Submit Reply'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Upgrade to Pro Modal */}
      <TrialOfferSnackbar
        open={trialOfferOpen}
        onClose={() => setTrialOfferOpen(false)}
        trialToken={(function () { try { const i = sessionStorage.getItem('trial_invite'); return i ? JSON.parse(i).token : null; } catch (_) { return null; } })()}
        source={(function () { try { const i = sessionStorage.getItem('trial_invite'); return i ? JSON.parse(i).source : null; } catch (_) { return null; } })()}
      />
      <UpgradeToProModal
        open={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        featureName={selectedFeature}
      />

      {/* AI Auto-Reply Dialog */}
      <Dialog open={aiReplyDialogOpen} onClose={() => setAiReplyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{
          fontFamily: 'Inter, sans-serif',
          fontSize: '20px',
          fontWeight: 600,
          color: '#0B91D6',
          borderBottom: '1px solid #e5e7eb',
          pb: 2
        }}>
          Activate AI Review Reply
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={3}>
            {/* Trigger Time Selection */}
            <div data-tutorial="ai-reply-time" style={{ width: '100%' }}>
              <FormControl fullWidth>
                <InputLabel sx={{ fontFamily: 'Inter, sans-serif' }}>When to Reply</InputLabel>
                <Select
                  value={aiReplySelectedTime}
                  onChange={(e) => setAiReplySelectedTime(e.target.value)}
                  label="When to Reply"
                  sx={{ fontFamily: 'Inter, sans-serif' }}
                >
                  <MenuItem value="Immediately">Immediately</MenuItem>
                  <MenuItem value="After 1 hour">After 1 hour</MenuItem>
                  <MenuItem value="After 2 hours">After 2 hours</MenuItem>
                  <MenuItem value="After 4 hours">After 4 hours</MenuItem>
                  <MenuItem value="After 24 hours">After 24 hours</MenuItem>
                </Select>
              </FormControl>
            </div>

            {/* Rating Selection */}
            <Box>
              <Typography variant="body2" sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                mb: 2,
                color: '#374151'
              }}>
                Select Ratings to Auto-Reply To
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                {['5', '4', '3', '2', '1'].map((rating) => (
                  <Chip
                    key={rating}
                    label={`${rating} Star${rating !== '1' ? 's' : ''}`}
                    onClick={() => handleAiReplyRatingToggle(rating)}
                    color={aiReplySelectedRatings.includes(rating) ? 'primary' : 'default'}
                    variant={aiReplySelectedRatings.includes(rating) ? 'filled' : 'outlined'}
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '14px',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: aiReplySelectedRatings.includes(rating)
                          ? 'rgba(11, 145, 214, 0.8)'
                          : 'rgba(11, 145, 214, 0.1)'
                      }
                    }}
                  />
                ))}
              </Stack>
            </Box>

            {/* Info Text */}
            <Box sx={{
              backgroundColor: '#f3f4f6',
              p: 2,
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <Typography variant="body2" sx={{
                fontFamily: 'Inter, sans-serif',
                color: '#6b7280',
                fontSize: '13px',
                lineHeight: '18px'
              }}>
                <strong>Note:</strong> AI will automatically generate appropriate replies based on the review content and rating.
                Replies will be sent to Google reviews only.
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={() => setAiReplyDialogOpen(false)}
            sx={{
              fontFamily: 'Inter, sans-serif',
              textTransform: 'none',
              color: '#6b7280'
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAiReplySave}
            variant="contained"
            disabled={aiReplySaving || aiReplySelectedRatings.length === 0}
            data-tutorial="set-ai-reply"
            sx={{
              fontFamily: 'Inter, sans-serif',
              textTransform: 'none',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)' },
              '&:disabled': { backgroundColor: '#d1d5db' }
            }}
          >
            {aiReplySaving ? 'Activating...' : 'Set AI Auto Reply'}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default BusinessProfile;
















